import { savePollArchive } from './poll-archive.js';
import { preEventCountdown } from './pre-event-countdown.js';
import { createPreEventShare, type PreEventShare } from './pre-event-share.js';
import { validPollVote, pollResultCsv } from './poll-results.js';
import { copyText } from '../clipboard.js';
import { installControlsAutoHide } from './controls-auto-hide.js';
import { securityOverviewRows } from './security-overview.js';
import { handleMeetingBack } from './mobile-back.js';
import { installWebMeetingBack } from './web-meeting-back.js';
import '../styles/conference.css';
import { Track, RemoteTrack, DisconnectReason } from 'livekit-client';
import { LiveKitClientManager, ChatMessage, captureDisplayMedia } from './livekit-client.js';
import { P2PMeshManager } from './p2p-mesh-manager.js';
import { VORBELLO_LOGO } from '../assets/logo.js';
import { APP_VERSION, APP_BUILD_DATE, APP_BUILD_TAG } from '../version.js';
import { fixWebmDuration } from './webm-duration-fixer.js';
import { formatChatTime } from './chat-time.js';
import { recordingScene, recordingFit } from './recording-scene.js';

export interface ConferenceConfig {
  countdownStartsAt?: string;
  roomName: string;
  roomSlug: string;
  meetingInviteUrl?: string;
  roomInviteUrl?: string;
  encryptionKey?: Uint8Array;
  isWebinar?: boolean;
  webinarAudience?: boolean;
  requestWebinarPermission?: (participantId: string, medium: 'camera' | 'microphone', allow: boolean) => Promise<void>;
  admission?: {
    list: () => Promise<Array<{id:string;name:string;expiresAt:number}>>;
    decide: (id:string,decision:'approved'|'rejected') => Promise<void>;
  };
  token: string;
  requiresVerifiedServer?: boolean;
  onServerConnected?: () => void;
  onAttendanceParticipantConnected?: (identity: string, name: string) => void;
  onAttendanceParticipantDisconnected?: (identity: string) => void;
  pollArchiveOrganization?: string;
  livekitUrl: string;
  displayName: string;
  avatarUrl?: string;
  appMode?: 'demo' | 'production';
  simulatedParticipants?: number;
  initialCameraEnabled?: boolean;
  initialMicEnabled?: boolean;
  initialStream?: MediaStream | null;
  isAdmin?: boolean;
  isPlatformAdmin?: boolean;
  isModerator?: boolean;
  isHost?: boolean;
  canRecord?: boolean;
  isCreator?: boolean;
  role?: 'ADMIN' | 'MODERATOR' | 'MEMBER' | 'admin' | 'moderator' | 'user';
  roomSettings?: {
    type?: 'permanent' | 'meeting';
    muteOnEntry?: boolean;
    cameraOffOnEntry?: boolean;
    waitingRoomEnabled?: boolean;
    waitForHost?: boolean;
    allowJoinBeforeHost?: boolean;
    allowGuestInvites?: boolean;
    allowScreenShare?: 'hosts_only' | 'everyone';
    attendanceTracking?: boolean;
    autoRecord?: boolean;
    autoRecordFormat?: 'video-fhd' | 'video-hd' | 'audio';
    autoRecordLayout?: 'composite' | 'screen_only' | 'cameras_only';
    autoRecordContainer?: 'webm' | 'mp4';
    autoRecordNotify?: boolean;
    creatorName?: string;
    pinCode?: string;
    accessPolicy?: 'invitation' | 'org_members' | 'public';
    screenShareQuality?: 'hd' | 'fullhd';
    cameraQuality?: 'hd' | 'fullhd';
  };
  screenShareQuality?: 'hd' | 'fullhd';
  cameraQuality?: 'hd' | 'fullhd';
  onEndMeeting?: () => Promise<void>;
  onLeave: (reason?: string) => void;
}

// Crisp SVG Vector Icons for Ultra-Premium Aesthetics
const ICONS = {
  micOn: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  micOff: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  camOn: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>`,
  camOff: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="m22 8-6 4 6 4V8Z"/><path d="M14 6H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8"/><path d="m10 10-6 6"/></svg>`,
  screen: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/><path d="m9 10 3-3 3 3"/><path d="M12 7v6"/></svg>`,
  chat: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  users: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  leave: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.8 19.8 0 0 1-3.12-8.68A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/><line x1="23" x2="1" y1="1" y2="23"/></svg>`,
  moreVert: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>`,
  pencil: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>`,
  pin: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="17" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>`,
  trash: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>`,
  micSm: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  micOffSm: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  hourglass: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>`,
  userX: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" x2="9" y1="9" y2="15"/><line x1="9" x2="15" y1="9" y2="15"/></svg>`,
  hand: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></svg>`,
};

export class ConferenceUI {
  private preEventShare: PreEventShare | null = null;
  private endingForAll = false;
  private pipRefreshTimer?: ReturnType<typeof setInterval>;
  private pipVideo?: HTMLVideoElement;
  private disposeWebMeetingBack?: () => void;
  private disposeControlsAutoHide?: () => void;
  private backgroundImageUrl?: string;
  private config: ConferenceConfig;
  private manager: LiveKitClientManager;
  private container: HTMLElement | null = null;
  private timerInterval: any = null;
  private elapsedSeconds = 0;
  private isChatOpen = false;
  private isParticipantsOpen = false;
  private pinnedIdentity: string | null = null;
  private isStageLockedByAdmin: boolean = false;
  private messages: ChatMessage[] = [];
  private chatReplyTo: ChatMessage['replyTo'] = undefined;
  private chatReactions = new Map<string, Map<string, string>>();
  private participantNameOverrides = new Map<string, string>();
  private isLocalHandRaised = false;
  private unreadChatCount = 0;
  private userExplicitlyMuted = false;
  private participants: Array<{
    identity: string;
    name: string;
    isSpeaking: boolean;
    isMuted?: boolean;
    isCamOff?: boolean;
    isHandRaised?: boolean;
    role?: 'admin' | 'moderator' | 'user';
    avatarUrl?: string;
  }> = [];
  private p2pMesh: P2PMeshManager | null = null;
  private isP2PMode = false;
  private isScreenShareFlowInProgress = false;
  private tabRelayCleanup: (() => void) | null = null;

  // Local Meeting Recording State (Zoom Style)
  private isRecording = false;
  private roomRecordingActive = false;
  private roomRecorderName = '';
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingSeconds = 0;
  private recordingStartTime = 0;
  private recordingStopTime = 0;
  private recordingInterval: any = null;
  private recordingStream: MediaStream | null = null;
  private recordingMode: 'video' | 'audio' = 'video';
  private selectedRecordingType: 'video-hd' | 'video-fhd' | 'audio' = 'video-hd';
  private selectedRecordingLayout: 'composite' | 'screen_only' | 'cameras_only' = 'composite';
  private selectedRecordingContainer: 'webm' | 'mp4' = 'webm';
  private recordingContainer: 'webm' | 'mp4' = 'webm';
  private recordingQuality: 'hd' | 'fhd' = 'hd';
  private recordedMimeType: string = '';
  private recordingCanvasInterval: any = null;
  private recordingAudioCtx: AudioContext | null = null;
  private recordingAudioDest: MediaStreamAudioDestinationNode | null = null;
  private recordingAudioNodes: AudioNode[] = [];
  private recordingLocalMicSource: MediaStreamAudioSourceNode | null = null;
  private recordingLocalMicTrack: MediaStreamTrack | null = null;
  private recordingScreenAudioSource: MediaStreamAudioSourceNode | null = null;
  private recordingScreenAudioTrack: MediaStreamTrack | null = null;
  private recordingAudioSyncInterval: any = null;
  private telemetryInterval: any = null;
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  // Screen/Window picker state
  private availableSources: Array<{ id: string; name: string; thumbnail: string | null; isScreen: boolean }> = [];
  private selectedSourceId: string | null = null;
  private selectedSourceName: string = '';
  private currentPickerTab: 'windows' | 'screens' = 'windows';
  private isShareAudioEnabled: boolean = false;
  private selectedScreenQuality: 'hd' | 'fullhd' = 'hd';
  private selectedCameraQuality: 'hd' | 'fullhd' = 'hd';
  private remotePendingRetryCount: number = 0;

  // Global moderation toggle states
  private isAllMuted = false;
  private isAllCamOff = false;
  private webinarMicAllowed = false;
  private webinarCameraAllowed = false;
  private webinarPermissionQueue: Array<'microphone' | 'camera'> = [];
  private webinarPermissionDialog: HTMLElement | null = null;

  // Web Audio VU Meter state
  private vuAudioContext: AudioContext | null = null;
  private vuMediaStream: MediaStream | null = null;
  private vuAnimationId: number | null = null;
  private trackHealthInterval: any = null;
  private localAudioMonitorInterval: any = null;
  private localAudioMonitorCtx: AudioContext | null = null;
  private localAudioMonitorAnalyser: AnalyserNode | null = null;
  private currentViewMode: 'gallery' | 'speaker' | 'studio' = 'gallery';
  private studioCurrentPage: number = 1;
  private studioPageSize: number = 8;
  private userExplicitlyChangedViewMode = false;
  private activeSpeakerIdentity: string | null = null;
  private selectedVideoEffect: string = 'none';
  private lastAttachedRemoteTrack: RemoteTrack | null = null;


  // Screen Audio Loopback Safeguard state
  private localAudioBus: BroadcastChannel | null = null;
  private isLocalMachineScreenSharing: boolean = false;
  private isRemoteScreenAudioMuted: boolean = false;

  // Active Speaker Anti-Flicker Arbiter (Zoom & Google Meet Hysteresis)
  private currentStageSpeakerId: string | null = null;
  private stageSpeakerSwitchTime: number = 0;
  private candidateSpeakerId: string | null = null;
  private candidateSpeakerStartTime: number = 0;
  private lastStageRenderedSpeakerId: string | null = null;
  private readonly SPEAKER_MIN_HOLD_MS = 1500;
  private readonly SPEAKER_TAKEOVER_MS = 500;

  // Whiteboard state
  private isWhiteboardOpen = false;
  private wbCanvas: HTMLCanvasElement | null = null;
  private wbCtx: CanvasRenderingContext2D | null = null;
  private wbColor = '#38bdf8';
  private wbSize = 3;
  private wbTool: 'pen' | 'rect' | 'circle' | 'eraser' = 'pen';
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;

  private isPollsOpen = false;
  private pollVoters = new Set<string>();
  private activePoll: { id: string; question: string; options: string[]; votes: { [optIndex: number]: number }; totalVotes: number; myVoteIndex: number | null; creatorName: string; creatorIdentity: string; isClosed: boolean } | null = null;
  public role: 'admin' | 'moderator' | 'user' = 'user';
  public isAdmin: boolean = false;
  public isModerator: boolean = false;
  public isHost: boolean = false;

  // Waiting Room & In-meeting File Sharing State
  public isWaitingInLobby = false;
  private waitingParticipants: Array<{ identity: string; name: string; timestamp: number }> = [];
  private admissionPollTimer: ReturnType<typeof setInterval> | null = null;
  private admissionPolling = false;
  private admissionStopped = false;
  private pendingFileAttachment: { name: string; size: number; type: string; dataUrl: string } | null = null;
  private waitingRoomKnockInterval: any = null;
  private participantSearchQuery: string = '';

  public get canInvite(): boolean {
    if (this.isAdmin || this.isModerator) return true;
    return this.config.roomSettings?.allowGuestInvites !== false;
  }

  constructor(config: ConferenceConfig) {
    this.config = config;
    const adminLoggedIn = typeof localStorage !== 'undefined' && localStorage.getItem('vorbello_admin_authenticated') === 'true';
    const moderatorLoggedIn = typeof localStorage !== 'undefined' && localStorage.getItem('vorbello_moderator_authenticated') === 'true';
    const rawRole = (config.role || (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_user_role') : null) || '').toLowerCase();

    if ((config.isAdmin || rawRole === 'admin') && adminLoggedIn) {
      this.role = 'admin';
      this.isAdmin = true;
      this.isModerator = true;
      this.isHost = config.isHost !== undefined ? config.isHost : true;
      this.currentViewMode = 'gallery';
      this.pinnedIdentity = null;
    } else if (config.isModerator || moderatorLoggedIn || rawRole === 'moderator') {
      this.role = 'moderator';
      this.isAdmin = false;
      this.isModerator = true;
      this.isHost = Boolean(config.isHost);
      this.currentViewMode = 'gallery';
      this.pinnedIdentity = null;
    } else {
      this.role = 'user';
      this.isAdmin = false;
      this.isModerator = false;
      this.isHost = false;
    }

    if (this.config.roomSettings?.autoRecordLayout) {
      this.selectedRecordingLayout = this.config.roomSettings.autoRecordLayout;
    }
    const isDesktopApp = typeof window !== 'undefined' && Boolean((window as any).vorbelloDesktop?.isDesktop);
    const savedContainer = (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_default_auto_record_container') : null) as any;
    if (this.config.roomSettings?.autoRecordContainer) {
      this.selectedRecordingContainer = (this.config.roomSettings.autoRecordContainer === 'mp4' && isDesktopApp) ? 'mp4' : 'webm';
    } else if (savedContainer === 'mp4' && isDesktopApp) {
      this.selectedRecordingContainer = 'mp4';
    }

    const configScreenQ = this.config.screenShareQuality || this.config.roomSettings?.screenShareQuality;
    const savedScreenQ = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_screen_quality') : null;
    this.selectedScreenQuality = (configScreenQ || savedScreenQ || 'hd') as 'hd' | 'fullhd';

    const configCamQ = this.config.cameraQuality || this.config.roomSettings?.cameraQuality;
    const savedCamQ = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_camera_quality') : null;
    this.selectedCameraQuality = (configCamQ || savedCamQ || 'hd') as 'hd' | 'fullhd';

    this.manager = new LiveKitClientManager({
      onTrackSubscribed: (track, pub, participant) => {
        console.info('[LiveKit UI] TrackSubscribed:', track.kind, track.source, 'from', participant.identity);
        if (track.kind === Track.Kind.Audio) {
          this.ensureRemoteAudioElement(participant.identity, track, pub);
          if (this.isRecording) {
            setTimeout(() => this.syncRecordingAudioInputs(), 150);
          }
        } else if (track.kind === Track.Kind.Video) {
          const isScreenShare = track.source === Track.Source.ScreenShare || pub?.source === Track.Source.ScreenShare || pub?.trackName === 'screen_share' || (pub as any)?.name === 'screen_share' || (track as any)?.name === 'screen_share';
          if (isScreenShare) {
            console.info('[LiveKit UI] Remote screen share track subscribed from', participant.identity);
            const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
            if (spotlightVideo) {
              spotlightVideo.muted = true;
              spotlightVideo.playsInline = true;
              spotlightVideo.setAttribute('muted', '');
              spotlightVideo.setAttribute('playsinline', '');
              if (this.lastAttachedRemoteTrack && this.lastAttachedRemoteTrack !== track) {
                try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
              }
              this.lastAttachedRemoteTrack = track;
              try {
                track.attach(spotlightVideo);
                console.info('[LiveKit UI] Direct attached remote screen track to spotlight video');
                spotlightVideo.play().catch(e => {
                  if (e.name !== 'AbortError') console.warn('Spotlight play error:', e);
                });
              } catch (e) {
                console.warn('[LiveKit UI] Error direct attaching remote screen share:', e);
              }
            }
            this.updateStageLayout();
          } else {
            // Camera video track from participant
            const p = this.participants.find(part => part.identity === participant.identity);
            if (p) p.isCamOff = false;
            const tile = document.getElementById(`tile-${participant.identity}`);
            if (tile) {
              let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
              const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
              if (!videoEl) {
                videoEl = document.createElement('video');
                videoEl.className = 'remote-cam-video';
                videoEl.autoplay = true;
                videoEl.playsInline = true;
                videoEl.setAttribute('playsinline', '');
                videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; border-radius:inherit; z-index:1;';
                tile.prepend(videoEl);
              }
              track.attach(videoEl);
              this.adaptVideoElementFit(videoEl, tile);
              videoEl.style.display = 'block';
              videoEl.play().catch(() => {});
              if (avatar) avatar.style.display = 'none';
            }
          }
          setTimeout(() => this.updateStageLayout(), 100);
          setTimeout(() => this.updateStageLayout(), 400);
        }
        this.addOrUpdateParticipant(participant.identity, participant.name || 'Participant');
        this.renderVideoGrid();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onTrackUnsubscribed: (track, _pub, participant) => {
        console.info('[LiveKit UI] TrackUnsubscribed:', track.kind, track.source, 'from', participant.identity);
        if (track.kind === Track.Kind.Audio) {
          const isScreenAudio = _pub?.source === Track.Source.ScreenShareAudio || track?.source === Track.Source.ScreenShareAudio;
          const elementId = isScreenAudio ? `audio-remote-${participant.identity}-screen` : `audio-remote-${participant.identity}`;
          const audioEl = document.getElementById(elementId) as HTMLAudioElement | null;
          if (audioEl) {
            track.detach(audioEl);
            audioEl.srcObject = null;
            audioEl.remove();
          }
        } else if (track.kind === Track.Kind.Video) {
          if (track.source === Track.Source.Camera) {
            const tile = document.getElementById(`tile-${participant.identity}`);
            if (tile) {
              const videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
              if (videoEl) {
                track.detach(videoEl);
                videoEl.srcObject = null;
                videoEl.style.display = 'none';
              }
              const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
              if (avatar) avatar.style.display = 'flex';
            }
          } else if (track.source === Track.Source.ScreenShare || _pub?.source === Track.Source.ScreenShare || _pub?.trackName === 'screen_share' || (_pub as any)?.name === 'screen_share' || (track as any)?.name === 'screen_share') {
            console.info('[LiveKit UI] Remote screen share unsubscribed/ended by', participant.identity);
            const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
            if (spotlightVideo) {
              try { track.detach(spotlightVideo); } catch (_) {}
              spotlightVideo.srcObject = null;
            }
            this.lastAttachedRemoteTrack = null;
            this.remotePendingRetryCount = 0;
            this.updateStageLayout();
            this.renderVideoGrid();
            this.updateControlsState();
            setTimeout(() => {
              this.updateStageLayout();
              this.renderVideoGrid();
            }, 100);
            setTimeout(() => {
              this.updateStageLayout();
              this.renderVideoGrid();
            }, 300);
          }
        }
        this.renderVideoGrid();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onTrackMuted: (pub, participant) => {
        console.info('[LiveKit] Track muted:', pub.kind, pub.source, participant.identity);
        const p = this.participants.find(part => part.identity === participant.identity);
        if (p) {
          if (pub.kind === Track.Kind.Audio) {
            if (pub.source === Track.Source.ScreenShareAudio) {
              const screenAudioEl = document.getElementById(`audio-remote-${participant.identity}-screen`) as HTMLAudioElement | null;
              if (screenAudioEl) screenAudioEl.muted = true;
            } else {
              p.isMuted = true;
              // IMMEDIATELY MUTE REMOTE AUDIO ELEMENT WITHOUT PAUSING (preserves autoplay permissions on unmute)
              const audioEl = document.getElementById(`audio-remote-${participant.identity}`) as HTMLAudioElement | null;
              if (audioEl) {
                audioEl.muted = true;
              }
              this.setParticipantSpeaking(participant.identity, false);
            }
          } else if (pub.kind === Track.Kind.Video) {
            const isScreen = pub.source === Track.Source.ScreenShare || pub.trackName === 'screen_share' || (pub as any)?.name === 'screen_share';
            if (isScreen) {
              console.info('[LiveKit UI] Remote screen share muted by', participant.identity);
              this.updateStageLayout();
            } else {
              const hasLiveTrack = Boolean(pub.track && (pub.track as any).mediaStreamTrack?.readyState === 'live');
              if (!hasLiveTrack) {
                p.isCamOff = true;
                const tile = document.getElementById(`tile-${participant.identity}`);
                if (tile) {
                  const videoEl = tile.querySelector('video.remote-cam-video') as HTMLElement | null;
                  const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                  if (videoEl) videoEl.style.display = 'none';
                  if (avatar) avatar.style.display = 'flex';
                }
              }
            }
          }
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
      },
      onTrackUnmuted: (pub, participant) => {
        console.info('[LiveKit] Track unmuted:', pub.kind, pub.source, participant.identity);
        const p = this.participants.find(part => part.identity === participant.identity);
        if (p) {
          if (pub.kind === Track.Kind.Audio) {
            if (pub.source === Track.Source.ScreenShareAudio) {
              const elementId = `audio-remote-${participant.identity}-screen`;
              let audioEl = document.getElementById(elementId) as HTMLAudioElement | null;
              if (!audioEl && pub.track) {
                this.ensureRemoteAudioElement(participant.identity, pub.track, pub);
              } else if (audioEl) {
                const shouldMute = participant.identity === this.manager.getRoom()?.localParticipant?.identity ||
                  this.isRemoteScreenAudioMuted;
                audioEl.muted = shouldMute;
                audioEl.volume = shouldMute ? 0 : 1.0;
                if (pub.track) pub.track.attach(audioEl);
                if (!shouldMute && audioEl.paused) audioEl.play().catch(() => {});
              }
            } else {
              p.isMuted = false;
              let audioEl = document.getElementById(`audio-remote-${participant.identity}`) as HTMLAudioElement | null;
              if (!audioEl && pub.track) {
                this.ensureRemoteAudioElement(participant.identity, pub.track, pub);
              } else if (audioEl) {
                audioEl.muted = false;
                audioEl.volume = 1.0;
                if (pub.track) pub.track.attach(audioEl);
                if (audioEl.paused) {
                  audioEl.play().catch(() => {});
                }
              }
              this.setParticipantSpeaking(participant.identity, false);
            }
          } else if (pub.kind === Track.Kind.Video) {
            if (pub.source === Track.Source.Camera) {
              p.isCamOff = false;
              const tile = document.getElementById(`tile-${participant.identity}`);
              if (tile) {
                let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
                const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                if (!videoEl) {
                  videoEl = document.createElement('video');
                  videoEl.className = 'remote-cam-video';
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  videoEl.setAttribute('playsinline', '');
                  videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; border-radius:inherit; z-index:1;';
                  tile.prepend(videoEl);
                }
                if (pub.track) {
                  pub.track.attach(videoEl);
                }
                this.adaptVideoElementFit(videoEl, tile);
                videoEl.style.display = 'block';
                videoEl.play().catch(() => {});
                if (avatar) avatar.style.display = 'none';
              }
            } else if (pub.source === Track.Source.ScreenShare) {
              console.info('[LiveKit UI] Remote screen share unmuted by', participant.identity);
              this.updateStageLayout();
            }
          }
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
      },
      onReconnecting: () => {
        console.warn('[LiveKit] Reconnecting to room...');
        const pill = document.getElementById('conf-status-pill');
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> Reconectare Rețea în Curs...`;
        }
      },
      onReconnected: () => {
        console.info('[LiveKit UI] Reconnected - performing full track resync');
        const pill = document.getElementById('conf-status-pill');
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Conexiune OK`;
        }
        // Force full resync of all remote audio/video after reconnect
        this.resyncAllRemoteTracks();
        this.renderVideoGrid();
        this.updateStageLayout();
      },
      onTrackPublished: (pub, participant) => {
        console.info('[LiveKit UI] TrackPublished:', pub.kind, pub.source, 'from', participant.identity);
        this.addOrUpdateParticipant(participant.identity, participant.name || 'Participant');
        if (!pub.isSubscribed) {
          pub.setSubscribed(true);
        }
        this.updateStageLayout();
        this.renderVideoGrid();
        this.updateControlsState();
        const myIdentity = this.manager.getRoom()?.localParticipant?.identity || '';
        const isRemote = participant.identity !== myIdentity && participant.identity !== 'self';
        const isScreenShare = pub.source === Track.Source.ScreenShare || pub.trackName === 'screen_share' || (pub as any).name === 'screen_share';
        if (isScreenShare && isRemote) {
          if (this.manager.isScreenShareEnabled) {
            console.info('[LiveKit UI] Remote screen share arrived, yielding local screen share (Zoom/Meet standard)...');
            this.showFloatingToast(`🖥️ ${participant.name || 'Un participant'} a preluat prezentarea ecranului. Partajarea ta a fost oprită.`, 4500);
            this.manager.enableScreenShare(false).catch(e => console.warn(e));
            this.handleScreenShareState();
          }
          setTimeout(() => this.updateStageLayout(), 250);
          setTimeout(() => this.updateStageLayout(), 600);
          setTimeout(() => this.updateStageLayout(), 1200);
        }
      },
      onTrackUnpublished: (pub, participant) => {
        console.info('[LiveKit UI] TrackUnpublished:', pub.kind, pub.source, 'from', participant.identity);
        const myIdentity = this.manager.getRoom()?.localParticipant?.identity || '';
        const isRemote = participant.identity !== myIdentity && participant.identity !== 'self';
        const isScreenShare = pub.source === Track.Source.ScreenShare || pub.trackName === 'screen_share' || (pub as any).name === 'screen_share';
        if (isScreenShare && isRemote) {
          if (this.manager.isScreenShareEnabled) {
            console.info('[LiveKit UI] Remote screen track unpublished, keeping spotlight for local share.');
            this.updateStageLayout();
            return;
          }
          console.info('[LiveKit UI] Remote screen share unpublished by', participant.identity);
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          if (spotlightVideo) {
            if (pub.track) {
              try { pub.track.detach(spotlightVideo); } catch (_) {}
            }
            if (this.lastAttachedRemoteTrack) {
              try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
              this.lastAttachedRemoteTrack = null;
            }
            spotlightVideo.srcObject = null;
          }
          this.remotePendingRetryCount = 0;
          this.updateStageLayout();
          this.renderVideoGrid();
          this.updateControlsState();
          setTimeout(() => {
            this.updateStageLayout();
            this.renderVideoGrid();
          }, 150);
        }
      },
      onLocalTrackPublished: (pub) => {
        console.info('[LiveKit UI] LocalTrackPublished:', pub.kind, pub.source);
        if (pub.source === Track.Source.Camera) {
          const selfP = this.participants.find(p => p.identity === 'self');
          if (selfP) selfP.isCamOff = false;
        }
        this.updateSelfTile();
        this.updateControlsState();
        this.updateStageLayout();
        this.renderVideoGrid();
      },
      onLocalTrackUnpublished: (pub) => {
        console.info('[LiveKit UI] LocalTrackUnpublished:', pub.kind, pub.source);
        if (pub.source === Track.Source.Camera) {
          const selfP = this.participants.find(p => p.identity === 'self');
          if (selfP) selfP.isCamOff = true;
        }
        this.updateSelfTile();
        this.updateControlsState();
        this.updateStageLayout();
        this.renderVideoGrid();
      },
      onLocalPermissionsChanged: () => { this.refreshWebinarPermissions(); },
      onParticipantConnected: (participant) => {
        this.config.onAttendanceParticipantConnected?.(participant.identity, participant.name || 'Participant');
        this.addOrUpdateParticipant(participant.identity, participant.name || 'Participant');
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
        this.updateStageLayout();

        // Send only our own current hand state and text history to the new peer.
        // Strict waiting rooms require server-side admission before sharing history.
        setTimeout(() => {
          if (this.manager.getRoom()?.state !== 'connected' || this.isWaitingInLobby) return;
          const local = this.manager.getRoom()?.localParticipant;
          if (!local) return;
          void this.manager.sendDataMessage({ type: 'hand_raise', identity: local.identity,
            name: this.manager.displayName || local.name, isRaised: this.isLocalHandRaised }, participant.identity);
          if (this.config.roomSettings?.waitingRoomEnabled) return;
          const history = this.messages.filter(m => !m.file && typeof m.text === 'string' && m.text.length <= 4000).slice(-500);
          for (const message of history) {
            void this.manager.sendDataMessage({ type: 'session_history', messages: [message] }, participant.identity);
          }
        }, 1000);

        // LiveKit's original participant name does not change when only a data message is sent.
        // Share our current name with peers who join after a local rename.
        const local = this.manager.getRoom()?.localParticipant;
        if (local && this.manager.displayName && this.manager.displayName !== local.name) {
          setTimeout(() => {
            if (this.manager.getRoom()?.state !== 'connected') return;
            void this.manager.sendDataMessage({ type: 'moderation', action: 'rename-user',
              targetIdentity: local.identity, newName: this.manager.displayName });
          }, 500);
        }

        // If I am currently sharing screen, immediately send screen_share_status so the new peer sees it instantly!
        if (this.manager.isScreenShareEnabled) {
          this.manager.sendDataMessage({
            type: 'screen_share_status',
            isSharing: true,
            identity: this.manager.getRoom()?.localParticipant?.identity || '',
            sharerName: this.manager.displayName || this.manager.getRoom()?.localParticipant?.name || 'Participant',
          }).catch(() => {});
        }

        // Only the Primary Room Host broadcasts layout sync to new participants, and only if a stage is currently pinned
        if (this.isHost && this.pinnedIdentity) {
          setTimeout(() => {
            this.broadcastAdminLayoutSync().catch(() => {});
          }, 800);
        }

        // If conference is currently recorded by this host, immediately sync recording status to the new participant
        if (this.isRecording) {
          setTimeout(() => {
            this.manager.sendDataMessage({
              type: 'recording_status',
              isRecording: true,
              recorderName: this.config.displayName || 'Gazdă',
              recordingSeconds: this.recordingSeconds,
            }).catch(() => {});
          }, 600);
        }

        if (this.isWaitingInLobby) {
          const isStrictWaitingRoom = !!this.config.roomSettings?.waitingRoomEnabled;
          try {
            const meta = JSON.parse(participant.metadata || '{}');
            const isHostPart = meta.isAdmin || meta.isHost || meta.role === 'admin' || meta.role === 'ADMIN';
            if (isHostPart) {
              if (isStrictWaitingRoom) {
                console.info('[ConferenceUI] Host joined room in Waiting Room mode; sending knock for admission approval...');
                this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
              } else {
                console.info('[ConferenceUI] Host has joined the room! Automatically admitting waiting participant from wait-for-host...');
                this.handleWaitingRoomDecision('admitted');
              }
            }
          } catch (_) {}
        }
      },
      onParticipantDisconnected: (participant) => {
        this.config.onAttendanceParticipantDisconnected?.(participant.identity);
        this.participantNameOverrides.delete(participant.identity);
        console.info('[LiveKit] Participant disconnected:', participant.identity);
        this.participants = this.participants.filter(p => p.identity !== participant.identity);
        const audioEl = document.getElementById(`audio-remote-${participant.identity}`);
        if (audioEl) audioEl.remove();
        const tile = document.getElementById(`tile-${participant.identity}`);
        if (tile) tile.remove();
        if (this.pinnedIdentity === participant.identity) this.pinnedIdentity = null;
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
      },
      onActiveSpeakersChanged: (speakers) => {
        const activeIds = new Set(speakers.map(s => s.identity));
        const room = this.manager.getRoom();
        const selfIdentity = room?.localParticipant?.identity;
        const isSelfInSpeakers = selfIdentity ? activeIds.has(selfIdentity) : false;

        // 1. Update visual speaking indicators instantly for all participant tiles
        this.participants.forEach(p => {
          if (p.identity === 'self') {
            if (isSelfInSpeakers && this.manager.isMicEnabled) {
              this.setParticipantSpeaking('self', true);
            } else {
              this.setParticipantSpeaking('self', false);
            }
          } else {
            const isSpeaking = activeIds.has(p.identity) && !p.isMuted;
            this.setParticipantSpeaking(p.identity, isSpeaking);
            if (isSpeaking) {
              const audioEl = document.getElementById(`audio-remote-${p.identity}`) as HTMLAudioElement | null;
              if (audioEl) {
                if (audioEl.muted) audioEl.muted = false;
                if (audioEl.paused) audioEl.play().catch(() => {});
              }
            }
          }
        });

        // 2. Anti-Flicker Stage Arbiter (Zoom & Google Meet Hysteresis)
        const stageArea = document.getElementById('stage-area');
        const isSpotlightActive = stageArea?.classList.contains('has-spotlight') || this.shouldUseFilmstrip() || this.currentViewMode === 'speaker';

        // Only dynamically arbitrate stage when stage is NOT locked to pinned admin and not screen sharing
        const hasRemoteScreenShare = Array.from(room?.remoteParticipants?.values() || []).some(rp => rp.isScreenShareEnabled);
        if (isSpotlightActive && !this.pinnedIdentity && !this.manager.isScreenShareEnabled && !hasRemoteScreenShare) {
          const now = Date.now();
          let topSpeakerId: string | null = null;
          for (const s of speakers) {
            const sId = s.identity === selfIdentity ? 'self' : s.identity;
            const p = this.participants.find(part => part.identity === sId);
            const isMuted = sId === 'self' ? !this.manager.isMicEnabled : !!p?.isMuted;
            if (!isMuted) {
              topSpeakerId = sId;
              break;
            }
          }

          if (topSpeakerId) {
            this.activeSpeakerIdentity = topSpeakerId;

            if (!this.currentStageSpeakerId) {
              // No current stage speaker: immediate placement
              this.currentStageSpeakerId = topSpeakerId;
              this.stageSpeakerSwitchTime = now;
              this.candidateSpeakerId = null;
              this.updateStageLayout();
            } else if (this.currentStageSpeakerId === topSpeakerId) {
              // Current speaker is continuing to speak: reset challenger
              this.candidateSpeakerId = null;
            } else {
              // Challenger wants the stage: require sustained speech & hold duration to prevent flickering
              const timeOnStage = now - this.stageSpeakerSwitchTime;
              if (this.candidateSpeakerId !== topSpeakerId) {
                this.candidateSpeakerId = topSpeakerId;
                this.candidateSpeakerStartTime = now;
              } else {
                const candidateDuration = now - this.candidateSpeakerStartTime;
                if (timeOnStage >= this.SPEAKER_MIN_HOLD_MS && candidateDuration >= this.SPEAKER_TAKEOVER_MS) {
                  this.currentStageSpeakerId = topSpeakerId;
                  this.stageSpeakerSwitchTime = now;
                  this.candidateSpeakerId = null;
                  this.updateStageLayout();
                }
              }
            }
          }
        }
      },
      onChatMessage: (msg) => {
        const existingIdx = this.messages.findIndex(m => m.id === msg.id);
        if (existingIdx !== -1) {
          this.messages[existingIdx] = msg;
        } else {
          this.messages.push(msg);
        }
        this.renderChatMessages();
        if (!this.isChatOpen && !msg.isSelf) {
          this.unreadChatCount++;
          this.updateChatBadge();
          this.playChatChime();
          this.showZoomChatNotification(msg);
        } else if (this.isChatOpen && !msg.isSelf) {
          this.playChatChime();
        }
      },
      onChatReaction: (data) => { this.applyChatReaction(data.messageId, data.emoji, data.identity, data.active); },
      onSessionHistory: (history) => {
        if (this.isWaitingInLobby || this.config.roomSettings?.waitingRoomEnabled) return;
        const localIdentity = this.manager.getRoom()?.localParticipant.identity;
        for (const msg of history.slice(0, 50)) {
          if (!msg || typeof msg.id !== 'string' || typeof msg.text !== 'string' || msg.text.length > 4000 ||
              typeof msg.senderName !== 'string' || typeof msg.senderIdentity !== 'string' || typeof msg.timestamp !== 'string' ||
              this.messages.some(m => m.id === msg.id)) continue;
          this.messages.push({ id: msg.id, text: msg.text, senderName: msg.senderName,
            senderIdentity: msg.senderIdentity, timestamp: msg.timestamp, isSelf: msg.senderIdentity === localIdentity,
            replyTo: msg.replyTo && typeof msg.replyTo.id === 'string' && typeof msg.replyTo.senderName === 'string' && typeof msg.replyTo.text === 'string' ? { id: msg.replyTo.id.slice(0,100), senderName: msg.replyTo.senderName.slice(0,160), text: msg.replyTo.text.slice(0,160) } : undefined });
        }
        this.renderChatMessages();
      },
      onFileTransferProgress: (info) => {
        let existing = this.messages.find(m => m.id === info.transferId);
        if (!existing) {
          existing = {
            id: info.transferId,
            senderName: info.senderName,
            senderIdentity: info.senderIdentity,
            text: info.text,
            timestamp: info.timestamp,
            isSelf: false,
            file: {
              name: info.name,
              size: info.size,
              type: '',
              dataUrl: '',
              progress: info.progress,
            } as any,
          };
          this.messages.push(existing);
          this.renderChatMessages();
          if (!this.isChatOpen) {
            this.unreadChatCount++;
            this.updateChatBadge();
          }
        } else {
          if (existing.file) {
            (existing.file as any).progress = info.progress;
          }
          const bar = document.getElementById(`transfer-bar-${info.transferId}`);
          const pct = document.getElementById(`transfer-pct-${info.transferId}`);
          if (bar && pct) {
            bar.style.width = `${info.progress}%`;
            pct.textContent = `${info.progress}%`;
          } else {
            this.renderChatMessages();
          }
        }
      },
      onModerationMessage: (data) => {
        console.info('[LiveKit] Moderation message received:', data);
        const room = this.manager.getRoom();
        const localPart = room?.localParticipant;
        const myIdentity = (localPart?.identity || '').trim();
        const myName = (localPart?.name || this.config.displayName || '').trim().toLowerCase();

        const senderId = (data.senderIdentity || '').trim();
        const isSender = (senderId !== '' && (senderId === myIdentity || (localPart && senderId === localPart.identity)));

        // If I am the sender who triggered this command, do not mute/disable myself:
        if (isSender) return;

        const targetId = (data.targetIdentity || '').trim();
        const targetName = (data.targetName || '').trim().toLowerCase();

        const isTargeted = targetId === 'self' ||
          (targetId !== '' && (
            targetId === myIdentity ||
            targetId.toLowerCase() === myIdentity.toLowerCase() ||
            (myIdentity.length > 3 && targetId.includes(myIdentity)) ||
            (targetId.length > 3 && myIdentity.includes(targetId))
          )) ||
          (targetName !== '' && myName !== '' && (
            targetName === myName ||
            myName.includes(targetName) ||
            targetName.includes(myName)
          ));

        if (data.action === 'mute-all' || (data.action === 'mute-user' && isTargeted)) {
          this.manager.enableMicrophone(false).then(() => {
            this.userExplicitlyMuted = true;
            this.manager.isMicEnabled = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isMuted = true;
            this.updateControlsState();
            this.setParticipantSpeaking('self', false);
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('🔇 Gazda a oprit microfonul dumneavoastră.');
          });
        } else if (data.action === 'unmute-all' || (data.action === 'unmute-user' && isTargeted)) {
          if (this.config.webinarAudience) { this.showFloatingToast(this.webinarMicAllowed ? 'Gazda vă invită să porniți microfonul.' : 'Microfonul nu este încă autorizat în webinar.'); return; }
          this.manager.enableMicrophone(true).then((enabled) => {
            if (!enabled) { this.manager.isMicEnabled = false; this.updateControlsState(); this.showFloatingToast('Microfonul nu a putut fi activat. Verificați permisiunile.'); return; }
            this.userExplicitlyMuted = false;
            this.manager.isMicEnabled = true;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isMuted = false;
            this.updateControlsState();
            this.setParticipantSpeaking('self', false);
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('🔊 Gazda a reactivat microfonul dumneavoastră.');
          }).catch(() => {
            this.showFloatingToast('🔊 Gazda vă invită să porniți microfonul.');
          });
        } else if (data.action === 'stop-cam-all' || (data.action === 'stop-cam-user' && isTargeted)) {
          this.manager.enableCamera(false).then(() => {
            this.manager.isCameraEnabled = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isCamOff = true;
            this.updateControlsState();
            this.updateSelfTile();
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('📷 Gazda a oprit camera dumneavoastră.');
          });
        } else if (data.action === 'start-cam-all' || (data.action === 'start-cam-user' && isTargeted)) {
          if (this.config.webinarAudience) { this.showFloatingToast(this.webinarCameraAllowed ? 'Gazda vă invită să porniți camera.' : 'Camera nu este încă autorizată în webinar.'); return; }
          this.manager.enableCamera(true).then((enabled) => {
            if (!enabled) { this.manager.isCameraEnabled = false; this.updateControlsState(); this.showFloatingToast('Camera nu a putut fi activată. Verificați permisiunile.'); return; }
            this.manager.isCameraEnabled = true;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isCamOff = false;
            this.updateControlsState();
            this.updateSelfTile();
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('📹 Gazda a reactivat camera dumneavoastră.');
          }).catch(() => {
            this.showFloatingToast('📹 Gazda vă invită să porniți camera.');
          });
        } else if (data.action === 'kick') {
          if (isTargeted) {
            console.warn('[LiveKit UI] This participant was kicked by host. Disconnecting...');
            this.showFloatingToast('⛔ Ați fost eliminat din conferință de către gazdă.');
            try {
              this.manager.enableMicrophone(false);
              this.manager.enableCamera(false);
            } catch (_) {}
            setTimeout(async () => {
              await this.cleanup();
              this.config.onLeave('⛔ Ați fost eliminat din conferință de către gazdă.');
            }, 300);
          } else {
            // Remove the kicked participant from our local list and stage grid
            const kickedId = data.targetIdentity;
            console.info('[LiveKit UI] Remote participant kicked by host:', kickedId, data.targetName);
            if (kickedId) {
              this.participants = this.participants.filter(p => p.identity !== kickedId && (!targetName || p.name.trim().toLowerCase() !== targetName));
              const audioEl = document.getElementById(`audio-remote-${kickedId}`);
              if (audioEl) audioEl.remove();
              const tile = document.getElementById(`tile-${kickedId}`);
              if (tile) tile.remove();
              if (this.pinnedIdentity === kickedId) this.pinnedIdentity = null;
              this.renderVideoGrid();
              this.renderParticipantsList();
              this.updateControlsState();
              this.updateStageLayout();
            }
          }
        } else if (data.action === 'rename-user') {
          const target = data.targetIdentity;
          const newName = (data.newName || '').trim();
          if (newName) {
            if (isTargeted) {
              this.config.displayName = newName;
              this.manager.displayName = newName;
              try { localStorage.setItem('vorbello_display_name', newName); } catch (_) {}
              const selfP = this.participants.find(p => p.identity === 'self');
              if (selfP) selfP.name = `${newName} (Tu)`;
              this.showFloatingToast(`✏️ Numele dumneavoastră a fost actualizat la "${newName}".`);
            } else if (target) {
              this.participantNameOverrides.set(target, newName);
              const p = this.participants.find(part => part.identity === target);
              if (p) p.name = newName;
              this.messages.forEach(message => { if (message.senderIdentity === target) message.senderName = newName; });
              if (this.isChatOpen) this.renderChatMessages();
            }
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.updateStageLayout();
          }
        } else if (data.action === 'make-cohost') {
          if (isTargeted) {
            this.role = 'moderator';
            this.isModerator = true;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.role = 'moderator';
            this.updateControlsState();
            this.renderParticipantsList();
            this.showFloatingToast('🛡️ Ați fost numit Co-Gazdă (Co-Host) de către Gazdă! Puteți partaja ecranul și modera sala.', 6000);
          } else {
            const p = this.participants.find(part => part.identity === targetId || (targetName && part.name.trim().toLowerCase() === targetName));
            if (p) p.role = 'moderator';
            this.renderParticipantsList();
            this.showFloatingToast(`🛡️ ${data.targetName || 'Un participant'} a fost promovat la Co-Gazdă (Co-Host).`);
          }
        } else if (data.action === 'revoke-cohost') {
          if (isTargeted) {
            this.role = 'user';
            this.isModerator = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.role = 'user';
            this.updateControlsState();
            this.renderParticipantsList();
            this.showFloatingToast('ℹ️ Rolul dumneavoastră de Co-Gazdă a fost revocat.');
          } else {
            const p = this.participants.find(part => part.identity === targetId || (targetName && part.name.trim().toLowerCase() === targetName));
            if (p) p.role = 'user';
            this.renderParticipantsList();
          }
        } else if (data.action === 'make-host') {
          if (isTargeted) {
            this.role = 'admin';
            this.isAdmin = true;
            this.isModerator = true;
            this.isHost = true;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.role = 'admin';
            this.updateControlsState();
            this.renderParticipantsList();
            this.showFloatingToast('👑 Ați primit rolul de Gazdă Principală (Host) a acestei conferințe!', 6000);
          } else {
            const formerHost = this.participants.find(part => part.role === 'admin' && part.identity !== targetId);
            if (formerHost) formerHost.role = 'moderator';
            const newHost = this.participants.find(part => part.identity === targetId || (targetName && part.name.trim().toLowerCase() === targetName));
            if (newHost) newHost.role = 'admin';
            this.renderParticipantsList();
            this.showFloatingToast(`👑 ${data.targetName || 'Un participant'} este acum noua Gazdă a întâlnirii.`);
          }
        }
      },
      onScreenShareStatus: async (data) => {
        console.info('[LiveKit UI] Screen share status:', data);
        const myIdentity = this.manager.getRoom()?.localParticipant?.identity || '';
        const isRemote = data.identity !== myIdentity && data.identity !== 'self';
        if (data.isSharing && isRemote) {
          // If local participant was already sharing, yield and stop local share (Zoom/Meet standard behavior)
          if (this.manager.isScreenShareEnabled) {
            console.info('[ConferenceUI] Another participant started sharing screen. Stopping local screen share...');
            this.showFloatingToast(`🖥️ ${data.sharerName || 'Un alt participant'} a preluat partajarea ecranului. Partajarea ta a fost oprită.`, 4500);
            try {
              await this.manager.enableScreenShare(false);
            } catch (_) {}
            this.handleScreenShareState();
          } else {
            this.showFloatingToast(`🖥️ ${data.sharerName} a început partajarea ecranului.`);
          }

          const room = this.manager.getRoom();
          if (room) {
            const rp = room.remoteParticipants.get(data.identity);
            if (rp) {
              const sp = rp.getTrackPublication(Track.Source.ScreenShare) ||
                Array.from(rp.trackPublications.values()).find(p => p.source === Track.Source.ScreenShare || p.trackName === 'screen_share' || (p as any).name === 'screen_share');
              if (sp && !sp.isSubscribed) {
                sp.setSubscribed(true);
              }
            }
          }
          this.updateStageLayout();
          setTimeout(() => this.updateStageLayout(), 250);
          setTimeout(() => this.updateStageLayout(), 600);
          setTimeout(() => this.updateStageLayout(), 1200);
        } else if (!data.isSharing && isRemote) {
          if (this.manager.isScreenShareEnabled) {
            console.info('[LiveKit UI] Remote screen share ended, keeping local share active.');
            this.updateStageLayout();
            return;
          }
          console.info('[LiveKit UI] Remote screen share status=false received from', data.identity);
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          if (spotlightVideo) {
            if (this.lastAttachedRemoteTrack) {
              try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
            }
            this.lastAttachedRemoteTrack = null;
            spotlightVideo.srcObject = null;
          }
          this.remotePendingRetryCount = 0;
          this.updateStageLayout();
          this.renderVideoGrid();
          this.updateControlsState();
          setTimeout(() => {
            this.updateStageLayout();
            this.renderVideoGrid();
          }, 150);
          setTimeout(() => {
            this.updateStageLayout();
            this.renderVideoGrid();
          }, 500);
        } else {
          this.updateStageLayout();
        }
      },
      onAudioPlaybackChanged: (canPlayback) => {
        console.info('[LiveKit UI] Audio playback capability changed:', canPlayback);
        this.handleAudioPlaybackBanner(!canPlayback);
      },
      onLocalScreenStreamReady: (stream) => {
        if (this.preEventShare && stream.getVideoTracks()[0] !== this.preEventShare.video) {
          this.preEventShare.dispose(); this.preEventShare = null;
        }
        console.info('[Vorbello Conference] onLocalScreenStreamReady received immediately (0ms lag)');
        const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
        if (spotlightVideo) {
          this.attachScreenStreamToSpotlight(stream, spotlightVideo);
        }
        const hasAudio = stream.getAudioTracks().length > 0;
        this.notifyLocalScreenAudioActive(hasAudio);
        if (hasAudio) {
          this.muteAllRemoteScreenAudio(true);
          this.showFloatingToast('🔊 Partajare ACTIVĂ CU SUNET (sunetul de sistem/filă se transmite tuturor)', 4500);
        } else {
          this.showFloatingToast('ℹ️ Partajare activă FĂRĂ SUNET. Sfat: Pentru sunet, la alegerea ecranului bifați „Partajează și sunetul din sistem” (Share audio) sau alegeți o filă Chrome.', 7000);
        }
        this.handleScreenShareState();
        this.updateStageLayout();
      },
      onLocalScreenShareEnded: () => {
        this.preEventShare?.dispose(); this.preEventShare = null;
        this.tabRelayCleanup?.();
        console.info('[Vorbello Conference] Local screen share ended');
        this.notifyLocalScreenAudioActive(false);
        this.handleScreenShareState();
        this.updateStageLayout();
        this.renderVideoGrid();
        this.updateControlsState();
        setTimeout(() => {
          this.updateStageLayout();
          this.renderVideoGrid();
        }, 100);
        setTimeout(() => {
          this.updateStageLayout();
          this.renderVideoGrid();
        }, 300);
      },
      onEncryptionViolation: () => {
        this.showFloatingToast('Conexiunea a fost oprită: criptarea nu a putut fi verificată.');
      },
      onRemoteEncryptionProblem: () => {
        this.showFloatingToast('Un flux sau mesaj nu a putut fi verificat și a fost blocat. Verifică dispozitivul participantului.', 6000);
      },
      onDisconnected: (reason) => {
        if (reason === DisconnectReason.ROOM_DELETED) {
          if (!this.endingForAll) {
            if (this.isRecording) this.stopRecording();
            void this.cleanup();
            this.config.onLeave('Conferința a fost încheiată de gazdă pentru toți participanții.');
          }
          return;
        }
        console.info('[Vorbello Conference] Disconnected event received');
        const pill = document.getElementById('conf-status-pill');
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> În Curs de Reconectare...`;
        }
      },
      onHandRaise: (data) => {
        console.info('[LiveKit] Hand raise received:', data);
        if (data.identity === 'all' || (data as any).isLowerAll) {
          this.isLocalHandRaised = false;
          this.participants.forEach(part => { part.isHandRaised = false; });
          this.showFloatingToast(`✋ Administratorul a coborât toate mâinile din sală.`);
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateControlsState();
          return;
        }
        const p = this.participants.find(part => part.identity === data.identity);
        if (p) {
          p.isHandRaised = data.isRaised;
        } else if (data.identity === 'self') {
          this.isLocalHandRaised = data.isRaised;
          const selfP = this.participants.find(part => part.identity === 'self');
          if (selfP) selfP.isHandRaised = data.isRaised;
        }
        if (data.isRaised) {
          const name = p ? p.name : data.participantName;
          this.showFloatingToast(`✋ ${name} a ridicat mâna!`);
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
      },
      onReaction: (data) => {
        console.info('[LiveKit] Reaction received:', data);
        this.triggerFloatingReaction(data.emoji, data.identity, data.participantName);
      },
      onRecordingStatus: (data) => {
        console.info('[LiveKit UI] Remote recording status received:', data);
        const wasActive = this.roomRecordingActive;
        this.roomRecordingActive = !!data.isRecording;
        this.roomRecorderName = data.recorderName || '';

        if (data.isRecording) {
          if (!wasActive) {
            this.showFloatingToast(`🔴 ${data.recorderName || 'Gazda'} înregistrează această ședință.`);
          }
          if (typeof data.recordingSeconds === 'number' && !this.isRecording) {
            this.recordingSeconds = data.recordingSeconds;
            this.recordingStartTime = Date.now() - (data.recordingSeconds * 1000);
          } else if (!this.recordingStartTime && !this.isRecording) {
            this.recordingStartTime = Date.now();
          }
          if (!this.recordingInterval && !this.isRecording) {
            this.recordingInterval = setInterval(() => {
              this.recordingSeconds = Math.max(0, Math.floor((Date.now() - this.recordingStartTime) / 1000));
              this.updateRecordingTimerDisplay();
            }, 1000);
          }
        } else {
          if (wasActive) {
            this.showFloatingToast('⏹️ Înregistrarea ședinței a fost oprită.');
          }
          if (this.recordingInterval && !this.isRecording) {
            clearInterval(this.recordingInterval);
            this.recordingInterval = null;
          }
          this.recordingSeconds = 0;
        }

        this.updateRecordingTimerDisplay();
        this.updateRecordingUI(this.isRecording || this.roomRecordingActive);
      },
      onQueryRecordingStatus: () => {
        if (this.isRecording) {
          this.manager.sendDataMessage({
            type: 'recording_status',
            isRecording: true,
            recorderName: this.config.displayName || 'Gazdă',
            recordingSeconds: this.recordingSeconds,
          }).catch(() => {});
        }
      },
      onRecordingCommand: (data) => {
        if (data && data.action === 'stop') {
          if (this.isRecording) {
            const fromWho = (data as any).requesterName || (data as any).requestedBy || 'organizatorul';
            this.showFloatingToast(`⏹️ Gazda (${fromWho}) a oprit înregistrarea.`);
            this.stopRecording();
          }
        }
      },
      onMeetingEndedByHost: (data) => {
        console.warn('[ConferenceUI] Meeting ended by host for all participants:', data);
        this.cleanup();
        this.config.onLeave(data.reason || `⚠️ Gazda (${data.hostName || 'organizatorul'}) a încheiat ședința pentru toți participanții.`);
      },
      onWhiteboardDraw: (data) => {
        this.handleRemoteWhiteboardDraw(data);
      },
      onWhiteboardClear: () => {
        this.clearWhiteboard(false);
      },
      onWhiteboardToggle: (data) => {
        if (data.isOpen) {
          this.openWhiteboard(false);
          this.showFloatingToast(`🎨 ${data.senderName} a deschis Tabla Interactivă.`);
        } else {
          this.closeWhiteboard(false);
          this.showFloatingToast(`🎨 ${data.senderName} a închis Tabla Interactivă.`);
        }
      },
      onPollLaunch: (data) => {
        this.handleRemotePollLaunch(data);
      },
      onPollVote: (data) => {
        this.handleRemotePollVote(data);
      },
      onPollClose: (data) => {
        this.handleRemotePollClose(data);
      },
      onWaitingRoomKnock: (data) => {
        this.handleWaitingRoomKnock(data);
      },
      onWaitingRoomDecision: (data) => {
        const room = this.manager.getRoom();
        const myIdentity = room?.localParticipant?.identity || 'self';
        const myName = (this.config.displayName || '').trim().toLowerCase();
        const targetId = (data.targetIdentity || '').trim();
        if (targetId === myIdentity || targetId === 'self' || targetId.toLowerCase() === myName) {
          this.handleWaitingRoomDecision(data.decision);
          return;
        }

        // Real-time synchronization across co-admins and attendees:
        this.waitingParticipants = this.waitingParticipants.filter(w => w.identity !== targetId);
        const banner = document.getElementById('waiting-room-host-banner');
        if (banner) {
          if (this.waitingParticipants.length === 0) {
            banner.remove();
          } else {
            const next = this.waitingParticipants[0];
            this.showWaitingRoomBanner(next.name, next.identity);
          }
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onWaitingRoomMoveBack: (data) => {
        const room = this.manager.getRoom();
        const myIdentity = room?.localParticipant?.identity || 'self';
        const targetId = (data.targetIdentity || '').trim();
        if (targetId === myIdentity || targetId === 'self') {
          this.enterWaitingRoomLobby(true);
          return;
        }
        const p = this.participants.find(part => part.identity === targetId);
        if (p && !this.waitingParticipants.some(w => w.identity === targetId)) {
          this.waitingParticipants.push({
            identity: p.identity,
            name: p.name,
            timestamp: Date.now(),
          });
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateControlsState();
          this.updateStageLayout();
        }
      },
      onHostStatus: (data) => {
        console.info('[ConferenceUI] Received host_status broadcast from host:', data.hostName);
        if (this.isWaitingInLobby) {
          const isStrictWaitingRoom = !!this.config.roomSettings?.waitingRoomEnabled;
          if (isStrictWaitingRoom) {
            console.info('[ConferenceUI] Host is online; sending waiting_room_knock for admission approval...');
            this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
          } else {
            console.info('[ConferenceUI] Host is online; auto-admitting from wait-for-host...');
            this.handleWaitingRoomDecision('admitted');
          }
        }
      },
      onAdminLayoutSync: (data) => {
        console.info('[ConferenceUI] Received admin layout sync from host:', data);
        const myLiveKitId = this.manager.getRoom()?.localParticipant?.identity || '';
        if (data.senderIdentity && data.senderIdentity === myLiveKitId) return;

        // If I am the Primary Room Host, do not let non-primary co-hosts override my stage layout!
        if (this.isHost && !data.isPrimaryHost) {
          console.info('[ConferenceUI] Preserving Primary Host stage layout; ignoring secondary admin sync');
          return;
        }

        const isMePinned = Boolean(data.pinnedIdentity && (data.pinnedIdentity === myLiveKitId || data.pinnedIdentity === 'self'));

        this.isStageLockedByAdmin = Boolean(data.isStageLocked && data.pinnedIdentity);

        if (data.viewMode === 'studio') {
          this.currentViewMode = 'studio';
          this.pinnedIdentity = null;
          this.isStageLockedByAdmin = false;
          this.studioCurrentPage = 1;
          const viewIcon = document.getElementById('view-mode-icon');
          const viewText = document.getElementById('view-mode-text');
          const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
          const sheetViewText = document.getElementById('sheet-label-view-mode');
          if (viewIcon) viewIcon.innerText = '🗂️';
          if (viewText) viewText.innerText = 'Studio 16:9';
          if (sheetViewIcon) sheetViewIcon.innerText = '🗂️';
          if (sheetViewText) sheetViewText.innerText = 'Mod: Studio 16:9';
          this.updateViewModePopoverChecks();
          this.showFloatingToast('👑 Gazda a setat modul: Studio 16:9 Paginat');
        } else if (data.viewMode === 'speaker') {
          this.currentViewMode = 'speaker';
          this.pinnedIdentity = data.pinnedIdentity ? (isMePinned ? 'self' : data.pinnedIdentity) : null;
          const viewIcon = document.getElementById('view-mode-icon');
          const viewText = document.getElementById('view-mode-text');
          const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
          const sheetViewText = document.getElementById('sheet-label-view-mode');
          if (viewIcon) viewIcon.innerText = this.isStageLockedByAdmin ? '🔒' : '👤';
          if (viewText) viewText.innerText = this.isStageLockedByAdmin ? 'Scenă Fixată' : 'Vorbitor Activ';
          if (sheetViewIcon) sheetViewIcon.innerText = this.isStageLockedByAdmin ? '🔒' : '👤';
          if (sheetViewText) sheetViewText.innerText = this.isStageLockedByAdmin ? 'Scenă Fixată' : 'Mod: Vorbitor';
          this.updateViewModePopoverChecks();
          if (data.pinnedName) {
            this.showFloatingToast(`👑 Gazda a fixat scena pe: ${data.pinnedName}`);
          }
        } else {
          this.currentViewMode = 'gallery';
          this.pinnedIdentity = null;
          this.isStageLockedByAdmin = false;
          const viewIcon = document.getElementById('view-mode-icon');
          const viewText = document.getElementById('view-mode-text');
          const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
          const sheetViewText = document.getElementById('sheet-label-view-mode');
          if (viewIcon) viewIcon.innerText = '⊞';
          if (viewText) viewText.innerText = 'Grilă';
          if (sheetViewIcon) sheetViewIcon.innerText = '⊞';
          if (sheetViewText) sheetViewText.innerText = 'Mod: Grilă';
          this.updateViewModePopoverChecks();
          this.showFloatingToast('👑 Gazda a setat modul: Grilă Dinamică');
        }
        this.updateStageLayout();
        this.renderVideoGrid();
        this.renderParticipantsList();
      },
    });
    this.manager.displayName = config.displayName;
    this.initLocalAudioCoordination();
  }

  private initLocalAudioCoordination(): void {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.localAudioBus = new BroadcastChannel('vorbello_local_machine_audio_sync');
      }
    } catch (_) {}
  }

  private notifyLocalScreenAudioActive(active: boolean): void {
    try {
      if (this.localAudioBus) {
        this.localAudioBus.postMessage({
          type: active ? 'LOCAL_SCREEN_AUDIO_ACTIVE' : 'LOCAL_SCREEN_AUDIO_INACTIVE',
          sharerIdentity: this.manager.getRoom()?.localParticipant?.identity || 'self',
        });
      }
    } catch (_) {}
  }

  private muteAllRemoteScreenAudio(muted: boolean): void {
    document.querySelectorAll<HTMLAudioElement>('audio[id$="-screen"]').forEach(a => {
      a.muted = muted;
      if (muted) {
        a.volume = 0;
      } else {
        a.volume = 1.0;
        if (a.paused) a.play().catch(() => {});
      }
    });
  }

  private toggleRemoteScreenAudioMute(): void {
    this.isRemoteScreenAudioMuted = !this.isRemoteScreenAudioMuted;
    this.muteAllRemoteScreenAudio(this.isRemoteScreenAudioMuted);
    const msg = this.isRemoteScreenAudioMuted
      ? '🔇 Sunetul ecranului partajat a fost oprit (Muted).'
      : '🔊 Sunetul ecranului partajat a fost pornit.';
    this.showFloatingToast(msg, 3000);
    this.updateStageLayout();
  }

  /**
   * Safely attaches a local screen capture stream to the spotlight video preview element.
   * Extracts ONLY the video track into a new MediaStream and completely discards all audio tracks.
   * If a stream containing an audio track (e.g. system loopback) is assigned to a <video> element,
   * Chromium/Electron can output the captured audio to the local speakers. The loopback then captures
   * that speaker output, producing an instant acoustic-free feedback loop / microfonie even when
   * all microphones are muted!
   */
  private attachScreenStreamToSpotlight(stream: MediaStream | null, videoEl: HTMLVideoElement): void {
    if (!videoEl) return;
    videoEl.muted = true;
    videoEl.volume = 0;
    videoEl.playsInline = true;
    videoEl.setAttribute('muted', '');
    videoEl.setAttribute('playsinline', '');

    if (!stream) {
      videoEl.srcObject = null;
      return;
    }

    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      videoEl.srcObject = null;
      return;
    }

    const currentStream = videoEl.srcObject as MediaStream | null;
    const currentTrack = currentStream?.getVideoTracks()[0];
    if (currentTrack !== videoTrack || !videoEl.srcObject) {
      // Pass EXCLUSIVELY the video track in a fresh MediaStream with zero audio tracks!
      videoEl.srcObject = new MediaStream([videoTrack]);
    }
    videoEl.onloadedmetadata = () => {
      videoEl.play().catch(() => {});
    };
    videoEl.play().catch(() => {});
  }

  public setParticipantSpeaking(identity: string, isSpeaking: boolean) {
    const p = this.participants.find(part => part.identity === identity);
    if (!p) return;

    const isSelf = identity === 'self';
    const isMuted = isSelf ? !this.manager.isMicEnabled : !!p.isMuted;
    const effectiveSpeaking = isSpeaking && !isMuted;

    p.isSpeaking = effectiveSpeaking;

    const tile = document.getElementById(`tile-${identity}`);
    if (tile) {
      tile.classList.toggle('speaking', effectiveSpeaking);
      const iconEl = tile.querySelector('.speaker-indicator-icon');
      if (iconEl) iconEl.innerHTML = isMuted ? ICONS.micOffSm : effectiveSpeaking ? '🔊' : ICONS.users;

      const audioInd = tile.querySelector('.tile-audio-indicator') as HTMLElement | null;
      if (audioInd) {
        if (isMuted) {
          audioInd.className = 'tile-audio-indicator is-muted';
          audioInd.innerHTML = '<span class="indicator-icon">🔇</span> Microfon Oprit';
        } else if (effectiveSpeaking) {
          audioInd.className = 'tile-audio-indicator is-speaking';
          audioInd.innerHTML = '<span class="audio-wave-anim"><span></span><span></span><span></span></span> 🔊 Vorbește (Sunet Live)';
        } else {
          audioInd.className = 'tile-audio-indicator';
          audioInd.innerHTML = '<span class="indicator-icon">🎤</span> HD Audio Pornit';
        }
      }
    }
  }

  private startLocalAudioLevelMonitor() {
    if (this.localAudioMonitorInterval) {
      clearInterval(this.localAudioMonitorInterval);
      this.localAudioMonitorInterval = null;
    }

    if (!this.manager.isMicEnabled) {
      this.setParticipantSpeaking('self', false);
      return;
    }

    try {
      const room = this.manager.getRoom();
      const localMicPub = room?.localParticipant?.getTrackPublication(Track.Source.Microphone);
      const mst = (localMicPub?.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
      const stream = (mst && mst.readyState === 'live') ? new MediaStream([mst]) : this.manager.getLocalMicStream();

      if (stream && stream.getAudioTracks().length > 0) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          if (!this.localAudioMonitorCtx || this.localAudioMonitorCtx.state === 'closed') {
            this.localAudioMonitorCtx = new AudioCtx();
          }
          if (this.localAudioMonitorCtx.state === 'suspended') {
            this.localAudioMonitorCtx.resume().catch(() => {});
          }
          const src = this.localAudioMonitorCtx.createMediaStreamSource(stream);
          this.localAudioMonitorAnalyser = this.localAudioMonitorCtx.createAnalyser();
          this.localAudioMonitorAnalyser.fftSize = 128;
          this.localAudioMonitorAnalyser.smoothingTimeConstant = 0.4;
          src.connect(this.localAudioMonitorAnalyser);

          const data = new Uint8Array(this.localAudioMonitorAnalyser.frequencyBinCount);
          this.localAudioMonitorInterval = setInterval(() => {
            if (!this.manager.isMicEnabled) {
              this.setParticipantSpeaking('self', false);
              return;
            }
            if (!this.localAudioMonitorAnalyser) return;
            this.localAudioMonitorAnalyser.getByteFrequencyData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) sum += data[i];
            const avg = sum / data.length;
            this.setParticipantSpeaking('self', avg > 10);
          }, 120);
        }
      }
    } catch (e) {
      console.warn('[ConferenceUI] Local audio monitor error:', e);
    }
  }

  public async start(): Promise<void> {
    this.renderLayout();
    if (this.config.countdownStartsAt && Date.parse(this.config.countdownStartsAt) > Date.now()) {
      const target=this.config.countdownStartsAt;
      const screen=this.container?.querySelector<HTMLElement>('.conference-screen');
      if (screen) {
        const countdown=document.createElement('div');
        countdown.style.cssText='position:absolute;top:12px;left:50%;transform:translateX(-50%);z-index:25;display:flex;align-items:center;gap:10px;padding:6px 9px 6px 14px;border-radius:999px;background:rgba(15,23,42,.88);color:#dbeafe;border:1px solid #6478c7;font-weight:600;font-size:13px;font-variant-numeric:tabular-nums';
        const label=document.createElement('span'); label.setAttribute('role','status');
        const close=document.createElement('button'); close.type='button'; close.setAttribute('aria-label','Ascunde cronometrul pe acest dispozitiv'); close.title='Ascunde cronometrul pe acest dispozitiv'; close.textContent='×';
        close.style.cssText='border:0;border-radius:50%;width:26px;height:26px;background:rgba(255,255,255,.1);color:#fff;font-size:20px;line-height:1;cursor:pointer';
        close.addEventListener('click',()=>countdown.remove()); countdown.append(label,close);
        screen.appendChild(countdown);
        const tick=()=>{ if(!countdown.isConnected) return; if(Date.now()>=Date.parse(target)){countdown.remove();return;} label.textContent=`Începe în ${preEventCountdown(target) || ''}`; setTimeout(tick,1000); };
        tick();
      }
    }
    (window as any).VorbelloNativeBridge?.setMeetingActive?.(true);
    let nativePipTimer: ReturnType<typeof setInterval> | undefined;
    (window as any).vorbelloSetNativePip = (enabled: boolean) => {
      if (nativePipTimer) clearInterval(nativePipTimer);
      nativePipTimer = undefined;
      document.getElementById('native-pip-video')?.remove();
      document.documentElement.classList.toggle('vorbello-native-pip', enabled);
      if (!enabled) return;
      const preview = document.createElement('video');
      preview.id = 'native-pip-video';
      preview.autoplay = true; preview.muted = true; preview.playsInline = true;
      document.body.appendChild(preview);
      const update = () => {
        const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.conference-screen video'));
        const visible = videos.filter(v => v.srcObject instanceof MediaStream && v.srcObject.getVideoTracks().some(t => t.readyState === 'live') && v.getBoundingClientRect().width > 0 && getComputedStyle(v).display !== 'none');
        const selected = visible.find(v => v.id === 'spotlight-video') || visible.find(v => !v.muted) || visible[0];
        if (selected && preview.srcObject !== selected.srcObject) { preview.srcObject = selected.srcObject; void preview.play().catch(() => {}); }
      };
      update();
      nativePipTimer = setInterval(update, 1000);
    };
    (window as any).vorbelloHandleMeetingBack = handleMeetingBack;
    if (!(window as any).VorbelloNativeBridge && window.matchMedia('(pointer: coarse)').matches) {
      this.disposeWebMeetingBack?.();
      this.disposeWebMeetingBack = installWebMeetingBack(() => handleMeetingBack(), () => {
        if (!document.pictureInPictureElement && navigator.userActivation?.isActive && document.pictureInPictureEnabled) {
          document.getElementById('btn-mobile-floating-pip')?.click();
        } else {
          this.showFloatingToast('Apasă pictograma ferestrei video, apoi Acasă pentru a deschide alte aplicații.');
        }
      });
    }
    const screen = this.container?.querySelector<HTMLElement>('.conference-screen');
    const controls = screen?.querySelector<HTMLElement>('.conference-controls');
    if (screen && controls) {
      this.disposeControlsAutoHide?.();
      this.disposeControlsAutoHide = installControlsAutoHide(screen, controls, () => this.isRecording || this.roomRecordingActive, 5000, screen.querySelector<HTMLElement>('.conference-header'), () => (this.isAdmin || this.isModerator || this.isHost) && localStorage.getItem('vorbello_pin_controls') === 'true');
    }
    this.startTimer();

    // Early global mobile/tablet touch unlock for WebRTC media playback
    if (typeof window !== 'undefined' && !(window as any)._vorbelloEarlyTouchUnlockAttached) {
      (window as any)._vorbelloEarlyTouchUnlockAttached = true;
      const unlockMedia = async () => {
        try {
          if (this.manager.getRoom() && !this.manager.getRoom()?.canPlaybackAudio) {
            await this.manager.getRoom()?.startAudio();
          }
        } catch (_) {}
        document.querySelectorAll('audio').forEach((a: any) => {
          if (a.paused && !a.muted) a.play().catch(() => {});
        });
        document.querySelectorAll('video').forEach((v: any) => {
          if (v.srcObject && v.paused) v.play().catch(() => {});
        });
      };
      ['touchstart', 'touchend', 'pointerdown', 'click'].forEach(evt => {
        window.addEventListener(evt, unlockMedia, { passive: true });
      });
    }

    const initCam = this.config.webinarAudience ? false : (this.config.initialCameraEnabled !== undefined ? this.config.initialCameraEnabled : true);
    const initMic = this.config.webinarAudience ? false : (this.config.initialMicEnabled !== undefined ? this.config.initialMicEnabled : !this.userExplicitlyMuted);

    this.manager.isCameraEnabled = initCam;
    this.manager.isMicEnabled = initMic;
    if (!initMic) this.userExplicitlyMuted = true;

    // Add self to participants list
    this.participants.push({
      identity: 'self',
      name: `${this.config.displayName} (Tu)`,
      isSpeaking: false,
      isMuted: !initMic,
      isCamOff: !initCam,
      role: this.role,
      avatarUrl: this.config.avatarUrl,
    });

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const pCountParam = this.config.requiresVerifiedServer ? 0 : this.config.simulatedParticipants || parseInt(urlParams.get('participants') || '0', 10);
      if (pCountParam > 1) {
        const names = ['Ana Popescu', 'Mihai Ionescu', 'Elena Dumitru', 'Radu Vasilescu', 'Cristian Tudor', 'Maria Enache', 'Andrei Dobre'];
        for (let i = 0; i < pCountParam - 1; i++) {
          this.participants.push({
            identity: `part-${i + 1}`,
            name: names[i] || `Participant ${i + 1}`,
            isSpeaking: i === 0,
            isMuted: i % 2 === 1,
            isCamOff: false,
            role: i === 0 ? 'moderator' : 'user',
          });
        }
      }
    } catch (_) {}

    this.updateStageLayout();
    this.renderVideoGrid();
    this.updateControlsState();

    // 1. Immediately adopt transferred stream or acquire local camera preview (zero delay / no black screen!)
    if (!this.config.webinarAudience && this.config.initialStream && this.config.initialStream.active) {
      this.manager.setInitialCameraStream(this.config.initialStream);
    } else if (this.manager.isCameraEnabled) {
      try {
        await this.manager.enableCamera(true);
      } catch (camErr) {
        console.warn('[ConferenceUI] Immediate camera preview error:', camErr);
      }
    }
    this.updateSelfTile();

    (window as any).activeConference = this;
    (window as any).vorbelloDisconnect = () => this.cleanup();
    (window as any).vorbelloSetParticipants = (n: number) => {
      const names = ['Ana Popescu', 'Mihai Ionescu', 'Elena Dumitru', 'Radu Vasilescu', 'Cristian Tudor', 'Maria Enache', 'Andrei Dobre'];
      this.participants = [{
        identity: 'self',
        name: `${this.config.displayName} (Tu)`,
        isSpeaking: false,
        isMuted: false,
        isCamOff: false,
        role: this.role,
      }];
      for (let i = 0; i < n - 1; i++) {
        this.participants.push({
          identity: `part-${i + 1}`,
          name: names[i] || `Participant ${i + 1}`,
          isSpeaking: i === 0,
          isMuted: i % 2 === 1,
          isCamOff: false,
          role: i === 0 ? 'moderator' : 'user',
        });
      }
      this.updateStageLayout();
      this.renderVideoGrid();
      this.renderParticipantsList();
    };
    this.beforeUnloadHandler = () => {
      this.cleanup();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    const isProduction = this.config.appMode === 'production';

    try {
      await this.manager.connect(this.config.livekitUrl, this.config.token, this.config.encryptionKey);
      const connectedPill = document.getElementById('conf-status-pill');
      if (connectedPill) connectedPill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Conexiune OK`;
      this.refreshWebinarPermissions();
      this.config.onServerConnected?.();
      this.manager.getRoom()?.remoteParticipants.forEach(p => this.config.onAttendanceParticipantConnected?.(p.identity, p.name || 'Participant'));
      if (this.config.admission && (this.isHost || this.isAdmin)) this.startAdmissionPolling();

      const canManageAdmission = this.isHost || this.isAdmin;
      const shouldWaitForHost = !canManageAdmission && (this.config.roomSettings?.waitForHost !== false);
      const isWaitingRoomUser = !canManageAdmission && (this.config.roomSettings?.waitingRoomEnabled || shouldWaitForHost);
      if (isWaitingRoomUser) {
        // If it's a waiting-for-host room, check if the host is already in the room
        let hostPresent = false;
        const livekitRoom = this.manager.getRoom();
        if (livekitRoom) {
          livekitRoom.remoteParticipants.forEach(p => {
            try {
              const meta = JSON.parse(p.metadata || '{}');
              if (meta.isAdmin || meta.isHost || meta.role === 'admin' || meta.role === 'ADMIN') {
                hostPresent = true;
              }
            } catch (_) {}
          });
        }

        if (this.config.roomSettings?.waitingRoomEnabled || !hostPresent) {
          console.info('[ConferenceUI] Connected in Waiting Room mode (waiting for host approval or host presence)...');
          this.enterWaitingRoomLobby(false);
          await this.manager.sendWaitingRoomKnock(this.config.displayName);
          this.waitingRoomKnockInterval = setInterval(() => {
            if (this.isWaitingInLobby) {
              this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
            } else {
              clearInterval(this.waitingRoomKnockInterval);
              this.waitingRoomKnockInterval = null;
            }
          }, 8000);
          return;
        }
      }

      if (this.isHost || this.isAdmin) {
        this.manager.sendDataMessage({
          type: 'host_status',
          hostConnected: true,
          hostName: this.config.displayName,
        }).catch(() => {});
        // Also broadcast initial layout so that Admin is immediately spotlighted on stage for everyone
        setTimeout(() => {
          this.broadcastAdminLayoutSync().catch(() => {});
        }, 1200);
      }

      // Immediately sync all remote participants already in the room upon connection:
      const livekitRoom = this.manager.getRoom();
      if (livekitRoom) {
        livekitRoom.remoteParticipants.forEach(rp => {
          this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');
        });
      }
      this.resyncAllRemoteTracks();
      this.renderVideoGrid();
      this.renderParticipantsList();

      const pill = document.getElementById('conf-status-pill');
      if (pill) {
        pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Conexiune OK`;
      }
      this.updateTelemetryPopover(true);

      // 2. Publish camera to room
      try {
        console.info('[ConferenceUI] Publishing camera to room...');
        if (this.manager.isCameraEnabled) {
          await this.manager.enableCamera(true);
        }
      } catch (camErr) {
        console.warn('[ConferenceUI] Publish camera warning:', camErr);
      }

      // Fast track publication queue for LiveKit Single PeerConnection (Protocol 15)
      await new Promise(r => setTimeout(r, 100));

      // 3. Publish microphone to room ONLY if user did not explicitly mute and isMicEnabled is true
      try {
        console.info('[ConferenceUI] Publishing microphone to room...');
        if (!this.userExplicitlyMuted && this.manager.isMicEnabled) {
          await this.manager.enableMicrophone(true);
        } else {
          console.info('[ConferenceUI] User selected mute; keeping microphone muted.');
          this.manager.isMicEnabled = false;
          const selfP = this.participants.find(p => p.identity === 'self');
          if (selfP) selfP.isMuted = true;
          this.updateControlsState();
        }
      } catch (micErr) {
        console.warn('[ConferenceUI] Publish microphone warning:', micErr);
      }

      this.updateControlsState();
      this.updateSelfTile();

      if (livekitRoom) {
        livekitRoom.remoteParticipants.forEach(rp => {
          this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');
        });
      }
      this.resyncAllRemoteTracks();
      this.updateStageLayout();
      this.renderVideoGrid();
      this.renderParticipantsList();

      // Ensure periodic resync during the first 2 seconds to catch any slow-joining peers:
      setTimeout(() => {
        this.resyncAllRemoteTracks();
        this.updateStageLayout();
        this.renderVideoGrid();
        this.renderParticipantsList();
      }, 200);
      setTimeout(() => {
        this.resyncAllRemoteTracks();
        this.updateStageLayout();
        this.renderVideoGrid();
        this.renderParticipantsList();
      }, 600);
      setTimeout(() => {
        this.resyncAllRemoteTracks();
        this.updateStageLayout();
        this.renderVideoGrid();
        this.renderParticipantsList();
      }, 1500);

      if (this.manager.isMicEnabled) {
        this.startLocalAudioLevelMonitor();
      }
    } catch (err) {
      console.warn('[Vorbello Conference] Connecting to LiveKit:', err);
      if (this.config.encryptionKey || this.config.requiresVerifiedServer) {
        await this.cleanup();
        throw new Error(this.config.encryptionKey ? 'Conectarea criptată nu a putut fi verificată. Conferința nu a fost pornită.' : 'Conectarea la serverul Vorbello nu a reușit. Prezența PP nu a fost confirmată. Încearcă din nou sau contactează organizatorul.');
      }
      const pill = document.getElementById('conf-status-pill');
      if (isProduction) {
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> În Așteptare Server VPS (Port 7880)`;
        }
        this.updateTelemetryPopover(false, 'În Așteptare Server VPS (Port 7880)');
        this.updateSelfTile();
      } else {
        console.warn('[Vorbello Conference] Live SFU offline, activating interactive demo mode:', err);
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> Mod Demo / Test Activ (Fără Server)`;
        }
        this.updateTelemetryPopover(false, 'Mod Demo Local');
        this.updateSelfTile();

        // Add simulated remote participants ONLY in demo mode
        this.addOrUpdateParticipant('part-ana', 'Ana Popescu (Moderator)');
        this.addOrUpdateParticipant('part-mihai', 'Mihai Ionescu (Membru)');
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();

        this.messages.push({
          id: 'msg-welcome',
          senderName: 'Ana Popescu',
          senderIdentity: 'part-ana',
          text: 'Bună ziua! Aceasta este o cameră de test Vorbello. Puteți porni camera, microfonul și ecranul din butoanele de jos.',
          timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
          isSelf: false,
        });
        this.renderChatMessages();
      }
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const pCountParam = this.config.requiresVerifiedServer ? 0 : parseInt(urlParams.get('participants') || '0', 10);
      if (pCountParam > 1) {
        (window as any).vorbelloSetParticipants?.(pCountParam);
      }
    } catch (_) {}

    // Query current room recording status upon connecting
    setTimeout(() => {
      this.manager.sendDataMessage({
        type: 'query_recording_status',
        identity: this.manager.getRoom()?.localParticipant?.identity || '',
      }).catch(() => {});
    }, 600);

    // Auto-record strictly if room configured and current session is the actual host/creator
    if (this.config.roomSettings?.autoRecord && this.isHost) {
      setTimeout(() => {
        // Option 1 Master Lock: Check if another host/admin is ALREADY actively recording this room!
        if (this.roomRecordingActive) {
          console.info('[ConferenceUI] Auto-record skipped: Room is already actively recorded by', this.roomRecorderName);
          this.showFloatingToast(`ℹ️ Înregistrarea automată este deja activă pe celălalt dispozitiv (${this.roomRecorderName || 'Admin'}).`);
          return;
        }
        if (!this.isRecording) {
          if (this.config.roomSettings?.autoRecordLayout) {
            this.selectedRecordingLayout = this.config.roomSettings.autoRecordLayout;
          }
          const rawFormat = this.config.roomSettings?.autoRecordFormat;
          const isDesktop = typeof window !== 'undefined' && Boolean((window as any).vorbelloDesktop?.isDesktop);
          const rawContainer = (this.config.roomSettings?.autoRecordContainer === 'mp4' && isDesktop) ? 'mp4' : 'webm';
          const format: 'video-fhd' | 'video-hd' | 'audio' = 
            (rawFormat === 'video-fhd' || rawFormat === 'audio') ? rawFormat : 'video-hd';
          const container: 'webm' | 'mp4' = (rawContainer === 'mp4') ? 'mp4' : 'webm';
          const layoutName = this.selectedRecordingLayout === 'cameras_only'
            ? 'Cameră Curată (Doar Camera Mea Admin)'
            : (this.selectedRecordingLayout === 'screen_only' ? 'Doar Ecran' : 'Studio PiP');
          const formatLabel = `${format === 'video-fhd' ? 'Video Full HD (1080p)' : (format === 'audio' ? 'Doar Audio HD' : 'Video HD (720p)')} [${container.toUpperCase()}] • Layout: ${layoutName}`;
          console.info(`[ConferenceUI] Auto-record is enabled for this room; starting host recording in format: ${formatLabel}...`);
          this.startRecording(format, container).catch(e => console.warn('[ConferenceUI] Auto-record failed:', e));
        }
      }, 1500);
    }

    // Start periodic track health monitoring
    this.startTrackHealthChecker();
  }

  public async startP2PMeshFallback(): Promise<void> {
    console.info('[ConferenceUI] Starting P2P WebRTC Mesh Fallback via signal.php...');
    this.isP2PMode = true;

    const pill = document.getElementById('conf-status-pill');
    if (pill) {
      pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Conectat Direct (P2P WebRTC)`;
    }
    this.updateTelemetryPopover(true, 'Conexiune P2P WebRTC Directă Activă');

    // Build combined local stream for P2P mesh (both camera and microphone)
    const combinedTracks: MediaStreamTrack[] = [];
    const camStream = this.manager.getLocalCameraStream();
    if (camStream && camStream.active && camStream.getVideoTracks().length > 0) {
      combinedTracks.push(...camStream.getVideoTracks());
    }
    const micStream = this.manager.getLocalMicStream();
    if (micStream && micStream.active && micStream.getAudioTracks().length > 0) {
      combinedTracks.push(...micStream.getAudioTracks());
    }

    const needVideo = this.manager.isCameraEnabled && combinedTracks.filter(t => t.kind === 'video').length === 0;
    const needAudio = this.manager.isMicEnabled && combinedTracks.filter(t => t.kind === 'audio').length === 0;

    if (needVideo || needAudio) {
      try {
        const freshStream = await navigator.mediaDevices.getUserMedia({
          video: needVideo ? { facingMode: 'user' } : false,
          audio: needAudio ? { echoCancellation: true, noiseSuppression: true } : false,
        });
        freshStream.getTracks().forEach(t => combinedTracks.push(t));
      } catch (e) {
        console.warn('[P2P UI] getUserMedia fallback warning:', e);
      }
    }

    const localStream = new MediaStream(combinedTracks);

    // Attach local stream to self tile
    if (localStream && localStream.getVideoTracks().length > 0) {
      const selfTile = document.getElementById('tile-self');
      if (selfTile) {
        let videoEl = selfTile.querySelector('video') as HTMLVideoElement | null;
        if (videoEl && videoEl.srcObject !== localStream) {
          videoEl.srcObject = localStream;
          videoEl.play().catch(() => {});
        }
      }
    }

    const peerId = 'peer_' + Math.random().toString(36).substring(2, 9);
    this.p2pMesh = new P2PMeshManager({
      peerId,
      displayName: this.config.displayName,
      roomSlug: this.config.roomSlug || 'sala-vps-live',
      callbacks: {
        onParticipantConnected: (identity, name) => {
          console.info('[P2P UI] Remote participant connected:', identity, name);
          this.addOrUpdateParticipant(identity, name);
          this.showFloatingToast(`👋 ${name} s-a conectat în sală!`);
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateStageLayout();
        },
        onParticipantDisconnected: (identity) => {
          console.info('[P2P UI] Remote participant left:', identity);
          const p = this.participants.find(part => part.identity === identity);
          if (p) {
            this.showFloatingToast(`🚪 ${p.name} a părăsit conferința`);
          }
          this.participants = this.participants.filter(part => part.identity !== identity);
          const audioEl = document.getElementById(`audio-remote-${identity}`);
          if (audioEl) audioEl.remove();
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateStageLayout();
        },
        onRemoteTrack: (identity, name, track, stream) => {
          console.info('[P2P UI] Remote track received:', track.kind, 'from', name, identity);
          this.addOrUpdateParticipant(identity, name);
          const p = this.participants.find(part => part.identity === identity);

          if (track.kind === 'video') {
            if (p) p.isCamOff = false;
            this.renderVideoGrid();
            const attachVideo = () => {
              const tile = document.getElementById(`tile-${identity}`);
              if (tile) {
                let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
                const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                if (!videoEl) {
                  videoEl = document.createElement('video');
                  videoEl.className = 'remote-cam-video';
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  videoEl.muted = true; // Crucial for mobile autoplay!
                  videoEl.setAttribute('playsinline', '');
                  videoEl.setAttribute('muted', '');
                  videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1;';
                  tile.prepend(videoEl);
                }
                if (videoEl.srcObject !== stream) {
                  videoEl.srcObject = stream;
                }
                videoEl.style.display = 'block';
                videoEl.play().catch(e => console.warn('[P2P UI] Video play error:', e));
                if (avatar) avatar.style.display = 'none';
              }
              this.updateStageLayout();
            };
            attachVideo();
            setTimeout(attachVideo, 80);
            setTimeout(attachVideo, 300);
          } else if (track.kind === 'audio') {
            if (p) p.isMuted = false;
            let audioEl = document.getElementById(`audio-remote-${identity}`) as HTMLAudioElement | null;
            if (!audioEl) {
              audioEl = document.createElement('audio');
              audioEl.id = `audio-remote-${identity}`;
              audioEl.autoplay = true;
              (audioEl as any).playsInline = true;
              audioEl.setAttribute('playsinline', '');
              document.body.appendChild(audioEl);
            }
            audioEl.srcObject = stream;
            audioEl.play().catch(async (e) => {
              console.warn('[P2P UI] Remote audio autoplay error:', e);
              this.handleAudioPlaybackBanner(true);
            });
          }
          this.renderParticipantsList();
        },
        onChatMessage: (msg) => {
          const existingIdx = this.messages.findIndex(m => m.id === msg.id);
          const fullMsg: ChatMessage = {
            id: msg.id,
            senderName: msg.senderName,
            senderIdentity: msg.senderIdentity || 'remote',
            text: msg.text || '',
            timestamp: msg.timestamp || new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
            isSelf: false,
            file: msg.file,
            replyTo: msg.replyTo,
          };
          if (existingIdx !== -1) {
            this.messages[existingIdx] = fullMsg;
          } else {
            this.messages.push(fullMsg);
          }
          this.renderChatMessages();
          if (!this.isChatOpen) {
            this.unreadChatCount++;
            this.updateChatBadge();
            this.playChatChime();
            this.showZoomChatNotification(fullMsg);
          } else {
            this.playChatChime();
          }
        },
        onFileTransferProgress: (info: any) => {
          let existing = this.messages.find(m => m.id === info.transferId);
          if (!existing) {
            existing = {
              id: info.transferId,
              senderName: info.senderName,
              senderIdentity: info.senderIdentity,
              text: info.text,
              timestamp: info.timestamp,
              isSelf: false,
              file: {
                name: info.name,
                size: info.size,
                type: '',
                dataUrl: '',
                progress: info.progress,
              } as any,
            };
            this.messages.push(existing);
            this.renderChatMessages();
            if (!this.isChatOpen) {
              this.unreadChatCount++;
              this.updateChatBadge();
            }
          } else {
            if (existing.file) {
              (existing.file as any).progress = info.progress;
            }
            const bar = document.getElementById(`transfer-bar-${info.transferId}`);
            const pct = document.getElementById(`transfer-pct-${info.transferId}`);
            if (bar && pct) {
              bar.style.width = `${info.progress}%`;
              pct.textContent = `${info.progress}%`;
            } else {
              this.renderChatMessages();
            }
          }
        },
        onDataMessage: (data) => {
          if (data.type === 'chat_reaction' && typeof data.identity === 'string' && typeof data.messageId === 'string' && ['👍','❤️','👏','😂'].includes(data.emoji) && typeof data.active === 'boolean') {
            this.applyChatReaction(data.messageId, data.emoji, data.identity, data.active);
          } else if (data.type === 'hand-raise') {
            const p = this.participants.find(part => part.identity === data.identity);
            if (p) {
              p.isHandRaised = data.raised;
              this.renderVideoGrid();
              this.renderParticipantsList();
            }
          } else if (data.type === 'track_state') {
            const p = this.participants.find(part => part.identity === data.identity);
            if (p) {
              if (data.isCamOff !== undefined) {
                p.isCamOff = data.isCamOff;
                const tile = document.getElementById(`tile-${data.identity}`);
                if (tile) {
                  const vid = tile.querySelector('video.remote-cam-video') as HTMLElement | null;
                  const av = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                  if (vid) vid.style.display = data.isCamOff ? 'none' : 'block';
                  if (av) av.style.display = data.isCamOff ? 'flex' : 'none';
                }
              }
              if (data.isMuted !== undefined) {
                p.isMuted = data.isMuted;
              }
              this.renderVideoGrid();
              this.renderParticipantsList();
            }
          }
        },
      },
    });

    await this.p2pMesh.start(localStream);
    this.renderVideoGrid();
    this.updateControlsState();
    this.updateSelfTile();
    if (this.manager.isMicEnabled) {
      this.startLocalAudioLevelMonitor();
    }
  }


  /**
   * Ensures a remote audio element exists and is properly attached for a participant.
   * Reuses existing elements to prevent audio glitches.
   */
  private ensureRemoteAudioElement(identity: string, track: any, pub: any): void {
    const isScreenAudio = pub?.source === Track.Source.ScreenShareAudio || track?.source === Track.Source.ScreenShareAudio;
    const elementId = isScreenAudio ? `audio-remote-${identity}-screen` : `audio-remote-${identity}`;
    let audioEl = document.getElementById(elementId) as HTMLAudioElement | null;
    if (!audioEl) {
      audioEl = document.createElement('audio');
      audioEl.id = elementId;
      audioEl.autoplay = true;
      audioEl.setAttribute('playsinline', '');
      audioEl.volume = 1.0;
      document.body.appendChild(audioEl);
    }

    // Route to user's selected speaker output device if supported
    const preferredSpeakerId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_speaker') : null;
    if (preferredSpeakerId && preferredSpeakerId !== 'default' && typeof (audioEl as any).setSinkId === 'function') {
      (audioEl as any).setSinkId(preferredSpeakerId).catch(() => {});
    }

    // Always re-attach — track.attach is idempotent if already attached
    track.attach(audioEl);

    if (isScreenAudio) {
      const shouldMuteScreenAudio = identity === this.manager.getRoom()?.localParticipant?.identity ||
        pub?.participant?.isLocal ||
        this.isRemoteScreenAudioMuted;

      audioEl.muted = shouldMuteScreenAudio;
      audioEl.volume = shouldMuteScreenAudio ? 0 : 1.0;
      if (shouldMuteScreenAudio) {
        console.info('[LiveKit UI] Screen audio element muted for local sharer:', elementId);
        return;
      }
    }

    const isMuted = pub?.isMuted ?? false;
    audioEl.muted = isMuted;
    if (!isMuted) {
      audioEl.play().catch(async (e) => {
        console.warn('[LiveKit] Remote audio autoplay error:', e);
        try {
          await this.manager.getRoom()?.startAudio();
          await audioEl?.play();
        } catch (_) {
          this.handleAudioPlaybackBanner(true);
        }
      });
    }
  }

  private handleAudioPlaybackBanner(show: boolean) {
    let banner = document.getElementById('audio-unlock-banner');
    if (show) {
      if (!banner) {
        banner = document.createElement('div');
        banner.id = 'audio-unlock-banner';
        banner.className = 'audio-unlock-banner';
        banner.innerHTML = '<span>🔊</span> <span>Faceți clic pentru a activa sunetul participanților</span>';
        banner.onclick = async () => {
          try {
            await this.manager.getRoom()?.startAudio();
            banner?.remove();
            document.querySelectorAll('audio[id^="audio-remote-"]').forEach((a: any) => {
              if (a.id.endsWith('-screen')) {
                const shouldMute = this.manager.isScreenShareEnabled ||
                  this.isLocalMachineScreenSharing ||
                  this.isRemoteScreenAudioMuted;
                a.muted = shouldMute;
                a.volume = shouldMute ? 0 : 1.0;
                if (!shouldMute) a.play().catch(() => {});
              } else {
                a.muted = false;
                a.play().catch(() => {});
              }
            });
          } catch (_) {}
        };
        document.body.appendChild(banner);
      }
    } else {
      if (banner) banner.remove();
    }
  }

  private updateChatBadge() {
    const headerCount = document.getElementById('header-chat-count');
    if (headerCount) headerCount.textContent = String(this.isChatOpen ? 0 : this.unreadChatCount);
    const chatBtn = document.getElementById('btn-open-chat');
    const mobileMoreBtn = document.getElementById('btn-more-mobile');
    const sheetChatBtn = document.getElementById('sheet-btn-chat');

    if (this.unreadChatCount > 0 && !this.isChatOpen) {
      const badgeText = this.unreadChatCount > 9 ? '9+' : String(this.unreadChatCount);

      // 1. Desktop Chat Button Badge
      if (chatBtn) {
        let badge = chatBtn.querySelector('.chat-unread-badge') as HTMLElement | null;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'chat-unread-badge';
          chatBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      }

      // 2. Mobile More Button Badge (Pill notification on "⋯")
      if (mobileMoreBtn) {
        let badge = mobileMoreBtn.querySelector('.chat-unread-badge') as HTMLElement | null;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'chat-unread-badge';
          mobileMoreBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      }

      // 3. Mobile Sheet Chat Item Badge
      if (sheetChatBtn) {
        let badge = sheetChatBtn.querySelector('.chat-unread-badge') as HTMLElement | null;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'chat-unread-badge sheet-chat-badge';
          sheetChatBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      }
    } else {
      chatBtn?.querySelector('.chat-unread-badge')?.remove();
      mobileMoreBtn?.querySelector('.chat-unread-badge')?.remove();
      sheetChatBtn?.querySelector('.chat-unread-badge')?.remove();
    }
  }

  /**
   * Force full resync of all remote tracks after reconnection.
   * Re-attaches all audio elements and video elements from scratch.
   */
  private resyncAllRemoteTracks(): void {
    const room = this.manager.getRoom();
    if (!room || room.state !== 'connected') return;

    console.info('[LiveKit UI] resyncAllRemoteTracks: syncing', room.remoteParticipants.size, 'remote participants');

    room.remoteParticipants.forEach((rp) => {
      // Ensure participant is in our list
      this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');

      // 1. Re-attach audio
      const audioPub = rp.getTrackPublication(Track.Source.Microphone);
      if (audioPub) {
        if (!audioPub.isSubscribed) {
          try { audioPub.setSubscribed(true); } catch (_) {}
        }
        if (audioPub.track) {
          this.ensureRemoteAudioElement(rp.identity, audioPub.track, audioPub);
        }
      }

      // 2. Re-attach screen audio
      const screenAudioPub = rp.getTrackPublication(Track.Source.ScreenShareAudio);
      if (screenAudioPub) {
        if (!screenAudioPub.isSubscribed) {
          try { screenAudioPub.setSubscribed(true); } catch (_) {}
        }
        if (screenAudioPub.track) {
          this.ensureRemoteAudioElement(rp.identity, screenAudioPub.track, screenAudioPub);
        }
      }

      // 3. Re-attach video (Camera)
      const videoPub = rp.getTrackPublication(Track.Source.Camera);
      const tile = document.getElementById(`tile-${rp.identity}`);
      if (tile) {
        let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
        const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
        if (videoPub && videoPub.track && videoPub.isSubscribed && !videoPub.isMuted) {
          if (!videoEl) {
            videoEl = document.createElement('video');
            videoEl.className = 'remote-cam-video';
            videoEl.autoplay = true;
            videoEl.playsInline = true;
            videoEl.setAttribute('playsinline', '');
            videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1;';
            tile.prepend(videoEl);
          }
          videoPub.track.attach(videoEl);
          videoEl.style.display = 'block';
          if (avatar) avatar.style.display = 'none';
        } else {
          if (videoEl) {
            videoEl.style.display = 'none';
            videoEl.srcObject = null;
          }
          if (avatar) avatar.style.display = 'flex';
        }
      }

      // 4. CRITICAL: Re-attach and ensure Screen Share (Instant visibility for new joiners)
      let screenPub = rp.getTrackPublication(Track.Source.ScreenShare);
      if (!screenPub) {
        for (const pub of rp.trackPublications.values()) {
          if (pub.source === Track.Source.ScreenShare || pub.trackName === 'screen_share' || (pub as any).name === 'screen_share') {
            screenPub = pub;
            break;
          }
        }
      }
      if (screenPub) {
        if (!screenPub.isSubscribed) {
          console.info(`[LiveKit UI] resyncAllRemoteTracks: Auto-subscribing remote screen share from ${rp.identity}`);
          try { screenPub.setSubscribed(true); } catch (_) {}
        }
        if (screenPub.track) {
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          if (spotlightVideo) {
            spotlightVideo.muted = true;
            spotlightVideo.playsInline = true;
            spotlightVideo.setAttribute('muted', '');
            spotlightVideo.setAttribute('playsinline', '');
            if (this.lastAttachedRemoteTrack && this.lastAttachedRemoteTrack !== screenPub.track) {
              try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
            }
            this.lastAttachedRemoteTrack = screenPub.track;
            try {
              screenPub.track.attach(spotlightVideo);
              spotlightVideo.play().catch(() => {});
            } catch (_) {}
          }
        }
      }
    });

    // Sync layout immediately
    this.updateStageLayout();

    // Try to unlock audio again
    try {
      room.startAudio().catch(() => {});
    } catch (_) {}
  }

  /**
   * Reconciles internal this.participants against the active LiveKit room.remoteParticipants.
   * Purges ghost/stale participants who disconnected silently or whose disconnect packet was missed.
   * Returns true if any participant was pruned or added.
   */
  public reconcileParticipants(): boolean {
    const room = this.manager.getRoom();
    if (!room || room.state !== 'connected') return false;

    const livekitRemoteIds = new Set<string>();
    room.remoteParticipants.forEach(rp => {
      livekitRemoteIds.add(rp.identity);
    });

    const initialLength = this.participants.length;

    const myLocalId = room.localParticipant?.identity;

    // 1. Purge any stale participants who are no longer connected to the LiveKit SFU:
    this.participants = this.participants.filter(p => {
      if (p.identity === 'self') return true;
      if (myLocalId && p.identity === myLocalId) return false;
      return livekitRemoteIds.has(p.identity);
    });

    // 2. Ensure all active remote participants in the room are tracked:
    room.remoteParticipants.forEach(rp => {
      if (!myLocalId || rp.identity !== myLocalId) {
        this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');
      }
    });

    const changed = this.participants.length !== initialLength;
    if (changed) {
      console.info(`[LiveKit Sync] Reconciled participants: pruned from ${initialLength} to ${this.participants.length}`);

      // Clean up orphaned DOM elements for disconnected participants:
      document.querySelectorAll('.video-tile').forEach(tile => {
        const id = tile.id.replace('tile-', '');
        if (id !== 'self' && !livekitRemoteIds.has(id)) {
          const v = tile.querySelector('video') as HTMLVideoElement | null;
          if (v && v.srcObject) v.srcObject = null;
          tile.remove();
        }
      });
      document.querySelectorAll('audio[id^="audio-remote-"]').forEach(audio => {
        const id = audio.id.replace('audio-remote-', '').replace('-screen', '');
        if (!livekitRemoteIds.has(id)) {
          audio.remove();
        }
      });

      this.updateControlsState();
    }

    return changed;
  }

  /**
   * Periodic health checker that runs every 3 seconds.
   * Reconciles room participants and ensures all remote audio elements are properly connected.
   */
  private startTrackHealthChecker(): void {
    if (this.trackHealthInterval) {
      clearInterval(this.trackHealthInterval);
    }

    this.trackHealthInterval = setInterval(() => {
      const room = this.manager.getRoom();
      if (!room || room.state !== 'connected') return;

      // 0. Auto-reconcile active participants against SFU room state
      if (this.reconcileParticipants()) {
        this.renderVideoGrid();
        this.renderParticipantsList();
      }

      // 1. Ensure all remote audio elements are alive and playing
      room.remoteParticipants.forEach((rp) => {
        const audioPub = rp.getTrackPublication(Track.Source.Microphone);
        if (audioPub && audioPub.track && audioPub.isSubscribed && !audioPub.isMuted) {
          const audioEl = document.getElementById(`audio-remote-${rp.identity}`) as HTMLAudioElement | null;
          if (!audioEl) {
            this.ensureRemoteAudioElement(rp.identity, audioPub.track, audioPub);
          } else if (audioEl.paused) {
            audioEl.play().catch(() => {});
          }
        }
        const screenAudioPub = rp.getTrackPublication(Track.Source.ScreenShareAudio);
        if (screenAudioPub && screenAudioPub.track && screenAudioPub.isSubscribed && !screenAudioPub.isMuted) {
          const screenAudioEl = document.getElementById(`audio-remote-${rp.identity}-screen`) as HTMLAudioElement | null;
          if (!screenAudioEl) {
            this.ensureRemoteAudioElement(rp.identity, screenAudioPub.track, screenAudioPub);
          } else if (screenAudioEl.paused) {
            screenAudioEl.play().catch(() => {});
          }
        }

        // 2. CRITICAL FIX: Auto-recover remote screen share spotlight
        // If any remote participant has an active ScreenShare track but the spotlight is not visible,
        // force updateStageLayout() to re-attach it. This recovers from transient SFU muting,
        // network reconnections, or race conditions where the spotlight was hidden prematurely.
        const screenPub = rp.getTrackPublication(Track.Source.ScreenShare) ||
          Array.from(rp.trackPublications.values()).find(p => p.source === Track.Source.ScreenShare || p.trackName === 'screen_share' || (p as any).name === 'screen_share');
        if (screenPub && (screenPub.track || screenPub.isSubscribed)) {
          const spotlightContainer = document.getElementById('spotlight-container');
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          const isSpotlightHidden = !spotlightContainer || spotlightContainer.style.display === 'none';
          const isSpotlightEmpty = !spotlightVideo || !spotlightVideo.srcObject;
          if (isSpotlightHidden || isSpotlightEmpty) {
            console.info(`[LiveKit Health] Remote screen share from ${rp.identity} exists but spotlight is hidden/empty — recovering`);
            this.updateStageLayout();
          }
        }
      });
    }, 3000);
  }

  private addOrUpdateParticipant(identity: string, name: string) {
    const room = this.manager.getRoom();
    const myLocalId = room?.localParticipant?.identity;
    if (identity === 'self' || (myLocalId && identity === myLocalId)) {
      // Local participant is already represented by 'self' tile
      return;
    }

    name = this.participantNameOverrides.get(identity) || name;
    let p = this.participants.find(part => part.identity === identity);
    if (!p) {
      p = { identity, name, isSpeaking: false, isMuted: true, isCamOff: true, role: 'user' };
      this.participants.push(p);
    } else {
      p.name = name;
    }

    if (room && room.state === 'connected' && identity !== 'self') {
      const rp = room.remoteParticipants.get(identity);
      if (rp) {
        try {
          if (rp.metadata) {
            const meta = typeof rp.metadata === 'string' ? JSON.parse(rp.metadata) : rp.metadata;
            if (meta.role) {
              const r = String(meta.role).toLowerCase();
              p.role = (r === 'admin' ? 'admin' : (r === 'moderator' ? 'moderator' : 'user'));
            }
            if (meta.avatarUrl) {
              p.avatarUrl = meta.avatarUrl;
            }
          }
        } catch (_) {}
        const audioPub = rp.getTrackPublication(Track.Source.Microphone);
        p.isMuted = !audioPub || audioPub.isMuted;
        const videoPub = rp.getTrackPublication(Track.Source.Camera);
        if (videoPub) {
          const hasLiveTrack = Boolean(videoPub.track && (videoPub.track as any).mediaStreamTrack?.readyState === 'live');
          p.isCamOff = hasLiveTrack ? false : (videoPub.isMuted || !videoPub.isSubscribed);
        } else {
          p.isCamOff = true;
        }
      }
    }
  }

  private startTimer() {
    this.elapsedSeconds = 0;
    this.timerInterval = setInterval(() => {
      this.elapsedSeconds++;
      const mins = String(Math.floor(this.elapsedSeconds / 60)).padStart(2, '0');
      const secs = String(this.elapsedSeconds % 60).padStart(2, '0');
      const timerEl = document.getElementById('conf-clock');
      if (timerEl) {
        timerEl.innerText = `${mins}:${secs}`;
      }
    }, 1000);
  }

  private renderLayout() {
    let el = document.getElementById('conference-container');
    if (!el) {
      el = document.createElement('div');
      el.id = 'conference-container';
      document.body.appendChild(el);
    }
    if (typeof document !== 'undefined') {
      document.body.classList.add('in-conference');
      document.querySelectorAll('.toast-notification').forEach(el => el.remove());
    }
    this.container = el;
    const cleanRoomTitle = (this.config.roomName || 'Sală Conferință').replace(/\s*\(VPS[^)]*\)/gi, '').trim();
    const isMobileClient = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const clientAppTitle = (window as any).vorbelloDesktop 
      ? `Vorbello Desktop v${APP_VERSION}` 
      : (isMobileClient ? `Vorbello Mobile v${APP_VERSION}` : `Vorbello Web v${APP_VERSION}`);

    this.container.innerHTML = `
      <div class="conference-screen">
        <!-- Header -->
        <header class="conference-header">
          <div class="conf-brand-group" style="display: flex; align-items: center; gap: 0.75rem; min-width: 0; flex-shrink: 1;">
            <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" style="width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; filter: drop-shadow(0 0 12px rgba(56, 189, 248, 0.65));" />
            <div style="display: flex; align-items: center; gap: 0.65rem; min-width: 0;">
              <span class="conf-room-title">${cleanRoomTitle}</span>
              ${this.config.isWebinar ? `<span class="webinar-mode-badge">WEBINAR · ${this.config.webinarAudience ? 'CURSANT' : 'GAZDĂ'}</span>` : ''}
              ${this.canInvite ? `
              <button id="btn-header-quick-invite" class="conf-invite-quick-pill" title="Distribuie linkul conferinței" style="margin: 0; white-space: nowrap; flex-shrink: 0;">
                <span>🔗</span> Distribuie
              </button>
              ` : ''}
            </div>
          </div>

          <div class="conf-header-right" style="display: flex; align-items: center; gap: 0.45rem; flex-shrink: 0; flex-wrap: nowrap; position: relative;">
            <button type="button" class="conf-header-icon conf-header-counter" id="btn-header-participants" title="Participanți" aria-label="Participanți">${ICONS.users}<span id="header-participant-count">1</span></button>
            <button type="button" class="conf-header-icon conf-header-counter" id="btn-header-chat" title="Mesaje noi" aria-label="Mesaje noi">${ICONS.chat}<span id="header-chat-count">0</span></button>
            <button type="button" class="conf-header-icon conf-header-counter" id="btn-header-polls" title="Sondaje active" aria-label="Sondaje active"><span aria-hidden="true">▥</span><span id="header-poll-count">0</span></button>
            <button type="button" class="conf-header-icon" id="btn-header-whiteboard" aria-label="Deschide tabla" title="Deschide sau închide tabla"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 5 4 4M4 20l4.5-1L20 7.5a2.8 2.8 0 0 0-4-4L4.5 15 4 20Z"/><path d="M13 20h7"/></svg></button>
            ${(this.isAdmin || this.isModerator || this.isHost) ? `<button type="button" class="conf-header-icon" id="btn-header-pin" aria-label="Fixează sau eliberează bara" title="Fixează sau eliberează bara de jos" aria-pressed="${localStorage.getItem('vorbello_pin_controls') === 'true'}">${ICONS.pin}</button>` : ''}
            <!-- Zoom-Style View Switcher Button & Popover Container (Anchored directly under the button) -->
            <div style="position: relative; display: inline-flex; align-items: center;">
              <button id="btn-toggle-view-mode" title="Comută modul de afișare: Vorbitor Activ, Grilă Dinamică sau Studio 16:9 Paginat" style="display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; color: #f1f5f9; font-size: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
                <span id="view-mode-icon">⊞</span>
                <span id="view-mode-text">Grilă</span>
                <span style="font-size: 0.65rem; opacity: 0.7; margin-left: 2px;">▼</span>
              </button>

              <!-- View Mode Dropdown Popover (Zoom Enterprise Style - Positioned directly below the button) -->
              <div class="view-mode-popover" id="view-mode-popover" style="display: none;">
                <div class="view-mode-option" id="opt-view-speaker" data-mode="speaker">
                  <span class="view-mode-opt-icon">👤</span>
                  <div class="view-mode-opt-text">
                    <strong>Vorbitor Activ</strong>
                    <small>Ecran mare pentru cine vorbește</small>
                  </div>
                  <span class="view-mode-check" id="check-view-speaker"></span>
                </div>
                <div class="view-mode-option selected" id="opt-view-gallery" data-mode="gallery">
                  <span class="view-mode-opt-icon">⊞</span>
                  <div class="view-mode-opt-text">
                    <strong>Grilă Dinamică</strong>
                    <small>Toți participanții redimensionați automat</small>
                  </div>
                  <span class="view-mode-check" id="check-view-gallery">✓</span>
                </div>
                <div class="view-mode-option" id="opt-view-studio" data-mode="studio">
                  <span class="view-mode-opt-icon">🗂️</span>
                  <div class="view-mode-opt-text">
                    <strong>Studio 16:9 Paginat</strong>
                    <small>Carduri 16:9 proporționale, paginare 6-16</small>
                  </div>
                  <span class="view-mode-check" id="check-view-studio"></span>
                </div>
              </div>
            </div>

            <div class="conf-rec-badge" id="conf-rec-badge" style="display: none;" title="Apasă pentru a opri înregistrarea">
              <span class="rec-dot"></span>
              <span class="rec-label">REC</span>
              <span class="rec-timer" id="rec-timer">00:00</span>
              <span class="rec-stop-badge-hint" id="rec-stop-badge-hint">⏹️ Oprește</span>
            </div>

            <div class="conf-timer">
              <span>⏱️</span> <span id="conf-clock">00:00</span>
            </div>
            <button type="button" class="conf-security-button" id="btn-header-security" aria-label="Siguranța întâlnirii: starea nu este verificată" title="Siguranța întâlnirii — stare neverificată"><svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.3 4 5.4v5.8c0 4.8 2.9 8.4 8 10.6 5.1-2.2 8-5.8 8-10.6V5.4L12 2.3Zm0 3.1 5 1.9v3.9c0 3.3-1.7 5.8-5 7.5-3.3-1.7-5-4.2-5-7.5V7.3l5-1.9Z"/></svg></button>
            <div class="conf-quality-pill" id="conf-status-pill" title="Apasă pentru Telemetrie Rețea & Diagnostic LiveKit SFU" style="cursor: pointer;">
              <span class="conf-quality-dot"></span> Se conectează
            </div>
            <span id="btn-header-version" title="Panou Versiune Vorbello & Diagnostic Sistem" style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; padding: 3px 9px; border-radius: 8px; font-weight: 700; font-size: 0.72rem; border: 1px solid rgba(56, 189, 248, 0.35); cursor: pointer; transition: all 0.2s; white-space: nowrap;">v${APP_VERSION}</span>
          </div>
        </header>

        <!-- Live Network Telemetry Popover (Secure & Role-Aware) -->
        <div class="conf-telemetry-popover" id="network-telemetry-popover" style="display: none;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.4rem; font-weight: 700; color: #fff; font-size: 0.85rem;">
              <span>🛡️</span> ${this.isAdmin ? 'Diagnostic Sistem & Server (Admin)' : 'Stare Conexiune & Calitate Audio-Video'}
            </div>
            <button id="btn-close-telemetry" style="background: none; border: none; color: #94a3b8; font-size: 1.25rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>
          ${this.isAdmin ? `
            <div class="telemetry-row">
              <span class="telemetry-label">Server VPS LiveKit:</span>
              <span class="telemetry-val" id="telemetry-vps-url" style="color: #38bdf8;">${this.config.livekitUrl || 'wss://live.vorbello.eu'}</span>
            </div>
            <div class="telemetry-row"><span class="telemetry-label">IP VPS configurat:</span><span class="telemetry-val">2.58.32.179</span></div>
            <div class="telemetry-row">
              <span class="telemetry-label">Bitrate Emisie (Upload):</span>
              <span class="telemetry-val" id="telemetry-upload-bitrate" style="color: #38bdf8; font-family: monospace; font-weight: 700;">-- kbps</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Bitrate Recepție (Download):</span>
              <span class="telemetry-val" id="telemetry-download-bitrate" style="color: #38bdf8; font-family: monospace; font-weight: 700;">-- kbps</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Pachete Pierdute:</span>
              <span class="telemetry-val" id="telemetry-loss-val" style="color: #34d399; font-weight: 600;">0.0%</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Limitare Calitate Rețea:</span>
              <span class="telemetry-val" id="telemetry-quality-limit" style="color: #34d399; font-size: 0.75rem;">Fără limitări</span>
            </div>
          ` : ''}
          <div class="telemetry-row">
            <span class="telemetry-label">Stare Conexiune:</span>
            <span class="telemetry-val" id="telemetry-conn-state" style="color: #10b981;">● Conectat Securizat HD</span>
          </div>
          <div class="telemetry-row">
            <span class="telemetry-label">Latență RTT (Ping):</span>
            <span class="telemetry-val" id="telemetry-rtt-val" style="color: #34d399;">< 20 ms (Excelent)</span>
          </div>
          <div class="telemetry-row">
            <span class="telemetry-label">${this.isAdmin ? 'Flux Cameră Video:' : 'Calitate Imagine:'}</span>
            <span class="telemetry-val" id="telemetry-cam-val" style="color: #34d399;">1280x720 HD (Flux Local Activ)</span>
          </div>
          ${this.isAdmin ? `
            <div class="telemetry-row">
              <span class="telemetry-label">Securitate & Criptare:</span>
              <span class="telemetry-val" style="color: #6ee7b7;">Transport DTLS-SRTP</span>
            </div>
            <div class="telemetry-row"><span class="telemetry-label">Versiune DTLS:</span><span class="telemetry-val" id="telemetry-dtls-version">Se verifică...</span></div>
            <div class="telemetry-row"><span class="telemetry-label">E2EE local:</span><span class="telemetry-val" id="telemetry-e2ee">${this.config.encryptionKey && this.manager.getRoom()?.state === 'connected' && this.manager.getRoom()?.localParticipant.isE2EEEnabled ? 'Activă local' : 'Neconfirmată / inactivă'}</span></div>
            <div class="telemetry-row">
              <span class="telemetry-label">Codec Audio / Video:</span>
              <span class="telemetry-val">Opus 48kHz HD / VP8</span>
            </div>
          ` : ''}

          ${this.isAdmin ? `
            <div id="telemetry-actions-box" style="margin-top: 0.65rem; padding-top: 0.65rem; border-top: 1px solid rgba(255, 255, 255, 0.08); display: flex; flex-direction: column; gap: 0.45rem;">
              <div id="telemetry-status-help" style="font-size: 0.72rem; color: #cbd5e1; line-height: 1.4;">
                Panou tehnic de control administrator.
              </div>
              <div style="display: flex; gap: 0.45rem; flex-wrap: wrap;">
                <button id="btn-telemetry-retry" class="btn btn-sm" style="flex: 1; min-width: 110px; font-size: 0.75rem; font-weight: 600; padding: 6px 10px; justify-content: center; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.4); color: #38bdf8; border-radius: 8px; cursor: pointer;">
                  🔄 Reîncearcă VPS
                </button>
              </div>
            </div>
          ` : `
            <div style="margin-top: 0.5rem; padding-top: 0.5rem; border-top: 1px solid rgba(255, 255, 255, 0.08); font-size: 0.72rem; color: #94a3b8; text-align: center;">
              🔒 Comunicație privată securizată pe platforma Vorbello.
            </div>
          `}
        </div>

        <button type="button" id="btn-mobile-floating-pip" class="mobile-floating-pip" aria-label="Deschide fereastra video" title="Fereastră video"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="3"/><rect x="12" y="12" width="6" height="5" rx="1"/></svg></button>
        <!-- Body Area -->
        <div class="conference-body">
          <main class="stage-area" id="stage-area">
            <!-- Top Filmstrip Carousel Preview Bar (Zoom / Meet Style: active when count > 4 on mobile/tablet or > 6 on desktop) -->
            <div class="top-filmstrip-bar" id="top-filmstrip-bar" style="display: none;">
              <div class="filmstrip-scroll-container" id="filmstrip-scroll-container"></div>
            </div>

            <!-- Dedicated Spotlight Container for Screen Sharing / Pinned Speaker -->
            <div class="spotlight-container" id="spotlight-container" style="display: none;">
              <div class="spotlight-header">
                <div class="spotlight-title">
                  <span class="conf-quality-dot"></span>
                  <span id="spotlight-title-text">Partajare Activă (HD Crisp)</span>
                </div>
                <div style="display: flex; align-items: center; gap: 6px;">
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-toggle-spotlight-size" title="Atingeți sau faceți click pentru a mări ecranul partajat (Ecran Complet / Zoom)">
                    <span>🔍</span> <span id="label-spotlight-size">Mărește</span>
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-switch-spotlight-source" style="display: none; padding: 4px 10px; font-size: 0.75rem;" title="Schimbă fereastra sau ecranul fără să oprești partajarea">
                    <span>🔄</span> Schimbă Sursa
                  </button>
                  <button class="btn btn-danger btn-sm" id="btn-stop-spotlight" style="padding: 4px 10px; font-size: 0.75rem;">
                    Oprește Partajarea
                  </button>
                </div>
              </div>
              <div class="spotlight-viewport" id="spotlight-viewport" title="Click sau atingeți pentru a comuta ecranul complet / zoom">
                <video id="spotlight-video" autoplay playsinline muted></video>
                <div id="spotlight-pinned-card" style="display: none;" class="spotlight-pinned-card">
                  <div class="spotlight-avatar-circle" id="spotlight-avatar-char">M</div>
                  <div class="spotlight-pinned-name" id="spotlight-pinned-name">Mihai Director (Tu)</div>
                  <div class="spotlight-pinned-badge">📌 Participant Fixat pe Scenă</div>
                </div>
              </div>
            </div>

            <!-- Collaborative Whiteboard Container (Full Stage Live Sync) -->
            <div id="whiteboard-container" class="whiteboard-overlay-container" style="display: none;">
              <div class="whiteboard-toolbar">
                <div class="wb-item-title">
                  <span class="wb-toolbar-title">
                    <span>🎨</span> <span>Tablă Albă Live</span>
                  </span>
                </div>
                <div class="wb-item-tools">
                  <button type="button" class="wb-tool-btn active" data-tool="pen" title="Creion / Desen Liber">✏️ <span class="wb-btn-label">Creion</span></button>
                  <button type="button" class="wb-tool-btn" data-tool="eraser" title="Radieră">🧽 <span class="wb-btn-label">Radieră</span></button>
                </div>
                <div class="wb-item-colors">
                  <div class="wb-color-palette">
                    <button type="button" class="wb-color-btn" style="background: #ffffff;" data-color="#ffffff" title="Alb"></button>
                    <button type="button" class="wb-color-btn active" style="background: #38bdf8;" data-color="#38bdf8" title="Sky Blue"></button>
                    <button type="button" class="wb-color-btn" style="background: #ef4444;" data-color="#ef4444" title="Roșu"></button>
                    <button type="button" class="wb-color-btn" style="background: #10b981;" data-color="#10b981" title="Verde"></button>
                    <button type="button" class="wb-color-btn" style="background: #f59e0b;" data-color="#f59e0b" title="Galben"></button>
                    <button type="button" class="wb-color-btn" style="background: #a855f7;" data-color="#a855f7" title="Mov"></button>
                  </div>
                </div>
                <div class="wb-item-actions">
                  <button type="button" class="wb-tool-btn" id="btn-wb-clear" title="Curăță toată tabla">🗑️ <span class="wb-btn-label">Șterge</span></button>
                  <button type="button" class="wb-tool-btn" id="btn-wb-download" title="Descarcă ca imagine PNG">💾 <span class="wb-btn-label">Salvează</span></button>
                  <button type="button" class="btn btn-danger btn-sm" id="btn-wb-close" title="Închide Tabla">✖️ <span class="wb-btn-label">Închide</span></button>
                </div>
              </div>
              <div class="whiteboard-canvas-wrapper" id="wb-canvas-wrapper">
                <canvas id="whiteboard-canvas"></canvas>
              </div>
            </div>

            <!-- Participant Video Grid -->
            <div class="video-grid" id="video-grid"></div>

            <!-- Studio 16:9 Pagination Controls Bar -->
            <div class="studio-pagination-bar" id="studio-pagination-bar" style="display: none;">
              <button class="studio-page-btn" id="btn-studio-prev" title="Pagina anterioară">◀ Anterior</button>
              <span class="studio-page-info" id="studio-page-info">Pagina 1 din 1</span>
              <button class="studio-page-btn" id="btn-studio-next" title="Pagina următoare">Următor ▶</button>
            </div>
          </main>

          <!-- Side Panel (Chat & Participants & Polls) -->
          <aside class="conf-side-panel" id="side-panel" style="display: none;">
            <div class="panel-header">
              <h3 class="panel-title" id="panel-title">Chat Cameră</h3>
              <button id="btn-close-panel" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 1.25rem;">&times;</button>
            </div>
            
            <!-- Chat View -->
            <div id="chat-container" style="display: flex; flex-direction: column; height: 100%;">
              <div class="chat-history" id="chat-history"></div>
              <details class="chat-audience-info"><summary>Cine poate vedea mesajele?</summary><p>Mesajele din acest chat sunt trimise în conversația întâlnirii. Participanții le pot vedea și pot păstra copii. Istoricul disponibil poate depinde de sesiune.</p></details>
              <div id="chat-reply-preview" style="display:none;padding:6px 12px;color:#a5b4fc;font-size:.78rem;border-top:1px solid rgba(255,255,255,.1)"><span id="chat-reply-description"></span><button id="chat-reply-cancel" type="button" aria-label="Anulează răspunsul" style="margin-left:8px;background:none;border:0;color:inherit;cursor:pointer">✕</button></div>
              <div class="chat-emoji-row" aria-label="Emoticoane">${['😊','👍','❤️','👏','😂'].map(emoji => `<button type="button" class="chat-emoji-insert" data-emoji="${emoji}" aria-label="Adaugă ${emoji}">${emoji}</button>`).join('')}</div>
              <div id="chat-file-preview-strip" style="display: none; padding: 6px 12px; background: rgba(56, 189, 248, 0.12); border-top: 1px solid rgba(56, 189, 248, 0.3); font-size: 0.78rem; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #38bdf8;">
                  <span>📎</span>
                  <span id="chat-file-preview-name" style="font-weight: 600; overflow: hidden; text-overflow: ellipsis; max-width: 170px;"></span>
                  <span id="chat-file-preview-size" style="opacity: 0.8; font-size: 0.72rem;"></span>
                </div>
                <button type="button" id="btn-cancel-file-preview" style="background: none; border: none; color: #f87171; cursor: pointer; font-size: 1.1rem; padding: 0 4px; line-height: 1;" title="Anulează atașamentul">&times;</button>
              </div>
              <div class="chat-input-row" style="display: flex; align-items: center; gap: 6px; width: 100%; box-sizing: border-box; overflow: hidden;">
                <input type="file" id="chat-file-input" style="display: none;" />
                <button type="button" class="chat-attach-btn" id="btn-attach-file" title="Atașează documente, PDF sau fișiere curs (max 25MB)">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l7.88-7.88"/>
                  </svg>
                </button>
                <input type="text" class="chat-input" id="chat-text-input" placeholder="Scrie un mesaj..." style="flex: 1 1 0%; min-width: 0;" />
                <button class="btn btn-primary btn-sm" id="btn-send-chat" style="flex-shrink: 0; white-space: nowrap; padding: 0.45rem 0.8rem;">Trimite</button>
              </div>
            </div>

            <!-- Participants View -->
            <div id="participants-container" style="display: none; flex-direction: column; height: 100%; overflow-y: auto;">
              ${this.canInvite ? `
              <!-- 🔗 Dedicated Invite / Share Card inside Participants (Admin & Host Only) -->
              <div class="invite-participant-card" id="invite-participant-card">
                <div class="invite-header">
                  <div class="invite-title"><span>🔗</span> Invită participanți</div>
                </div>
                <div class="invite-input-row">
                  <input type="text" class="invite-input-field" id="input-invite-room-url" readonly value="" placeholder="Se generează linkul..." />
                  <button class="btn btn-primary btn-sm" id="btn-copy-invite-link" style="white-space:nowrap; padding: 0.45rem 0.75rem; font-size: 0.75rem;">📋 Copiază</button>
                </div>
                <div style="display: flex; gap: 0.4rem; margin-top: 2px;">
                  <button class="btn btn-secondary btn-sm" id="btn-copy-invite-text" style="flex: 1; font-size: 0.72rem; padding: 0.35rem 0.6rem; display: flex; align-items: center; justify-content: center; gap: 4px;">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="2"/><path d="m5 7 7 5 7-5"/></svg> Copiază textul invitației
                  </button>
                </div>
              </div>
              ` : ''}

              ${this.isAdmin ? `
              <!-- Global Moderation Actions (Strictly Admin Only) -->
              <div class="participants-global-actions">
                <button class="btn btn-secondary btn-sm" id="btn-mute-all" title="Oprește sau pornește microfonul tuturor participanților">
                  🔇 Mute Toți
                </button>
                <button class="btn btn-secondary btn-sm" id="btn-stop-all-cam" title="Oprește sau pornește camera tuturor participanților">
                  📷 Stop Camere
                </button>
                <button class="btn btn-secondary btn-sm" id="btn-lower-all-hands" title="Coboară toate mâinile ridicate din sală (Zoom / Meet)">
                  ✋ Mâini Jos
                </button>
              </div>
              <div style="font-size: 0.7rem; color: var(--text-muted); padding: 0.25rem 1rem 0.5rem 1rem; border-bottom: 1px solid rgba(255, 255, 255, 0.05);">
                * Acțiunile globale afectează participanții din sală.
              </div>
              ` : ''}

              <!-- 🔍 Dynamic Search & Filter Box for Participants (Zoom & Google Meet Style) -->
              <div class="participants-search-box">
                <div style="position: relative; display: flex; align-items: center; width: 100%;">
                  <span style="position: absolute; left: 10px; font-size: 0.82rem; color: #94a3b8; pointer-events: none;">🔍</span>
                  <input type="text" id="input-search-participants" placeholder="Caută participant..." autocomplete="off" style="width: 100%; box-sizing: border-box; padding: 7px 30px 7px 32px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14); border-radius: 10px; color: #fff; font-size: 0.8rem; outline: none; transition: border-color 0.2s ease;" />
                  <button type="button" id="btn-clear-search-participants" style="display: none; position: absolute; right: 8px; background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 1.1rem; line-height: 1; padding: 2px 4px;" title="Șterge căutarea">&times;</button>
                </div>
              </div>

              <div id="participants-list"></div>
            </div>

            <!-- Live Polls View in Side Panel -->
            <div id="polls-container" class="polls-drawer-container" style="display: none;">
              <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 0.5rem; border-bottom: 1px solid rgba(255,255,255,0.08);">
                <div style="font-weight: 700; font-size: 0.9rem; color: #fff; display: flex; align-items: center; gap: 6px;">
                  <span>📊</span> Sondaje Live
                </div>
                ${(this.isAdmin || this.isModerator) ? `
                <button class="btn btn-primary btn-sm" id="btn-create-poll-open" style="font-size: 0.72rem; padding: 4px 10px;">
                  <span aria-hidden="true" style="color:#fff;font-size:1.2em;font-weight:700;line-height:1">+</span> Creează Sondaj
                </button>
                ` : ''}
              </div>
              <div id="polls-list-content" style="display: flex; flex-direction: column; gap: 0.75rem;"></div>
            </div>
          </aside>
        </div>

        <!-- Floating Ultra-Premium Ergonomic Controls Dock (Essential Always Visible + On-Click Tools) -->
        <footer class="conference-controls">
          <div class="controls-dock-bar" id="controls-dock-bar">
            <!-- 1. Microfon (Always Visible) -->
            <div class="device-split"><button class="control-btn ${this.manager.isMicEnabled ? 'active-on' : 'active-off'}" id="btn-toggle-mic" title="Microfon (Mute/Unmute)">
              <span class="btn-icon">${this.manager.isMicEnabled ? ICONS.micOn : ICONS.micOff}</span>
              <span class="btn-label" id="label-mic">${this.manager.isMicEnabled ? 'Microfon' : 'Oprit'}</span>
            </button>
            <div class="quick-device-wrapper"><button type="button" class="quick-device-trigger" id="btn-quick-audio" aria-label="Alege microfonul și difuzorul" aria-expanded="false"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 15 6-6 6 6"/></svg></button><div class="quick-device-menu" id="quick-audio-menu" style="display:none"></div></div></div>

            <!-- 2. Cameră (Always Visible) -->
            <div class="device-split"><button class="control-btn ${this.manager.isCameraEnabled ? 'active-on' : 'active-off'}" id="btn-toggle-cam" title="Cameră Video (Pornit/Oprit)">
              <span class="btn-icon">${this.manager.isCameraEnabled ? ICONS.camOn : ICONS.camOff}</span>
              <span class="btn-label" id="label-cam">${this.manager.isCameraEnabled ? 'Cameră' : 'Oprită'}</span>
            </button>
            <div class="quick-device-wrapper"><button type="button" class="quick-device-trigger" id="btn-quick-camera" aria-label="Alege camera" aria-expanded="false"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 15 6-6 6 6"/></svg></button><div class="quick-device-menu" id="quick-camera-menu" style="display:none"></div></div></div>

            <!-- 3. Partajare Ecran (Always Visible + Hot-Swap 3-Option Menu) -->
            <div class="screen-share-wrapper" id="screen-share-wrapper" ${this.config.webinarAudience ? 'style="display:none"' : ''}>
              <button class="control-btn ${this.manager.isScreenShareEnabled ? 'active-screen' : ''}" id="btn-toggle-screen" title="Partajează Ecranul (Prezentare)">
                <span class="btn-icon">${ICONS.screen}</span>
                <span class="btn-label" id="label-screen">${this.manager.isScreenShareEnabled ? 'Oprește' : 'Partajare'}</span>
              </button>
              <div class="screen-share-active-popover" id="screen-share-active-menu" style="display: none;">
                <!-- Opțiunea 1: Schimbă Sursa -->
                <button type="button" class="screen-popover-item" id="btn-menu-switch-source">
                  <span class="screen-popover-icon">🔄</span>
                  <div class="screen-popover-text">
                    <strong style="display: block; font-size: 0.8rem; color: #f8fafc;">Schimbă Sursa</strong>
                    <small style="display: block; font-size: 0.68rem; color: #94a3b8;">Alege altă fereastră sau ecran din mers</small>
                  </div>
                </button>
                <!-- Opțiunea 2: Sunet Partajare (Riga Specială) -->
                <button type="button" class="screen-popover-item" id="btn-menu-toggle-audio">
                  <span class="screen-popover-icon" id="menu-share-audio-icon">${this.isShareAudioEnabled ? '🔊' : '🔇'}</span>
                  <div class="screen-popover-text">
                    <strong style="display: block; font-size: 0.8rem; color: #f8fafc;" id="menu-share-audio-title">${this.isShareAudioEnabled ? 'Sunet Partajare: Activ' : 'Sunet Partajare: Oprit'}</strong>
                    <small style="display: block; font-size: 0.68rem; color: #94a3b8;" id="menu-share-audio-desc">${this.isShareAudioEnabled ? 'Sunetul de sistem/tab este inclus' : 'Faceți clic pentru a activa sunetul'}</small>
                  </div>
                </button>
                <!-- Opțiunea 3: Oprește Partajarea -->
                <button type="button" class="screen-popover-item danger" id="btn-menu-stop-share">
                  <span class="screen-popover-icon">⏹️</span>
                  <div class="screen-popover-text">
                    <strong style="display: block; font-size: 0.8rem; color: #fca5a5;">Oprește Partajarea</strong>
                    <small style="display: block; font-size: 0.68rem; color: #cbd5e1;">Revenire la camera video</small>
                  </div>
                </button>
              </div>
            </div>

            <!-- 4. Chat (Always Visible) -->
            <button class="control-btn" id="btn-open-chat" title="Chat Conferință">
              <span class="btn-icon">${ICONS.chat}</span>
              <span class="btn-label">Chat</span>
              <span class="chat-unread-badge" id="chat-unread-badge" style="display: none;">0</span>
            </button>

            <!-- 5. Participanți (Always Visible) -->
            <button class="control-btn" id="btn-open-participants" title="Listă Participanți">
              <span class="btn-icon">${ICONS.users}</span>
              <span class="btn-label" id="label-participants">Participanți (${this.participants.length})</span>
            </button>

            <!-- Mobile Flip Camera (Front / Rear) - Only visible on mobile touch devices -->
            <button class="control-btn btn-flip-cam-mobile" id="btn-flip-cam" title="Comută Camera (Față / Spate)">
              <span class="btn-icon">🔄</span>
            </button>

            <!-- 6. More Options Popover Trigger ("Celelalte opțiuni la click") -->
            <div class="more-options-wrapper" id="more-options-wrapper">
              <button class="control-btn btn-more-options ${this.isLocalHandRaised ? 'has-active-tool' : ''}" id="btn-more-options" title="Mai multe instrumente și opțiuni">
                <span class="btn-icon">⋯</span>
                <span class="btn-label">Mai multe</span>
                ${this.isLocalHandRaised ? '<span class="dock-hand-dot" id="dock-hand-indicator" title="Mâna este ridicată">✋</span>' : '<span class="dock-hand-dot" id="dock-hand-indicator" style="display: none;" title="Mâna este ridicată">✋</span>'}
              </button>

              <!-- Floating Popover for Secondary / Advanced Tools on Desktop -->
              <div class="more-options-popover" id="more-options-popover" style="display: none;">
                <div class="popover-header">Instrumente & Setări</div>
                
                <div class="popover-grid">
                  ${(this.isAdmin || this.isHost) ? `<button class="popover-item-btn" id="btn-pre-event-share" title="Imagine, muzică și cronometru înaintea cursului"><span class="popover-item-icon">⏳</span><span class="popover-item-label">Înainte de curs</span></button>` : ''}
                  <button class="popover-item-btn" id="btn-browser-picture-in-picture" title="Deschide imaginea într-o fereastră mică"><span class="popover-item-icon">▣</span><span class="popover-item-label">Fereastră video</span></button>
                  ${(this.isAdmin || this.isModerator || this.isHost) ? `<button class="popover-item-btn" id="btn-pin-controls" aria-pressed="${localStorage.getItem('vorbello_pin_controls') === 'true'}"><span class="popover-item-icon">📌</span><span class="popover-item-label">${localStorage.getItem('vorbello_pin_controls') === 'true' ? 'Eliberează bara' : 'Fixează bara'}</span></button>` : ''}
                  <button class="popover-item-btn" id="btn-open-security-overview" title="Vezi setările de siguranță ale întâlnirii"><span class="popover-item-icon">🛡️</span><span class="popover-item-label">Siguranță</span></button>
                  ${(this.isAdmin || this.isModerator) ? `<button class="popover-item-btn" id="btn-open-admin-quick" title="Administrare participanți"><span class="popover-item-icon">${ICONS.users}</span><span class="popover-item-label">Administrare</span></button>` : ''}
                  <!-- Whiteboard Button -->
                  <button class="popover-item-btn ${this.isWhiteboardOpen ? 'active' : ''}" id="btn-toggle-whiteboard" title="Tablă Colaborativă Interactivă">
                    <span class="popover-item-icon">🎨</span>
                    <span class="popover-item-label" id="label-whiteboard">${this.isWhiteboardOpen ? 'Închide Tablă' : 'Tablă Albă'}</span>
                  </button>

                  <!-- Live Polls Button -->
                  <button class="popover-item-btn" id="btn-open-polls" title="Sondaje Live în Conferință">
                    <span class="popover-item-icon">📊</span>
                    <span class="popover-item-label">Sondaj</span>
                  </button>

                  <!-- Ridică Mâna -->
                  <button class="popover-item-btn ${this.isLocalHandRaised ? 'active-hand' : ''}" id="btn-raise-hand" title="Ridică sau coboară mâna">
                    <span class="popover-item-icon">✋</span>
                    <span class="popover-item-label" id="label-hand">${this.isLocalHandRaised ? 'Coboară Mâna' : 'Ridică Mâna'}</span>
                  </button>

                  <!-- Reacții Live Button -->
                  <button class="popover-item-btn" id="btn-open-reactions" title="Reacții Rapide Live">
                    <span class="popover-item-icon">🎉</span>
                    <span class="popover-item-label">Reacții</span>
                  </button>

                  <!-- Dispozitive & Setări -->
                  <button class="popover-item-btn" id="btn-open-devices" title="Setări Audio & Video">
                    <span class="popover-item-icon">⚙️</span>
                    <span class="popover-item-label">Dispozitive</span>
                  </button>

                  ${(this.isAdmin || this.isModerator) ? `
                  <!-- Network & Realtime Transfer Monitor (Admin) -->
                  <button class="popover-item-btn" id="btn-open-telemetry-monitor" title="Monitor Date & Bitrate în Timp Real">
                    <span class="popover-item-icon">📊</span>
                    <span class="popover-item-label">Monitor Rețea</span>
                  </button>
                  ` : ''}

                  ${(this.isHost || this.config.canRecord === true) ? `
                  <!-- Record Button (Host Only) -->
                  <button class="popover-item-btn" id="btn-toggle-record" title="Înregistrare Conferință HD">
                    <span class="popover-item-icon">⏺️</span>
                    <span class="popover-item-label" id="label-record">Înregistrează</span>
                  </button>
                  ` : ''}
                </div>

                <!-- Live Quick Reactions Emoji Bar directly inside the popover! -->
                <div class="popover-reactions-bar">
                  <span class="popover-reactions-title">Trimite Reacție Rapidă:</span>
                  <div class="popover-emojis-row">
                    <button class="btn-reaction-emoji" data-emoji="👏" title="Aplauze">👏</button>
                    <button class="btn-reaction-emoji" data-emoji="👍" title="Bravo">👍</button>
                    <button class="btn-reaction-emoji" data-emoji="❤️" title="Inimă">❤️</button>
                    <button class="btn-reaction-emoji" data-emoji="🎉" title="Felicitări">🎉</button>
                    <button class="btn-reaction-emoji" data-emoji="😂" title="Amuzant">😂</button>
                    <button class="btn-reaction-emoji" data-emoji="😮" title="Uimire">😮</button>
                    <button class="btn-reaction-emoji" data-emoji="🔥" title="Foc">🔥</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 7. Părăsește Conferința (Always Visible) -->
            <button class="control-btn leave" id="btn-leave-conf" title="Părăsește sala de conferință">
              <span class="btn-icon">${ICONS.leave}</span>
              <span class="btn-label">Părăsește</span>
            </button>
          </div>
        </footer>

        <!-- Mobile Actions Sheet (Zoom Style) -->
        <div class="mobile-more-sheet-overlay" id="mobile-more-sheet-overlay" style="display: none;">
          <div class="mobile-more-sheet" id="mobile-more-sheet">
            <div class="sheet-drag-handle"></div>
            <div class="sheet-header">
              <div class="sheet-title">Opțiuni Sală Conferință</div>
              <button class="sheet-close-btn" id="btn-close-more-sheet">&times;</button>
            </div>

            <!-- Zoom-Style Quick Reactions Bar directly in the sheet -->
            <div class="sheet-quick-reactions-section">
              <div class="sheet-section-title">Trimite Reacție Rapidă:</div>
              <div class="sheet-quick-emojis-row">
                <button class="sheet-quick-emoji" data-emoji="👏" title="Aplauze">👏</button>
                <button class="sheet-quick-emoji" data-emoji="👍" title="Bravo">👍</button>
                <button class="sheet-quick-emoji" data-emoji="❤️" title="Inimă">❤️</button>
                <button class="sheet-quick-emoji" data-emoji="🎉" title="Felicitări">🎉</button>
                <button class="sheet-quick-emoji" data-emoji="😂" title="Amuzant">😂</button>
                <button class="sheet-quick-emoji" data-emoji="😮" title="Uimire">😮</button>
                <button class="sheet-quick-emoji" data-emoji="🔥" title="Foc">🔥</button>
                <button class="sheet-quick-emoji" data-emoji="🚀" title="Rachetă">🚀</button>
              </div>
            </div>

            <div class="sheet-grid">
              <button class="sheet-item-btn" id="sheet-btn-picture-in-picture"><div class="sheet-icon-circle">▣</div><span class="sheet-item-label">Fereastră video</span></button>
              <button class="sheet-item-btn" id="sheet-btn-participants">
                <div class="sheet-icon-circle">👥</div>
                <span class="sheet-item-label">Participanți (<span id="sheet-part-count">${this.participants.length}</span>)</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-chat">
                <div class="sheet-icon-circle">💬</div>
                <span class="sheet-item-label">Chat</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-flip-cam">
                <div class="sheet-icon-circle">🔄</div>
                <span class="sheet-item-label">Comută Camera</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-raise-hand">
                <div class="sheet-icon-circle">✋</div>
                <span class="sheet-item-label">${this.isLocalHandRaised ? 'Coboară Mâna' : 'Ridică Mâna'}</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-reactions">
                <div class="sheet-icon-circle">🎉</div>
                <span class="sheet-item-label">Reacții</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-whiteboard">
                <div class="sheet-icon-circle">🎨</div>
                <span class="sheet-item-label">Tablă Albă</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-polls">
                <div class="sheet-icon-circle">📊</div>
                <span class="sheet-item-label">Sondaje</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-screen">
                <div class="sheet-icon-circle">💻</div>
                <span class="sheet-item-label">Partajează</span>
              </button>
              ${(this.isHost || this.config.canRecord === true) ? `
              <button class="sheet-item-btn" id="sheet-btn-record">
                <div class="sheet-icon-circle">⏺️</div>
                <span class="sheet-item-label">Înregistrare</span>
              </button>
              ` : ''}
              <button class="sheet-item-btn" id="sheet-btn-devices">
                <div class="sheet-icon-circle">⚙️</div>
                <span class="sheet-item-label">Setări</span>
              </button>
              ${(this.isAdmin || this.isModerator) ? `
              <button class="sheet-item-btn" id="sheet-btn-telemetry-monitor">
                <div class="sheet-icon-circle">📊</div>
                <span class="sheet-item-label">Monitor Rețea</span>
              </button>
              ` : ''}
              <button class="sheet-item-btn" id="sheet-btn-view-mode">
                <div class="sheet-icon-circle" id="sheet-icon-view-mode">⊞</div>
                <span class="sheet-item-label" id="sheet-label-view-mode">Mod Galerie / Vorbitor</span>
              </button>
              ${this.canInvite ? `
              <button class="sheet-item-btn sheet-item-invite" id="sheet-btn-invite">
                <div class="sheet-icon-circle" style="background: rgba(99, 102, 241, 0.25); color: #818cf8;">🔗</div>
                <span class="sheet-item-label" style="color: #818cf8; font-weight: 600;">Invită în Cameră (Copiază Link)</span>
              </button>
              ` : ''}
            </div>
          </div>
        </div>

        <!-- Mobile Floating Reactions Dock (triggered by Reacții button) -->
        <div class="mobile-floating-reactions-dock" id="mobile-floating-reactions-dock" style="display: none;">
          <div class="dock-emojis-row">
            <button class="sheet-quick-emoji" data-emoji="👏" title="Aplauze">👏</button>
            <button class="sheet-quick-emoji" data-emoji="👍" title="Bravo">👍</button>
            <button class="sheet-quick-emoji" data-emoji="❤️" title="Inimă">❤️</button>
            <button class="sheet-quick-emoji" data-emoji="🎉" title="Felicitări">🎉</button>
            <button class="sheet-quick-emoji" data-emoji="😂" title="Amuzant">😂</button>
            <button class="sheet-quick-emoji" data-emoji="😮" title="Uimire">😮</button>
            <button class="sheet-quick-emoji" data-emoji="🔥" title="Foc">🔥</button>
            <button class="sheet-quick-emoji" data-emoji="🚀" title="Rachetă">🚀</button>
          </div>
          <button class="dock-close-btn" id="btn-close-reactions-dock" title="Închide">&times;</button>
        </div>

        <!-- Floating Reactions Stream Container (Zoom / Teams Style) -->
        <div class="floating-reactions-stream" id="floating-reactions-stream"></div>

        <!-- Floating Live Poll Popup (When poll is active and drawer is closed) -->
        <div id="live-poll-popup" class="live-poll-card-float" style="display: none;"></div>
      </div>

      <!-- Create Poll Modal -->
      <div class="picker-modal-overlay" id="create-poll-modal" style="display: none; align-items: center; justify-content: center; z-index: 10005;">
        <div style="background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 18px; padding: 1.5rem; width: 440px; max-width: 90vw; display: flex; flex-direction: column; gap: 1rem; box-shadow: 0 20px 50px rgba(0,0,0,0.8);">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 0.5rem;">
            <div style="font-size: 1rem; font-weight: 700; color: #fff; display: flex; align-items: center; gap: 6px;">
              <span>📊</span> Creează Sondaj Live în Sală
            </div>
            <button id="btn-close-create-poll" style="background: none; border: none; color: #94a3b8; font-size: 1.3rem; cursor: pointer;">&times;</button>
          </div>
          <div>
            <label style="font-size: 0.75rem; color: #cbd5e1; font-weight: 600; display: block; margin-bottom: 4px;">Întrebare:</label>
            <input type="text" id="input-poll-question" class="invite-input-field" style="width: 100%; font-family: inherit;" placeholder="Ex: Sunteți de acord cu planul de lansare?" />
          </div>
          <div>
            <label style="font-size: 0.75rem; color: #cbd5e1; font-weight: 600; display: block; margin-bottom: 4px;">Opțiuni de răspuns:</label>
            <div id="poll-options-inputs-list" style="display: flex; flex-direction: column; gap: 6px;">
              <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 1 (ex: Da, perfect)" />
              <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 2 (ex: Nu, necesită revizuire)" />
            </div>
            <button type="button" id="btn-add-poll-option" class="btn btn-secondary btn-sm" style="margin-top: 6px; font-size: 0.72rem; padding: 3px 8px;">
              ➕ Adaugă altă opțiune
            </button>
          </div>
          <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.5rem;">
            <button type="button" id="btn-cancel-create-poll" class="btn btn-secondary btn-sm">Anulează</button>
            <button type="button" id="btn-submit-launch-poll" class="btn btn-primary btn-sm">🚀 Lansează Sondaj</button>
          </div>
        </div>
      </div>

      <div class="picker-modal-overlay" id="security-overview-modal" style="display:none;align-items:center;justify-content:center;z-index:10005;">
        <section role="dialog" aria-modal="true" aria-labelledby="security-overview-title" class="conf-modal-scrollable-card" style="background:#101522;border:1px solid rgba(255,255,255,.16);border-radius:20px;padding:1.5rem;width:540px;max-width:92vw;max-height:85vh;overflow:auto;color:#f8fafc;">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:1rem;"><h3 id="security-overview-title" style="margin:0;">🛡️ Siguranța întâlnirii</h3><button id="btn-close-security-overview" type="button" aria-label="Închide" style="background:none;border:0;color:#cbd5e1;font-size:1.6rem;cursor:pointer;">&times;</button></div>
          <p style="color:#cbd5e1;font-size:.8rem;line-height:1.5;">Setările de siguranță ale întâlnirii</p>
          <div id="security-overview-rows" style="display:grid;gap:.75rem;font-size:.85rem;"></div>
        </section>
      </div>

      <!-- Device Settings Modal (Microphone, Camera, Speaker Selection & VU Meter) -->
      <div class="picker-modal-overlay" id="device-settings-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div class="conf-modal-scrollable-card" style="background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem; width: 500px; max-width: 92vw; max-height: 88vh; max-height: 88dvh; overflow-y: auto; -webkit-overflow-scrolling: touch; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); display: flex; flex-direction: column; gap: 1.25rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.75rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <div style="font-size: 1.4rem;">⚙️</div>
              <div>
                <h3 style="margin: 0; font-size: 1.15rem; font-weight: 700; color: #fff;">Setări Dispozitive Audio & Video</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Selectați microfonul, camera și difuzoarele active</div>
              </div>
            </div>
            <button id="btn-close-device-settings" style="background: none; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>

          <!-- Version Banner in Device Settings -->
          <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.25); border-radius: 12px; padding: 0.65rem 1rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <span style="font-size: 1.2rem;">🚀</span>
              <div>
                <div style="font-size: 0.85rem; font-weight: 700; color: #38bdf8;">${clientAppTitle}</div>
                <div style="font-size: 0.7rem; color: var(--text-muted);">${APP_BUILD_TAG} • Build ${APP_BUILD_DATE}</div>
              </div>
            </div>
            <span style="font-size: 0.7rem; background: #10b981; color: #fff; padding: 2px 8px; border-radius: 12px; font-weight: 700;">STABIL</span>
          </div>

          <!-- Microphone Selection & Live VU Meter -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🎤 Selectează Microfonul</span>
              <span id="vu-meter-label" style="font-size: 0.75rem; color: #6ee7b7; font-weight: 600;">Semnal audio live</span>
            </label>
            <select class="form-select" id="conf-select-mic" style="width: 100%; margin-bottom: 0.6rem;"></select>
            
            <!-- Live VU Meter Bar -->
            <div style="background: rgba(0, 0, 0, 0.5); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; height: 14px; overflow: hidden; position: relative;">
              <div id="device-vu-meter-bar" style="height: 100%; width: 0%; background: linear-gradient(90deg, #10b981 0%, #eab308 75%, #ef4444 100%); transition: width 0.06s ease; border-radius: 7px;"></div>
            </div>
            <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 4px;">Vorbiți în microfon pentru a verifica captura sonoră în timp real.</div>
          </div>

          <!-- Camera Selection -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label">📹 Selectează Camera Web</label>
            <select class="form-select" id="conf-select-cam" style="width: 100%;"></select>
          </div>

          <!-- Camera Quality Selection -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🎥 Rezoluție Cameră Video</span>
              <span id="label-camera-quality" style="font-size: 0.72rem; color: #38bdf8; font-weight: 600;">${this.selectedCameraQuality === 'fullhd' ? 'Full HD 1080p' : 'HD 720p'}</span>
            </label>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
              <button type="button" class="btn-picker-quality ${this.selectedCameraQuality === 'hd' ? 'active' : ''}" id="btn-cam-quality-hd" style="padding: 8px 12px; font-size: 0.82rem; font-weight: 600; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); background: ${this.selectedCameraQuality === 'hd' ? '#3b82f6' : 'rgba(15,23,42,0.7)'}; color: ${this.selectedCameraQuality === 'hd' ? '#fff' : '#94a3b8'}; cursor: pointer;">⚡ HD 720p (30 FPS)</button>
              <button type="button" class="btn-picker-quality ${this.selectedCameraQuality === 'fullhd' ? 'active' : ''}" id="btn-cam-quality-fullhd" style="padding: 8px 12px; font-size: 0.82rem; font-weight: 600; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); background: ${this.selectedCameraQuality === 'fullhd' ? '#3b82f6' : 'rgba(15,23,42,0.7)'}; color: ${this.selectedCameraQuality === 'fullhd' ? '#fff' : '#94a3b8'}; cursor: pointer;">🎥 Full HD 1080p (30 FPS)</button>
            </div>
            <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 4px;">HD 720p este recomandat pentru un consum redus de resurse și bandă pe server.</div>
          </div>

          <!-- Screen Share Quality Selection -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🖥️ Rezoluție Partajare Ecran</span>
              <span id="label-screen-quality" style="font-size: 0.72rem; color: #38bdf8; font-weight: 600;">${this.selectedScreenQuality === 'fullhd' ? 'Full HD 1080p' : 'HD 720p'}</span>
            </label>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
              <button type="button" class="btn-picker-quality ${this.selectedScreenQuality === 'hd' ? 'active' : ''}" id="btn-modal-share-quality-hd" style="padding: 8px 12px; font-size: 0.82rem; font-weight: 600; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); background: ${this.selectedScreenQuality === 'hd' ? '#3b82f6' : 'rgba(15,23,42,0.7)'}; color: ${this.selectedScreenQuality === 'hd' ? '#fff' : '#94a3b8'}; cursor: pointer;">⚡ HD 720p (30 FPS)</button>
              <button type="button" class="btn-picker-quality ${this.selectedScreenQuality === 'fullhd' ? 'active' : ''}" id="btn-modal-share-quality-fullhd" style="padding: 8px 12px; font-size: 0.82rem; font-weight: 600; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); background: ${this.selectedScreenQuality === 'fullhd' ? '#3b82f6' : 'rgba(15,23,42,0.7)'}; color: ${this.selectedScreenQuality === 'fullhd' ? '#fff' : '#94a3b8'}; cursor: pointer;">🖥️ Full HD 1080p (30 FPS)</button>
            </div>
            <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 4px;">Rezoluția va fi aplicată la transmiterea ferestrelor și ecranului.</div>
          </div>

          <!-- Video Effects & Background Blur (Roadmap Feature) -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🎭 Efect Cameră & Estompare Fundal</span>
              <span id="label-active-effect" style="font-size: 0.72rem; color: #818cf8; font-weight: 600;">Normal</span>
            </label>
            <div class="effect-selector-grid">
              <button type="button" class="effect-btn active" data-effect="none" title="Fără filtru pe cameră">
                <span style="font-size: 1.1rem;">🚫</span>
                <span>Normal</span>
              </button>
              <button type="button" class="effect-btn" data-effect="blur-soft" title="Estompare fină a fundalului">
                <span style="font-size: 1.1rem;">🌫️</span>
                <span>Blur Fin</span>
              </button>
              <button type="button" class="effect-btn" data-effect="blur-heavy" title="Estompare intensă">
                <span style="font-size: 1.1rem;">🌫️</span>
                <span>Blur Intens</span>
              </button>
              <button type="button" class="effect-btn" id="btn-select-background" title="Alege o imagine de pe dispozitiv">
                <span style="font-size: 1.1rem;">🖼️</span><span>Fundal propriu</span>
              </button>
              <input type="file" id="background-image-input" accept="image/png,image/jpeg,image/webp" hidden>
              <button type="button" class="effect-btn" data-effect="studio" title="Filtru de culoare, vizibil doar în previzualizarea proprie">
                <span style="font-size: 1.1rem;">✨</span>
                <span>Studio (local)</span>
              </button>
              <button type="button" class="effect-btn" data-effect="warm" title="Filtru de culoare, vizibil doar în previzualizarea proprie">
                <span style="font-size: 1.1rem;">🌆</span>
                <span>Ambient (local)</span>
              </button>
            </div>
            <p style="font-size:0.72rem;color:var(--text-muted)">Fundalul este procesat pe dispozitiv și se vede în imaginea transmisă.</p>
          </div>

          <div class="form-group" style="margin-bottom:0;">
            <label class="form-label" for="camera-exposure">☀️ Iluminare cameră</label>
            <input type="range" id="camera-exposure" disabled style="width:100%;">
            <div id="camera-exposure-status" style="font-size:0.72rem;color:var(--text-muted)">Disponibilă dacă dispozitivul permite reglarea expunerii.</div>
          </div>

          <!-- Speaker Output & Test Sound -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🔊 Ieșire Sunet (Difuzoare / Căști)</span>
              <button id="btn-test-speakers" class="btn btn-secondary btn-sm" style="padding: 2px 8px; font-size: 0.72rem; display: inline-flex; align-items: center; gap: 4px;">
                🔔 Test Sunet
              </button>
            </label>
            <select class="form-select" id="conf-select-speaker" style="width: 100%;"></select>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-primary" id="btn-save-device-settings" style="padding: 0.55rem 1.4rem;">
              Salvează & Aplică
            </button>
          </div>
        </div>
      </div>

      <!-- Rename Participant Modal (Zoom-style) -->
      <div class="rename-modal-overlay" id="rename-modal" style="display: none;">
        <div class="rename-modal-card">
          <div style="font-size: 1.15rem; font-weight: 700; margin-bottom: 0.4rem; color: #fff;">
            Redenumește Participantul
          </div>
          <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 1.25rem;">
            Introduceți noul nume afișat în conferință pentru acest participant.
          </div>
          <input type="text" id="rename-input" class="chat-input" style="width: 100%; margin-bottom: 1.25rem;" />
          <div style="display: flex; justify-content: flex-end; gap: 0.75rem;">
            <button class="btn btn-secondary btn-sm" id="btn-cancel-rename">Anulează</button>
            <button class="btn btn-primary btn-sm" id="btn-save-rename">Salvează Noul Nume</button>
          </div>
        </div>
      </div>

      <!-- Screen & Window Share Picker Modal (Zoom style) -->
      <div class="picker-modal-overlay" id="screen-picker-modal" style="display: none;">
        <div class="picker-modal-card">
          <div class="picker-modal-header">
            <div>
              <div style="font-size: 1.2rem; font-weight: 700; color: #fff;">Alege ce dorești să partajezi</div>
              <div style="font-size: 0.825rem; color: var(--text-secondary); margin-top: 3px;">
                Selectează o fereastră activă (browser, document, etc.) sau un ecran complet
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <button class="btn btn-secondary btn-sm" id="btn-refresh-sources" style="padding: 6px 14px; font-size: 0.8rem;" title="Actualizează lista de ferestre">
                🔄 Reîmprospătează
              </button>
              <button id="btn-close-picker" style="background: none; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
            </div>
          </div>
          <div class="picker-tip-banner">
            <span style="font-size: 1.15rem;">💡</span>
            <div><strong>Sfat pentru fundal:</strong> Pentru a vedea Vorbello pe tot ecranul în timp ce prezentați alt program, alegeți <strong>Ferestre Aplicații</strong> și păstrați fereastra partajată deschisă, fără a o minimiza. La partajarea întregului ecran se vede ceea ce este afișat pe monitor.</div>
          </div>
          <div class="picker-tabs">
            <button class="picker-tab-btn active" id="tab-picker-screens">🖥️ Ecrane Complete (Recomandat)</button>
            <button class="picker-tab-btn" id="tab-picker-windows">🪟 Ferestre Aplicații</button>
          </div>
          <div class="picker-grid" id="picker-sources-grid"></div>
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; border-top: 1px solid rgba(255, 255, 255, 0.08); background: rgba(0, 0, 0, 0.25); border-radius: 10px; margin: 8px 16px 0; flex-wrap: wrap; gap: 8px;">
            <label style="display: flex; align-items: center; gap: 8px; color: #e2e8f0; font-size: 0.85rem; cursor: pointer; user-select: none;">
              <input type="checkbox" id="share-system-audio" style="accent-color: #3b82f6; width: 16px; height: 16px;" /> Partajează sunetul computerului (poate include și alte aplicații)
            </label>
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 0.82rem; color: #94a3b8; font-weight: 600;">Calitate:</span>
              <div class="picker-quality-group" style="display: inline-flex; background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px; padding: 2px;">
                <button type="button" class="btn-picker-quality active" id="btn-share-quality-hd" style="padding: 4px 10px; font-size: 0.78rem; font-weight: 600; border-radius: 6px; border: none; background: #3b82f6; color: #fff; cursor: pointer;">⚡ HD 720p (30 FPS)</button>
                <button type="button" class="btn-picker-quality" id="btn-share-quality-fullhd" style="padding: 4px 10px; font-size: 0.78rem; font-weight: 600; border-radius: 6px; border: none; background: transparent; color: #94a3b8; cursor: pointer;">🖥️ Full HD 1080p (30 FPS)</button>
              </div>
            </div>
          </div>
          <div class="picker-footer">
            <button class="btn btn-secondary btn-sm" id="btn-share-browser-experimental">Filă browser (experimental)</button>
            <button class="btn btn-secondary btn-sm" id="btn-cancel-picker">Anulează</button>
            <button class="btn btn-primary btn-sm" id="btn-confirm-picker" disabled>Partajează Fereastra Selectată</button>
          </div>
        </div>
      </div>

      <!-- Custom Vorbello Leave Conference Confirmation Modal -->
      <div class="picker-modal-overlay" id="confirm-leave-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: rgba(14, 19, 32, 0.95); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem 2.2rem; width: 440px; max-width: 90vw; margin: auto; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85), 0 0 35px rgba(239, 68, 68, 0.2); text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1.25rem;">
          
          <div style="position: relative;">
            <div style="width: 72px; height: 72px; border-radius: 50%; background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.35); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 24px rgba(239, 68, 68, 0.25);">
              <img src="${VORBELLO_LOGO}" alt="Vorbello" style="width: 44px; height: 44px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.5));" />
            </div>
            <div style="position: absolute; bottom: -2px; right: -2px; width: 26px; height: 26px; border-radius: 50%; background: #ef4444; color: #fff; display: flex; align-items: center; justify-content: center; border: 2px solid #0e1320; box-shadow: 0 0 10px rgba(239, 68, 68, 0.6);">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </div>
          </div>

          <div>
            <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.45rem; letter-spacing: -0.02em;">Părăsiți Conferința?</h3>
            <p style="font-size: 0.85rem; color: #94a3b8; line-height: 1.5; margin: 0;">
              ${(this.isHost || this.isAdmin) 
                ? 'În calitate de gazdă / organizator, puteți încheia ședința pentru toți participanții sau puteți părăsi doar dumneavoastră.' 
                : 'Sunteți pe cale să vă deconectați din sala de conferință. Puteți reveni oricând din panoul principal.'}
            </p>
          </div>

          <div style="display: flex; flex-direction: column; gap: 0.65rem; width: 100%; margin-top: 0.5rem;">
            ${(this.isHost || this.isAdmin) ? `
              <button class="btn btn-danger" id="btn-end-meeting-for-all" style="width: 100%; padding: 0.8rem 1rem; font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; justify-content: center; gap: 0.65rem; background: linear-gradient(135deg, #dc2626, #b91c1c); border: 1px solid #ef4444; border-radius: 12px; color: #fff; cursor: pointer; box-shadow: 0 4px 15px rgba(220, 38, 38, 0.45);">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
                <span>Încheie Ședința pentru Toți</span>
              </button>
              <button class="btn btn-secondary" id="btn-confirm-leave-modal" style="width: 100%; padding: 0.7rem 1rem; font-size: 0.85rem; display: flex; align-items: center; justify-content: center; gap: 0.65rem; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 12px; color: #cbd5e1; cursor: pointer;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Părăsește Doar Tu (Lasă Sala Deschisă)</span>
              </button>
            ` : `
              <button class="btn btn-danger" id="btn-confirm-leave-modal" style="width: 100%; padding: 0.75rem 1rem; font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; justify-content: center; gap: 0.65rem; background: #dc2626; border-radius: 12px; color: #fff; cursor: pointer;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Da, Părăsește Conferința</span>
              </button>
            `}
            <button class="btn btn-secondary" id="btn-cancel-leave-modal" style="width: 100%; padding: 0.65rem 1rem; font-size: 0.825rem; display: flex; align-items: center; justify-content: center; gap: 0.5rem; background: transparent; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; color: #94a3b8; cursor: pointer;">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>Anulează (Rămâi în Conferință)</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Version & Diagnostics Modal -->
      <div class="picker-modal-overlay" id="version-info-modal" style="display: none; align-items: center; justify-content: center; z-index: 10001;">
        <div class="conf-modal-scrollable-card" style="background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(56, 189, 248, 0.4); border-radius: 20px; padding: 2rem; width: 560px; max-width: 92vw; max-height: 88vh; max-height: 88dvh; overflow-y: auto; -webkit-overflow-scrolling: touch; box-shadow: 0 25px 70px rgba(0, 0, 0, 0.9); display: flex; flex-direction: column; gap: 1.25rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" style="width: 36px; height: 36px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.5));" />
              <div>
                <h3 style="margin: 0; font-size: 1.2rem; font-weight: 700; color: #fff; display: flex; align-items: center; gap: 0.5rem;">
                  <span>Panou Versiune & Sistem</span>
                  <span style="background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 0.7rem; padding: 2px 8px; border-radius: 20px; font-weight: 700;">v${APP_VERSION}</span>
                </h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Platformă Privată de Videoconferință Vorbello</div>
              </div>
            </div>
            <button id="btn-close-version-info" style="background: none; border: none; color: #94a3b8; font-size: 1.6rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>

          <!-- Diagnostic Cards Grid (Clean & Protected) -->
          <div style="display: flex; flex-direction: column; gap: 0.65rem;">
            <div style="background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.45rem;">
                <div style="display: flex; align-items: center; gap: 0.6rem;">
                  <span style="font-size: 1.1rem;">🏷️</span>
                  <span style="font-size: 0.85rem; color: #cbd5e1; font-weight: 700;">Versiune Software:</span>
                </div>
                <strong style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); padding: 2px 10px; border-radius: 6px; font-size: 0.88rem; font-family: monospace; font-weight: 700;">v${APP_VERSION}</strong>
              </div>
              <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px; padding: 0.6rem 0.85rem;">
                <div style="font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.6px; color: #64748b; font-weight: 700; margin-bottom: 0.3rem;">Detalii Versiune & Actualizări:</div>
                <div style="color: #94a3b8; font-size: 0.78rem; line-height: 1.5;">${APP_BUILD_TAG}</div>
              </div>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">📅</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Data Compilării & Build:</span>
              </div>
              <span style="color: #fff; font-size: 0.825rem; font-weight: 600;">${APP_BUILD_DATE}</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">🛡️</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Securitate & Criptare:</span>
              </div>
              <span style="color: #34d399; font-size: 0.825rem; font-weight: 600;">Transport DTLS-SRTP</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">📹</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Cameră Video:</span>
              </div>
              <span id="version-modal-cam-name" style="color: #f8fafc; font-size: 0.8rem; font-weight: 600; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Detectare...</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">🎤</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Microfon Audio:</span>
              </div>
              <span id="version-modal-mic-name" style="color: #f8fafc; font-size: 0.8rem; font-weight: 600; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Detectare...</span>
            </div>

            ${this.isAdmin ? `
              <div style="margin-top: 0.35rem; padding: 0.75rem 0.95rem; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: 10px;">
                <div style="font-size: 0.75rem; font-weight: 700; color: #6ee7b7; margin-bottom: 0.35rem;">🔒 Diagnostic Administrator:</div>
                <div style="font-size: 0.74rem; color: #cbd5e1; display: flex; flex-direction: column; gap: 3px;">
                  <div>• Server Semnalizare: <code>app.vorbello.eu/signal.php</code></div>
                  <div>• Conexiune SFU: <code>${this.config.livekitUrl || 'wss://live.vorbello.eu'}</code></div>
                </div>
              </div>
            ` : ''}
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-secondary btn-sm" id="btn-version-open-devices" style="font-size: 0.8rem; padding: 0.5rem 1rem; display: flex; align-items: center; gap: 0.4rem;">
              <span>⚙️</span> Deschide Setări Dispozitive
            </button>
            <button class="btn btn-primary btn-sm" id="btn-version-ok" style="font-size: 0.8rem; padding: 0.5rem 1.4rem;">
              Închide
            </button>
          </div>
        </div>
      </div>

      <!-- Recording Options Modal (Zoom-Style) -->
      <div class="picker-modal-overlay" id="recording-options-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem; width: 540px; max-width: 92vw; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); display: flex; flex-direction: column; gap: 1rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.75rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <span style="font-size: 1.5rem;">🔴</span>
              <div>
                <h3 style="margin: 0; font-size: 1.15rem; font-weight: 700; color: #fff;">Înregistrare Locală Conferință</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Salvare directă pe calculatorul dumneavoastră la 30 FPS</div>
              </div>
            </div>
            <button id="btn-close-rec-options" style="background: none; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>

          <div style="font-size: 0.825rem; color: #cbd5e1; line-height: 1.45;">
            Selectați calitatea și modul de înregistrare (ambele opțiuni video rulează la <strong>30 FPS fluid</strong>). La finalizare, fișierul se salvează instantaneu în folderul <strong>Videos/Vorbello Recordings</strong> și se descarcă automat.
          </div>

          <!-- Video Composition Layout Selector -->
          <div id="rec-layout-section" style="display: flex; flex-direction: column; gap: 0.5rem; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.78rem; font-weight: 700; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.5px;">📐 Compoziție Cadru Video</span>
              <span style="font-size: 0.7rem; color: var(--text-muted);">Ce include imaginea</span>
            </div>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem;" id="rec-layout-selector">
              <button type="button" class="rec-layout-btn selected" id="rec-layout-composite" data-layout="composite">
                <span style="font-size: 1.15rem;">🖼️</span>
                <span style="font-weight: 700; font-size: 0.78rem;">Studio PiP</span>
                <span style="font-size: 0.65rem; color: #94a3b8;">Camere în galerie; la partajare sau tablă, conținutul și maximum 8 camere vizibile</span>
              </button>
              <button type="button" class="rec-layout-btn" id="rec-layout-screen" data-layout="screen_only">
                <span style="font-size: 1.15rem;">🖥️</span>
                <span style="font-weight: 700; font-size: 0.78rem;">Doar Ecran</span>
                <span style="font-size: 0.65rem; color: #94a3b8;">Numai ecranul partajat; fără partajare camerele nu apar</span>
              </button>
              <button type="button" class="rec-layout-btn" id="rec-layout-cameras" data-layout="cameras_only">
                <span style="font-size: 1.15rem;">👤</span>
                <span style="font-weight: 700; font-size: 0.78rem;">Cameră Curată</span>
                <span style="font-size: 0.65rem; color: #94a3b8;">Camera ta; ceilalți participanți nu apar în imagine</span>
              </button>
            </div>
          </div>

          <div class="rec-options-grid">
            <div class="rec-option-card selected" id="rec-card-video-hd" data-type="video-hd">
              <div class="rec-option-icon">🖥️</div>
              <div class="rec-option-title">HD 720p (30 FPS)</div>
              <div style="font-size: 0.68rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 9999px; padding: 2px 8px; display: inline-block; margin-bottom: 6px;">Echilibrat • ~12 MB/min</div>
              <div class="rec-option-desc">1280x720 la 30 fps, video 1.5 Mbps, audio 96 kbps. Text lizibil, mișcare fluidă și fișiere compacte.</div>
            </div>

            <div class="rec-option-card" id="rec-card-video-fhd" data-type="video-fhd">
              <div class="rec-option-icon">🎬</div>
              <div class="rec-option-title">Full HD 1080p (30 FPS)</div>
              <div style="font-size: 0.68rem; font-weight: 700; color: #c084fc; background: rgba(192, 132, 252, 0.15); border: 1px solid rgba(192, 132, 252, 0.3); border-radius: 9999px; padding: 2px 8px; display: inline-block; margin-bottom: 6px;">Claritate Maximă • ~22 MB/min</div>
              <div class="rec-option-desc">1920x1080 la 30 fps, video 2.5 Mbps, audio 128 kbps. Rezoluție nativă Full HD pentru detalii maxime.</div>
            </div>

            <div class="rec-option-card" id="rec-card-audio" data-type="audio" style="grid-column: 1 / -1; display: flex; align-items: center; text-align: left; gap: 0.85rem; padding: 0.75rem 1rem;">
              <div class="rec-option-icon" style="font-size: 1.6rem; margin-bottom: 0;">🎙️</div>
              <div style="flex: 1;">
                <div class="rec-option-title" style="margin-bottom: 2px; font-size: 0.85rem;">Audio HD Exclusiv</div>
                <div class="rec-option-desc" style="font-size: 0.72rem;">Doar vocile tuturor participanților mixate curat la 96 kbps (dimensiune minimă, ~0.7 MB/min).</div>
              </div>
            </div>
          </div>

          <!-- Format Container Selector (WebM vs MP4) -->
          <div style="display: flex; flex-direction: column; gap: 0.5rem; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.78rem; font-weight: 700; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.5px;">📦 Format Fișier (Container)</span>
              <span style="font-size: 0.7rem; color: var(--text-muted);">Extensie salvare</span>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;" id="rec-container-selector">
              <button type="button" class="rec-container-btn ${this.selectedRecordingContainer === 'webm' ? 'selected' : ''}" id="rec-container-webm" data-container="webm">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 1.05rem;">⚡</span>
                  <span style="font-weight: 700; font-size: 0.8rem; color: #a5b4fc;">WebM (.webm)</span>
                </div>
                <div style="font-size: 0.65rem; color: #cbd5e1;">Format WebM • Sunet Opus</div>
              </button>
              <button type="button" class="rec-container-btn ${this.selectedRecordingContainer === 'mp4' ? 'selected' : ''}" id="rec-container-mp4" data-container="mp4">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 1.05rem;">🎬</span>
                  <span style="font-weight: 700; font-size: 0.8rem; color: #fff;">MP4 (.mp4)</span>
                </div>
                <div style="font-size: 0.65rem; color: #94a3b8;">Format H.264 / AAC • Compatibilitate largă</div>
              </button>
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-secondary btn-sm" id="btn-cancel-recording" style="padding: 0.5rem 1.2rem;">Anulează</button>
            <button class="btn btn-primary btn-sm" id="btn-confirm-start-recording" style="background: #b4232d; border: 1px solid #ffb4b4; color: #fff; padding: 0.5rem 1.5rem; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 0.65rem;"><svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="currentColor"/></svg><span>Începe Înregistrarea</span></button>
          </div>
        </div>
      </div>

      <!-- Recording Success Modal -->
      <div class="picker-modal-overlay" id="recording-success-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem; width: 480px; max-width: 90vw; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); display: flex; flex-direction: column; gap: 1rem; text-align: center;">
          <div style="font-size: 2.8rem; line-height: 1;">🎉</div>
          <h3 style="margin: 0; font-size: 1.2rem; font-weight: 700; color: #fff;">Înregistrare Salvată cu Succes!</h3>
          <div id="rec-success-filename" style="font-weight: 700; color: #38bdf8; font-size: 0.88rem; word-break: break-all; background: rgba(56, 189, 248, 0.1); padding: 0.5rem; border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.25);"></div>
          <div id="rec-success-details" style="font-size: 0.8rem; color: #94a3b8; line-height: 1.4;"></div>

          <div style="display: flex; justify-content: center; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-secondary btn-sm" id="btn-open-rec-folder" style="display: none; padding: 0.5rem 1.2rem;">📂 Deschide Dosarul</button>
            <button class="btn btn-primary btn-sm" id="btn-close-rec-success" style="padding: 0.5rem 1.5rem;">Am înțeles</button>
          </div>
        </div>
      </div>
    `;

    this.attachListeners();
    this.attachPickerListeners();
    this.attachDeviceSettingsListeners();
    this.attachVersionModalListeners();
    this.attachTileActionListeners();
  }

  private adaptVideoElementFit(videoEl: HTMLVideoElement, tile: HTMLElement) {
    if (!videoEl || !tile) return;
    const checkFit = () => {
      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      if (vw > 0 && vh > 0) {
        const isPortrait = vh > vw * 1.05; // 9:16 portrait video from smartphone
        const manualFit = tile.getAttribute('data-fit-mode');
        if (manualFit === 'cover') {
          videoEl.classList.remove('fit-contain', 'is-portrait');
          videoEl.style.setProperty('object-fit', 'cover', 'important');
          tile.classList.remove('tile-has-portrait');
        } else if (manualFit === 'contain' || isPortrait) {
          // Automatic 9:16 smartphone framing: whole person visible, no head crop, no zoom!
          videoEl.classList.add('fit-contain', 'is-portrait');
          videoEl.style.setProperty('object-fit', 'contain', 'important');
          videoEl.style.backgroundColor = '#040711';
          tile.classList.add('tile-has-portrait');
          tile.style.backgroundColor = '#040711';
        } else {
          videoEl.classList.remove('fit-contain', 'is-portrait');
          videoEl.style.setProperty('object-fit', 'cover', 'important');
          tile.classList.remove('tile-has-portrait');
        }
      }
    };

    videoEl.addEventListener('loadedmetadata', checkFit);
    videoEl.addEventListener('loadeddata', checkFit);
    videoEl.addEventListener('canplay', checkFit);
    videoEl.addEventListener('playing', checkFit);
    videoEl.addEventListener('timeupdate', checkFit, { once: true });
    videoEl.addEventListener('resize', checkFit);
    checkFit();
    setTimeout(checkFit, 100);
    setTimeout(checkFit, 350);
    setTimeout(checkFit, 800);
    setTimeout(checkFit, 2000);
  }

  private renderVideoGrid() {
    const grid = document.getElementById('video-grid');
    if (!grid) return;

    // Prune any stale/ghost participants who left without sending a disconnect packet:
    this.reconcileParticipants();

    // Do not show participants who are currently in the waiting lobby on the main stage:
    const activeParticipants = this.participants.filter(p => !this.waitingParticipants.some(wp => wp.identity === p.identity));
    const count = activeParticipants.length;
    const paginationBar = document.getElementById('studio-pagination-bar');
    const stageArea = document.getElementById('stage-area');
    const currentRoom = this.manager.getRoom();
    const hasRemoteScreenPub = Boolean(currentRoom && currentRoom.state === 'connected' && Array.from(currentRoom.remoteParticipants.values()).some((rp: any) => {
      if (!rp.isScreenShareEnabled) return false;
      const sp = rp.getTrackPublication(Track.Source.ScreenShare) ||
        Array.from((rp.trackPublications as Map<string, any>).values()).find((p: any) => p.source === Track.Source.ScreenShare || p.trackName === 'screen_share' || p.name === 'screen_share');
      return Boolean(sp && sp.track && sp.track.mediaStreamTrack && sp.track.mediaStreamTrack.readyState === 'live');
    }));
    const isLocalScreenActive = this.manager.isScreenShareEnabled && !!(this.manager.getLocalScreenStream()?.active && this.manager.getLocalScreenStream()?.getVideoTracks().some(t => t.readyState === 'live'));
    const isSpotlightActive = stageArea?.classList.contains('has-spotlight') || isLocalScreenActive || hasRemoteScreenPub || Boolean(this.pinnedIdentity);
    let displayedParticipants = activeParticipants;

    if (this.currentViewMode === 'studio' && !isSpotlightActive) {
      const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
      if (winW <= 1024) {
        // Mobile & Tablet studio view: clean 2-column digital vertical scroll (max 4 cards visible at once on tablet)
        displayedParticipants = activeParticipants;
        grid.className = `video-grid mode-studio studio-mobile-scroll count-${count > 6 ? 'many' : count}`;
        if (paginationBar) paginationBar.style.display = 'none';
      } else {
        // Tablet / Desktop studio view: strict 16:9 proportional engine with pagination (max 16/page)
        const pageSize = 16;
        this.studioPageSize = pageSize;

        const totalPages = Math.max(1, Math.ceil(count / pageSize));
        this.studioCurrentPage = Math.min(Math.max(1, this.studioCurrentPage), totalPages);

        const startIndex = (this.studioCurrentPage - 1) * pageSize;
        displayedParticipants = activeParticipants.slice(startIndex, startIndex + pageSize);
        const visibleOnPage = displayedParticipants.length;

        // Select layout class dynamically based on number of active cards on the page:
        let layoutClass = 'studio-layout-16';
        if (visibleOnPage <= 1) {
          layoutClass = 'studio-layout-1';
        } else if (visibleOnPage === 2) {
          layoutClass = 'studio-layout-2';
        } else if (visibleOnPage === 3) {
          layoutClass = 'studio-layout-3';
        } else if (visibleOnPage === 4) {
          layoutClass = 'studio-layout-4';
        } else if (visibleOnPage <= 6) {
          layoutClass = 'studio-layout-6';
        } else if (visibleOnPage <= 8) {
          layoutClass = 'studio-layout-8';
        } else if (visibleOnPage <= 12) {
          layoutClass = 'studio-layout-12';
        } else {
          layoutClass = 'studio-layout-16';
        }

        grid.className = `video-grid mode-studio ${layoutClass}`;

        if (paginationBar) {
          paginationBar.style.display = totalPages > 1 ? 'flex' : 'none';
          const prevBtn = document.getElementById('btn-studio-prev') as HTMLButtonElement | null;
          const nextBtn = document.getElementById('btn-studio-next') as HTMLButtonElement | null;
          const info = document.getElementById('studio-page-info');
          if (prevBtn) prevBtn.disabled = this.studioCurrentPage <= 1;
          if (nextBtn) nextBtn.disabled = this.studioCurrentPage >= totalPages;
          if (info) info.textContent = `Pagina ${this.studioCurrentPage} din ${totalPages} • ${count} Participanți`;
        }
      }
    } else {
      if (paginationBar) paginationBar.style.display = 'none';
      grid.className = `video-grid count-${count > 6 ? 'many' : count}`;
    }

    const currentIdentities = new Set(displayedParticipants.map(p => p.identity));

    // 1. Remove stale tiles for departed or non-visible page participants
    grid.querySelectorAll('.video-tile').forEach(tile => {
      const id = tile.id.replace('tile-', '');
      if (!currentIdentities.has(id)) {
        const vid = tile.querySelector('video') as HTMLVideoElement | null;
        if (vid && vid.srcObject) vid.srcObject = null;
        tile.remove();
      }
    });

    // 2. Incrementally add or update each active participant tile
    displayedParticipants.forEach(p => {
      const isSelf = p.identity === 'self';
      const isMuted = isSelf ? !this.manager.isMicEnabled : !!p.isMuted;
      const isCamOff = isSelf ? !this.manager.isCameraEnabled : !!p.isCamOff;
      const isPinned = this.pinnedIdentity === p.identity;
      const isSpeaking = !!p.isSpeaking && !isMuted;
      const isHandRaised = isSelf ? this.isLocalHandRaised : !!p.isHandRaised;

      const speakerIconText = isMuted ? ICONS.micOffSm : (isSpeaking ? '🔊' : ICONS.users);
      const speakerIconColor = isMuted ? '#f87171' : (isSpeaking ? '#34d399' : 'rgba(255,255,255,0.6)');

      let tile = document.getElementById(`tile-${p.identity}`);
      if (!tile) {
        tile = document.createElement('div');
        tile.id = `tile-${p.identity}`;
        tile.className = `video-tile ${isSpeaking ? 'speaking' : ''} ${isPinned ? 'is-pinned-tile' : ''} ${isCamOff ? 'has-cam-off' : ''}`;
        if (isPinned) {
          tile.style.border = '2px solid #6366f1';
          tile.style.boxShadow = '0 0 15px rgba(99,102,241,0.4)';
        }
        const isPartAdmin = p.role === 'admin' || (isSelf && this.isAdmin);
        const isPartMod = !isPartAdmin && (p.role === 'moderator' || (isSelf && this.isModerator));
        const roleBadgeHtml = isPartAdmin 
          ? `<span class="role-badge role-admin">👑 Admin</span>`
          : (isPartMod ? `<span class="role-badge role-mod">🛡️ Mod</span>` : '');

        const avatarContent = p.avatarUrl
          ? `<img src="${p.avatarUrl}" class="avatar-photo-img" style="width: 88px; height: 88px; border-radius: 50%; object-fit: cover; box-shadow: 0 8px 30px rgba(0,0,0,0.6); border: 2.5px solid rgba(255,255,255,0.25);" alt="${p.name}" />`
          : p.name.charAt(0);

        tile.innerHTML = `
          <div class="tile-hand-badge" id="hand-badge-${p.identity}" style="${isHandRaised ? '' : 'display: none;'}">${ICONS.hand}</div>
          <div class="avatar-placeholder" style="${isCamOff ? '' : 'display: none;'}">${avatarContent}</div>
          <div class="tile-badge">
            <span class="speaker-indicator-icon" style="color: ${speakerIconColor}; font-size: 0.72rem;">${speakerIconText}</span>
            <span class="participant-name-text">${p.name}</span>
            ${roleBadgeHtml}
            <span class="pinned-badge" style="background: #6366f1; color: #fff; font-size: 0.65rem; padding: 1px 5px; border-radius: 4px; margin-left: 4px; font-weight: 700; display: ${isPinned ? 'inline-block' : 'none'};">📌</span>
          </div>

          <!-- 3-Dots Action Menu Trigger (Visible for all participants) -->
          <div class="tile-actions-wrapper">
            <button class="tile-action-trigger" data-id="${p.identity}" title="Opțiuni participant">
              ${ICONS.moreVert}
            </button>
          </div>
        `;
        grid.appendChild(tile);
      } else {
        // Tile already exists - update properties in-place without destroying DOM:
        tile.classList.toggle('speaking', isSpeaking);
        tile.classList.toggle('is-pinned-tile', isPinned);
        tile.classList.toggle('has-cam-off', isCamOff);
        tile.style.border = isPinned ? '2px solid #6366f1' : '';
        tile.style.boxShadow = isPinned ? '0 0 15px rgba(99,102,241,0.4)' : '';

        // Remove legacy bulky audio indicator and old dropdowns if still in DOM
        tile.querySelector('.tile-audio-indicator')?.remove();
        tile.querySelector('.tile-dropdown')?.remove();

        // Update hand badge
        let handBadge = tile.querySelector('.tile-hand-badge') as HTMLElement | null;
        if (!handBadge && isHandRaised) {
          handBadge = document.createElement('div');
          handBadge.className = 'tile-hand-badge';
          handBadge.id = `hand-badge-${p.identity}`;
          handBadge.innerHTML = ICONS.hand;
          tile.prepend(handBadge);
        } else if (handBadge && (!handBadge.querySelector('svg') || handBadge.innerHTML.includes('✋'))) {
          handBadge.innerHTML = ICONS.hand;
        }
        if (handBadge) {
          handBadge.style.display = isHandRaised ? 'flex' : 'none';
        }

        const nameEl = tile.querySelector('.participant-name-text');
        if (nameEl && nameEl.textContent !== p.name) nameEl.textContent = p.name;

        const speakerIcon = tile.querySelector('.speaker-indicator-icon') as HTMLElement | null;
        if (speakerIcon) {
          speakerIcon.innerHTML = speakerIconText;
          speakerIcon.style.color = speakerIconColor;
        }

        const pinnedBadge = tile.querySelector('.pinned-badge') as HTMLElement | null;
        if (pinnedBadge) pinnedBadge.style.display = isPinned ? 'inline-block' : 'none';

        const isPartAdmin = p.role === 'admin' || (isSelf && this.isAdmin);
        const isPartMod = !isPartAdmin && (p.role === 'moderator' || (isSelf && this.isModerator));
        let roleBadge = tile.querySelector('.role-badge') as HTMLElement | null;
        if (isPartAdmin || isPartMod) {
          if (!roleBadge) {
            roleBadge = document.createElement('span');
            const badgeWrapper = tile.querySelector('.tile-badge');
            if (badgeWrapper) badgeWrapper.appendChild(roleBadge);
          }
          roleBadge.className = `role-badge ${isPartAdmin ? 'role-admin' : 'role-mod'}`;
          roleBadge.removeAttribute('style');
          roleBadge.textContent = isPartAdmin ? '👑 Admin' : '🛡️ Mod';
        } else if (roleBadge) {
          roleBadge.remove();
        }

        // Ensure 3-dots action menu trigger is always present
        if (!tile.querySelector('.tile-actions-wrapper')) {
          const actionsWrapper = document.createElement('div');
          actionsWrapper.className = 'tile-actions-wrapper';
          actionsWrapper.innerHTML = `
            <button class="tile-action-trigger" data-id="${p.identity}" title="Opțiuni participant">
              ${ICONS.moreVert}
            </button>
          `;
          tile.appendChild(actionsWrapper);
        }
      }
    });

    // 3. Connect remote video and audio tracks seamlessly without recreation
    if (this.isP2PMode) return;
    const room = this.manager.getRoom();
    if (room && room.state === 'connected') {
      room.remoteParticipants.forEach(remotePart => {
        const p = this.participants.find(part => part.identity === remotePart.identity);

        // --- AUDIO TRACK SYNCHRONIZATION ---
        const audioPub = remotePart.getTrackPublication(Track.Source.Microphone);
        if (audioPub && audioPub.track) {
          let audioEl = document.getElementById(`audio-remote-${remotePart.identity}`) as HTMLAudioElement | null;
          if (!audioEl) {
            audioEl = document.createElement('audio');
            audioEl.id = `audio-remote-${remotePart.identity}`;
            audioEl.autoplay = true;
            audioEl.setAttribute('playsinline', '');
            audioEl.volume = 1.0;
            document.body.appendChild(audioEl);
          }
          if (!audioPub.track.attachedElements.includes(audioEl)) {
            audioPub.track.attach(audioEl);
          }
          const isAudioMuted = !!audioPub.isMuted || !!p?.isMuted;
          audioEl.muted = isAudioMuted;
          // NEVER pause audioEl! Pausing kills autoplay permissions on mobile devices.
          if (audioEl.paused) {
            audioEl.play().catch(() => {});
          }
        }

        // --- VIDEO TRACK SYNCHRONIZATION ---
        const tile = document.getElementById(`tile-${remotePart.identity}`);
        if (!tile) return;
        const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;

        let videoPub = remotePart.getTrackPublication(Track.Source.Camera);
        if (!videoPub) {
          for (const pub of remotePart.videoTrackPublications.values()) {
            const isScreen = pub.source === Track.Source.ScreenShare || pub.trackName === 'screen_share' || (pub as any).name === 'screen_share';
            if (!isScreen) {
              videoPub = pub;
              break;
            }
          }
        }
        if (videoPub && !videoPub.isSubscribed) {
          try { videoPub.setSubscribed(true); } catch (_) {}
        }
        let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;

        if (videoPub && videoPub.track) {
          if (!videoEl) {
            videoEl = document.createElement('video');
            videoEl.className = 'remote-cam-video';
            videoEl.autoplay = true;
            videoEl.playsInline = true;
            videoEl.muted = true;
            videoEl.setAttribute('muted', '');
            videoEl.setAttribute('playsinline', '');
            videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; border-radius:inherit; z-index:1;';
            tile.prepend(videoEl);
            videoPub.track.attach(videoEl);
          } else {
            if (!videoPub.track.attachedElements.includes(videoEl) || !videoEl.srcObject) {
              videoPub.track.attach(videoEl);
            }
          }
          this.adaptVideoElementFit(videoEl, tile);
          videoEl.muted = true;
          videoEl.setAttribute('muted', '');

          const mst = (videoPub.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
          const hasLiveTrack = Boolean(mst && mst.readyState !== 'ended');
          const isCamOff = !hasLiveTrack && videoPub.isMuted;
          videoEl.style.display = isCamOff ? 'none' : 'block';
          if (avatar) avatar.style.display = isCamOff ? 'flex' : 'none';
          if (!isCamOff && videoEl.paused) {
            videoEl.play().catch(() => {});
          }

          videoEl.onloadedmetadata = () => {
            if (videoEl) this.adaptVideoElementFit(videoEl, tile);
            if (hasLiveTrack || !videoPub.isMuted) {
              if (videoEl) videoEl.style.display = 'block';
              if (avatar) avatar.style.display = 'none';
              videoEl?.play().catch(() => {});
            }
          };
        } else {
          if (videoEl) {
            videoEl.style.display = 'none';
          }
          if (avatar) avatar.style.display = 'flex';
        }
      });
    }

    // 4. Update local self tile video
    this.updateSelfTile();

    // 5. Update production waiting banner
    this.updateProdWaitingBanner();

    // 6. Update top filmstrip carousel previews (Zoom / Meet Style)
    this.renderFilmstripPreviews();
  }

  private shouldUseFilmstrip(): boolean {
    if (this.userExplicitlyChangedViewMode) {
      if (this.currentViewMode === 'gallery' || this.currentViewMode === 'studio') {
        return false;
      }
      return this.currentViewMode === 'speaker';
    }
    const count = this.participants.length;
    if (count > 4) {
      return true;
    }
    return this.currentViewMode === 'speaker';
  }

  private renderFilmstripPreviews() {
    const filmstripBar = document.getElementById('top-filmstrip-bar');
    const container = document.getElementById('filmstrip-scroll-container');
    if (!filmstripBar || !container) return;

    // Per user direct requirement: NEVER show miniatures at the top!
    // Miniatures live exclusively in video-grid AT THE BOTTOM!
    filmstripBar.style.display = 'none';
    container.innerHTML = '';
  }



  private updateProdWaitingBanner() {
    // Permanently disabled per user request: floating waiting banner removed from stage.
    // Sharing is now handled seamlessly via the dedicated Invite card in Participants.
  }

  private attachTileActionListeners() {
    // 1. Single global floating dropdown attached directly to document.body
    // Immune to any parent transform, backdrop-filter, or overflow:hidden
    document.getElementById('global-tile-dropdown')?.remove();
    const globalDropdown = document.createElement('div');
    globalDropdown.id = 'global-tile-dropdown';
    globalDropdown.className = 'tile-dropdown';
    document.body.appendChild(globalDropdown);

      // Delegated click listener for actions inside the global dropdown:
      globalDropdown.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;

        // 1. Rename
        const renameBtn = target.closest('.action-rename') as HTMLElement | null;
        if (renameBtn) {
          e.stopPropagation();
          const id = renameBtn.getAttribute('data-id');
          if (id) this.openRenameModal(id);
          this.closeAllDropdowns();
          return;
        }

        // 2. Pin / Spotlight
        const pinBtn = target.closest('.action-pin') as HTMLElement | null;
        if (pinBtn) {
          e.stopPropagation();
          if (pinBtn.hasAttribute('disabled') || pinBtn.classList.contains('disabled') || (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin)) {
            this.showFloatingToast('🔒 Scena a fost fixată de Administrator și nu poate fi modificată.');
            this.closeAllDropdowns();
            return;
          }
          const id = pinBtn.getAttribute('data-id');
          const newId = this.pinnedIdentity === id ? null : id;
          this.setPinState(newId, true);
          this.closeAllDropdowns();
          return;
        }

        // 3. Toggle Fit
        const toggleFitBtn = target.closest('.action-toggle-fit') as HTMLElement | null;
        if (toggleFitBtn) {
          e.stopPropagation();
          const id = toggleFitBtn.getAttribute('data-id');
          const tileEl = document.getElementById(`tile-${id}`);
          if (tileEl) {
            const vid = tileEl.querySelector('video') as HTMLVideoElement | null;
            const currentFit = tileEl.getAttribute('data-fit-mode') || (vid?.classList.contains('fit-contain') ? 'contain' : 'cover');
            const newFit = currentFit === 'contain' ? 'cover' : 'contain';
            tileEl.setAttribute('data-fit-mode', newFit);
            if (vid) {
              this.adaptVideoElementFit(vid, tileEl);
            }
            this.showFloatingToast(newFit === 'contain' ? '📐 Încadrare completă (Fără tăiere / Decupare 9:16)' : '🔍 Umplere completă a cadrului (Zoom / Crop)');
          }
          this.closeAllDropdowns();
          return;
        }

        // 4. Lower Hand
        const lowerHandBtn = target.closest('.action-lower-hand') as HTMLElement | null;
        if (lowerHandBtn) {
          e.stopPropagation();
          const id = lowerHandBtn.getAttribute('data-id');
          if (id === 'self') {
            this.isLocalHandRaised = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isHandRaised = false;
            this.manager.sendHandRaise(false);
            this.updateControlsState();
          } else if (id) {
            const p = this.participants.find(part => part.identity === id);
            if (p) {
              p.isHandRaised = false;
              this.manager.sendDataMessage({
                type: 'hand_raise',
                identity: id,
                name: p.name,
                isRaised: false,
              });
            }
          }
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.closeAllDropdowns();
          return;
        }

        // 5. Mute Toggle
        const revokeDeviceBtn = target.closest('.action-revoke-webinar-device') as HTMLElement | null;
        if (revokeDeviceBtn) {
          e.stopPropagation();
          const id = revokeDeviceBtn.getAttribute('data-id');
          const medium = revokeDeviceBtn.getAttribute('data-medium');
          if (id && (medium === 'camera' || medium === 'microphone') && this.config.requestWebinarPermission) {
            void this.changeWebinarPermission(id, medium, false);
          }
          this.closeAllDropdowns();
          return;
        }
        const muteBtn = target.closest('.action-mute') as HTMLElement | null;
        if (muteBtn) {
          e.stopPropagation();
          const id = muteBtn.getAttribute('data-id');
          if (id === 'self') {
            if (this.config.webinarAudience && !this.webinarMicAllowed && !this.manager.isMicEnabled) {
              this.showFloatingToast('În webinar, microfonul poate fi pornit după permisiunea gazdei.');
              this.closeAllDropdowns(); return;
            }
            this.manager.enableMicrophone(!this.manager.isMicEnabled).then(() => {
              this.updateControlsState();
              this.renderVideoGrid();
            });
          } else if (id) {
            if (!this.isAdmin && !this.isModerator) {
              const audioEl = document.getElementById(`audio-remote-${id}`) as HTMLAudioElement | null;
              if (audioEl) {
                audioEl.muted = !audioEl.muted;
                const p = this.participants.find(part => part.identity === id);
                if (p) p.isMuted = audioEl.muted;
                this.showFloatingToast(audioEl.muted ? '🔇 Ai oprit sunetul pentru acest participant.' : '🔊 Sunetul a fost reactivat.');
                this.renderVideoGrid();
              }
              this.closeAllDropdowns();
              return;
            }
            const p = this.participants.find(part => part.identity === id);
            if (p) {
              if (this.config.isWebinar && p.isMuted && this.config.requestWebinarPermission) {
                void this.config.requestWebinarPermission(id, 'microphone', true).then(() => this.showFloatingToast('Participantul poate porni microfonul.')).catch(() => this.showFloatingToast('Permisiunea nu a putut fi acordată.'));
                this.closeAllDropdowns(); return;
              }
              this.manager.sendDataMessage({
                type: 'moderation',
                action: p.isMuted ? 'unmute-user' : 'mute-user',
                targetIdentity: id,
              });
              this.showFloatingToast(p.isMuted ? 'Solicitarea de activare a microfonului a fost trimisă.' : 'Solicitarea de oprire a microfonului a fost trimisă.');
              this.renderParticipantsList();
              this.renderVideoGrid();
            }
          }
          this.closeAllDropdowns();
          return;
        }

        // 6. Camera Toggle
        const camBtn = target.closest('.action-cam') as HTMLElement | null;
        if (camBtn) {
          e.stopPropagation();
          const id = camBtn.getAttribute('data-id');
          if (id === 'self') {
            if (this.config.webinarAudience && !this.webinarCameraAllowed && !this.manager.isCameraEnabled) {
              this.showFloatingToast('În webinar, camera poate fi pornită după permisiunea gazdei.');
              this.closeAllDropdowns(); return;
            }
            this.manager.enableCamera(!this.manager.isCameraEnabled).then(() => {
              this.updateControlsState();
              this.updateSelfTile();
              this.renderVideoGrid();
            });
          } else if (id) {
            if (!this.isAdmin && !this.isModerator) {
              this.showFloatingToast('⚠️ Doar Administratorul și Moderatorii pot opri camera altor participanți.');
              this.closeAllDropdowns();
              return;
            }
            const p = this.participants.find(part => part.identity === id);
            if (p) {
              if (this.config.isWebinar && p.isCamOff && this.config.requestWebinarPermission) {
                void this.config.requestWebinarPermission(id, 'camera', true).then(() => this.showFloatingToast('Participantul poate porni camera.')).catch(() => this.showFloatingToast('Permisiunea nu a putut fi acordată.'));
                this.closeAllDropdowns(); return;
              }
              this.manager.sendDataMessage({
                type: 'moderation',
                action: p.isCamOff ? 'start-cam-user' : 'stop-cam-user',
                targetIdentity: id,
              });
              this.showFloatingToast(p.isCamOff ? 'Solicitarea de activare a camerei a fost trimisă.' : 'Solicitarea de oprire a camerei a fost trimisă.');
              this.renderParticipantsList();
              this.renderVideoGrid();
            }
          }
          this.closeAllDropdowns();
          return;
        }

        // 7. Kick
        const kickBtn = target.closest('.action-kick') as HTMLElement | null;
        if (kickBtn) {
          e.stopPropagation();
          if (!this.isAdmin) {
            this.showFloatingToast('⚠️ Doar Administratorul poate elimina participanți din conferință.');
            this.closeAllDropdowns();
            return;
          }
          const id = kickBtn.getAttribute('data-id');
          if (id) {
            this.kickParticipant(id);
          }
          this.closeAllDropdowns();
          return;
        }
      });

    // 2. Remove previous document click listener if any to prevent duplicate calls
    if ((this as any)._globalTileClickListener) {
      document.removeEventListener('click', (this as any)._globalTileClickListener);
      (this as any)._globalTileClickListener = null;
    }

    // Master document-level delegated listener for 3-dots trigger and tile clicks
    const docClickListener = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // 1. More options 3-dots trigger clicked:
      const trigger = target.closest('.tile-action-trigger') as HTMLElement | null;
      if (trigger) {
        e.stopPropagation();
        e.preventDefault();
        const id = trigger.getAttribute('data-id');
        let gd = document.getElementById('global-tile-dropdown') as HTMLElement | null;
        if (!gd) {
          gd = document.createElement('div');
          gd.id = 'global-tile-dropdown';
          gd.className = 'tile-dropdown';
          document.body.appendChild(gd);
        }
        if (!id) return;

        const isAlreadyOpen = gd.classList.contains('show') && gd.getAttribute('data-target-id') === id;
        if (isAlreadyOpen) {
          this.closeAllDropdowns();
          return;
        }

        const isSelf = id === 'self';
        const p = isSelf
          ? { identity: 'self', name: `${this.manager.displayName || this.config.displayName} (Tu)`, role: 'admin' as const, isMuted: !this.manager.isMicEnabled, isCamOff: !this.manager.isCameraEnabled, isHandRaised: this.isLocalHandRaised }
          : (this.participants.find(part => part.identity === id) || {
              identity: id,
              name: trigger.closest('.video-tile')?.querySelector('.participant-name-text')?.textContent || 'Participant',
              role: 'user' as const,
              isMuted: true,
              isCamOff: true,
              isHandRaised: false
            });

        const isMuted = isSelf ? !this.manager.isMicEnabled : !!p.isMuted;
        const isCamOff = isSelf ? !this.manager.isCameraEnabled : !!p.isCamOff;
        const isHandRaised = isSelf ? this.isLocalHandRaised : !!p.isHandRaised;
        const canRename = isSelf || this.isAdmin || this.isModerator;
        const canToggleMic = isSelf || this.isAdmin || this.isModerator;
        const canToggleCam = isSelf || this.isAdmin || this.isModerator;
        const canLowerHand = isSelf || this.isAdmin || this.isModerator;
        const canModerateUser = !isSelf && p.role !== 'admin' && (this.isAdmin || this.isModerator);

        let pinHtml = '';
        if (this.isStageLockedByAdmin && !this.isAdmin && !this.isHost) {
          pinHtml = `
            <button class="tile-dropdown-item action-pin disabled" data-id="${p.identity}" disabled style="opacity: 0.65; cursor: not-allowed;" title="Scena a fost fixată de Administrator">
              <span>🔒</span> <span>Scenă Fixată de Admin</span>
            </button>
          `;
        } else if (this.isHost) {
          pinHtml = `
            <button class="tile-dropdown-item action-pin" data-id="${p.identity}">
              <span>${this.pinnedIdentity === p.identity ? '✖️' : '👑'}</span> <span>${this.pinnedIdentity === p.identity ? 'Anulează Evidențierea pe Scenă' : 'Evidențiază pe Scenă (pentru toți)'}</span>
            </button>
          `;
        } else {
          pinHtml = `
            <button class="tile-dropdown-item action-pin" data-id="${p.identity}">
              <span>📌</span> <span>${this.pinnedIdentity === p.identity ? 'Anulează Fixarea pentru mine' : 'Fixează pentru mine'}</span>
            </button>
          `;
        }

        gd.setAttribute('data-target-id', p.identity);
        gd.innerHTML = `
          ${pinHtml}
          ${canRename ? `
            <button class="tile-dropdown-item action-rename" data-id="${p.identity}">
              ${ICONS.pencil} <span>Redenumește</span>
            </button>
          ` : ''}
          ${canToggleMic ? `
            <button class="tile-dropdown-item action-mute" data-id="${p.identity}">
              ${isMuted ? ICONS.micOn : ICONS.micOff} <span>${isMuted ? 'Activează Microfon' : 'Oprește Microfon'}</span>
            </button>
          ` : ''}
          ${canToggleCam ? `
            <button class="tile-dropdown-item action-cam" data-id="${p.identity}">
              ${isCamOff ? ICONS.camOn : ICONS.camOff} <span>${isCamOff ? 'Activează Cameră' : 'Oprește Camera'}</span>
            </button>
          ` : ''}
          ${canModerateUser && this.config.isWebinar && this.config.requestWebinarPermission && /^guest-[a-f0-9]{32}$/.test(p.identity) ? `
            <button class="tile-dropdown-item action-revoke-webinar-device" data-id="${p.identity}" data-medium="microphone">${ICONS.micOff} <span>Retrage dreptul de a vorbi</span></button>
            <button class="tile-dropdown-item action-revoke-webinar-device" data-id="${p.identity}" data-medium="camera">${ICONS.camOff} <span>Retrage dreptul la cameră</span></button>
          ` : ''}
          <button class="tile-dropdown-item action-toggle-fit" data-id="${p.identity}" title="Comută între încadrare completă 9:16 și decupare">
            <span>📐</span> <span>Comută Încadrare (Fit / Fill)</span>
          </button>
          ${(isHandRaised && canLowerHand) ? `
            <button class="tile-dropdown-item action-lower-hand" data-id="${p.identity}">
              <span>✋ Coboară Mâna</span>
            </button>
          ` : ''}
          ${(canModerateUser && this.isAdmin) ? `
            <button class="tile-dropdown-item danger action-kick" data-id="${p.identity}">
              ${ICONS.trash} <span>Elimină din Cameră</span>
            </button>
          ` : ''}
        `;

        gd.style.display = 'flex';
        gd.classList.add('show');

        // Position flawlessly using viewport coordinates with boundary clamping
        const rect = trigger.getBoundingClientRect();
        gd.style.position = 'fixed';
        gd.style.zIndex = '999999';

        const menuWidth = gd.offsetWidth || 230;
        const menuHeight = gd.offsetHeight || 260;

        // Smart horizontal alignment:
        // By default, try to align to the right of the button:
        let leftPos = rect.right - menuWidth;
        // If it overflows the left side of the screen (< 10px, e.g. leftmost tile on mobile):
        if (leftPos < 10) {
          leftPos = Math.max(10, rect.left);
        }
        // Ensure it doesn't overflow right edge:
        if (leftPos + menuWidth > window.innerWidth - 10) {
          leftPos = Math.max(10, window.innerWidth - menuWidth - 10);
        }

        gd.style.left = `${leftPos}px`;
        gd.style.right = 'auto';

        if (rect.bottom + menuHeight > window.innerHeight - 70) {
          gd.style.top = 'auto';
          gd.style.bottom = `${Math.max(10, window.innerHeight - rect.top + 6)}px`;
        } else {
          gd.style.top = `${rect.bottom + 6}px`;
          gd.style.bottom = 'auto';
        }

        // Also mark the tile as dropdown-open for hover styling
        document.querySelectorAll('.video-tile.dropdown-open').forEach(t => t.classList.remove('dropdown-open'));
        trigger.closest('.video-tile')?.classList.add('dropdown-open');
        return;
      }

      // 2. Click inside the global dropdown itself - handled by dropdown listener
      if (target.closest('#global-tile-dropdown')) {
        return;
      }

      // 3. Tile clicked outside 3-dots on touch devices: first tap reveals controls
      const clickedTile = target.closest('.video-tile') as HTMLElement | null;
      if (clickedTile && !target.closest('.tile-actions-wrapper')) {
        const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
        if (isTouchDevice) {
          if (!clickedTile.classList.contains('tile-touch-active')) {
            document.querySelectorAll('.video-tile.tile-touch-active').forEach(t => t.classList.remove('tile-touch-active'));
            clickedTile.classList.add('tile-touch-active');
            setTimeout(() => {
              clickedTile.classList.remove('tile-touch-active');
            }, 3500);
            return;
          }
        }
      }

      // 4. Clicked anywhere else: close dropdowns and clear touch states
      this.closeAllDropdowns();
      if (!target.closest('.video-tile')) {
        document.querySelectorAll('.video-tile.tile-touch-active').forEach(t => t.classList.remove('tile-touch-active'));
      }
    };
    (this as any)._globalTileClickListener = docClickListener;
    document.addEventListener('click', docClickListener);

    // Double click on tile toggles fit
    document.addEventListener('dblclick', (e) => {
      const tileEl = (e.target as HTMLElement).closest('.video-tile') as HTMLElement | null;
      if (!tileEl) return;
      const vid = tileEl.querySelector('video') as HTMLVideoElement | null;
      if (!vid) return;
      const currentFit = tileEl.getAttribute('data-fit-mode') || (vid.classList.contains('fit-contain') ? 'contain' : 'cover');
      const newFit = currentFit === 'contain' ? 'cover' : 'contain';
      tileEl.setAttribute('data-fit-mode', newFit);
      this.adaptVideoElementFit(vid, tileEl);
      this.showFloatingToast(newFit === 'contain' ? '📐 Încadrare completă (Fără tăiere / Decupare 9:16)' : '🔍 Umplere completă a cadrului (Zoom / Crop)');
    });

    window.addEventListener('resize', () => this.closeAllDropdowns(), { passive: true });
  }

  private closeAllDropdowns() {
    const gd = document.getElementById('global-tile-dropdown');
    if (gd) {
      gd.classList.remove('show');
      gd.style.display = 'none';
      gd.removeAttribute('data-target-id');
    }
    document.querySelectorAll('.video-tile.dropdown-open').forEach(d => {
      d.classList.remove('dropdown-open');
    });
  }

  private openRenameModal(identity: string) {
    const isSelf = identity === 'self';
    const currentName = isSelf
      ? (this.manager.displayName || this.config.displayName || 'Participant')
      : (this.participants.find(part => part.identity === identity)?.name || '');
    if (!currentName && !isSelf) return;

    const modal = document.getElementById('rename-modal');
    const input = document.getElementById('rename-input') as HTMLInputElement;
    const saveBtn = document.getElementById('btn-save-rename');
    const cancelBtn = document.getElementById('btn-cancel-rename');

    if (modal && input) {
      input.value = currentName.replace(' (Tu)', '').trim();
      modal.style.display = 'flex';
      input.focus();

      const onSave = () => {
        const newName = input.value.trim();
        if (newName) {
          if (isSelf) {
            this.config.displayName = newName;
            this.manager.displayName = newName;
            try { localStorage.setItem('vorbello_display_name', newName); } catch (_) {}
            const selfP = this.participants.find(part => part.identity === 'self');
            if (selfP) selfP.name = `${newName} (Tu)`;
            this.manager.sendDataMessage({
              type: 'moderation',
              action: 'rename-user',
              targetIdentity: this.manager.getRoom()?.localParticipant?.identity || 'self',
              newName: newName,
            }).then(sent => {
              if (!sent) this.showFloatingToast('Numele a fost schimbat local, dar nu a putut fi transmis participanților.');
            }).catch(() => this.showFloatingToast('Numele nu a putut fi transmis participanților.'));
          } else {
            const p = this.participants.find(part => part.identity === identity);
            if (p) p.name = newName;
            this.participantNameOverrides.set(identity, newName);
            this.manager.sendDataMessage({
              type: 'moderation',
              action: 'rename-user',
              targetIdentity: identity,
              newName: newName,
            }).then(sent => {
              if (!sent) this.showFloatingToast('Numele nu a putut fi transmis participanților.');
            }).catch(() => this.showFloatingToast('Numele nu a putut fi transmis participanților.'));
          }
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateStageLayout();
        }
        cleanupModal();
      };

      const onCancel = () => {
        cleanupModal();
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') onSave();
        if (e.key === 'Escape') onCancel();
      };

      const cleanupModal = () => {
        modal.style.display = 'none';
        saveBtn?.removeEventListener('click', onSave);
        cancelBtn?.removeEventListener('click', onCancel);
        input.removeEventListener('keydown', onKeyDown);
      };

      saveBtn?.addEventListener('click', onSave);
      cancelBtn?.addEventListener('click', onCancel);
      input.addEventListener('keydown', onKeyDown);
    }
  }

  private renderChatMessages() {
    const container = document.getElementById('chat-history');
    if (!container) return;

    container.innerHTML = this.messages.map(m => {
      let fileBlock = '';
      if (m.file && m.file.dataUrl) {
        const ext = m.file.name.split('.').pop()?.toLowerCase() || '';
        let fileIcon = '📄';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) fileIcon = '🖼️';
        else if (['pdf'].includes(ext)) fileIcon = '📕';
        else if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) fileIcon = '📝';
        else if (['xls', 'xlsx', 'csv'].includes(ext)) fileIcon = '📊';
        else if (['ppt', 'pptx', 'key'].includes(ext)) fileIcon = '📽️';
        else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) fileIcon = '📦';
        else if (['mp3', 'wav', 'm4a', 'ogg'].includes(ext)) fileIcon = '🎵';
        else if (['mp4', 'webm', 'mov'].includes(ext)) fileIcon = '🎬';

        const sizeFormatted = m.file.size > 1024 * 1024
          ? (m.file.size / (1024 * 1024)).toFixed(1) + ' MB'
          : Math.max(1, Math.round(m.file.size / 1024)) + ' KB';

        const isImg = /^(?:data:image\/(?:jpeg|png|gif|webp);base64,)[A-Za-z0-9+/=]+$/i.test(m.file.dataUrl);

        fileBlock = `
          <div class="chat-file-card" style="margin-top: 6px; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 8px; display: flex; flex-direction: column; gap: 6px;">
            ${isImg ? `
              <div style="max-height: 140px; overflow: hidden; border-radius: 6px; cursor: pointer; background: rgba(0,0,0,0.3);" class="chat-download-file" data-message-id="${this.escapeAttribute(m.id)}" title="Atingeți pentru descărcare">
                <img src="${m.file.dataUrl}" alt="${this.escapeAttribute(m.file.name)}" style="width: 100%; height: auto; object-fit: contain; display: block;" />
              </div>
            ` : ''}
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 6px; overflow: hidden; min-width: 0;">
                <span style="font-size: 1.25rem; flex-shrink: 0;">${fileIcon}</span>
                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <div style="font-weight: 600; font-size: 0.8rem; color: #f8fafc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${this.escapeAttribute(m.file.name)}">${this.escapeHtml(m.file.name)}</div>
                  <div style="font-size: 0.68rem; color: var(--text-muted);">${sizeFormatted}</div>
                </div>
              </div>
              <button type="button" data-message-id="${this.escapeAttribute(m.id)}" class="chat-download-file btn btn-secondary btn-sm" style="padding: 3px 8px; font-size: 0.72rem; flex-shrink: 0; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; border-radius: 6px; cursor: pointer;" title="Descarcă fișierul pe dispozitiv">
                ⬇️ Descarcă
              </button>
            </div>
          </div>
        `;
      } else if (m.file && !m.file.dataUrl) {
        const ext = m.file.name.split('.').pop()?.toLowerCase() || '';
        let fileIcon = '📄';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) fileIcon = '🖼️';
        else if (['pdf'].includes(ext)) fileIcon = '📕';
        else if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) fileIcon = '📝';
        else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) fileIcon = '📦';

        const sizeFormatted = m.file.size > 1024 * 1024
          ? (m.file.size / (1024 * 1024)).toFixed(1) + ' MB'
          : Math.max(1, Math.round(m.file.size / 1024)) + ' KB';

        const prog = (m.file as any).progress || 0;

        fileBlock = `
          <div class="chat-file-card" style="margin-top: 6px; background: rgba(0,0,0,0.25); border: 1px solid rgba(56,189,248,0.3); border-radius: 8px; padding: 8px; display: flex; flex-direction: column; gap: 6px;">
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 6px; overflow: hidden; min-width: 0;">
                <span style="font-size: 1.25rem; flex-shrink: 0;">${fileIcon}</span>
                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <div style="font-weight: 600; font-size: 0.8rem; color: #f8fafc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${this.escapeAttribute(m.file.name)}">${this.escapeHtml(m.file.name)}</div>
                  <div style="font-size: 0.68rem; color: #38bdf8;">${sizeFormatted} • Se recepționează (<span id="transfer-pct-${m.id}">${prog}%</span>)...</div>
                </div>
              </div>
              <div style="font-size: 0.72rem; color: #38bdf8; display: flex; align-items: center; gap: 4px;">
                <span style="display: inline-block; width: 12px; height: 12px; border: 2px solid #38bdf8; border-top-color: transparent; border-radius: 50%; animation: spin 0.8s linear infinite;"></span>
              </div>
            </div>
            <div style="width: 100%; height: 4px; background: rgba(255,255,255,0.08); border-radius: 2px; overflow: hidden;">
              <div id="transfer-bar-${m.id}" style="width: ${prog}%; height: 100%; background: linear-gradient(90deg, #38bdf8, #818cf8); transition: width 0.15s ease;"></div>
            </div>
          </div>
        `;
      }

      const reactions = this.chatReactions.get(m.id);
      const reactionButtons = ['👍','❤️','👏','😂'].map(emoji => {
        const count = reactions ? Array.from(reactions.values()).filter(value => value === emoji).length : 0;
        return `<button type="button" class="chat-message-reaction" data-message-id="${this.escapeAttribute(m.id)}" data-emoji="${emoji}" aria-label="Reacționează ${emoji}">${emoji}${count ? ` ${count}` : ''}</button>`;
      }).join('');
      const reactionSummary = ['👍','❤️','👏','😂'].map(emoji => {
        const count = reactions ? Array.from(reactions.values()).filter(value => value === emoji).length : 0;
        return count ? `<button type="button" class="chat-message-reaction" data-message-id="${this.escapeAttribute(m.id)}" data-emoji="${emoji}" aria-label="Reacție ${emoji}, ${count}">${emoji} ${count}</button>` : '';
      }).join('');
      const reply = m.replyTo ? `<div class="chat-reply-quote"><strong>${this.escapeHtml(m.replyTo.senderName)}</strong> · ${this.escapeHtml(m.replyTo.text)}</div>` : '';
      return `
        <div class="chat-bubble ${m.isSelf ? 'self' : ''}">
          <div class="chat-sender">${this.escapeHtml(m.senderName)} • ${this.escapeHtml(formatChatTime(m.timestamp))}</div>
          ${reply}
          ${m.text ? `<div>${this.escapeHtml(m.text)}</div>` : ''}
          ${fileBlock}
          <div class="chat-reaction-summary">${reactionSummary}</div>
          <div class="chat-message-actions"><button type="button" class="chat-message-reply" data-message-id="${this.escapeAttribute(m.id)}">Răspunde</button><button type="button" class="chat-reactions-trigger" title="Adaugă o reacție" aria-label="Adaugă o reacție" aria-expanded="false"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10" cy="12" r="7"/><path d="M7 14c1 1.4 5 1.4 6 0M7.5 9.5h.01M12.5 9.5h.01M18 4v6M15 7h6"/></svg></button><span class="chat-reaction-picker" hidden>${reactionButtons}</span></div>
        </div>
      `;
    }).join('');

    container.scrollTop = container.scrollHeight;
  }

  private applyChatReaction(messageId: string, emoji: string, identity: string, active: boolean): void {
    if (!this.messages.some(m => m.id === messageId) || !['👍','❤️','👏','😂'].includes(emoji) || !identity) return;
    const reactions = this.chatReactions.get(messageId) || new Map<string, string>();
    if (active) reactions.set(identity, emoji);
    else if (reactions.get(identity) === emoji) reactions.delete(identity);
    this.chatReactions.set(messageId, reactions);
    this.renderChatMessages();
  }

  private renderParticipantsList() {
    const container = document.getElementById('participants-list');
    if (!container) return;

    // Prune stale participants so the drawer count exactly matches active LiveKit connections:
    this.reconcileParticipants();

    const waitingSectionHtml = ((this.isAdmin || this.isModerator) && this.waitingParticipants.length > 0) ? `
      <div class="waiting-room-panel-card" style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.35); border-radius: 10px; padding: 10px; margin-bottom: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-weight: 700; font-size: 0.8rem; color: #fbbf24; display: flex; align-items: center; gap: 6px;">
            ⏳ În Sala de Așteptare (${this.waitingParticipants.length})
          </span>
          <button class="btn btn-primary btn-sm btn-admit-all-panel" style="font-size: 0.7rem; padding: 2px 8px; border-radius: 6px;">
            Admite pe toți
          </button>
        </div>
        <div style="display: flex; flex-direction: column; gap: 6px;">
          ${this.waitingParticipants.map(wp => `
            <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(0,0,0,0.25); padding: 6px 8px; border-radius: 6px;">
              <div style="font-size: 0.8rem; font-weight: 600; color: #f8fafc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 125px;" title="${this.escapeAttribute(wp.name)}">
                ${this.escapeHtml(wp.name)}
              </div>
              <div style="display: flex; gap: 4px;">
                <button class="btn btn-primary btn-sm btn-admit-user-panel" data-id="${this.escapeAttribute(wp.identity)}" style="padding: 2px 7px; font-size: 0.72rem; border-radius: 4px;">Admite</button>
                <button class="btn btn-secondary btn-sm btn-reject-user-panel" data-id="${this.escapeAttribute(wp.identity)}" style="padding: 2px 7px; font-size: 0.72rem; border-radius: 4px; color: #f87171;">Respinge</button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    ` : '';

    // Sort participants: self first, then raised hands float to the top (Zoom & Meet style), then admins/mods, then alphabetical
    const sortedParticipants = [...this.participants].sort((a, b) => {
      if (a.identity === 'self') return -1;
      if (b.identity === 'self') return 1;
      const aRaised = (a.isHandRaised ? 1 : 0);
      const bRaised = (b.isHandRaised ? 1 : 0);
      if (aRaised !== bRaised) return bRaised - aRaised;
      const aAdmin = a.role === 'admin' ? 2 : (a.role === 'moderator' ? 1 : 0);
      const bAdmin = b.role === 'admin' ? 2 : (b.role === 'moderator' ? 1 : 0);
      if (aAdmin !== bAdmin) return bAdmin - aAdmin;
      return a.name.localeCompare(b.name);
    });

    const query = this.participantSearchQuery.trim().toLowerCase();
    const filteredParticipants = query
      ? sortedParticipants.filter(p => {
          const nameMatch = p.name.toLowerCase().includes(query);
          const isSelfTu = p.identity === 'self' && ('tu'.includes(query) || 'self'.includes(query));
          const roleMatch = (p.role && p.role.toLowerCase().includes(query)) || (this.isAdmin && p.identity === 'self' && 'administrator'.includes(query));
          return nameMatch || isSelfTu || roleMatch;
        })
      : sortedParticipants;

    const panelTitle = document.getElementById('panel-title');
    if (panelTitle && this.isParticipantsOpen) {
      if (query) {
        panelTitle.innerText = `Participanți (${filteredParticipants.length}/${this.participants.length})`;
      } else {
        panelTitle.innerText = `Participanți Conectați (${this.participants.length})`;
      }
    }

    if (query && filteredParticipants.length === 0) {
      container.innerHTML = waitingSectionHtml + `
        <div style="padding: 2.2rem 1rem; text-align: center; color: var(--text-muted); font-size: 0.85rem;">
          <div style="font-size: 1.8rem; margin-bottom: 0.4rem; opacity: 0.7;">🔍</div>
          <div style="color: #cbd5e1; font-weight: 600;">Niciun participant găsit</div>
          <div style="font-size: 0.75rem; margin-top: 0.3rem; color: #94a3b8;">Nu am găsit rezultate pentru „<span style="color: #38bdf8;">${this.escapeHtml(this.participantSearchQuery.trim())}</span>”</div>
        </div>
      `;
      return;
    }

    container.innerHTML = waitingSectionHtml + filteredParticipants.map(p => {
      const isSelf = p.identity === 'self';
      const isPartAdmin = p.role === 'admin' || (isSelf && this.isAdmin);
      const isPartMod = !isPartAdmin && (p.role === 'moderator' || (isSelf && this.isModerator));
      const roleLabel = isPartAdmin ? '👑 Administrator' : (isPartMod ? '🛡️ Moderator' : 'Participant');
      const subtitle = isSelf 
        ? (this.isAdmin ? 'Tu (👑 Administrator / Gazdă)' : (this.isModerator ? 'Tu (🛡️ Moderator)' : 'Tu (Participant)'))
        : roleLabel;

      return `
        <div class="participant-item" id="part-item-${p.identity}">
          <div style="display: flex; align-items: center; gap: 0.6rem;">
            ${p.avatarUrl ? `
              <img src="${p.avatarUrl}" style="width: 32px; height: 32px; border-radius: 50%; object-fit: cover; border: 1.5px solid ${isPartAdmin ? '#f59e0b' : (isPartMod ? '#38bdf8' : 'rgba(255,255,255,0.2)')};" alt="${p.name}" />
            ` : `
              <div style="width: 32px; height: 32px; border-radius: 50%; background: ${isPartAdmin ? 'linear-gradient(135deg, #f59e0b, #d97706)' : (isPartMod ? 'linear-gradient(135deg, #38bdf8, #0284c7)' : 'var(--accent-gradient)')}; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; font-weight: 700; color: #fff;">
                ${p.name.charAt(0)}
              </div>
            `}
            <div>
              <div style="font-weight: 600; font-size: 0.85rem; color: #f8fafc; display: flex; align-items: center; gap: 6px;">
                <span>${p.name}</span>
                ${(isSelf ? this.isLocalHandRaised : p.isHandRaised) && (!(this.isAdmin || this.isModerator) || isSelf) ? '<span class="participant-hand-badge" title="Mână ridicată">✋</span>' : ''}
              </div>
              <div style="font-size: 0.7rem; color: ${isPartAdmin ? '#fbbf24' : (isPartMod ? '#38bdf8' : 'var(--text-muted)')}; font-weight: ${isPartAdmin || isPartMod ? '600' : '400'};">${subtitle}</div>
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0; position: relative;">
            ${(this.isAdmin || this.isModerator) && p.isHandRaised && !isSelf ? `
              <button class="btn btn-secondary btn-sm btn-lower-user-hand" data-id="${p.identity}" title="Coboară mâna participantului" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(245, 158, 11, 0.2); border: 1px solid rgba(245, 158, 11, 0.5); color: #fbbf24; font-size: 0.85rem;">
                ✋
              </button>
            ` : ''}
            ${(this.isAdmin || this.isHost || this.isModerator) ? `
              <button class="btn btn-secondary btn-sm btn-pin-user" data-id="${p.identity}" title="${this.pinnedIdentity === p.identity ? 'Anulează Fixarea' : 'Fixează pe Scenă'}" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; ${this.pinnedIdentity === p.identity ? 'background: #6366f1; border: 1px solid #818cf8; color: #fff;' : 'background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #cbd5e1;'}">
                ${ICONS.pin}
              </button>
            ` : ''}
            ${!isSelf ? `
              ${(this.isAdmin || this.isModerator) ? `
                <button class="btn btn-secondary btn-sm btn-mute-user" data-id="${p.identity}" title="${p.isMuted ? 'Activează Microfon' : 'Oprește Microfon'}" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; ${p.isMuted ? 'background: rgba(239, 68, 68, 0.2); border: 1px solid rgba(239, 68, 68, 0.45); color: #f87171;' : 'background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #cbd5e1;'}">
                  ${p.isMuted ? ICONS.micOffSm : ICONS.micSm}
                </button>
              ` : `
                <span style="font-size: 0.72rem; color: ${p.isMuted ? '#f87171' : '#34d399'}; padding: 3px 6px; background: rgba(255,255,255,0.05); border-radius: 6px;">
                  ${p.isMuted ? '🔇' : '🎤'}
                </span>
              `}
              ${(this.isAdmin || this.isHost || this.isModerator) ? `
                <button class="btn btn-secondary btn-sm btn-part-menu-trigger" data-id="${p.identity}" title="Opțiuni participant (Co-Gazdă, Gazdă, Moderare)" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #cbd5e1;">
                  ${ICONS.moreVert}
                </button>
                <div class="participant-menu-dropdown" id="part-dropdown-${p.identity}" style="display: none;">
                  ${this.config.isWebinar && this.config.requestWebinarPermission && !isPartAdmin && /^guest-[a-f0-9]{32}$/.test(p.identity) ? `
                    <button type="button" class="participant-dropdown-item btn-revoke-webinar-device" data-id="${p.identity}" data-medium="microphone">${ICONS.micOff} Retrage dreptul de a vorbi</button>
                    <button type="button" class="participant-dropdown-item btn-revoke-webinar-device" data-id="${p.identity}" data-medium="camera">${ICONS.camOff} Retrage dreptul la cameră</button>
                  ` : ''}
                  ${(this.isHost && !isPartAdmin) ? `
                    <button type="button" class="participant-dropdown-item btn-action-toggle-cohost" data-id="${p.identity}">
                      🛡️ ${isPartMod ? 'Retrage Co-Gazdă' : 'Fă Co-Gazdă'}
                    </button>
                  ` : ''}
                  ${(this.isHost && !isPartAdmin) ? `
                    <button type="button" class="participant-dropdown-item btn-action-make-host" data-id="${p.identity}">
                      👑 Cedează rolul de gazdă
                    </button>
                  ` : ''}
                  ${(this.isAdmin || this.isModerator) && !isPartAdmin ? `
                    <button type="button" class="participant-dropdown-item btn-rename-user" data-id="${p.identity}">
                      ✏️ Redenumește
                    </button>
                    <button type="button" class="participant-dropdown-item btn-wait-user" data-id="${p.identity}">
                      ⏳ Trimite în Sala de Așteptare
                    </button>
                    <div style="height: 1px; background: rgba(255,255,255,0.08); margin: 3px 0;"></div>
                    <button type="button" class="participant-dropdown-item danger btn-kick-user" data-id="${p.identity}">
                      ❌ Elimină din Sală
                    </button>
                  ` : ''}
                </div>
              ` : ''}
            ` : '<span style="font-size: 0.75rem; color: var(--status-active); font-weight: 600; margin-left: 4px;">● Tu</span>'}
          </div>
        </div>
      `;
    }).join('');

    if (!query && this.participants.length === 1 && this.config.appMode === 'production' && (this.isAdmin || this.isModerator)) {
      container.innerHTML += `
        <div style="padding: 1.25rem 1rem; text-align: center; color: var(--text-muted); font-size: 0.8rem; border: 1px dashed rgba(56, 189, 248, 0.35); background: rgba(56, 189, 248, 0.05); border-radius: 14px; margin: 1.25rem 0.85rem 0.75rem 0.85rem; box-sizing: border-box;">
          <div style="font-size: 1.4rem; margin-bottom: 0.3rem;">👥</div>
          <div style="color: #f1f5f9; font-weight: 600; font-size: 0.82rem;">Ești singur în sală</div>
          <div style="margin-top: 0.25rem; line-height: 1.35; font-size: 0.73rem;">Distribuie linkul conferinței către invitați pentru a se alătura în timp real.</div>
          <button class="btn btn-primary btn-sm btn-empty-copy-link" style="margin-top: 0.65rem; font-size: 0.75rem; padding: 0.35rem 0.9rem; border-radius: 8px;">
            📋 Copiază Linkul Camerei
          </button>
        </div>
      `;
    }

    // Empty state copy link listener
    container.querySelector('.btn-empty-copy-link')?.addEventListener('click', (e) => {
      this.copyInviteLink(e.currentTarget as HTMLElement);
    });

    // Sidebar moderation controls
    container.querySelectorAll('.btn-lower-user-hand').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const p = this.participants.find(part => part.identity === id);
        if (p) {
          p.isHandRaised = false;
          await this.manager.sendDataMessage({
            type: 'hand_raise',
            identity: id,
            name: p.name,
            isRaised: false,
          });
          this.renderVideoGrid();
          this.renderParticipantsList();
        }
      });
    });
    container.querySelectorAll('.btn-pin-user').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const newId = this.pinnedIdentity === id ? null : id;
        this.setPinState(newId, true);
      });
    });

    container.querySelectorAll('.btn-rename-user').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        this.openRenameModal(id!);
      });
    });

    // Toggle 3-dots participant dropdown
    container.querySelectorAll('.btn-revoke-webinar-device').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const el = e.currentTarget as HTMLElement;
        const id = el.getAttribute('data-id');
        const medium = el.getAttribute('data-medium');
        if (id && (medium === 'camera' || medium === 'microphone')) await this.changeWebinarPermission(id, medium, false);
        document.querySelectorAll('.participant-menu-dropdown').forEach(d => (d as HTMLElement).style.display = 'none');
      });
    });
    container.querySelectorAll('.btn-part-menu-trigger').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const targetDropdown = document.getElementById(`part-dropdown-${id}`);
        const isShown = targetDropdown && targetDropdown.style.display !== 'none';
        // Close all participant dropdowns
        document.querySelectorAll('.participant-menu-dropdown').forEach(d => (d as HTMLElement).style.display = 'none');
        if (targetDropdown && !isShown) {
          targetDropdown.style.display = 'flex';
        }
      });
    });

    // Make Co-Host / Revoke Co-Host Action
    container.querySelectorAll('.btn-action-toggle-cohost').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        document.querySelectorAll('.participant-menu-dropdown').forEach(d => (d as HTMLElement).style.display = 'none');
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const p = this.participants.find(part => part.identity === id);
        if (!p || !id || !this.isHost || p.role === 'admin') return;

        const isCurrentlyMod = p.role === 'moderator';
        const newRole = isCurrentlyMod ? 'user' : 'moderator';
        p.role = newRole;

        const action = isCurrentlyMod ? 'revoke-cohost' : 'make-cohost';
        await this.manager.sendDataMessage({
          type: 'moderation',
          action: action,
          targetIdentity: id,
          targetName: p.name,
          senderIdentity: this.manager.getRoom()?.localParticipant?.identity || 'self',
          senderName: this.config.displayName
        });

        this.showFloatingToast(isCurrentlyMod 
          ? `ℹ️ Rolul de Co-Gazdă pentru "${p.name}" a fost revocat.` 
          : `🛡️ "${p.name}" este acum Co-Gazdă (Co-Host)! Poate partaja ecranul și modera sala.`);
        this.renderParticipantsList();
      });
    });

    // Make Host (Transfer Meeting Ownership)
    container.querySelectorAll('.btn-action-make-host').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        document.querySelectorAll('.participant-menu-dropdown').forEach(d => (d as HTMLElement).style.display = 'none');
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const p = this.participants.find(part => part.identity === id);
        if (!p || !id || !this.isHost || p.role === 'admin') return;

        const confirmed = window.confirm(`👑 Sigur doriți să cedați rolul de Gazdă Principală către "${p.name}"?\n\nDumneavoastră veți deveni Co-Gazdă a acestei întâlniri.`);
        if (!confirmed) return;

        this.isHost = false;
        this.role = 'moderator';
        this.isModerator = true;
        const selfP = this.participants.find(part => part.identity === 'self');
        if (selfP) selfP.role = 'moderator';

        p.role = 'admin';

        await this.manager.sendDataMessage({
          type: 'moderation',
          action: 'make-host',
          targetIdentity: id,
          targetName: p.name,
          senderIdentity: this.manager.getRoom()?.localParticipant?.identity || 'self',
          senderName: this.config.displayName
        });

        this.updateControlsState();
        this.renderParticipantsList();
        this.showFloatingToast(`👑 Ați cedat rolul de Gazdă către "${p.name}". Dumneavoastră sunteți acum Co-Gazdă.`);
      });
    });

    container.querySelectorAll('.btn-mute-user').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const p = this.participants.find(part => part.identity === id);
        if (p && id) {
          if (this.config.isWebinar && p.isMuted && this.config.requestWebinarPermission) {
            try { await this.config.requestWebinarPermission(id, 'microphone', true); this.showFloatingToast('Participantul poate porni microfonul.'); }
            catch (_) { this.showFloatingToast('Permisiunea nu a putut fi acordată.'); }
            return;
          }
          await this.manager.sendDataMessage({
            type: 'moderation',
            action: p.isMuted ? 'unmute-user' : 'mute-user',
            targetIdentity: id,
          });
          this.showFloatingToast(p.isMuted ? 'Solicitarea de activare a microfonului a fost trimisă.' : 'Solicitarea de oprire a microfonului a fost trimisă.');
          this.renderVideoGrid();
          this.renderParticipantsList();
        }
      });
    });

    container.querySelectorAll('.btn-kick-user').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) {
          await this.kickParticipant(id);
        }
      });
    });

    container.querySelectorAll('.btn-admit-user-panel').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) this.admitWaitingParticipant(id);
      });
    });

    container.querySelectorAll('.btn-reject-user-panel').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) this.rejectWaitingParticipant(id);
      });
    });

    container.querySelector('.btn-admit-all-panel')?.addEventListener('click', () => {
      this.admitAllWaitingParticipants();
    });

    container.querySelectorAll('.btn-wait-user').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) await this.moveParticipantToWaitingRoom(id);
      });
    });
  }

  private updateControlsState() {
    // Mic button
    const micBtn = document.getElementById('btn-toggle-mic');
    if (micBtn && this.config.webinarAudience) {
      micBtn.setAttribute('aria-disabled', String(!this.webinarMicAllowed));
      micBtn.title = this.webinarMicAllowed ? 'Pornește sau oprește microfonul' : 'Gazda trebuie să permită folosirea microfonului';
    }
    const micLabel = document.getElementById('label-mic');
    if (micBtn && micLabel) {
      micBtn.className = `control-btn ${this.manager.isMicEnabled ? 'active-on' : 'active-off'}`;
      micLabel.innerText = this.manager.isMicEnabled ? 'Microfon Pornit' : 'Microfon Oprit';
      const icon = micBtn.querySelector('.btn-icon') as HTMLElement | null;
      if (icon) icon.innerHTML = this.manager.isMicEnabled ? ICONS.micOn : ICONS.micOff;
    }

    // Cam button
    const camBtn = document.getElementById('btn-toggle-cam');
    if (camBtn && this.config.webinarAudience) {
      camBtn.setAttribute('aria-disabled', String(!this.webinarCameraAllowed));
      camBtn.title = this.webinarCameraAllowed ? 'Pornește sau oprește camera' : 'Gazda trebuie să permită folosirea camerei';
    }
    const camLabel = document.getElementById('label-cam');
    if (camBtn && camLabel) {
      camBtn.className = `control-btn ${this.manager.isCameraEnabled ? 'active-on' : 'active-off'}`;
      camLabel.innerText = this.manager.isCameraEnabled ? 'Cameră Pornită' : 'Cameră Oprită';
      const icon = camBtn.querySelector('.btn-icon') as HTMLElement | null;
      if (icon) icon.innerHTML = this.manager.isCameraEnabled ? ICONS.camOn : ICONS.camOff;
    }

    // Screen button
    const screenBtn = document.getElementById('btn-toggle-screen');
    const screenLabel = document.getElementById('label-screen');
    const isScreenSharing = this.manager.isScreenShareEnabled || (this.manager.getRoom()?.localParticipant?.isScreenShareEnabled ?? false);
    const allowPolicy = this.config.roomSettings?.allowScreenShare;
    const isPrivileged = this.isHost || this.isAdmin || this.isModerator;
    if (screenBtn) {
      screenBtn.className = `control-btn ${isScreenSharing ? 'active-screen' : ''}`;
      if (allowPolicy === 'hosts_only' && !isPrivileged) {
        screenBtn.setAttribute('title', '🔒 Partajare ecran restricționată (Doar Gazdă / Co-Gazdă)');
        screenBtn.classList.add('screen-restricted');
      } else {
        screenBtn.removeAttribute('title');
        screenBtn.classList.remove('screen-restricted');
      }
    }
    if (screenLabel) {
      screenLabel.innerText = isScreenSharing ? 'Oprește Partajarea' : 'Partajează Ecran';
    }

    // Hand button (Opțiunea 1)
    const handBtn = document.getElementById('btn-raise-hand');
    const handLabel = document.getElementById('label-hand');
    const moreBtn = document.getElementById('btn-more-options');
    const handDot = document.getElementById('dock-hand-indicator');
    if (handBtn && handLabel) {
      if (this.isLocalHandRaised) {
        handBtn.classList.add('active-hand');
        handLabel.innerText = 'Coboară Mâna';
      } else {
        handBtn.classList.remove('active-hand');
        handLabel.innerText = 'Ridică Mâna';
      }
    }
    if (this.isLocalHandRaised) {
      moreBtn?.classList.add('has-hand-raised');
      if (handDot) handDot.style.display = 'inline-block';
    } else {
      moreBtn?.classList.remove('has-hand-raised');
      if (handDot) handDot.style.display = 'none';
    }

    // Participants count in button
    const partBtn = document.getElementById('btn-open-participants');
    const headerCount = document.getElementById('header-participant-count');
    if (headerCount) headerCount.textContent = String(this.participants.length);
    if (partBtn) {
      const label = partBtn.querySelector('span:last-child') as HTMLElement | null;
      if (label) label.innerText = `Participanți (${this.participants.length})`;
    }

    // Sync view mode button text and icon
    const viewIcon = document.getElementById('view-mode-icon');
    const viewText = document.getElementById('view-mode-text');
    const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
    const sheetViewText = document.getElementById('sheet-label-view-mode');

    const label = this.currentViewMode === 'speaker' ? 'Vorbitor Activ' : this.currentViewMode === 'studio' ? 'Studio 16:9' : 'Grilă Dinamică';
    const icon = this.currentViewMode === 'speaker' ? '👤' : this.currentViewMode === 'studio' ? '🗂️' : '⊞';
    if (viewIcon) viewIcon.innerText = icon;
    if (viewText) viewText.innerText = label;
    if (sheetViewIcon) sheetViewIcon.innerText = icon;
    if (sheetViewText) sheetViewText.innerText = `Mod: ${label}`;
  }

  private async changeWebinarPermission(identity: string, medium: 'camera' | 'microphone', allow: boolean): Promise<void> {
    if (!this.config.requestWebinarPermission || !this.config.isWebinar || !/^guest-[a-f0-9]{32}$/.test(identity)) return;
    try {
      await this.config.requestWebinarPermission(identity, medium, allow);
      this.showFloatingToast(allow ? 'Permisiunea a fost acordată.' : (medium === 'camera' ? 'Dreptul la cameră a fost retras.' : 'Dreptul de a vorbi a fost retras.'));
    } catch (_) {
      this.showFloatingToast('Permisiunea nu a putut fi modificată. Încearcă din nou.');
    }
  }

  private refreshWebinarPermissions(): void {
    if (!this.config.webinarAudience) return;
    const permissions = this.manager.getRoom()?.localParticipant.permissions;
    const sources = permissions?.canPublishSources || [];
    const allowed = (source: number) => Boolean(permissions?.canPublish && (sources.length === 0 || sources.includes(source)));
    const previousMic = this.webinarMicAllowed;
    const previousCamera = this.webinarCameraAllowed;
    this.webinarMicAllowed = allowed(2);
    this.webinarCameraAllowed = allowed(1);
    if (!this.webinarMicAllowed) {
      if (previousMic || this.manager.isMicEnabled) void this.manager.enableMicrophone(false).catch(() => {});
      this.manager.isMicEnabled = false;
    }
    if (!this.webinarCameraAllowed) {
      if (previousCamera || this.manager.isCameraEnabled) void this.manager.enableCamera(false).catch(() => {});
      this.manager.isCameraEnabled = false;
    }
    this.updateControlsState();
    if (!previousMic && this.webinarMicAllowed) this.queueWebinarPermission('microphone');
    if (!previousCamera && this.webinarCameraAllowed) this.queueWebinarPermission('camera');
  }

  private queueWebinarPermission(medium: 'microphone' | 'camera'): void {
    if (this.webinarPermissionDialog?.dataset.medium === medium || this.webinarPermissionQueue.includes(medium)) return;
    this.webinarPermissionQueue.push(medium);
    this.showNextWebinarPermission();
  }

  private showNextWebinarPermission(): void {
    if (this.webinarPermissionDialog) return;
    const medium = this.webinarPermissionQueue.shift();
    if (!medium) return;
    if (medium === 'microphone' ? !this.webinarMicAllowed : !this.webinarCameraAllowed) {
      this.showNextWebinarPermission(); return;
    }
    const dialog = document.createElement('div');
    dialog.className = 'webinar-permission-overlay';
    dialog.dataset.medium = medium;
    const name = medium === 'microphone' ? 'microfonul' : 'camera';
    dialog.innerHTML = `<div class="webinar-permission-card" role="dialog" aria-modal="true" aria-labelledby="webinar-permission-title"><h2 id="webinar-permission-title">Gazda îți permite să pornești ${name}</h2><p>Dispozitivul rămâne oprit până alegi să îl pornești.</p><div class="webinar-permission-actions"><button type="button" data-choice="later">Nu acum</button><button type="button" data-choice="enable">Pornește ${name}</button></div></div>`;
    this.webinarPermissionDialog = dialog;
    document.body.appendChild(dialog);
    dialog.querySelector<HTMLButtonElement>('[data-choice="enable"]')?.addEventListener('click', async () => {
      if (medium === 'microphone' ? !this.webinarMicAllowed : !this.webinarCameraAllowed) {
        this.showFloatingToast('Permisiunea nu mai este disponibilă.');
      } else {
        try {
          const enabled = medium === 'microphone' ? await this.manager.enableMicrophone(true) : await this.manager.enableCamera(true);
          if (!enabled) this.showFloatingToast('Dispozitivul nu a putut fi pornit. Verifică permisiunile dispozitivului.');
          this.updateControlsState(); this.updateSelfTile();
        } catch (_) { this.showFloatingToast('Dispozitivul nu a putut fi pornit.'); }
      }
      dialog.remove(); this.webinarPermissionDialog = null; this.showNextWebinarPermission();
    });
    dialog.querySelector<HTMLButtonElement>('[data-choice="later"]')?.addEventListener('click', () => {
      dialog.remove(); this.webinarPermissionDialog = null; this.showNextWebinarPermission();
    });
  }

  private refreshCameraAfterSwitch(): void {
    const video = document.querySelector<HTMLVideoElement>('#tile-self video.self-cam-video');
    const camera = this.manager.getRoom()?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
    if (video && camera) { camera.detach(video); video.srcObject = null; }
    this.updateSelfTile();
    this.updateStageLayout();
  }

  private updateSelfTile() {
    const selfTile = document.getElementById('tile-self');
    if (!selfTile) return;

    let existingVideo = selfTile.querySelector('video.self-cam-video') as HTMLVideoElement | null;
    const avatar = selfTile.querySelector('.avatar-placeholder') as HTMLElement | null;

    if (this.manager.isCameraEnabled) {
      const room = this.manager.getRoom();
      let localCamPub = room?.localParticipant?.getTrackPublication(Track.Source.Camera);
      if (!localCamPub && room?.localParticipant?.videoTrackPublications) {
        for (const pub of room.localParticipant.videoTrackPublications.values()) {
          if (pub.source !== Track.Source.ScreenShare && pub.trackName !== 'screen_share' && (pub as any).name !== 'screen_share') {
            localCamPub = pub;
            break;
          }
        }
      }
      const fallbackStream = this.manager.getLocalCameraStream();

      if (!existingVideo) {
        existingVideo = document.createElement('video');
        existingVideo.className = 'self-cam-video';
        existingVideo.autoplay = true;
        existingVideo.muted = true;
        existingVideo.setAttribute('muted', '');
        existingVideo.playsInline = true;
        existingVideo.setAttribute('playsinline', '');
        existingVideo.setAttribute('webkit-playsinline', '');
        existingVideo.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; border-radius:inherit; z-index:1; display:block;';
        selfTile.prepend(existingVideo);
        this.adaptVideoElementFit(existingVideo, selfTile);

        // Mobile / Tablet touch unlock for media autoplay (both video & audio)
        if (typeof window !== 'undefined' && !(window as any)._vorbelloTouchUnlockAttached) {
          (window as any)._vorbelloTouchUnlockAttached = true;
          const unlock = async () => {
            try {
              if (this.manager.getRoom() && !this.manager.getRoom()?.canPlaybackAudio) {
                await this.manager.getRoom()?.startAudio();
              }
            } catch (_) {}
            document.querySelectorAll('video').forEach(v => {
              if (v.srcObject && v.paused) v.play().catch(() => {});
            });
            document.querySelectorAll('audio').forEach(a => {
              if (a.paused && !a.muted) a.play().catch(() => {});
            });
          };
          ['touchstart', 'touchend', 'pointerdown', 'click'].forEach(evt => {
            window.addEventListener(evt, unlock, { passive: true });
          });
        }
      }

      // Mirror selfie camera (user), do NOT mirror rear environment camera!
      existingVideo.style.transform = this.manager.currentFacingMode === 'environment' || this.selectedVideoEffect === 'background' ? 'none' : 'scaleX(-1)';
      this.applyVideoEffectToElement(existingVideo);

      const showSelfIfReady = () => {
        if (!existingVideo) return;
        const hasLiveTrack = (existingVideo.srcObject instanceof MediaStream &&
          existingVideo.srcObject.getVideoTracks().some(t => t.readyState === 'live')) ||
          Boolean(localCamPub?.track && !localCamPub.isMuted);
        if (this.manager.isCameraEnabled && (hasLiveTrack || (localCamPub?.track && !localCamPub.isMuted))) {
          existingVideo.style.display = 'block';
          if (avatar) avatar.style.display = 'none';
        } else {
          existingVideo.style.display = 'none';
          if (avatar) avatar.style.display = 'flex';
        }
      };

      existingVideo.onloadedmetadata = () => {
        showSelfIfReady();
        existingVideo?.play().catch(() => {});
      };
      existingVideo.onloadeddata = showSelfIfReady;
      existingVideo.onplaying = showSelfIfReady;
      existingVideo.onresize = showSelfIfReady;

      const activeLocalStream = fallbackStream || this.manager.getLocalCameraStream();
      if (localCamPub && localCamPub.track) {
        if (!localCamPub.track.attachedElements.includes(existingVideo) || !existingVideo.srcObject) {
          localCamPub.track.attach(existingVideo);
        }
        existingVideo.play().catch(() => {});
        showSelfIfReady();
      } else if (activeLocalStream) {
        if (existingVideo.srcObject !== activeLocalStream) {
          existingVideo.srcObject = activeLocalStream;
        }
        existingVideo.play().catch(() => {});
        showSelfIfReady();
      } else {
        existingVideo.style.display = 'none';
        if (avatar) avatar.style.display = 'flex';
      }
    } else {
      if (existingVideo) {
        existingVideo.style.display = 'none';
      }
      if (avatar) avatar.style.display = 'flex';
    }
  }

  public async updateTelemetryPopover(isConnected?: boolean, statusDetail?: string): Promise<void> {
    const isConn = isConnected !== undefined ? isConnected : (this.manager.getRoom()?.state === 'connected' || this.isP2PMode);
    const vpsUrlEl = document.getElementById('telemetry-vps-url');
    const connStateEl = document.getElementById('telemetry-conn-state');
    const rttEl = document.getElementById('telemetry-rtt-val');
    const camValEl = document.getElementById('telemetry-cam-val');
    const upEl = document.getElementById('telemetry-upload-bitrate');
    const downEl = document.getElementById('telemetry-download-bitrate');
    const lossEl = document.getElementById('telemetry-loss-val');
    const limitEl = document.getElementById('telemetry-quality-limit');

    if (vpsUrlEl) {
      if (this.isP2PMode) {
        vpsUrlEl.textContent = 'app.vorbello.eu (Mesh P2P WebRTC Direct)';
        vpsUrlEl.style.color = '#34d399';
      } else {
        vpsUrlEl.textContent = this.config.livekitUrl || 'wss://live.vorbello.eu';
        vpsUrlEl.style.color = '#38bdf8';
      }
    }

    if (connStateEl) {
      if (this.isP2PMode) {
        connStateEl.style.color = '#10b981';
        connStateEl.textContent = '● Conectat Direct (Criptat E2E)';
      } else if (isConn) {
        connStateEl.style.color = '#10b981';
        connStateEl.textContent = this.isAdmin ? '● Conectat Stabil (LiveKit Securizat)' : '● Conectat Stabil (Criptat Securizat)';
      } else {
        connStateEl.style.color = '#eab308';
        connStateEl.textContent = statusDetail || '🟡 În Așteptare Conexiune Cloud Securizat';
      }
    }

    // Query real-time statistics from WebRTC peer connection
    let realStats = null;
    try {
      realStats = await this.manager.getRealtimeStats();
    } catch (_) {}
    const dtlsVersionEl = document.getElementById('telemetry-dtls-version');
    if (dtlsVersionEl) dtlsVersionEl.textContent = realStats?.dtlsVersions?.join(' / ') || 'Neraportată de dispozitiv';

    const connectionPill = document.getElementById('conf-status-pill');
    if (connectionPill && isConn) {
      const latency = realStats?.rttMs;
      const suffix = this.config.isPlatformAdmin && typeof latency === 'number' && Number.isFinite(latency) && latency >= 0 ? ` · ${Math.round(latency)} ms` : '';
      connectionPill.innerHTML = `<span class="conf-quality-dot" style="background:var(--status-active)"></span> Conexiune OK${suffix}`;
    }

    if (rttEl) {
      const rttVal = realStats?.rttMs || 18;
      if (this.isP2PMode) {
        rttEl.textContent = `< ${rttVal} ms (P2P Direct)`;
        rttEl.style.color = '#34d399';
      } else if (isConn) {
        rttEl.textContent = `${rttVal} ms (${rttVal < 35 ? 'Excelent' : (rttVal < 100 ? 'Bun' : 'Ridicată')})`;
        rttEl.style.color = rttVal < 50 ? '#34d399' : (rttVal < 120 ? '#fbbf24' : '#f87171');
      } else {
        rttEl.textContent = '-- ms (Fără conexiune la port 7880)';
        rttEl.style.color = '#94a3b8';
      }
    }

    if (camValEl) {
      if (realStats && realStats.outboundResolution) {
        camValEl.textContent = `${realStats.outboundResolution} (${realStats.outboundFps || 30} FPS HD)`;
        camValEl.style.color = '#34d399';
      } else {
        camValEl.textContent = this.manager.isCameraEnabled ? '1280x720 HD (30 FPS)' : 'Oprită';
        camValEl.style.color = this.manager.isCameraEnabled ? '#34d399' : '#f87171';
      }
    }

    if (upEl && realStats) {
      upEl.textContent = `${realStats.outboundBitrateKbps.toLocaleString()} kbps`;
      upEl.style.color = realStats.outboundBitrateKbps > 500 ? '#38bdf8' : (realStats.outboundBitrateKbps > 0 ? '#34d399' : '#94a3b8');
    }

    if (downEl && realStats) {
      downEl.textContent = `${realStats.inboundBitrateKbps.toLocaleString()} kbps`;
      downEl.style.color = realStats.inboundBitrateKbps > 500 ? '#38bdf8' : (realStats.inboundBitrateKbps > 0 ? '#34d399' : '#94a3b8');
    }

    if (lossEl && realStats) {
      lossEl.textContent = `${realStats.packetLossPercent}%`;
      lossEl.style.color = realStats.packetLossPercent === 0 ? '#34d399' : (realStats.packetLossPercent < 2 ? '#fbbf24' : '#f87171');
    }

    if (limitEl && realStats) {
      if (realStats.qualityLimitation === 'bandwidth') {
        limitEl.textContent = '⚠️ Limitare Rețea (Barieră Lățime Bandă)';
        limitEl.style.color = '#f87171';
      } else if (realStats.qualityLimitation === 'cpu') {
        limitEl.textContent = '⚠️ Limitare CPU Dispozitiv';
        limitEl.style.color = '#fbbf24';
      } else {
        limitEl.textContent = '✅ Fără limitări (Capacitate optimă)';
        limitEl.style.color = '#34d399';
      }
    }

    const helpEl = document.getElementById('telemetry-status-help');
    if (helpEl) {
      if (this.isP2PMode) {
        helpEl.innerHTML = `<span style="color: #34d399;">✅ Conexiune directă activă între dispozitive (P2P WebRTC prin <code>app.vorbello.eu</code>). Nu depinde de VPS.</span>`;
      } else if (isConn) {
        helpEl.innerHTML = `<span style="color: #34d399;">✅ Conexiune activă la serverul SFU (${this.config.livekitUrl || 'live.vorbello.eu'}). Transfer date optimizat.</span>`;
      } else {
        helpEl.innerHTML = `<span style="color: #fbbf24;">⚠️ Serverul LiveKit (<code>${this.config.livekitUrl || 'wss://live.vorbello.eu'}</code>) nu răspunde. Asigurați-vă că este pornit VPS-ul sau comutați în Mod Test.</span>`;
      }
    }
  }

  private startTelemetryMonitoring(): void {
    this.stopTelemetryMonitoring();
    this.updateTelemetryPopover();
    this.telemetryInterval = setInterval(() => {
      const popover = document.getElementById('network-telemetry-popover');
      if (popover && popover.style.display !== 'none') {
        this.updateTelemetryPopover();
      } else {
        this.stopTelemetryMonitoring();
      }
    }, 1000);
  }

  private stopTelemetryMonitoring(): void {
    if (this.telemetryInterval) {
      clearInterval(this.telemetryInterval);
      this.telemetryInterval = null;
    }
  }

  public activateInteractiveDemoMode(): void {
    const pill = document.getElementById('conf-status-pill');
    if (pill) {
      pill.innerHTML = `<span class="conf-quality-dot" style="background: #10b981;"></span> Mod Test / Demo Activ`;
    }
    this.updateTelemetryPopover(true, 'Mod Demo Local Activ');
    this.updateSelfTile();

    this.addOrUpdateParticipant('part-ana', 'Ana Popescu (Moderator)');
    this.addOrUpdateParticipant('part-mihai', 'Mihai Ionescu (Membru)');
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();

    this.messages.push({
      id: 'msg-' + Date.now(),
      senderName: 'Ana Popescu',
      senderIdentity: 'part-ana',
      text: 'Modul test / demo Vorbello este activ! Puteți testa camera, microfonul, partajarea de ecran și reacțiile.',
      timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
      isSelf: false,
    });
    this.renderChatMessages();
    this.showFloatingToast('🧪 Modul test / demo Vorbello a fost activat!');
  }

  public async retryVpsConnection(): Promise<void> {
    const pill = document.getElementById('conf-status-pill');
    if (pill) {
      pill.innerHTML = `<span class="conf-quality-dot" style="background: #38bdf8;"></span> Se conectează la Cloud Securizat...`;
    }
    this.showFloatingToast(`Se reîncearcă conexiunea Cloud Securizat (${this.config.livekitUrl})...`);
    try {
      await this.manager.connect(this.config.livekitUrl, this.config.token, this.config.encryptionKey);
      if (pill) {
        pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Conexiune OK`;
      }
      this.updateTelemetryPopover(true, this.isAdmin ? '● Conectat Stabil (LiveKit Securizat)' : '● Conectat Stabil (Criptat Securizat)');
      this.updateSelfTile();
      this.renderVideoGrid();
      this.showFloatingToast('✅ Conexiune stabilită cu succes la Cloud Securizat!');
    } catch (err) {
      console.warn('[Vorbello Conference] Retry Cloud connection failed:', err);
      if (pill) {
        pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> În Așteptare Conexiune Cloud`;
      }
      this.updateTelemetryPopover(false, 'În Așteptare Conexiune Cloud');
      this.showFloatingToast('⚠️ Conexiunea Cloud nu răspunde. Puteți activa Mod Test.');
    }
  }

  public getRoomInviteUrl(): string {
    if (this.config.meetingInviteUrl) return this.config.meetingInviteUrl;
    if (this.config.roomInviteUrl) return this.config.roomInviteUrl;
    const origin = typeof window !== 'undefined' && window.location.origin && window.location.origin.startsWith('http')
      ? window.location.origin
      : 'https://app.vorbello.eu';
    return `${origin}/?room=${encodeURIComponent(this.config.roomSlug)}`;
  }

  public async copyInviteLink(btnEl?: HTMLElement | null): Promise<void> {
    if (!this.canInvite) {
      this.showFloatingToast('⚠️ Doar Gazda/Administratorul sălii are permisiunea de a distribui invitații.');
      return;
    }
    const url = this.getRoomInviteUrl();
    if (!await copyText(url)) {
      this.showFloatingToast('Nu s-a putut copia. Selecta?i ?i copia?i manual invita?ia.');
      return;
    }
    if (btnEl) {
      const original = btnEl.innerHTML;
      btnEl.innerHTML = '✅ Link Copiat!';
      setTimeout(() => { btnEl.innerHTML = original; }, 2200);
    }
    this.showFloatingToast('🔗 Linkul conferinței a fost copiat în clipboard!');
  }

  public async copyInviteText(btnEl?: HTMLElement | null): Promise<void> {
    if (!this.canInvite) {
      this.showFloatingToast('⚠️ Doar Gazda/Administratorul sălii are permisiunea de a distribui invitații.');
      return;
    }
    const url = this.getRoomInviteUrl();
    const organizer = this.config.roomSettings?.creatorName || this.config.displayName || 'Gazda Conferinței';
    const pin = (this.config.roomSettings as any)?.pinCode ? `\n🔑 Cod PIN Acces: ${(this.config.roomSettings as any).pinCode}` : '';
    const text = [
      `Invitație la Videoconferință • Vorbello`,
      ``,
      `Ați fost invitat(ă) să participați la conferința audio-video:`,
      `📌 Eveniment / Sală: ${this.config.roomName}`,
      `👤 Organizator: ${organizer}${pin}`,
      ``,
      `🔗 Conectare Directă (clic pe link pentru acces):`,
      `${url}`,
      ``,
      `ℹ️ Vă puteți conecta instantaneu direct din browserul web (PC, tabletă sau telefon mobil, fără instalare) sau prin aplicația desktop Vorbello.`,
    ].join('\n');

    if (!await copyText(text)) {
      this.showFloatingToast('Nu s-a putut copia. Selecta?i ?i copia?i manual invita?ia.');
      return;
    }
    if (btnEl) {
      const original = btnEl.innerHTML;
      btnEl.innerHTML = '✅ Text Copiat!';
      setTimeout(() => { btnEl.innerHTML = original; }, 2200);
    }
    this.showFloatingToast('📨 Textul complet al invitației a fost copiat!');
  }

  public async applyVideoEffect(effect: string, imageUrl?: string): Promise<boolean> {
    const camera = this.manager.getRoom()?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
    const blur = effect === 'blur-soft' || effect === 'blur-heavy';
    if ((blur || effect === 'background') && !camera) {
      this.showFloatingToast('Pornește camera înainte de a activa estomparea fundalului.');
      return false;
    }
    try {
      if ((blur || effect === 'background') && camera) {
        const { BackgroundProcessor, BackgroundProcessorWrapper, supportsBackgroundProcessors } = await import('@livekit/track-processors');
        if (!supportsBackgroundProcessors()) throw new Error('unsupported');
        if (effect === 'background' && !imageUrl) return false;
        const options = effect === 'background' ? { mode: 'virtual-background' as const, imagePath: imageUrl! } : { mode: 'background-blur' as const, blurRadius: effect === 'blur-heavy' ? 15 : 8 };
        const existing = camera.getProcessor();
        if (existing instanceof BackgroundProcessorWrapper) await existing.switchTo(options);
        else await camera.setProcessor(BackgroundProcessor(options));
      } else if (camera?.getProcessor()) {
        await camera.stopProcessor();
      }
      if (effect !== 'background' && this.backgroundImageUrl) {
        URL.revokeObjectURL(this.backgroundImageUrl);
        this.backgroundImageUrl = undefined;
      }
      this.selectedVideoEffect = effect;
      localStorage.setItem('vorbello_video_effect', effect);
      const selfVideo = document.querySelector('video.self-cam-video') as HTMLVideoElement | null;
      if (selfVideo) this.applyVideoEffectToElement(selfVideo);
      this.updateStageLayout();
      return true;
    } catch (error) {
      console.warn('[ConferenceUI] Background effect unavailable:', error);
      try { if (camera?.getProcessor()) await camera.stopProcessor(); } catch (_) {}
      this.selectedVideoEffect = 'none';
      localStorage.setItem('vorbello_video_effect', 'none');
      this.showFloatingToast('Efectul nu este disponibil pe acest dispozitiv. Verifică și conexiunea la internet.');
      return false;
    }
  }

  public applyVideoEffectToElement(videoEl: HTMLVideoElement): void {
    videoEl.style.transform = this.manager.currentFacingMode === 'environment' || this.selectedVideoEffect === 'background' ? 'none' : 'scaleX(-1)';
    videoEl.classList.remove('video-filter-none', 'video-filter-blur-soft', 'video-filter-blur-heavy', 'video-filter-studio', 'video-filter-warm');
    if (this.selectedVideoEffect === 'studio' || this.selectedVideoEffect === 'warm') {
      videoEl.classList.add(`video-filter-${this.selectedVideoEffect}`);
    }
  }

  public async setPinState(identity: string | null, broadcast: boolean = true): Promise<void> {
    // If scene was locked by Admin/Host, regular attendees cannot unpin or override it
    if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
      this.showFloatingToast('🔒 Scena este fixată de către Administrator.');
      return;
    }

    const canBroadcast = broadcast && this.isHost;

    if (identity) {
      this.pinnedIdentity = identity;
      this.currentViewMode = 'speaker';
    } else {
      this.pinnedIdentity = null;
      // When unpinning: if 4 or fewer participants, return cleanly to Galerie!
      if (this.participants.length <= 4) {
        this.currentViewMode = 'gallery';
      } else {
        this.currentViewMode = 'speaker';
      }
      this.currentStageSpeakerId = null;
      this.lastStageRenderedSpeakerId = null;
      this.candidateSpeakerId = null;
    }
    this.userExplicitlyChangedViewMode = true;

    const isGal = this.currentViewMode === 'gallery';
    const viewIcon = document.getElementById('view-mode-icon');
    const viewText = document.getElementById('view-mode-text');
    const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
    const sheetViewText = document.getElementById('sheet-label-view-mode');
    if (viewIcon) viewIcon.innerText = isGal ? '⊞' : (this.pinnedIdentity ? '📌' : '👤');
    if (viewText) viewText.innerText = isGal ? 'Galerie' : (this.pinnedIdentity ? 'Fixat' : 'Vorbitor Activ');
    if (sheetViewIcon) sheetViewIcon.innerText = isGal ? '⊞' : (this.pinnedIdentity ? '📌' : '👤');
    if (sheetViewText) sheetViewText.innerText = isGal ? 'Mod: Galerie' : (this.pinnedIdentity ? 'Mod: Fixat' : 'Mod: Vorbitor');

    this.updateStageLayout();
    this.renderVideoGrid();
    this.renderParticipantsList();

    const p = this.participants.find(part => part.identity === identity) || (identity === 'self' ? { name: `${this.manager.displayName || this.config.displayName} (Tu)` } : null);
    if (identity && p) {
      if (canBroadcast) {
        this.showFloatingToast(`👑 ${p.name} a fost evidențiat pe scenă pentru toți participanții`);
      } else {
        this.showFloatingToast(`📌 ${p.name} a fost fixat pe ecranul tău`);
      }
    } else if (!identity) {
      if (canBroadcast) {
        this.showFloatingToast(`⊞ Mod Galerie restabilit pentru toți participanții`);
      } else {
        this.showFloatingToast(`⊞ Ai anulat fixarea locală`);
      }
    }

    if (canBroadcast) {
      await this.broadcastAdminLayoutSync();
    }
  }

  public async broadcastAdminLayoutSync(): Promise<void> {
    try {
      const room = this.manager.getRoom();
      const myLiveKitId = room?.localParticipant?.identity || 'self';
      const broadcastPinnedId = this.pinnedIdentity === 'self' 
        ? myLiveKitId 
        : (this.pinnedIdentity || null);
      
      const pinnedPart = this.pinnedIdentity
        ? (this.pinnedIdentity === 'self'
            ? { name: `${this.manager.displayName || this.config.displayName} (Admin)` }
            : this.participants.find(p => p.identity === this.pinnedIdentity))
        : null;

      const isLocked = Boolean(this.pinnedIdentity);
      this.isStageLockedByAdmin = isLocked;

      await this.manager.sendDataMessage({
        type: 'admin_layout_sync',
        senderIdentity: myLiveKitId,
        isPrimaryHost: Boolean(this.isHost),
        viewMode: this.currentViewMode,
        pinnedIdentity: broadcastPinnedId,
        pinnedName: pinnedPart?.name || '',
        adminName: this.config.displayName,
        isStageLocked: isLocked,
        timestamp: Date.now(),
      });
      console.info('[ConferenceUI] Broadcasted admin layout sync to room:', this.currentViewMode, broadcastPinnedId, 'isStageLocked:', isLocked);
    } catch (err) {
      console.warn('[ConferenceUI] Failed to broadcast admin layout sync:', err);
    }
  }

  public updateViewModePopoverChecks() {
    ['speaker', 'gallery', 'studio'].forEach(m => {
      const opt = document.getElementById(`opt-view-${m}`);
      const chk = document.getElementById(`check-view-${m}`);
      const isSel = this.currentViewMode === m;
      if (opt) opt.classList.toggle('selected', isSel);
      if (chk) chk.innerText = isSel ? '✓' : '';
    });
  }

  public async setViewMode(mode: 'gallery' | 'speaker' | 'studio', broadcast: boolean = false) {
    this.currentViewMode = mode;
    this.userExplicitlyChangedViewMode = true;
    this.studioCurrentPage = 1;

    const isGal = this.currentViewMode === 'gallery';
    const isStudio = this.currentViewMode === 'studio';
    const viewIcon = document.getElementById('view-mode-icon');
    const viewText = document.getElementById('view-mode-text');
    const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
    const sheetViewText = document.getElementById('sheet-label-view-mode');

    if (viewIcon) viewIcon.innerText = isStudio ? '🗂️' : (isGal ? '⊞' : (this.pinnedIdentity ? '📌' : '👤'));
    if (viewText) viewText.innerText = isStudio ? 'Studio 16:9' : (isGal ? 'Grilă' : (this.pinnedIdentity ? 'Fixat' : 'Vorbitor'));
    if (sheetViewIcon) sheetViewIcon.innerText = isStudio ? '🗂️' : (isGal ? '⊞' : (this.pinnedIdentity ? '📌' : '👤'));
    if (sheetViewText) sheetViewText.innerText = isStudio ? 'Mod: Studio 16:9' : (isGal ? 'Mod: Grilă' : (this.pinnedIdentity ? 'Mod: Fixat' : 'Mod: Vorbitor'));

    this.updateViewModePopoverChecks();
    this.updateStageLayout();
    this.renderVideoGrid();
    this.renderParticipantsList();

    const modeLabels: Record<string, string> = {
      speaker: '👤 Mod Vorbitor Activ activat.',
      gallery: '⊞ Mod Grilă Dinamică activat.',
      studio: '🗂️ Mod Studio 16:9 Paginat activat.'
    };
    this.showFloatingToast(modeLabels[mode] || mode);

    if (broadcast && this.isHost) {
      await this.broadcastAdminLayoutSync();
    }
  }

  public cycleViewMode(broadcast: boolean = false): void {
    const modes: Array<'speaker' | 'gallery' | 'studio'> = ['speaker', 'gallery', 'studio'];
    const curIdx = modes.indexOf(this.currentViewMode);
    const nextMode = modes[(curIdx + 1) % modes.length];
    this.setViewMode(nextMode, broadcast);
  }

  private updateStageLayout() {
    const stageArea = document.getElementById('stage-area');
    const spotlightContainer = document.getElementById('spotlight-container');
    const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement;
    const spotlightPinnedCard = document.getElementById('spotlight-pinned-card');
    const spotlightTitle = document.getElementById('spotlight-title-text');
    const spotlightBtn = document.getElementById('btn-stop-spotlight');

    if (!stageArea || !spotlightContainer || !spotlightVideo) return;

    const room = this.manager.getRoom();
    const localScreenPub = (room && room.state === 'connected')
      ? room.localParticipant.getTrackPublication(Track.Source.ScreenShare)
      : null;
    const screenStream = this.manager.getLocalScreenStream();
    const isLocalSharing = this.manager.isScreenShareEnabled && !!(screenStream && screenStream.active && screenStream.getVideoTracks().some(t => t.readyState === 'live'));

    // Check if any remote participant is ACTUALLY sharing screen:
    // Discovers any subscribed screen track published by any remote participant.
    let remoteScreenTrack: RemoteTrack | null = null;
    let remoteScreenSharerName = '';
    let isRemoteSharerPending = false;
    if (room && room.state === 'connected') {
      for (const rp of room.remoteParticipants.values()) {
        let screenPub = rp.getTrackPublication(Track.Source.ScreenShare);
        if (!screenPub) {
          for (const pub of rp.trackPublications.values()) {
            if (pub.source === Track.Source.ScreenShare || pub.trackName === 'screen_share' || (pub as any).name === 'screen_share') {
              screenPub = pub;
              break;
            }
          }
        }
        if (screenPub) {
          const tr = screenPub.track;
          if (tr) {
            const mst = (tr as any).mediaStreamTrack as MediaStreamTrack | undefined;
            if (!mst || mst.readyState === 'live') {
              remoteScreenTrack = tr;
              remoteScreenSharerName = this.participantNameOverrides.get(rp.identity) || rp.name || 'Participant';
              this.remotePendingRetryCount = 0;
              break;
            }
          } else {
            isRemoteSharerPending = true;
            remoteScreenSharerName = this.participantNameOverrides.get(rp.identity) || rp.name || 'Participant';
            if (this.lastAttachedRemoteTrack && (this.lastAttachedRemoteTrack as any).sid === screenPub.trackSid) {
              remoteScreenTrack = this.lastAttachedRemoteTrack;
              break;
            }
          }
        }
      }
    }

    // A maximized screen-share is a fixed overlay with display: flex !important.
    // Reset it when the share ends so it cannot cover the camera layout.
    if (!isLocalSharing && !remoteScreenTrack && !isRemoteSharerPending && spotlightContainer.classList.contains('is-screen-share')) {
      spotlightContainer.classList.remove('is-maximized', 'is-fill-mode');
      const sizeLabel = document.getElementById('label-spotlight-size');
      if (sizeLabel) sizeLabel.textContent = 'M\u0103re\u0219te';
    }

    const pinnedParticipant = this.pinnedIdentity
      ? (this.pinnedIdentity === 'self'
          ? (this.participants.find(p => p.identity === 'self') || { identity: 'self', name: `${this.manager.displayName || 'Tu'} (Tu)` })
          : this.participants.find(p => p.identity === this.pinnedIdentity))
      : null;

    // Only clear spotlight video when there is NEITHER a screen share NOR a pinned camera on stage.
    // Preserving srcObject when pinned eliminates the black flash/flicker whenever participants join or leave!
    if (!remoteScreenTrack && !isLocalSharing && !pinnedParticipant) {
      if (this.lastAttachedRemoteTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
        this.lastAttachedRemoteTrack = null;
      }
      if (spotlightVideo && spotlightVideo.srcObject) {
        spotlightVideo.srcObject = null;
        spotlightVideo.pause();
      }
    }

    // If a remote screen publication is pending its WebRTC track subscription, retry in background
    // without prematurely showing a black spotlight container over the cameras
    if (isRemoteSharerPending && !remoteScreenTrack) {
      if (this.remotePendingRetryCount < 4) {
        this.remotePendingRetryCount++;
        setTimeout(() => this.updateStageLayout(), 250);
      }
    }

    const banner = document.getElementById('prod-waiting-banner');
    if (banner && (isLocalSharing || remoteScreenTrack || pinnedParticipant)) {
      banner.style.display = 'none';
    }

    const switchSpotlightBtn = document.getElementById('btn-switch-spotlight-source');

    if (isLocalSharing) {
      if (this.lastAttachedRemoteTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
        this.lastAttachedRemoteTrack = null;
      }
      // 1. Local screen sharing spotlight mode (I am actively presenting!)
      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.add('is-screen-share');
      spotlightContainer.style.display = 'flex';
      if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
      spotlightVideo.style.display = 'block';
      spotlightVideo.style.transform = 'none';
      spotlightVideo.muted = true;
      spotlightVideo.setAttribute('muted', '');
      spotlightVideo.setAttribute('playsinline', '');

      if (spotlightTitle) {
        const hasAudio = (this.manager.getLocalScreenStream()?.getAudioTracks().length ?? 0) > 0;
        const audioBadge = hasAudio ? ' • 🔊 Cu Sunet' : ' • 🔇 Fără Sunet';
        const settings = this.manager.getLocalScreenStream()?.getVideoTracks()[0]?.getSettings();
        const dimensions = settings?.width && settings?.height ? `${settings.width}×${settings.height}` : 'rezoluție în curs';
        const fps = settings?.frameRate ? ` · ${Math.round(settings.frameRate)} FPS` : '';
        spotlightTitle.innerText = `Partajare: ${this.selectedSourceName || 'Ecran Activ'} (${dimensions}${fps})${audioBadge}`;
      }
      if (spotlightBtn) {
        spotlightBtn.style.display = 'inline-flex';
        spotlightBtn.innerText = 'Oprește Partajarea';
        spotlightBtn.className = 'btn btn-danger btn-sm';
      }
      if (switchSpotlightBtn) {
        switchSpotlightBtn.style.display = 'inline-flex';
      }

      if (screenStream) {
        this.attachScreenStreamToSpotlight(screenStream, spotlightVideo);
      } else if (localScreenPub && localScreenPub.track) {
        try {
          localScreenPub.track.attach(spotlightVideo);
        } catch (_) {}
      }
      spotlightVideo.muted = true;
      spotlightVideo.volume = 0;
      spotlightVideo.playsInline = true;
      spotlightVideo.setAttribute('muted', '');
      spotlightVideo.setAttribute('playsinline', '');
    } else if (remoteScreenTrack) {
      if (switchSpotlightBtn) switchSpotlightBtn.style.display = 'none';
      // 2. Remote screen sharing spotlight mode (Someone else is presenting)
      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.add('is-screen-share');
      spotlightContainer.style.display = 'flex';
      if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
      spotlightVideo.style.display = 'block';
      spotlightVideo.style.transform = 'none';
      spotlightVideo.muted = true;
      spotlightVideo.playsInline = true;
      spotlightVideo.setAttribute('muted', '');
      spotlightVideo.setAttribute('playsinline', '');

      let remoteHasAudio = false;
      if (room && room.state === 'connected') {
        for (const rp of room.remoteParticipants.values()) {
          if (rp.isScreenShareEnabled) {
            const sAudio = rp.getTrackPublication(Track.Source.ScreenShareAudio);
            if (sAudio && sAudio.isSubscribed && !sAudio.isMuted) {
              remoteHasAudio = true;
              break;
            }
          }
        }
      }

      if (spotlightTitle) {
        const audioBadge = remoteHasAudio ? ' • 🔊 Cu Sunet' : ' • 🔇 Fără Sunet';
        spotlightTitle.innerText = `🖥️ Ecran Partajat de ${remoteScreenSharerName}${audioBadge}`;
      }
      if (spotlightBtn) {
        if (remoteHasAudio) {
          spotlightBtn.style.display = 'inline-flex';
          const isMutedNow = this.isRemoteScreenAudioMuted || this.isLocalMachineScreenSharing;
          spotlightBtn.className = isMutedNow ? 'btn btn-secondary btn-sm' : 'btn btn-outline-info btn-sm';
          spotlightBtn.innerText = isMutedNow ? '🔇 Activează Sunet Ecran' : '🔊 Sunet Ecran Pornit (Mute)';
          spotlightBtn.title = isMutedNow ? 'Faceți clic pentru a activa sunetul ecranului partajat' : 'Faceți clic pentru a opri sunetul ecranului partajat';
          spotlightBtn.onclick = () => {
            this.toggleRemoteScreenAudioMute();
          };
        } else {
          spotlightBtn.style.display = 'none';
        }
      }

      if (this.lastAttachedRemoteTrack && this.lastAttachedRemoteTrack !== remoteScreenTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
      }
      this.lastAttachedRemoteTrack = remoteScreenTrack;

      if (!remoteScreenTrack.attachedElements.includes(spotlightVideo) || !spotlightVideo.srcObject) {
        try {
          remoteScreenTrack.attach(spotlightVideo);
          console.info('[ConferenceUI] Attached remote screen share track cleanly to spotlight');
        } catch (e) {
          console.warn('[ConferenceUI] Spotlight remote attach error:', e);
        }
      }
      spotlightVideo.play().catch(e => {
        if (e.name !== 'AbortError') console.warn('Spotlight remote play error:', e);
      });
    } else if (pinnedParticipant) {
      // 1. Pinned participant spotlight mode (ALWAYS takes precedence over default gallery!)
      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.remove('is-screen-share');
      spotlightContainer.style.display = 'flex';

      if (spotlightTitle) {
        spotlightTitle.innerText = `📌 Fixat pe Scenă: ${pinnedParticipant.name}`;
      }
      if (spotlightBtn) {
        if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
          spotlightBtn.style.display = 'inline-flex';
          spotlightBtn.innerText = '🔒 Fixat de Admin';
          spotlightBtn.className = 'btn btn-secondary btn-sm';
          spotlightBtn.setAttribute('title', 'Scena este fixată de Administrator');
          spotlightBtn.style.opacity = '0.85';
          spotlightBtn.style.cursor = 'default';
        } else {
          spotlightBtn.style.display = 'inline-flex';
          spotlightBtn.innerText = '✕ Anulează Fixarea';
          spotlightBtn.className = 'btn btn-secondary btn-sm';
          spotlightBtn.setAttribute('title', 'Anulează fixarea pe scenă');
          spotlightBtn.style.opacity = '1';
          spotlightBtn.style.cursor = 'pointer';
        }
      }

      const isSelf = pinnedParticipant.identity === 'self';
      let activeStream: MediaStream | null = null;
      let activeTrack: RemoteTrack | null = null;

      if (isSelf) {
        if (this.manager.isCameraEnabled) {
          const room = this.manager.getRoom();
          const localPub = room?.localParticipant?.getTrackPublication(Track.Source.Camera);
          if (localPub && localPub.track && !localPub.isMuted) {
            activeTrack = localPub.track as any;
          }
          activeStream = this.manager.getLocalCameraStream();
          if (!activeStream && localPub && localPub.track && (localPub.track as any).mediaStreamTrack) {
            activeStream = new MediaStream([(localPub.track as any).mediaStreamTrack]);
          }
        }
      } else {
        const room = this.manager.getRoom();
        if (room && room.state === 'connected') {
          const remotePart = room.remoteParticipants.get(pinnedParticipant.identity);
          if (remotePart) {
            const videoPub = remotePart.getTrackPublication(Track.Source.Camera);
            if (videoPub && videoPub.track && !videoPub.isMuted) {
              activeTrack = videoPub.track;
              if (videoPub.track.mediaStream) {
                activeStream = videoPub.track.mediaStream;
              } else if ((videoPub.track as any).mediaStreamTrack) {
                activeStream = new MediaStream([(videoPub.track as any).mediaStreamTrack]);
              }
            }
          }
        }
      }

      // Mirror spotlight video only if local camera and front-facing
      if (isSelf) {
        spotlightVideo.style.transform = this.manager.currentFacingMode === 'environment' || this.selectedVideoEffect === 'background' ? 'none' : 'scaleX(-1)';
      } else {
        spotlightVideo.style.transform = 'none';
      }

      const isDifferentTarget = this.lastStageRenderedSpeakerId !== pinnedParticipant.identity;

      if (activeTrack || activeStream) {
        if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
        spotlightVideo.style.display = 'block';
        spotlightVideo.muted = true;
        spotlightVideo.setAttribute('muted', '');
        spotlightVideo.setAttribute('playsinline', '');

        if (isDifferentTarget || !spotlightVideo.srcObject) {
          if (this.lastAttachedRemoteTrack) {
            try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
            this.lastAttachedRemoteTrack = null;
          }
          // Reset srcObject to force Chromium to switch rendering pipeline cleanly
          spotlightVideo.pause();
          spotlightVideo.srcObject = null;

          if (isSelf) {
            this.lastAttachedRemoteTrack = null;
            if (activeStream) {
              spotlightVideo.srcObject = activeStream;
            } else if (activeTrack) {
              activeTrack.attach(spotlightVideo);
            }
          } else {
            if (activeTrack) {
              this.lastAttachedRemoteTrack = activeTrack;
              activeTrack.attach(spotlightVideo);
            } else if (activeStream) {
              this.lastAttachedRemoteTrack = null;
              spotlightVideo.srcObject = activeStream;
            }
          }
          this.lastStageRenderedSpeakerId = pinnedParticipant.identity;
          spotlightVideo.play().catch(e => {
            if (e.name !== 'AbortError') console.warn('Spotlight pin play error:', e);
          });
        }
      } else {
        if (this.lastAttachedRemoteTrack) {
          try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
          this.lastAttachedRemoteTrack = null;
        }
        spotlightVideo.style.display = 'none';
        if (spotlightVideo.srcObject) {
          spotlightVideo.pause();
          spotlightVideo.srcObject = null;
        }
        this.lastStageRenderedSpeakerId = null;
        if (spotlightPinnedCard) {
          spotlightPinnedCard.style.display = 'flex';
          const avatarChar = document.getElementById('spotlight-avatar-char');
          const pinnedName = document.getElementById('spotlight-pinned-name');
          if (avatarChar) avatarChar.innerText = pinnedParticipant.name.charAt(0);
          if (pinnedName) pinnedName.innerText = pinnedParticipant.name;
        }
      }
    } else if (this.shouldUseFilmstrip() && this.participants.length > 1) {
      // 2. Automatic Side-by-Side Spotlight Mode (Desktop with 5+ participants, or speaker mode)
      // Hero active speaker on main stage, all participant miniatures stacked in right-hand column
      let targetSpeakerId: string | null = this.pinnedIdentity;
      if (!targetSpeakerId && this.currentStageSpeakerId) {
        targetSpeakerId = this.currentStageSpeakerId;
      }
      if (!targetSpeakerId && this.activeSpeakerIdentity) {
        targetSpeakerId = this.activeSpeakerIdentity;
      }
      if (!targetSpeakerId) {
        const adminP = this.participants.find(p => p.role === 'admin') || this.participants.find(p => p.identity === 'self');
        targetSpeakerId = adminP ? adminP.identity : (this.participants[0]?.identity ?? 'self');
      }
      const speakerPart = this.participants.find(p => p.identity === targetSpeakerId) || this.participants[0];

      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.remove('is-screen-share');
      spotlightContainer.style.display = 'flex';

      if (spotlightTitle) {
        spotlightTitle.innerText = this.pinnedIdentity
          ? `📌 Fixat pe Scenă: ${speakerPart.name}`
          : `👤 Vorbitor Activ: ${speakerPart.name}`;
      }
      if (spotlightBtn) {
        spotlightBtn.style.display = this.pinnedIdentity ? 'inline-block' : 'none';
        spotlightBtn.innerText = 'Anulează Fixarea';
      }

      const isSelf = speakerPart.identity === 'self';
      let activeStream: MediaStream | null = null;
      let activeTrack: RemoteTrack | null = null;

      if (isSelf) {
        if (this.manager.isCameraEnabled) {
          activeStream = this.manager.getLocalCameraStream();
        }
      } else {
        const room = this.manager.getRoom();
        if (room && room.state === 'connected') {
          const remotePart = room.remoteParticipants.get(speakerPart.identity);
          if (remotePart) {
            const videoPub = remotePart.getTrackPublication(Track.Source.Camera);
            if (videoPub && videoPub.track && !videoPub.isMuted) {
              activeTrack = videoPub.track;
              if (videoPub.track.mediaStream) {
                activeStream = videoPub.track.mediaStream;
              } else if ((videoPub.track as any).mediaStreamTrack) {
                activeStream = new MediaStream([(videoPub.track as any).mediaStreamTrack]);
              }
            }
          }
        }
      }

      // Avoid reattaching or restarting the video element if it's already showing this speaker/track
      const isSameSpeaker = this.lastStageRenderedSpeakerId === targetSpeakerId;
      if (activeTrack || activeStream) {
        if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
        spotlightVideo.style.display = 'block';
        spotlightVideo.style.transform = (isSelf && this.manager.currentFacingMode !== 'environment' && this.selectedVideoEffect !== 'background') ? 'scaleX(-1)' : 'none';
        spotlightVideo.muted = true;
        spotlightVideo.setAttribute('muted', '');
        spotlightVideo.setAttribute('playsinline', '');

        if (!isSameSpeaker || spotlightVideo.srcObject !== (activeTrack?.mediaStream || activeStream)) {
          if (this.lastAttachedRemoteTrack && this.lastAttachedRemoteTrack !== activeTrack) {
            try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
            this.lastAttachedRemoteTrack = null;
          }
          if (activeTrack) {
            this.lastAttachedRemoteTrack = activeTrack;
            activeTrack.attach(spotlightVideo);
          } else if (activeStream && spotlightVideo.srcObject !== activeStream) {
            this.lastAttachedRemoteTrack = null;
            spotlightVideo.srcObject = activeStream;
          }
        }
        spotlightVideo.play().catch(e => {
          if (e.name !== 'AbortError') console.warn('Spotlight speaker play error:', e);
        });
        this.lastStageRenderedSpeakerId = targetSpeakerId;
      } else {
        if (this.lastAttachedRemoteTrack) {
          try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
          this.lastAttachedRemoteTrack = null;
        }
        spotlightVideo.style.display = 'none';
        if (spotlightVideo.srcObject) spotlightVideo.srcObject = null;
        if (spotlightPinnedCard) {
          spotlightPinnedCard.style.display = 'flex';
          const avatarChar = document.getElementById('spotlight-avatar-char');
          const pinnedName = document.getElementById('spotlight-pinned-name');
          if (avatarChar) avatarChar.innerText = speakerPart.name.charAt(0);
          if (pinnedName) pinnedName.innerText = speakerPart.name;
        }
        this.lastStageRenderedSpeakerId = targetSpeakerId;
      }
    } else {
      // 4. Standard Gallery Grid Mode (Default for 1..4 participants):
      // Cleanly split screens across the viewport (1 = full, 2 = 50/50, 3 = 2+1, 4 = 2x2)
      if (this.lastAttachedRemoteTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
        this.lastAttachedRemoteTrack = null;
      }
      stageArea.classList.remove('has-spotlight');
      spotlightContainer.classList.remove('is-screen-share');
      spotlightContainer.style.display = 'none';
      if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
      if (spotlightVideo.srcObject) spotlightVideo.srcObject = null;
    }

    this.renderVideoGrid();
    this.updateControlsState();
  }

  private handleScreenShareState() {
    this.isScreenShareFlowInProgress = false;
    const screenActiveMenu = document.getElementById('screen-share-active-menu');
    if (screenActiveMenu && !this.manager.isScreenShareEnabled) {
      screenActiveMenu.style.display = 'none';
    }
    this.updateStageLayout();
    this.renderVideoGrid();
    this.updateControlsState();
  }

  private openPreEventShare(): void {
    if (!(this.isHost || this.isAdmin) || this.config.webinarAudience) return;
    if (this.manager.isScreenShareEnabled) { this.showFloatingToast('Oprește partajarea curentă înainte de a porni prezentarea.'); return; }
    if (document.getElementById('pre-event-share-dialog')) return;
    const overlay=document.createElement('div'); overlay.id='pre-event-share-dialog'; overlay.className='picker-modal-overlay';
    overlay.style.cssText='display:flex;z-index:100001;align-items:center;justify-content:center;padding:16px;background:rgba(3,8,20,.84)';
    overlay.innerHTML=`<div role="dialog" aria-modal="true" aria-labelledby="pre-event-share-title" style="width:min(560px,100%);max-height:90vh;overflow:auto;padding:24px;border:1px solid #536ca5;border-radius:20px;background:#151e32;color:#f8fafc;box-shadow:0 24px 80px #0009">
      <h2 id="pre-event-share-title" style="margin:0 0 8px">Înainte de curs</h2><p style="color:#aab8d2">Participanții conectați vor vedea imaginea și cronometrul și vor auzi muzica aleasă.</p>
      <form id="pre-event-share-form" style="display:grid;gap:14px">
      <label>Ora începerii <input id="pre-event-time" type="datetime-local" required class="form-input" style="width:100%;margin-top:6px;color-scheme:dark"></label>
      <label style="display:flex;gap:8px;align-items:center"><input id="pre-event-show-title" type="checkbox"> Afișează text pe imagine</label>
      <label id="pre-event-title-label" style="display:none">Text pe imagine <input id="pre-event-title" type="text" maxlength="60" class="form-input" style="width:100%;margin-top:6px" placeholder="Evenimentul începe în curând"></label>
      <label>Imagine de fundal <input id="pre-event-image" type="file" accept="image/*" class="form-input" style="width:100%;margin-top:6px"></label>
      <small style="color:#aab8d2">Dacă nu alegi o imagine, folosim fundalul Vorbello.</small>
      <label>Muzică <select id="pre-event-music" class="form-select" style="width:100%;margin-top:6px"><option value="none">Fără muzică</option><option value="ambient">Fundal ambiental simplu</option><option value="file">Fișier audio propriu</option><option value="youtube">Link YouTube (experimental)</option></select></label>
      <div id="pre-event-youtube-label" style="display:none"><label>Link YouTube <input id="pre-event-youtube" type="url" class="form-input" style="width:100%;margin-top:6px" placeholder="https://www.youtube.com/watch?v=..."></label><button type="button" id="pre-event-youtube-open" class="btn btn-secondary" style="margin-top:8px">Deschide YouTube</button><p style="color:#aab8d2">Pornește videoclipul. La pornirea prezentării alege fila YouTube și bifează partajarea sunetului. Repetarea se configurează pe YouTube.</p></div>
      <label id="pre-event-audio-label" style="display:none">Fișier audio <input id="pre-event-audio" type="file" accept="audio/*" class="form-input" style="width:100%;margin-top:6px"></label>
      <label style="display:flex;gap:8px;align-items:center"><input id="pre-event-loop" type="checkbox" checked> Repetă fișierul audio</label>
      <p id="pre-event-error" role="alert" style="color:#fca5a5;margin:0"></p>
      <div style="display:flex;gap:10px;flex-wrap:wrap"><button type="button" id="pre-event-cancel" class="btn btn-secondary">Anulează</button><button type="submit" id="pre-event-start" class="btn btn-primary">Pornește prezentarea</button></div>
      </form></div>`;
    document.body.append(overlay);
    const time=overlay.querySelector<HTMLInputElement>('#pre-event-time')!;
    const scheduled=Date.parse(this.config.countdownStartsAt || '');
    const target=new Date(Number.isFinite(scheduled) && scheduled > Date.now() ? scheduled : Date.now()+300000);
    const local=new Date(target.getTime()-target.getTimezoneOffset()*60000); time.value=local.toISOString().slice(0,16);
    const showTitle=overlay.querySelector<HTMLInputElement>('#pre-event-show-title')!;
    showTitle.addEventListener('change',()=>{overlay.querySelector<HTMLElement>('#pre-event-title-label')!.style.display=showTitle.checked?'block':'none';});
    const music=overlay.querySelector<HTMLSelectElement>('#pre-event-music')!;
    const audioLabel=overlay.querySelector<HTMLElement>('#pre-event-audio-label')!;
    music.addEventListener('change',()=>{audioLabel.style.display=music.value==='file'?'block':'none'; overlay.querySelector<HTMLElement>('#pre-event-youtube-label')!.style.display=music.value==='youtube'?'block':'none';});
    const youtubeInput=overlay.querySelector<HTMLInputElement>('#pre-event-youtube')!;
    const youtubeUrl=()=>{const url=new URL(youtubeInput.value.trim()); if(url.protocol!=='https:' || !['www.youtube.com','youtube.com','m.youtube.com','youtu.be','music.youtube.com'].includes(url.hostname)) throw new Error('Introdu un link YouTube valid.'); return url.toString();};
    overlay.querySelector('#pre-event-youtube-open')?.addEventListener('click',()=>{try {window.open(youtubeUrl(),'_blank','noopener,noreferrer');} catch (_) {overlay.querySelector<HTMLElement>('#pre-event-error')!.textContent='Introdu un link YouTube valid.';}});
    overlay.querySelector('#pre-event-cancel')?.addEventListener('click',()=>overlay.remove());
    overlay.querySelector<HTMLFormElement>('#pre-event-share-form')!.addEventListener('submit', async event=>{
      event.preventDefault();
      const error=overlay.querySelector<HTMLElement>('#pre-event-error')!, button=overlay.querySelector<HTMLButtonElement>('#pre-event-start')!;
      if(this.manager.isScreenShareEnabled){error.textContent='Oprește mai întâi partajarea curentă.';return;}
      const file=overlay.querySelector<HTMLInputElement>('#pre-event-audio')!.files?.[0] || null;
      if(music.value==='file' && !file){error.textContent='Alege fișierul audio.';return;}
      button.disabled=true; error.textContent='';
      let presentation: PreEventShare | null=null; let youtubeCapture: MediaStream | null=null;
      try {
        if (music.value==='youtube') {
          youtubeUrl();
          youtubeCapture=await navigator.mediaDevices.getDisplayMedia({video:true,audio:true});
          const surface=youtubeCapture.getVideoTracks()[0]?.getSettings().displaySurface;
          if(surface !== 'browser' || !youtubeCapture.getAudioTracks().length) throw new Error('Alege fila YouTube și activează partajarea sunetului filei. Dacă aplicația desktop nu oferă selecția filei, folosește un fișier audio propriu sau deschide Vorbello într-un browser compatibil.');
        }
        const date=new Date(time.value); if(!Number.isFinite(date.getTime())) throw new Error('Alege data și ora începerii.');
        presentation=await createPreEventShare({startsAt:date.toISOString(),title:overlay.querySelector<HTMLInputElement>('#pre-event-title')!.value,showTitle:showTitle.checked,image:overlay.querySelector<HTMLInputElement>('#pre-event-image')!.files?.[0]||null,audio:music.value==='file'?file:null,ambient:music.value==='ambient',capturedAudio:youtubeCapture,loop:overlay.querySelector<HTMLInputElement>('#pre-event-loop')!.checked});
        if(!overlay.isConnected || !this.manager.getRoom() || this.manager.isScreenShareEnabled){presentation.dispose();return;}
        const published=await this.manager.replaceScreenShareTrack(presentation.video,presentation.audio,'hd');
        if(!published) throw new Error('Prezentarea nu a putut fi partajată.');
        if(presentation.audio && !this.manager.getRoom()?.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio)?.track) {
          await this.manager.enableScreenShare(false); throw new Error('Sunetul nu a putut fi partajat.');
        }
        this.preEventShare=presentation; this.selectedSourceName='Prezentare înainte de curs'; this.isShareAudioEnabled=Boolean(presentation.audio);
        overlay.remove(); this.handleScreenShareState(); this.showFloatingToast('Prezentarea este partajată. Oprește partajarea când începe cursul.');
      } catch(e) { presentation?.dispose(); youtubeCapture?.getTracks().forEach(track=>track.stop()); error.textContent=e instanceof Error?e.message:'Prezentarea nu a putut fi pornită.'; button.disabled=false; }
    });
    time.focus();
  }

  // Screen and Window Picker Logic
  private async triggerScreenShareFlow() {
    if (this.config.webinarAudience) return;
    const allowPolicy = this.config.roomSettings?.allowScreenShare;
    const isPrivileged = this.isHost || this.isAdmin || this.isModerator;
    if (allowPolicy === 'hosts_only' && !isPrivileged) {
      this.showFloatingToast('🔒 În această sală, doar Gazda și Co-Gazdele au permisiunea de a partaja ecranul.', 5000);
      return;
    }

    if (this.isScreenShareFlowInProgress) {
      console.warn('[ConferenceUI] Screen share flow already in progress, ignoring duplicate trigger');
      return;
    }
    this.isScreenShareFlowInProgress = true;

    try {
      if (this.manager.isScreenShareEnabled) {
        await this.manager.enableScreenShare(false);
        this.handleScreenShareState();
        return;
      }

      const isMobile = typeof navigator !== 'undefined' && /android|iphone|ipad|ipod/i.test(navigator.userAgent);
      if (isMobile) {
        this.showFloatingToast('📱 Pe telefoane mobile (Android / iOS), Google și Apple nu permit capturarea ecranului din browser. În browser puteți viziona ecranul partajat de colegi de pe PC.', 6000);
        return;
      }

      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.getShareSources === 'function') {
        const modal = document.getElementById('screen-picker-modal');
        const tabWindows = document.getElementById('tab-picker-windows');
        const tabScreens = document.getElementById('tab-picker-screens');
        const grid = document.getElementById('picker-sources-grid');
        const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;

        this.selectedSourceId = null;
        this.selectedSourceName = '';
        this.currentPickerTab = 'screens';

        if (modal) modal.style.display = 'flex';
        tabScreens?.classList.add('active');
        tabWindows?.classList.remove('active');

        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.innerText = 'Partajează Ecranul Selectat';
        }

        if (grid) {
          grid.innerHTML = `
            <div style="grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3rem; gap: 0.85rem; color: #94a3b8;">
              <div class="conf-connecting-spinner" style="width: 36px; height: 36px; border-width: 3px;"></div>
              <div style="font-weight: 600; font-size: 0.95rem;">Se încarcă ferestrele și ecranele active...</div>
            </div>
          `;
        }

        try {
          const sources = await desktopApi.getShareSources();
          this.availableSources = sources || [];
          this.renderPickerSources();
        } catch (err) {
          console.warn('Desktop capturer error, falling back to direct media:', err);
          if (modal) modal.style.display = 'none';
          await this.manager.enableScreenShare(true, this.isShareAudioEnabled);
          this.handleScreenShareState();
        }
      } else {
        // Browser / PWA Mode: Immediately trigger browser native window/screen picker with 0ms lag
        this.pinnedIdentity = null;
        this.showFloatingToast('🖥️ Alegeți «Filă» (pentru YouTube/video) sau «Ecran Întreg» și bifați «Partajează sunetul»!', 4500);
        const success = await this.manager.enableScreenShare(true, this.isShareAudioEnabled);
        if (success) {
          this.showFloatingToast('✅ Partajarea ecranului este activă!', 2500);
          this.handleScreenShareState();
          setTimeout(() => {
            this.updateStageLayout();
          }, 100);
          setTimeout(() => this.updateStageLayout(), 400);
          setTimeout(() => this.updateStageLayout(), 1000);
        } else {
          this.showFloatingToast('ℹ️ Partajarea ecranului a fost anulată.');
          this.handleScreenShareState();
        }
      }
    } finally {
      this.isScreenShareFlowInProgress = false;
    }
  }

  public async switchScreenShareSource() {
    try {
      this.showFloatingToast('🔄 Selectați noua fereastră sau ecran...', 3000);

      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.getShareSources === 'function') {
        // Desktop app: re-open picker modal in switch mode
        const modal = document.getElementById('screen-picker-modal');
        const tabWindows = document.getElementById('tab-picker-windows');
        const tabScreens = document.getElementById('tab-picker-screens');
        const grid = document.getElementById('picker-sources-grid');
        const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;

        this.selectedSourceId = null;
        this.selectedSourceName = '';
        this.currentPickerTab = 'screens';

        if (modal) modal.style.display = 'flex';
        tabScreens?.classList.add('active');
        tabWindows?.classList.remove('active');

        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.innerText = 'Schimbă cu Sursa Selectată';
        }

        if (grid) {
          grid.innerHTML = `
            <div style="grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3rem; gap: 0.85rem; color: #94a3b8;">
              <div class="conf-connecting-spinner" style="width: 36px; height: 36px; border-width: 3px;"></div>
              <div style="font-weight: 600; font-size: 0.95rem;">Se încarcă ferestrele și ecranele active...</div>
            </div>
          `;
        }

        try {
          const sources = await desktopApi.getShareSources();
          this.availableSources = sources || [];
          this.renderPickerSources();
        } catch (err) {
          console.warn('Desktop capturer error, falling back to direct media:', err);
          if (modal) modal.style.display = 'none';
        }
        return;
      }

      // Web / Browser mode: Seamless source switch WITHOUT stopping or interrupting share
      if (typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
        const isFhd = this.selectedScreenQuality === 'fullhd';
        const targetWidth = isFhd ? 1920 : 1280;
        const targetHeight = isFhd ? 1080 : 720;
        let newStream: MediaStream | null = null;
        try {
          newStream = await captureDisplayMedia({
            video: {
              width: { ideal: targetWidth, max: targetWidth },
              height: { ideal: targetHeight, max: targetHeight },
              frameRate: { ideal: 30, max: 30 },
            },
            audio: this.isShareAudioEnabled ? { restrictOwnAudio: true } : false,
          });
        } catch (gdmErr: any) {
          if (gdmErr.name === 'NotAllowedError' || gdmErr.name === 'AbortError') {
            console.info('[ConferenceUI] Source change cancelled by user. Existing share continues.');
            this.showFloatingToast('ℹ️ Comutarea sursei a fost anulată. Partajarea curentă continuă.');
            return;
          }
          newStream = await captureDisplayMedia({ video: true, audio: this.isShareAudioEnabled ? { restrictOwnAudio: true } : false });
        }

        if (newStream && newStream.getVideoTracks().length > 0) {
          const newVideoTrack = newStream.getVideoTracks()[0];
          const newAudioTrack = newStream.getAudioTracks()[0] || null;
          const success = await this.manager.replaceScreenShareTrack(newVideoTrack, newAudioTrack, this.selectedScreenQuality);
          if (success) {
            this.selectedSourceName = 'Ecran / Fereastră Nouă';
            this.showFloatingToast('✅ Sursa de partajare a fost schimbată!', 2500);
            this.handleScreenShareState();
            this.updateStageLayout();
          } else {
            this.showFloatingToast('⚠️ Comutarea sursei nu a reușit. Partajarea curentă continuă.');
          }
        }
        return;
      }
    } catch (err: any) {
      if (err.name !== 'NotAllowedError' && err.name !== 'AbortError') {
        console.warn('[ConferenceUI] switchScreenShareSource error:', err);
        this.showFloatingToast('⚠️ Schimbarea sursei a fost anulată.');
      }
    }
  }

  private renderPickerSources() {
    const grid = document.getElementById('picker-sources-grid');
    const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;
    if (!grid) return;

    const filtered = this.availableSources.filter(s => {
      // Exclude Vorbello window itself from window list to avoid recursion
      if (s.name.includes('Vorbello') && !s.isScreen) return false;
      return this.currentPickerTab === 'windows' ? !s.isScreen : s.isScreen;
    });

    if (filtered.length === 0) {
      grid.innerHTML = `
        <div style="grid-column: 1 / -1; text-align: center; color: var(--text-muted); padding: 3rem 1rem;">
          Nu s-au găsit ${this.currentPickerTab === 'windows' ? 'ferestre deschise' : 'ecrane disponibile'}. Deschideți/restaurați fereastra dorită și apăsați butonul <strong>🔄 Reîmprospătează</strong> de mai sus.
        </div>
      `;
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = this.currentPickerTab === 'windows' ? 'Partajează Fereastra Selectată' : 'Partajează Ecranul Selectat';
      }
      return;
    }

    grid.innerHTML = filtered.map(s => {
      const isSelected = this.selectedSourceId === s.id;
      const safeName = s.name.replace(/"/g, '&quot;');
      const icon = s.isScreen ? '🖥️' : '🪟';
      return `
        <div class="picker-card ${isSelected ? 'selected' : ''}" data-id="${s.id}" data-name="${safeName}">
          <div class="picker-thumb-wrapper">
            ${s.thumbnail && s.thumbnail.length > 30 ? `
              <img src="${s.thumbnail}" class="picker-thumb" alt="${safeName}" onerror="this.style.display='none'; if(this.nextElementSibling) this.nextElementSibling.style.display='flex';" />
              <div style="font-size: 2.2rem; display: none; align-items: center; justify-content: center; width: 100%; height: 100%;">${icon}</div>
            ` : `<div style="font-size: 2.2rem; display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">${icon}</div>`}
          </div>
          <div class="picker-title" title="${safeName}">${s.name}</div>
        </div>
      `;
    }).join('');

    grid.querySelectorAll('.picker-card').forEach(card => {
      card.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute('data-id');
        const name = target.getAttribute('data-name') || (this.currentPickerTab === 'windows' ? 'Fereastră' : 'Ecran');
        this.selectedSourceId = id;
        this.selectedSourceName = name;
        grid.querySelectorAll('.picker-card').forEach(c => c.classList.remove('selected'));
        target.classList.add('selected');
        if (confirmBtn) {
          confirmBtn.disabled = false;
          confirmBtn.innerText = `Partajează: ${name.slice(0, 24)}${name.length > 24 ? '...' : ''}`;
        }
      });

      // Double-click to immediately share
      card.addEventListener('dblclick', async (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute('data-id');
        const name = target.getAttribute('data-name') || (this.currentPickerTab === 'windows' ? 'Fereastră' : 'Ecran');
        this.selectedSourceId = id;
        this.selectedSourceName = name;
        await this.confirmScreenPicker();
      });
    });
  }

  private attachPickerListeners() {
    const modal = document.getElementById('screen-picker-modal');
    const tabWindows = document.getElementById('tab-picker-windows');
    const tabScreens = document.getElementById('tab-picker-screens');
    const refreshBtn = document.getElementById('btn-refresh-sources') as HTMLButtonElement | null;
    const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;
    const cancelBtn = document.getElementById('btn-cancel-picker');
    const closeBtn = document.getElementById('btn-close-picker');
    document.getElementById('btn-share-browser-experimental')?.addEventListener('click', () => {
      if (modal) modal.style.display = 'none';
      void this.startExperimentalTabShare();
    });

    tabWindows?.addEventListener('click', () => {
      this.currentPickerTab = 'windows';
      tabWindows.classList.add('active');
      tabScreens?.classList.remove('active');
      this.selectedSourceId = null;
      this.selectedSourceName = '';
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = 'Partajează Fereastra Selectată';
      }
      this.renderPickerSources();
    });

    tabScreens?.addEventListener('click', () => {
      this.currentPickerTab = 'screens';
      tabScreens.classList.add('active');
      tabWindows?.classList.remove('active');
      this.selectedSourceId = null;
      this.selectedSourceName = '';
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = 'Partajează Ecranul Selectat';
      }
      this.renderPickerSources();
    });

    refreshBtn?.addEventListener('click', async () => {
      if (!refreshBtn) return;
      refreshBtn.disabled = true;
      refreshBtn.innerText = 'Se încarcă...';
      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.getShareSources === 'function') {
        try {
          this.availableSources = await desktopApi.getShareSources();
          this.renderPickerSources();
        } catch (e) {
          console.warn('Failed to refresh share sources:', e);
        }
      }
      refreshBtn.disabled = false;
      refreshBtn.innerText = '🔄 Reîmprospătează';
    });

    const closePicker = async () => {
      if (modal) modal.style.display = 'none';
      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.cancelShareSource === 'function') {
        await desktopApi.cancelShareSource();
      } else if (desktopApi && typeof desktopApi.selectShareSource === 'function') {
        await desktopApi.selectShareSource(null);
      }
    };

    const qualityHdBtn = document.getElementById('btn-share-quality-hd');
    const qualityFullHdBtn = document.getElementById('btn-share-quality-fullhd');
    const updateQualityUi = () => {
      if (this.selectedScreenQuality === 'hd') {
        qualityHdBtn?.classList.add('active');
        qualityHdBtn?.style.setProperty('background', '#3b82f6');
        qualityHdBtn?.style.setProperty('color', '#fff');
        qualityFullHdBtn?.classList.remove('active');
        qualityFullHdBtn?.style.setProperty('background', 'transparent');
        qualityFullHdBtn?.style.setProperty('color', '#94a3b8');
      } else {
        qualityFullHdBtn?.classList.add('active');
        qualityFullHdBtn?.style.setProperty('background', '#3b82f6');
        qualityFullHdBtn?.style.setProperty('color', '#fff');
        qualityHdBtn?.classList.remove('active');
        qualityHdBtn?.style.setProperty('background', 'transparent');
        qualityHdBtn?.style.setProperty('color', '#94a3b8');
      }
    };
    qualityHdBtn?.addEventListener('click', () => {
      this.selectedScreenQuality = 'hd';
      if (typeof localStorage !== 'undefined') localStorage.setItem('vorbello_screen_quality', 'hd');
      updateQualityUi();
    });
    qualityFullHdBtn?.addEventListener('click', () => {
      this.selectedScreenQuality = 'fullhd';
      if (typeof localStorage !== 'undefined') localStorage.setItem('vorbello_screen_quality', 'fullhd');
      updateQualityUi();
    });
    updateQualityUi();

    cancelBtn?.addEventListener('click', closePicker);
    closeBtn?.addEventListener('click', closePicker);

    confirmBtn?.addEventListener('click', async () => {
      await this.confirmScreenPicker();
    });
  }

  private async startExperimentalTabShare(): Promise<void> {
    const api = (window as any).vorbelloDesktop;
    if (!api?.startTabRelay || this.manager.getRoom()?.state !== 'connected') {
      this.showFloatingToast('Partajarea experimentală necesită aplicația desktop conectată.'); return;
    }
    if (this.tabRelayCleanup) { this.showFloatingToast('O selecție este deja în curs.'); return; }
    let closed = false, polling = false, published = false, publishTimer: any;
    let relayAudioElement: HTMLAudioElement | null = null;
    const peer = new RTCPeerConnection({ iceServers: [] });
    const incoming = new MediaStream();
    const pending: RTCIceCandidateInit[] = [];
    let interval: any;
    const timeout = setTimeout(() => { if (!published) { cleanup(); this.showFloatingToast('Selecția filei a expirat.'); } }, 120000);
    const cleanup = () => {
      if (closed) return; closed = true; clearInterval(interval); clearTimeout(publishTimer); clearTimeout(timeout);
      incoming.getTracks().forEach(t => { t.onended = null; t.stop(); });
      if (relayAudioElement) { relayAudioElement.pause(); relayAudioElement.srcObject = null; relayAudioElement.remove(); relayAudioElement = null; }
      peer.close(); void api.stopTabRelay();
      if (this.tabRelayCleanup === cleanup) this.tabRelayCleanup = null;
    };
    this.tabRelayCleanup = cleanup;
    peer.onicecandidate = e => { if (e.candidate && !closed) void api.sendTabRelay({ type:'candidate', candidate:e.candidate.toJSON() }); };
    peer.ontrack = event => {
      incoming.addTrack(event.track);
      if (event.track.kind === 'audio') {
        relayAudioElement = document.createElement('audio');
        relayAudioElement.autoplay = true;
        relayAudioElement.muted = true;
        relayAudioElement.srcObject = new MediaStream([event.track]);
        document.body.appendChild(relayAudioElement);
        void relayAudioElement.play().catch(error => console.warn('Tab audio decoder could not start:', error));
      }
      if (event.track.kind !== 'video' || published) return;
      publishTimer = setTimeout(async () => {
        if (closed || published) return;
        const video = incoming.getVideoTracks()[0]; if (!video) return;
        const success = await this.manager.replaceScreenShareTrack(video, incoming.getAudioTracks()[0] || null, this.selectedScreenQuality);
        if (closed) { if (success) await this.manager.enableScreenShare(false); return; }
        if (!success) { cleanup(); this.showFloatingToast('Nu s-a putut publica fila în sală.'); return; }
        published = true; await api.sendTabRelay({type:'published'}); this.selectedSourceName = 'Filă browser (experimental)'; this.handleScreenShareState(); this.updateStageLayout();
      }, 800);
    };
    peer.onconnectionstatechange = () => {
      if (!closed && (peer.connectionState === 'failed' || peer.connectionState === 'closed')) {
        if (published) void this.manager.enableScreenShare(false); else cleanup();
      }
    };
    try {
      await api.startTabRelay();
      this.showFloatingToast('În browser, apasă „Alege ce partajezi” și selectează fila.', 8000);
      interval = setInterval(async () => {
        if (closed || polling) return; polling = true;
        try {
          for (const msg of await api.readTabRelay()) {
            if (msg.type === 'offer') {
              await peer.setRemoteDescription(msg.sdp);
              for (const candidate of pending.splice(0)) await peer.addIceCandidate(candidate);
              await peer.setLocalDescription(await peer.createAnswer());
              await api.sendTabRelay({type:'answer',sdp:peer.localDescription?.toJSON()});
            } else if (msg.type === 'candidate') {
              if (peer.remoteDescription) await peer.addIceCandidate(msg.candidate); else pending.push(msg.candidate);
            } else if (msg.type === 'stop') {
              if (published) await this.manager.enableScreenShare(false); else cleanup();
            }
          }
        } catch (error) { console.warn('Tab relay failed', error); if (published) await this.manager.enableScreenShare(false); else cleanup(); }
        finally { polling = false; }
      }, 150);
    } catch (error) { console.warn('Tab relay start failed', error); cleanup(); this.showFloatingToast('Nu s-a putut deschide browserul.'); }
  }

  private async confirmScreenPicker() {
    const modal = document.getElementById('screen-picker-modal');
    if (!this.selectedSourceId) return;

    if (modal) modal.style.display = 'none';

    const desktopApi = (window as any).vorbelloDesktop;
    if (desktopApi && typeof desktopApi.prepareShareSource === 'function') {
      const prepared = await desktopApi.prepareShareSource(this.selectedSourceId);
      if (!prepared) {
        this.showFloatingToast('Sursa aleasă nu mai este disponibilă. Reîmprospătează lista și alege din nou.');
        return;
      }
    } else if (desktopApi && typeof desktopApi.selectShareSource === 'function') {
      await desktopApi.selectShareSource(this.selectedSourceId);
    }

    const titleEl = document.getElementById('spotlight-title-text');
    const qualityLabel = this.selectedScreenQuality === 'fullhd' ? '1080p FHD' : '720p HD';
    if (titleEl && this.selectedSourceName) {
      titleEl.innerText = `Partajare: ${this.selectedSourceName.slice(0, 32)} (${qualityLabel} @ 30 FPS)`;
    }

    const shareSystemAudio = Boolean((document.getElementById('share-system-audio') as HTMLInputElement | null)?.checked);
    this.isShareAudioEnabled = shareSystemAudio;

    // If screen share is already active, seamlessly swap source WITHOUT stopping (0ms interruption)
    if (this.manager.isScreenShareEnabled) {
      console.info('[ConferenceUI] Seamlessly switching screen share source to:', this.selectedSourceName);
      try {
        const isFhd = this.selectedScreenQuality === 'fullhd';
        const targetWidth = isFhd ? 1920 : 1280;
        const targetHeight = isFhd ? 1080 : 720;
        let newStream: MediaStream | null = null;
        try {
          newStream = await captureDisplayMedia({
            video: {
              width: { ideal: targetWidth, max: targetWidth },
              height: { ideal: targetHeight, max: targetHeight },
              frameRate: { ideal: 30, max: 30 },
            },
            audio: shareSystemAudio ? { restrictOwnAudio: true } : false,
          });
        } catch (gdmErr: any) {
          if (gdmErr.name === 'NotAllowedError' || gdmErr.name === 'AbortError') {
            console.info('[ConferenceUI] User cancelled source change dialog');
            this.showFloatingToast('ℹ️ Comutarea sursei a fost anulată. Partajarea curentă continuă.');
            return;
          }
          newStream = await captureDisplayMedia({ video: true, audio: shareSystemAudio ? { restrictOwnAudio: true } : false });
        }

        if (newStream && newStream.getVideoTracks().length > 0) {
          const newVideoTrack = newStream.getVideoTracks()[0];
          const newAudioTrack = newStream.getAudioTracks()[0] || null;
          const success = await this.manager.replaceScreenShareTrack(newVideoTrack, newAudioTrack, this.selectedScreenQuality);
          if (success) {
            this.handleScreenShareState();
            this.updateStageLayout();
            this.showFloatingToast(`✅ Sursa a fost schimbată: ${this.selectedSourceName.slice(0, 24)}`, 2500);
          } else {
            this.showFloatingToast('⚠️ Comutarea sursei nu a reușit. Partajarea curentă continuă.');
            this.handleScreenShareState();
          }
        }
      } catch (switchErr: any) {
        console.warn('[ConferenceUI] Source switch error:', switchErr);
        this.showFloatingToast('ℹ️ Comutarea sursei a fost anulată. Partajarea curentă continuă.');
      }
      return;
    }

    // Starting screen share fresh
    this.pinnedIdentity = null;
    const success = await this.manager.enableScreenShare(true, shareSystemAudio, this.selectedScreenQuality);
    if (success) {
      this.handleScreenShareState();
    } else {
      console.warn('Screen share failed or was aborted.');
      this.handleScreenShareState();
    }
  }

  private attachListeners() {
    // Mic toggle (Instant, responsive toggle)
    const micBtn = document.getElementById('btn-toggle-mic');
    micBtn?.addEventListener('click', async () => {
      if (this.config.webinarAudience && !this.webinarMicAllowed) { this.showFloatingToast('În webinar, microfonul poate fi pornit după permisiunea gazdei.'); return; }
      const targetState = !this.manager.isMicEnabled;
      this.manager.isMicEnabled = targetState;
      if (!targetState) {
        this.userExplicitlyMuted = true;
      }
      this.updateControlsState();

      const selfP = this.participants.find(p => p.identity === 'self');
      if (selfP) selfP.isMuted = !targetState;
      this.setParticipantSpeaking('self', false);

      if (this.p2pMesh && this.p2pMesh.localStream) {
        this.p2pMesh.localStream.getAudioTracks().forEach(t => t.enabled = targetState);
      }

      try {
        const ok = await this.manager.enableMicrophone(targetState);
        if (targetState && !ok) { this.manager.isMicEnabled = false; if (selfP) selfP.isMuted = true; this.showFloatingToast('Microfonul nu a putut fi pornit.'); }
        if (targetState) {
          this.startLocalAudioLevelMonitor();
        }
      } catch (e) {
        console.warn('[ConferenceUI] Mic toggle error:', e);
      } finally {
        this.updateControlsState();
        this.setParticipantSpeaking('self', false);
      }
    });

    // Camera toggle (Instant, responsive toggle)
    const camBtn = document.getElementById('btn-toggle-cam');
    camBtn?.addEventListener('click', async () => {
      if (this.config.webinarAudience && !this.webinarCameraAllowed) { this.showFloatingToast('În webinar, camera poate fi pornită după permisiunea gazdei.'); return; }
      const targetState = !this.manager.isCameraEnabled;
      this.manager.isCameraEnabled = targetState;
      this.updateControlsState();
      this.updateSelfTile();

      const selfP = this.participants.find(p => p.identity === 'self');
      if (selfP) selfP.isCamOff = !targetState;

      if (this.p2pMesh && this.p2pMesh.localStream) {
        this.p2pMesh.localStream.getVideoTracks().forEach(t => t.enabled = targetState);
      }

      try {
        const ok = await this.manager.enableCamera(targetState);
        if (!targetState && (this.selectedVideoEffect === 'blur-soft' || this.selectedVideoEffect === 'blur-heavy' || this.selectedVideoEffect === 'background')) {
          this.selectedVideoEffect = 'none';
          localStorage.setItem('vorbello_video_effect', 'none');
        }
        if (targetState && !ok) {
          this.manager.isCameraEnabled = false;
          if (selfP) selfP.isCamOff = true;
          this.showFloatingToast('⚠️ Camera nu a putut fi pornită (posibil ocupată de altă aplicație sau permisiune refuzată)');
        }
      } catch (e) {
        console.warn('[ConferenceUI] Cam toggle error:', e);
        if (targetState) {
          this.manager.isCameraEnabled = false;
          if (selfP) selfP.isCamOff = true;
          this.showFloatingToast('⚠️ Eroare la pornirea camerei (posibil ocupată de altă aplicație)');
        }
      } finally {
        this.updateControlsState();
        this.updateSelfTile();
        this.updateStageLayout();
      }
    });

    // Screen share toggle & on-the-fly selector popover (Schimbă Sursa, Sunet, Oprește)
    const screenBtn = document.getElementById('btn-toggle-screen');
    const screenActiveMenu = document.getElementById('screen-share-active-menu');
    document.getElementById('btn-pre-event-share')?.addEventListener('click', () => {
      const menu=document.getElementById('more-options-popover'); if(menu) menu.style.display='none';
      this.openPreEventShare();
    });

    screenBtn?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (this.config.webinarAudience) return;
      const allowPolicy = this.config.roomSettings?.allowScreenShare;
      const isPrivileged = this.isHost || this.isAdmin || this.isModerator;
      if (allowPolicy === 'hosts_only' && !isPrivileged) {
        this.showFloatingToast('🔒 În această sală, doar Gazda și Co-Gazdele au permisiunea de a partaja ecranul.', 5000);
        return;
      }

      if (this.manager.isScreenShareEnabled) {
        // If already sharing, toggle selector menu for switching sources, audio, or stopping
        if (screenActiveMenu) {
          const isShown = screenActiveMenu.style.display !== 'none';
          screenActiveMenu.style.display = isShown ? 'none' : 'flex';
        }
      } else {
        if (screenActiveMenu) screenActiveMenu.style.display = 'none';
        await this.triggerScreenShareFlow();
      }
    });

    // Close screen share popover and participant action menus on outside click
    document.addEventListener('click', (e) => {
      if (screenActiveMenu && !screenActiveMenu.contains(e.target as Node) && e.target !== screenBtn) {
        screenActiveMenu.style.display = 'none';
      }
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.btn-part-menu-trigger') && !target?.closest('.participant-menu-dropdown')) {
        document.querySelectorAll('.participant-menu-dropdown').forEach(d => (d as HTMLElement).style.display = 'none');
      }
    });

    // Opțiunea 1: Schimbă Sursa
    document.getElementById('btn-menu-switch-source')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (screenActiveMenu) screenActiveMenu.style.display = 'none';
      await this.switchScreenShareSource();
    });

    // Opțiunea 2: Sunet Partajare (Riga Specială din meniu)
    document.getElementById('btn-menu-toggle-audio')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      this.isShareAudioEnabled = !this.isShareAudioEnabled;
      const audioIcon = document.getElementById('menu-share-audio-icon');
      const audioTitle = document.getElementById('menu-share-audio-title');
      const audioDesc = document.getElementById('menu-share-audio-desc');
      if (audioIcon) audioIcon.innerText = this.isShareAudioEnabled ? '🔊' : '🔇';
      if (audioTitle) audioTitle.innerText = this.isShareAudioEnabled ? 'Sunet Partajare: Activ' : 'Sunet Partajare: Oprit';
      if (audioDesc) audioDesc.innerText = this.isShareAudioEnabled ? 'Sunetul de sistem/tab este inclus' : 'Faceți clic pentru a activa sunetul';

      const screenStream = this.manager.getLocalScreenStream();
      const audioTracks = screenStream?.getAudioTracks() || [];
      if (audioTracks.length > 0) {
        audioTracks.forEach(t => { t.enabled = this.isShareAudioEnabled; });
        const room = this.manager.getRoom();
        const audioPub = room?.localParticipant?.getTrackPublication(Track.Source.ScreenShareAudio);
        if (audioPub && audioPub.track) {
          try {
            if (this.isShareAudioEnabled) {
              await audioPub.track.unmute();
            } else {
              await audioPub.track.mute();
            }
          } catch (_) {}
        }
        this.showFloatingToast(this.isShareAudioEnabled ? '🔊 Sunetul partajării a fost activat.' : '🔇 Sunetul partajării a fost oprit.', 3000);
      } else {
        if (this.isShareAudioEnabled) {
          this.showFloatingToast('🔊 Selectați o Filă (Tab) sau Ecranul Întreg și asigurați-vă că bifați „Partajează conținutul audio” în fereastra de dialog a browserului.', 5000);
          if (screenActiveMenu) screenActiveMenu.style.display = 'none';
          await this.switchScreenShareSource();
        } else {
          this.showFloatingToast('🔇 Partajarea este fără sunet.', 3000);
        }
      }
      this.updateStageLayout();
    });

    // Opțiunea 3: Oprește Partajarea
    document.getElementById('btn-menu-stop-share')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (screenActiveMenu) screenActiveMenu.style.display = 'none';
      await this.manager.enableScreenShare(false);
      this.handleScreenShareState();
    });

    // Spotlight Header Button: Schimbă Sursa
    document.getElementById('btn-switch-spotlight-source')?.addEventListener('click', async () => {
      await this.switchScreenShareSource();
    });

    // Global chat file download handler (Android APK Native Bridge + Web fallback)
    (window as any).vorbelloDownloadFile = (msgId: string) => {
      const msg = this.messages.find(m => m.id === msgId);
      if (!msg || !msg.file || !msg.file.dataUrl) {
        this.showFloatingToast('⚠️ Fișierul nu mai este disponibil.');
        return;
      }
      const dataUrl = msg.file.dataUrl;
      const fileName = msg.file.name || 'document';
      const mimeType = msg.file.type || 'application/octet-stream';

      // 1. Android APK Native Bridge
      if ((window as any).VorbelloNativeBridge && typeof (window as any).VorbelloNativeBridge.downloadBase64File === 'function') {
        try {
          (window as any).VorbelloNativeBridge.downloadBase64File(dataUrl, fileName, mimeType);
          this.showFloatingToast(`⬇️ Descărcare salvată în folderul Descărcări: ${fileName}`);
          return;
        } catch (err) {
          console.warn('[Chat] VorbelloNativeBridge error, falling back:', err);
        }
      }

      // 2. Desktop & Mobile Web / PWA Download via real Blob ObjectURL (Chrome on Android does not block Blob URLs)
      try {
        const blob = this.dataUrlToBlob(dataUrl, mimeType);
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          if (link.parentNode) link.parentNode.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 4000);
        this.showFloatingToast(`⬇️ Fișier descărcat: ${fileName}`);
      } catch (err) {
        console.error('[Chat] Download error:', err);
        window.open(dataUrl, '_blank');
      }
    };

    // Raise Hand Toggle (Opțiunea 1)
    const handBtn = document.getElementById('btn-raise-hand');
    handBtn?.addEventListener('click', async () => {
      this.isLocalHandRaised = !this.isLocalHandRaised;
      const selfP = this.participants.find(p => p.identity === 'self');
      if (selfP) selfP.isHandRaised = this.isLocalHandRaised;

      this.updateControlsState();
      this.renderVideoGrid();
      this.renderParticipantsList();

      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage({ type: 'hand-raise', identity: 'self', raised: this.isLocalHandRaised });
      } else {
        await this.manager.sendHandRaise(this.isLocalHandRaised);
      }
      if (this.isLocalHandRaised) {
        this.showFloatingToast('✋ Ai ridicat mâna! Toți participanții au fost notificați.');
      } else {
        this.showFloatingToast('✋ Ai coborât mâna.');
      }
    });

    // Live Reactions Popover (Opțiunea 2)
    const reactionsBtn = document.getElementById('btn-open-reactions');
    const reactionsPopover = document.getElementById('reactions-popover');
    reactionsBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (reactionsPopover) {
        const isShown = reactionsPopover.style.display !== 'none';
        reactionsPopover.style.display = isShown ? 'none' : 'flex';
      }
    });

    // Close reactions popover on outside click
    document.addEventListener('click', (e) => {
      if (reactionsPopover && !reactionsPopover.contains(e.target as Node) && e.target !== reactionsBtn) {
        reactionsPopover.style.display = 'none';
      }
    });

    // Emoji reaction buttons
    reactionsPopover?.querySelectorAll('.btn-reaction-emoji').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji') || '👍';
        if (reactionsPopover) reactionsPopover.style.display = 'none';

        // Trigger local visual feedback immediately
        this.triggerFloatingReaction(emoji, 'self', `${this.config.displayName} (Tu)`);

        // Broadcast to all participants via LiveKit Data Channel
        await this.manager.sendReaction(emoji);
      });
    });

    // Stop / Unpin button inside spotlight header
    document.getElementById('btn-stop-spotlight')?.addEventListener('click', async () => {
      if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
        this.showFloatingToast('🔒 Doar Administratorul poate anula fixarea scenei.');
        return;
      }
      if (this.manager.isScreenShareEnabled) {
        await this.manager.enableScreenShare(false);
        this.handleScreenShareState();
      } else if (this.pinnedIdentity) {
        await this.setPinState(null, true);
      }
    });

    // Toggle Spotlight Size (Zoom & Google Meet Mobile Style: contain vs cover on mobile, maximize on desktop)
    const toggleSpotlightSize = () => {
      const container = document.getElementById('spotlight-container');
      const label = document.getElementById('label-spotlight-size');
      if (!container) return;
      if (window.innerWidth <= 768) {
        // Mobile Zoom & Meet behavior: Toggle between contain (Fit 16:9 full view) and cover (Fill screen)
        const isFill = container.classList.toggle('is-fill-mode');
        if (label) {
          label.innerText = isFill ? 'Potrivește' : 'Umple';
        }
        this.showFloatingToast(isFill ? '🔍 Umplere ecran (Zoom In / Decupat)' : '⤡ Potrivire ecran complet (Zoom Out / 16:9 Original)');
      } else {
        const isMax = container.classList.toggle('is-maximized');
        if (label) {
          label.innerText = isMax ? 'Micșorează' : 'Mărește';
        }
        this.showFloatingToast(isMax ? '🔍 Ecran mărit la maximum (Full View)' : '⤡ Revenire la aspect standard');
      }
    };

    document.getElementById('btn-toggle-spotlight-size')?.addEventListener('click', toggleSpotlightSize);
    document.getElementById('spotlight-viewport')?.addEventListener('dblclick', toggleSpotlightSize);

    // Double-tap gesture on mobile touch screens (Zoom & Google Meet gesture)
    let lastSpotlightTapTime = 0;
    document.getElementById('spotlight-viewport')?.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastSpotlightTapTime < 350 && now - lastSpotlightTapTime > 0) {
        e.preventDefault();
        toggleSpotlightSize();
      }
      lastSpotlightTapTime = now;
    });

    // Zoom-Style View Switcher & Dropdown Popover (Vorbitor Activ, Grilă Dinamică, Studio 16:9 Paginat)
    const viewBtn = document.getElementById('btn-toggle-view-mode');
    const viewPopover = document.getElementById('view-mode-popover');

    viewBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
        this.showFloatingToast('🔒 Scena este fixată de Administrator (Mod Spotlight).');
        return;
      }
      if (viewPopover) {
        const isShown = viewPopover.style.display !== 'none';
        viewPopover.style.display = isShown ? 'none' : 'flex';
        this.updateViewModePopoverChecks();
      }
    });

    // View Options click handlers (Strictly Local - Do NOT force on other users!)
    document.getElementById('opt-view-speaker')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (viewPopover) viewPopover.style.display = 'none';
      await this.setViewMode('speaker', false);
    });

    document.getElementById('opt-view-gallery')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (viewPopover) viewPopover.style.display = 'none';
      await this.setViewMode('gallery', false);
    });

    document.getElementById('opt-view-studio')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (viewPopover) viewPopover.style.display = 'none';
      await this.setViewMode('studio', false);
    });

    // Close view popover on outside click
    document.addEventListener('click', (e) => {
      if (viewPopover && viewPopover.style.display !== 'none') {
        const target = e.target as HTMLElement;
        if (!viewPopover.contains(target) && target !== viewBtn && !viewBtn?.contains(target)) {
          viewPopover.style.display = 'none';
        }
      }
    });

    // Studio 16:9 Pagination Buttons
    document.getElementById('btn-studio-prev')?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.studioCurrentPage > 1) {
        this.studioCurrentPage--;
        this.renderVideoGrid();
      }
    });

    document.getElementById('btn-studio-next')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const totalPages = Math.max(1, Math.ceil(this.participants.length / this.studioPageSize));
      if (this.studioCurrentPage < totalPages) {
        this.studioCurrentPage++;
        this.renderVideoGrid();
      }
    });

    // Keyboard navigation for studio pages
    document.addEventListener('keydown', (e) => {
      if (this.currentViewMode === 'studio') {
        if (e.key === 'ArrowLeft' && this.studioCurrentPage > 1) {
          this.studioCurrentPage--;
          this.renderVideoGrid();
        } else if (e.key === 'ArrowRight') {
          const totalPages = Math.max(1, Math.ceil(this.participants.length / this.studioPageSize));
          if (this.studioCurrentPage < totalPages) {
            this.studioCurrentPage++;
            this.renderVideoGrid();
          }
        }
      }
    });

    // Global Moderation: Lower All Hands (Zoom & Google Meet feature)
    const lowerAllHandsBtn = document.getElementById('btn-lower-all-hands');
    lowerAllHandsBtn?.addEventListener('click', async () => {
      this.isLocalHandRaised = false;
      this.participants.forEach(p => { p.isHandRaised = false; });
      this.showFloatingToast('✋ Ați coborât toate mâinile ridicate din sală.');
      this.renderVideoGrid();
      this.renderParticipantsList();
      this.updateControlsState();
      try {
        await this.manager.sendLowerAllHands();
      } catch (err) {
        console.warn('Failed to send lower all hands:', err);
      }
    });

    // Global Moderation: Toggle Microphone for All Non-Admins
    const muteAllBtn = document.getElementById('btn-mute-all');
    muteAllBtn?.addEventListener('click', async () => {
      this.isAllMuted = !this.isAllMuted;
      this.participants.forEach(p => {
        if (p.identity !== 'self') {
          p.isMuted = this.isAllMuted;
        }
      });
      if (muteAllBtn) {
        muteAllBtn.innerText = this.isAllMuted ? '🔊 Unmute Toți' : '🔇 Mute Toți';
        muteAllBtn.className = `btn btn-sm ${this.isAllMuted ? 'btn-primary' : 'btn-secondary'}`;
      }

      // Immediately silence or restore all remote audio elements locally for 0ms delay:
      document.querySelectorAll('audio[id^="audio-remote-"]').forEach((a: any) => {
        a.muted = this.isAllMuted;
      });

      const myId = this.manager.getRoom()?.localParticipant?.identity || 'self';
      await this.manager.sendDataMessage({
        type: 'moderation',
        action: this.isAllMuted ? 'mute-all' : 'unmute-all',
        senderIdentity: myId
      });

      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage({
          type: 'moderation',
          action: this.isAllMuted ? 'mute-all' : 'unmute-all',
          senderIdentity: myId
        });
      }

      this.renderVideoGrid();
      this.renderParticipantsList();
    });

    // Global Moderation: Toggle Camera for All Non-Admins
    const stopCamAllBtn = document.getElementById('btn-stop-all-cam');
    stopCamAllBtn?.addEventListener('click', async () => {
      this.isAllCamOff = !this.isAllCamOff;
      this.participants.forEach(p => {
        if (p.identity !== 'self') {
          p.isCamOff = this.isAllCamOff;
        }
      });
      if (stopCamAllBtn) {
        stopCamAllBtn.innerText = this.isAllCamOff ? '📹 Start Camere' : '📷 Stop Camere';
        stopCamAllBtn.className = `btn btn-sm ${this.isAllCamOff ? 'btn-primary' : 'btn-secondary'}`;
      }

      // Immediately toggle remote video elements locally:
      if (this.isAllCamOff) {
        document.querySelectorAll('video.remote-cam-video').forEach((v: any) => {
          v.style.display = 'none';
        });
        document.querySelectorAll('.video-tile:not(#tile-self) .avatar-placeholder').forEach((av: any) => {
          av.style.display = 'flex';
        });
      }

      const myId = this.manager.getRoom()?.localParticipant?.identity || 'self';
      await this.manager.sendDataMessage({
        type: 'moderation',
        action: this.isAllCamOff ? 'stop-cam-all' : 'start-cam-all',
        senderIdentity: myId
      });

      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage({
          type: 'moderation',
          action: this.isAllCamOff ? 'stop-cam-all' : 'start-cam-all',
          senderIdentity: myId
        });
      }

      this.renderVideoGrid();
      this.renderParticipantsList();
    });

    // Chat drawer
    document.getElementById('btn-open-chat')?.addEventListener('click', () => {
      this.isChatOpen = !this.isChatOpen;
      this.isParticipantsOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('btn-pin-controls')?.addEventListener('click', e => {
      if (!this.isAdmin && !this.isModerator && !this.isHost) return;
      const pinned = localStorage.getItem('vorbello_pin_controls') !== 'true';
      localStorage.setItem('vorbello_pin_controls', String(pinned));
      const button = e.currentTarget as HTMLElement;
      button.setAttribute('aria-pressed', String(pinned));
      const label = button.querySelector('.popover-item-label'); if (label) label.textContent = pinned ? 'Eliberează bara' : 'Fixează bara';
      document.getElementById('btn-header-pin')?.setAttribute('aria-pressed', String(pinned));
      this.showFloatingToast(pinned ? 'Bara de jos rămâne vizibilă.' : 'Bara de jos se ascunde automat.');
    });
    document.getElementById('btn-header-pin')?.addEventListener('click', () => document.getElementById('btn-pin-controls')?.click());
    document.getElementById('btn-header-whiteboard')?.addEventListener('click', () => document.getElementById('btn-toggle-whiteboard')?.click());
    const securityModal = document.getElementById('security-overview-modal');
    let securityTrigger: HTMLElement | null = null;
    const closeSecurity = () => { if (securityModal) securityModal.style.display = 'none'; securityTrigger?.focus(); };
    const openSecurity = async (event: Event) => {
      securityTrigger = event.currentTarget as HTMLElement;
      const rows = document.getElementById('security-overview-rows');
      if (!securityModal || !rows) return;
      rows.replaceChildren();
      const room = this.manager.getRoom();
      const e2eeLocalConfigured = Boolean(this.config.encryptionKey && room?.state === 'connected' && room.localParticipant.isE2EEEnabled);
      const stats = await this.manager.getRealtimeStats().catch(() => null);
      securityOverviewRows({ ...this.config.roomSettings, e2eeLocalConfigured, dtlsVersions: stats?.dtlsVersions }).forEach(([label, value]) => {
        const row = document.createElement('div');
        row.style.cssText = 'display:grid;grid-template-columns:minmax(110px,1fr) 2fr;gap:1rem;border-bottom:1px solid rgba(255,255,255,.08);padding:.5rem 0;';
        const name = document.createElement('strong'); name.textContent = label;
        const state = document.createElement('span'); state.textContent = value; state.style.color = '#cbd5e1';
        row.append(name, state); rows.append(row);
      });
      const more = document.getElementById('more-options-popover'); if (more) more.style.display = 'none';
      securityModal.style.display = 'flex';
      document.getElementById('btn-close-security-overview')?.focus();
    };
    document.getElementById('btn-open-security-overview')?.addEventListener('click', openSecurity);
    document.getElementById('btn-header-security')?.addEventListener('click', openSecurity);
    document.getElementById('btn-close-security-overview')?.addEventListener('click', closeSecurity);
    document.getElementById('btn-header-participants')?.addEventListener('click', () => document.getElementById('btn-open-participants')?.click());
    document.getElementById('btn-header-chat')?.addEventListener('click', () => document.getElementById('btn-open-chat')?.click());
    document.getElementById('btn-header-polls')?.addEventListener('click', () => document.getElementById('btn-open-polls')?.click());
    securityModal?.addEventListener('click', e => { if (e.target === securityModal) closeSecurity(); });
    securityModal?.addEventListener('keydown', e => { if ((e as KeyboardEvent).key === 'Escape') closeSecurity(); });

    // Participants drawer
    document.getElementById('btn-open-admin-quick')?.addEventListener('click', () => {
      if (!this.isAdmin && !this.isModerator) return;
      this.isParticipantsOpen = true;
      this.isChatOpen = false;
      this.isPollsOpen = false;
      const more = document.getElementById('more-options-popover');
      if (more) more.style.display = 'none';
      this.toggleDrawer();
    });
    document.getElementById('btn-open-participants')?.addEventListener('click', () => {
      this.isParticipantsOpen = !this.isParticipantsOpen;
      this.isChatOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    // Close drawer
    document.getElementById('btn-close-panel')?.addEventListener('click', () => {
      this.isChatOpen = false;
      this.isParticipantsOpen = false;
      this.isPollsOpen = false;
      this.participantSearchQuery = '';
      const sInput = document.getElementById('input-search-participants') as HTMLInputElement | null;
      if (sInput) sInput.value = '';
      const cBtn = document.getElementById('btn-clear-search-participants');
      if (cBtn) cBtn.style.display = 'none';
      this.toggleDrawer();
    });

    // Participants Search & Filter (Zoom & Google Meet Style)
    const searchInput = document.getElementById('input-search-participants') as HTMLInputElement | null;
    const clearSearchBtn = document.getElementById('btn-clear-search-participants');
    searchInput?.addEventListener('input', () => {
      this.participantSearchQuery = searchInput.value || '';
      if (clearSearchBtn) {
        clearSearchBtn.style.display = this.participantSearchQuery ? 'block' : 'none';
      }
      this.renderParticipantsList();
    });
    clearSearchBtn?.addEventListener('click', () => {
      if (searchInput) {
        searchInput.value = '';
        this.participantSearchQuery = '';
        clearSearchBtn.style.display = 'none';
        searchInput.focus();
        this.renderParticipantsList();
      }
    });

    // Quick Invite in Header
    document.getElementById('btn-header-quick-invite')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isParticipantsOpen = true;
      this.isChatOpen = false;
      this.toggleDrawer();
      const inviteInput = document.getElementById('input-invite-room-url') as HTMLInputElement | null;
      if (inviteInput) inviteInput.value = this.getRoomInviteUrl();
      this.copyInviteLink(document.getElementById('btn-header-quick-invite'));
    });

    // Copy link in invite card inside participants
    document.getElementById('btn-copy-invite-link')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.copyInviteLink(document.getElementById('btn-copy-invite-link'));
    });

    // Copy full invite text in invite card
    document.getElementById('btn-copy-invite-text')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.copyInviteText(document.getElementById('btn-copy-invite-text'));
    });

    // Mobile More Sheet & Desktop More Options Popover Handlers
    const moreSheetOverlay = document.getElementById('mobile-more-sheet-overlay');
    const openMoreSheet = () => {
      if (moreSheetOverlay) moreSheetOverlay.style.display = 'flex';
      const countEl = document.getElementById('sheet-part-count');
      if (countEl) countEl.textContent = String(this.participants.length);
    };
    const closeMoreSheet = () => {
      if (moreSheetOverlay) moreSheetOverlay.style.display = 'none';
    };

    const moreBtn = document.getElementById('btn-more-options');
    const morePopover = document.getElementById('more-options-popover');

    const toggleMoreOptions = (e: MouseEvent) => {
      e.stopPropagation();
      if (window.innerWidth <= 768) {
        openMoreSheet();
        return;
      }
      if (morePopover) {
        const isShown = morePopover.style.display !== 'none';
        morePopover.style.display = isShown ? 'none' : 'flex';
      }
    };

    moreBtn?.addEventListener('click', toggleMoreOptions);
    document.getElementById('btn-more-mobile')?.addEventListener('click', openMoreSheet);
    document.getElementById('btn-close-more-sheet')?.addEventListener('click', closeMoreSheet);
    moreSheetOverlay?.addEventListener('click', (e) => {
      if (e.target === moreSheetOverlay) closeMoreSheet();
    });

    // Close desktop more-options popover on outside click
    document.addEventListener('click', (e) => {
      if (morePopover && !morePopover.contains(e.target as Node) && e.target !== moreBtn && !moreBtn?.contains(e.target as Node)) {
        morePopover.style.display = 'none';
      }
    });

    // Close popover when an item inside is clicked
    morePopover?.querySelectorAll('.popover-item-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (morePopover) morePopover.style.display = 'none';
      });
    });

    // Popover quick reactions emojis
    morePopover?.querySelectorAll('.btn-reaction-emoji').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji') || '👏';
        if (morePopover) morePopover.style.display = 'none';
        this.triggerFloatingReaction(emoji, 'self', `${this.config.displayName} (Tu)`);
        await this.manager.sendReaction(emoji);
        this.showFloatingToast(`Reacție trimisă: ${emoji}`, 1600);
      });
    });

    const showBrowserPictureInPicture = async () => {
      if ((window as any).VorbelloNativeBridge?.openPictureInPicture) {
        (window as any).VorbelloNativeBridge.openPictureInPicture(); return;
      }
      if (!document.pictureInPictureEnabled) {
        this.showFloatingToast('Fereastra video nu este disponibilă în acest browser sau dispozitiv.'); return;
      }
      if (document.pictureInPictureElement) {
        try { await document.exitPictureInPicture(); } catch (_) { this.showFloatingToast('Fereastra video nu a putut fi închisă.'); }
        return;
      }
      const selectTrack = (): MediaStreamTrack | null => {
        const candidates: HTMLVideoElement[] = [];
        const stage = document.getElementById('spotlight-video') as HTMLVideoElement | null;
        if (stage && stage.getClientRects().length && getComputedStyle(stage).display !== 'none') candidates.push(stage);
        const addTile = (id: string) => { const video = document.getElementById(`tile-${id}`)?.querySelector<HTMLVideoElement>('video'); if (video) candidates.push(video); };
        if (this.pinnedIdentity) addTile(this.pinnedIdentity);
        const speaker = this.participants.find(p => p.isSpeaking);
        if (speaker) addTile(speaker.identity);
        document.querySelectorAll<HTMLVideoElement>('video.remote-cam-video, video.self-cam-video').forEach(v => candidates.push(v));
        for (const video of candidates) {
          const stream = video.srcObject;
          if (!(stream instanceof MediaStream)) continue;
          const track = stream.getVideoTracks().find(t => t.readyState === 'live' && t.enabled);
          if (track) return track;
        }
        return null;
      };
      const track = selectTrack();
      if (!track) { this.showFloatingToast('Nu este disponibilă o imagine video pentru fereastra mică.'); return; }
      const video = document.createElement('video');
      video.muted = true; video.autoplay = true; video.playsInline = true;
      video.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:.01;pointer-events:none;';
      video.srcObject = new MediaStream([track]);
      document.body.appendChild(video); this.pipVideo = video;
      const cleanup = () => { if (this.pipRefreshTimer) clearInterval(this.pipRefreshTimer); this.pipRefreshTimer = undefined; video.srcObject = null; video.remove(); if (this.pipVideo === video) this.pipVideo = undefined; };
      video.addEventListener('leavepictureinpicture', cleanup, { once: true });
      try {
        await video.play();
        await video.requestPictureInPicture();
        this.pipRefreshTimer = setInterval(() => {
          const next = selectTrack();
          const current = (video.srcObject as MediaStream | null)?.getVideoTracks()[0];
          if (next && next !== current) { video.srcObject = new MediaStream([next]); void video.play().catch(() => {}); }
        }, 750);
      } catch (_) { cleanup(); this.showFloatingToast('Browserul nu permite fereastra video pentru acest flux.'); }
    };
    document.getElementById('btn-browser-picture-in-picture')?.addEventListener('click', () => { void showBrowserPictureInPicture(); });
    document.getElementById('sheet-btn-picture-in-picture')?.addEventListener('click', () => { closeMoreSheet(); void showBrowserPictureInPicture(); });
    document.getElementById('btn-mobile-floating-pip')?.addEventListener('click', () => { void showBrowserPictureInPicture(); });

    // Mobile Camera Flip
    document.getElementById('btn-flip-cam')?.addEventListener('click', async () => {
      if (this.config.webinarAudience && !this.webinarCameraAllowed) {
        this.showFloatingToast('În webinar, camera cursanților este oprită implicit. Gazda trebuie să îți permită activarea ei.');
        return;
      }
      if (!this.manager.isCameraEnabled) {
        this.showFloatingToast('Pornește camera înainte de a o schimba.');
        return;
      }
      const btn = document.getElementById('btn-flip-cam');
      if (btn) btn.style.transform = 'rotate(180deg)';
      setTimeout(() => { if (btn) btn.style.transform = 'none'; }, 300);
      try {
        const changed = await this.manager.flipCamera();
        if (!changed) { this.showFloatingToast('Camera nu a putut fi schimbată. Verifică dacă există o altă cameră disponibilă.'); return; }
        if (this.isP2PMode && this.p2pMesh) {
          const newCamStream = this.manager.getLocalCameraStream();
          if (newCamStream) {
            this.p2pMesh.updateLocalStream(newCamStream);
          }
        }
        this.updateSelfTile();
        const modeLabel = this.manager.currentFacingMode === 'user' ? 'Față (Selfie)' : 'Spate (Principală)';
        this.showFloatingToast(`🔄 Camera a fost comutată: ${modeLabel}`);
      } catch (flipErr) {
        console.warn('[ConferenceUI] flipCamera error:', flipErr);
      }
    });

    document.getElementById('sheet-btn-flip-cam')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-flip-cam')?.click();
    });

    // Sheet button forwards
    document.getElementById('sheet-btn-participants')?.addEventListener('click', () => {
      closeMoreSheet();
      this.isParticipantsOpen = true;
      this.isChatOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('sheet-btn-chat')?.addEventListener('click', () => {
      closeMoreSheet();
      this.isChatOpen = true;
      this.isParticipantsOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('sheet-btn-raise-hand')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-raise-hand')?.click();
    });

    // Mobile Reactions: Open Dedicated Floating Reactions Dock on Stage
    document.getElementById('sheet-btn-reactions')?.addEventListener('click', () => {
      closeMoreSheet();
      const dock = document.getElementById('mobile-floating-reactions-dock');
      if (dock) {
        dock.style.display = dock.style.display === 'none' ? 'flex' : 'none';
      }
    });

    document.getElementById('btn-close-reactions-dock')?.addEventListener('click', () => {
      const dock = document.getElementById('mobile-floating-reactions-dock');
      if (dock) dock.style.display = 'none';
    });

    // Mobile Quick Emojis (both in sheet and in floating dock)
    document.querySelectorAll('.sheet-quick-emoji').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji') || '👏';
        this.triggerFloatingReaction(emoji, 'self', `${this.config.displayName} (Tu)`);
        await this.manager.sendReaction(emoji);
        this.showFloatingToast(`Reacție trimisă: ${emoji}`, 1600);
        if (btn.closest('#mobile-more-sheet')) {
          closeMoreSheet();
        }
      });
    });

    document.getElementById('sheet-btn-whiteboard')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-toggle-whiteboard')?.click();
    });

    document.getElementById('sheet-btn-polls')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-open-polls')?.click();
    });

    document.getElementById('sheet-btn-screen')?.addEventListener('click', () => {
      closeMoreSheet();
      if (this.config.webinarAudience) { this.showFloatingToast('În webinar, partajarea ecranului este disponibilă gazdei și prezentatorilor autorizați.'); return; }
      document.getElementById('btn-toggle-screen')?.click();
    });

    document.getElementById('sheet-btn-record')?.addEventListener('click', () => {
      closeMoreSheet();
      if (this.isRecording) {
        this.stopRecording();
      } else if (this.roomRecordingActive) {
        if (this.isHost || this.isAdmin) {
          this.manager.sendDataMessage({
            type: 'recording_command',
            action: 'stop',
            requesterName: this.config.displayName || 'Gazdă',
          }).catch(() => {});
          this.showFloatingToast(`⏹️ Ați solicitat oprirea înregistrării coordonate de ${this.roomRecorderName || 'organizator'}...`);
        } else {
          this.showFloatingToast(`ℹ️ Înregistrarea este gestionată de către ${this.roomRecorderName || 'organizator'}.`);
        }
      } else {
        if (!this.isHost && !this.isAdmin && !this.config.canRecord) {
          this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a înregistra.');
          return;
        }
        this.openRecordingOptionsModal();
      }
    });

    document.getElementById('sheet-btn-devices')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-open-devices')?.click();
    });

    document.getElementById('sheet-btn-view-mode')?.addEventListener('click', () => {
      closeMoreSheet();
      this.cycleViewMode(false);
    });

    document.getElementById('sheet-btn-invite')?.addEventListener('click', (e) => {
      e.stopPropagation();
      closeMoreSheet();
      this.copyInviteLink(document.getElementById('btn-header-quick-invite'));
    });

    // Live Network Telemetry Popover
    const statusPill = document.getElementById('conf-status-pill');
    const telemetryPopover = document.getElementById('network-telemetry-popover');
    statusPill?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (telemetryPopover) {
        const isShown = telemetryPopover.style.display !== 'none';
        telemetryPopover.style.display = isShown ? 'none' : 'flex';
        if (!isShown) {
          this.startTelemetryMonitoring();
        } else {
          this.stopTelemetryMonitoring();
        }
      }
    });

    document.getElementById('btn-open-telemetry-monitor')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const popover = document.getElementById('more-options-popover');
      if (popover) popover.style.display = 'none';
      if (telemetryPopover) {
        telemetryPopover.style.display = 'flex';
        this.startTelemetryMonitoring();
      }
    });

    document.getElementById('sheet-btn-telemetry-monitor')?.addEventListener('click', (e) => {
      e.stopPropagation();
      closeMoreSheet();
      if (telemetryPopover) {
        telemetryPopover.style.display = 'flex';
        this.startTelemetryMonitoring();
      }
    });

    document.getElementById('btn-close-telemetry')?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (telemetryPopover) {
        telemetryPopover.style.display = 'none';
        this.stopTelemetryMonitoring();
      }
    });
    document.addEventListener('click', (e) => {
      if (telemetryPopover && telemetryPopover.style.display !== 'none' && !telemetryPopover.contains(e.target as Node) && e.target !== statusPill) {
        telemetryPopover.style.display = 'none';
        this.stopTelemetryMonitoring();
      }
    });

    document.getElementById('btn-telemetry-retry')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      await this.retryVpsConnection();
    });

    // Send chat message and/or file
    document.querySelectorAll<HTMLButtonElement>('.chat-emoji-insert').forEach(button => button.addEventListener('click', () => {
      const input = document.getElementById('chat-text-input') as HTMLInputElement | null;
      if (!input) return;
      const emoji = button.dataset.emoji || '', start = input.selectionStart ?? input.value.length, end = input.selectionEnd ?? start;
      input.setRangeText(emoji, start, end, 'end'); input.focus();
    }));
    document.getElementById('chat-history')?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const trigger = target.closest('.chat-reactions-trigger') as HTMLButtonElement | null;
      if (trigger) {
        const picker = trigger.parentElement?.querySelector<HTMLElement>('.chat-reaction-picker');
        if (picker) { picker.hidden = !picker.hidden; trigger.setAttribute('aria-expanded', String(!picker.hidden)); }
        return;
      }
      const download = target.closest('.chat-download-file') as HTMLElement | null;
      if (download) { const downloadFile = (window as any).vorbelloDownloadFile; if (typeof downloadFile === 'function') downloadFile(download.dataset.messageId || ''); return; }
      const replyButton = target.closest('.chat-message-reply') as HTMLElement | null;
      if (replyButton) {
        const original = this.messages.find(m => m.id === replyButton.dataset.messageId);
        if (!original) return;
        this.chatReplyTo = { id: original.id, senderName: original.senderName, text: (original.text || original.file?.name || 'Fișier').slice(0,160) };
        const preview = document.getElementById('chat-reply-preview');
        if (preview) { const description = document.getElementById('chat-reply-description'); if (description) description.textContent = `Răspuns pentru ${this.chatReplyTo.senderName}: ${this.chatReplyTo.text}`; preview.style.display = 'block'; }
        (document.getElementById('chat-text-input') as HTMLInputElement | null)?.focus();
        return;
      }
      const reactionButton = target.closest('.chat-message-reaction') as HTMLElement | null;
      if (!reactionButton) return;
      const messageId = reactionButton.dataset.messageId || '', emoji = reactionButton.dataset.emoji || '';
      if (!this.messages.some(m => m.id === messageId) || !['👍','❤️','👏','😂'].includes(emoji)) return;
      const identity = this.manager.getRoom()?.localParticipant.identity || this.p2pMesh?.peerId || 'self';
      const active = this.chatReactions.get(messageId)?.get(identity) !== emoji;
      const data = { type: 'chat_reaction', messageId, emoji, active, identity };
      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage(data);
        this.applyChatReaction(messageId, emoji, identity, active);
      } else {
        void this.manager.sendDataMessage(data).then(sent => {
          if (sent) this.applyChatReaction(messageId, emoji, identity, active);
          else this.showFloatingToast('Reacția nu a putut fi trimisă.');
        });
      }
    });
    document.getElementById('chat-reply-cancel')?.addEventListener('click', () => {
      this.chatReplyTo = undefined;
      const preview = document.getElementById('chat-reply-preview'); if (preview) preview.style.display = 'none';
    });
    const sendHandler = async () => {
      const input = document.getElementById('chat-text-input') as HTMLInputElement;
      const text = (input?.value || '').trim();
      const fileToSend = this.pendingFileAttachment;

      if (!text && !fileToSend) return;
      if (fileToSend && this.chatReplyTo) { this.showFloatingToast('Trimite răspunsul ca text, apoi atașează fișierul separat.'); return; }
      const replyTo = this.chatReplyTo;

      if (input) input.value = '';
      this.pendingFileAttachment = null;
      const previewStrip = document.getElementById('chat-file-preview-strip');
      if (previewStrip) previewStrip.style.display = 'none';
      const fileInput = document.getElementById('chat-file-input') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';

      if (this.isP2PMode && this.p2pMesh) {
        const timestamp = new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        const p2pMsgId = 'msg-' + Date.now();
        const p2pMsg: ChatMessage = {
          id: p2pMsgId,
          senderName: this.config.displayName,
          senderIdentity: 'self',
          text,
          timestamp,
          isSelf: true,
          ...(replyTo ? { replyTo } : {}),
          file: fileToSend || undefined,
        };
        if (fileToSend) {
          this.p2pMesh.sendFileMessage(fileToSend, text, p2pMsgId);
        } else {
          this.p2pMesh.broadcastDataMessage({ type: 'chat', message: p2pMsg });
        }
        this.messages.push(p2pMsg);
        this.chatReplyTo = undefined;
        const replyPreview = document.getElementById('chat-reply-preview'); if (replyPreview) replyPreview.style.display = 'none';
        this.renderChatMessages();
        return;
      }

      let msg: ChatMessage | null = null;
      if (fileToSend) {
        msg = await this.manager.sendFileMessage(fileToSend, text);
      } else {
        msg = await this.manager.sendChatMessage(text, undefined, replyTo);
      }

      if (msg) {
        this.chatReplyTo = undefined;
        const replyPreview = document.getElementById('chat-reply-preview'); if (replyPreview) replyPreview.style.display = 'none';
        this.messages.push(msg);
        this.renderChatMessages();

        if (this.participants.some(p => p.identity === 'part-ana')) {
          setTimeout(() => {
            this.messages.push({
              id: `msg-rep-${Date.now()}`,
              senderName: 'Ana Popescu',
              senderIdentity: 'part-ana',
              text: fileToSend ? 'Mulțumesc pentru fișier! S-a recepționat fără probleme.' : 'Am recepționat mesajul tău! Calitatea conexiunii este excelentă.',
              timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
              isSelf: false,
            });
            this.renderChatMessages();
          }, 800);
        }
      } else { if (input) input.value = text; this.pendingFileAttachment = fileToSend; this.showFloatingToast('Mesajul nu a putut fi trimis.'); }
    };

    document.getElementById('btn-send-chat')?.addEventListener('click', sendHandler);
    document.getElementById('chat-text-input')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        sendHandler();
      }
    });

    const attachBtn = document.getElementById('btn-attach-file');
    const fileInput = document.getElementById('chat-file-input') as HTMLInputElement | null;
    const previewStrip = document.getElementById('chat-file-preview-strip');
    const previewName = document.getElementById('chat-file-preview-name');
    const previewSize = document.getElementById('chat-file-preview-size');
    const cancelPreviewBtn = document.getElementById('btn-cancel-file-preview');

    attachBtn?.addEventListener('click', () => {
      fileInput?.click();
    });

    fileInput?.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      const MAX_SIZE = 25 * 1024 * 1024; // 25 MB
      if (file.size > MAX_SIZE) {
        this.showFloatingToast('⚠️ Fișierul depășește limita maximă de 25MB pentru conferință.');
        fileInput.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        this.pendingFileAttachment = {
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          dataUrl,
        };

        if (previewStrip && previewName && previewSize) {
          previewName.innerText = file.name;
          const sz = file.size > 1024 * 1024 
            ? (file.size / (1024 * 1024)).toFixed(1) + ' MB'
            : Math.max(1, Math.round(file.size / 1024)) + ' KB';
          previewSize.innerText = `(${sz})`;
          previewStrip.style.display = 'flex';
        }
        document.getElementById('chat-text-input')?.focus();
      };
      reader.readAsDataURL(file);
    });

    cancelPreviewBtn?.addEventListener('click', () => {
      this.pendingFileAttachment = null;
      if (previewStrip) previewStrip.style.display = 'none';
      if (fileInput) fileInput.value = '';
    });

    // Custom Vorbello Leave Conference Modal Handlers
    const leaveModal = document.getElementById('confirm-leave-modal');
    const cancelLeaveBtn = document.getElementById('btn-cancel-leave-modal');
    const confirmLeaveBtn = document.getElementById('btn-confirm-leave-modal');

    document.getElementById('btn-leave-conf')?.addEventListener('click', () => {
      if (leaveModal) {
        leaveModal.style.display = 'flex';
      }
    });

    cancelLeaveBtn?.addEventListener('click', () => {
      if (leaveModal) leaveModal.style.display = 'none';
    });

    leaveModal?.addEventListener('click', (e) => {
      if (e.target === leaveModal) {
        leaveModal.style.display = 'none';
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && leaveModal && leaveModal.style.display === 'flex') {
        leaveModal.style.display = 'none';
      }
    });

    confirmLeaveBtn?.addEventListener('click', () => {
      if (leaveModal) leaveModal.style.display = 'none';
      if (this.isRecording) {
        this.stopRecording();
      }
      this.cleanup();
      this.config.onLeave();
    });

    document.getElementById('btn-end-meeting-for-all')?.addEventListener('click', async () => {
      if (this.endingForAll) return;
      this.endingForAll = true;
      if (this.activePoll && !this.activePoll.isClosed && this.isHost) this.closeActivePoll(true);
      try { await this.config.onEndMeeting?.(); }
      catch (error) { this.endingForAll = false; this.showFloatingToast(error instanceof Error ? error.message : 'Nu s-a putut încheia întâlnirea.'); return; }
      if (leaveModal) leaveModal.style.display = 'none';

      // 1. Broadcast meeting_ended_by_host to all participants
      try {
        await this.manager.sendDataMessage({
          type: 'meeting_ended_by_host',
          hostName: this.config.displayName,
          roomSlug: this.config.roomSlug,
          reason: 'Conferința a fost încheiată de către gazdă pentru toți participanții.',
        });
      } catch (e) {
        console.warn('[ConferenceUI] Broadcast meeting_ended_by_host error:', e);
      }

      // 2. Stop any active recording
      if (this.isRecording) {
        this.stopRecording();
      }

      // 3. Local cleanup and exit
      this.cleanup();
      this.config.onLeave('✅ Ați încheiat ședința pentru toți participanții.');
    });

    // Local & Remote Recording Controls (Zoom Style)
    document.getElementById('btn-toggle-record')?.addEventListener('click', () => {
      if (this.isRecording) {
        this.stopRecording();
      } else if (this.roomRecordingActive) {
        if (this.isHost || this.isAdmin) {
          this.manager.sendDataMessage({
            type: 'recording_command',
            action: 'stop',
            requesterName: this.config.displayName || 'Gazdă',
          }).catch(() => {});
          this.showFloatingToast(`⏹️ Ați solicitat oprirea înregistrării coordonate de ${this.roomRecorderName || 'organizator'}...`);
        } else {
          this.showFloatingToast(`ℹ️ Înregistrarea este gestionată de către ${this.roomRecorderName || 'organizator'}.`);
        }
      } else {
        if (!this.isHost && !this.isAdmin && !this.config.canRecord) {
          this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a înregistra.');
          return;
        }
        this.openRecordingOptionsModal();
      }
    });

    // Interactive Header REC Badge Click to Stop Recording (Host & Recorder)
    document.getElementById('conf-rec-badge')?.addEventListener('click', () => {
      if (this.isRecording) {
        this.stopRecording();
      } else if (this.roomRecordingActive && (this.isHost || this.isAdmin)) {
        this.manager.sendDataMessage({
          type: 'recording_command',
          action: 'stop',
          requesterName: this.config.displayName || 'Gazdă',
        }).catch(() => {});
        this.showFloatingToast(`⏹️ Ați solicitat oprirea înregistrării coordonate de ${this.roomRecorderName || 'organizator'}...`);
      } else if (this.roomRecordingActive) {
        this.showFloatingToast('🔒 Înregistrarea este gestionată de către organizatorul conferinței.');
      }
    });

    // Auto-resume media & wake up spotlight when returning to Vorbello tab in browser
    const resumeSpotlightPlayback = () => {
      this.updateStageLayout();
      const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
      if (spotlightVideo) {
        const screenStream = this.manager.getLocalScreenStream();
        if (this.manager.isScreenShareEnabled && screenStream) {
          this.attachScreenStreamToSpotlight(screenStream, spotlightVideo);
        } else if (spotlightVideo.srcObject) {
          spotlightVideo.play().catch(() => {});
        }
      }
    };
    window.addEventListener('focus', resumeSpotlightPlayback);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        resumeSpotlightPlayback();
      }
    });

    document.getElementById('btn-close-rec-options')?.addEventListener('click', () => {
      this.closeRecordingOptionsModal();
    });

    document.getElementById('btn-cancel-recording')?.addEventListener('click', () => {
      this.closeRecordingOptionsModal();
    });

    // Recording type selection cards (HD 720p 30fps, Full HD 1080p 30fps, Audio HD)
    const videoHdCard = document.getElementById('rec-card-video-hd');
    const videoFhdCard = document.getElementById('rec-card-video-fhd');
    const audioCard = document.getElementById('rec-card-audio');
    const recLayoutSection = document.getElementById('rec-layout-section');

    const selectRecCard = (type: 'video-hd' | 'video-fhd' | 'audio') => {
      this.selectedRecordingType = type;
      videoHdCard?.classList.toggle('selected', type === 'video-hd');
      videoFhdCard?.classList.toggle('selected', type === 'video-fhd');
      audioCard?.classList.toggle('selected', type === 'audio');
      if (recLayoutSection) {
        recLayoutSection.style.display = type === 'audio' ? 'none' : 'flex';
      }
    };

    videoHdCard?.addEventListener('click', () => selectRecCard('video-hd'));
    videoFhdCard?.addEventListener('click', () => selectRecCard('video-fhd'));
    audioCard?.addEventListener('click', () => selectRecCard('audio'));

    // Recording layout selector buttons
    const layoutCompositeBtn = document.getElementById('rec-layout-composite');
    const layoutScreenBtn = document.getElementById('rec-layout-screen');
    const layoutCamerasBtn = document.getElementById('rec-layout-cameras');

    const selectRecLayout = (layout: 'composite' | 'screen_only' | 'cameras_only') => {
      this.selectedRecordingLayout = layout;
      try {
        localStorage.setItem('vorbello_default_auto_record_layout', layout);
      } catch (_) {}
      layoutCompositeBtn?.classList.toggle('selected', layout === 'composite');
      layoutScreenBtn?.classList.toggle('selected', layout === 'screen_only');
      layoutCamerasBtn?.classList.toggle('selected', layout === 'cameras_only');
    };

    layoutCompositeBtn?.addEventListener('click', () => selectRecLayout('composite'));
    layoutScreenBtn?.addEventListener('click', () => selectRecLayout('screen_only'));
    layoutCamerasBtn?.addEventListener('click', () => selectRecLayout('cameras_only'));

    // Recording container selector buttons (WebM vs MP4)
    const containerWebmBtn = document.getElementById('rec-container-webm');
    const containerMp4Btn = document.getElementById('rec-container-mp4');

    const selectRecContainer = (container: 'webm' | 'mp4') => {
      this.selectedRecordingContainer = container;
      try {
        localStorage.setItem('vorbello_default_auto_record_container', container);
      } catch (_) {}
      containerWebmBtn?.classList.toggle('selected', container === 'webm');
      containerMp4Btn?.classList.toggle('selected', container === 'mp4');
    };

    containerWebmBtn?.addEventListener('click', () => selectRecContainer('webm'));
    containerMp4Btn?.addEventListener('click', () => selectRecContainer('mp4'));

    document.getElementById('btn-confirm-start-recording')?.addEventListener('click', async () => {
      this.closeRecordingOptionsModal();
      await this.startRecording(this.selectedRecordingType, this.selectedRecordingContainer);
    });

    document.getElementById('btn-close-rec-success')?.addEventListener('click', () => {
      const modal = document.getElementById('recording-success-modal');
      if (modal) modal.style.display = 'none';
    });

    // --- Whiteboard Listeners ---
    document.getElementById('btn-toggle-whiteboard')?.addEventListener('click', () => {
      this.toggleWhiteboard();
    });

    document.getElementById('btn-wb-close')?.addEventListener('click', () => {
      this.closeWhiteboard(true);
    });

    document.getElementById('btn-wb-clear')?.addEventListener('click', () => {
      this.clearWhiteboard(true);
    });

    document.getElementById('btn-wb-download')?.addEventListener('click', () => {
      this.downloadWhiteboardPNG();
    });

    document.querySelectorAll('.wb-tool-btn[data-tool]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.wb-tool-btn[data-tool]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const tool = btn.getAttribute('data-tool');
        if (tool === 'eraser' || tool === 'pen') {
          this.wbTool = tool;
        }
      });
    });

    document.querySelectorAll('.wb-color-btn[data-color]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.wb-color-btn[data-color]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const color = btn.getAttribute('data-color');
        if (color) {
          this.wbColor = color;
          this.wbTool = 'pen';
          document.querySelectorAll('.wb-tool-btn[data-tool]').forEach(b => {
            if (b.getAttribute('data-tool') === 'pen') b.classList.add('active');
            else b.classList.remove('active');
          });
        }
      });
    });

    // --- Polls Listeners ---
    document.getElementById('btn-open-polls')?.addEventListener('click', () => {
      this.isPollsOpen = !this.isPollsOpen;
      this.isChatOpen = false;
      this.isParticipantsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('btn-create-poll-open')?.addEventListener('click', () => {
      this.openCreatePollModal();
    });

    document.getElementById('btn-close-create-poll')?.addEventListener('click', () => {
      this.closeCreatePollModal();
    });

    document.getElementById('btn-cancel-create-poll')?.addEventListener('click', () => {
      this.closeCreatePollModal();
    });

    document.getElementById('btn-add-poll-option')?.addEventListener('click', () => {
      const inputsList = document.getElementById('poll-options-inputs-list');
      if (inputsList) {
        const count = inputsList.querySelectorAll('.poll-opt-input').length;
        if (count >= 6) {
          alert('Numărul maxim este de 6 opțiuni.');
          return;
        }
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'invite-input-field poll-opt-input';
        input.style.width = '100%';
        input.style.fontFamily = 'inherit';
        input.placeholder = `Opțiunea ${count + 1}`;
        inputsList.appendChild(input);
      }
    });

    document.getElementById('btn-submit-launch-poll')?.addEventListener('click', () => {
      this.submitNewPoll();
    });
  }

  private toggleDrawer() {
    const panel = document.getElementById('side-panel');
    const chatContainer = document.getElementById('chat-container');
    const partContainer = document.getElementById('participants-container');
    const pollsContainer = document.getElementById('polls-container');
    const title = document.getElementById('panel-title');

    if (!panel) return;

    if (this.isChatOpen) {
      this.unreadChatCount = 0;
      this.updateChatBadge();
      const popup = document.getElementById('zoom-chat-preview');
      if (popup) popup.remove();
      panel.style.display = 'flex';
      if (chatContainer) chatContainer.style.display = 'flex';
      if (partContainer) partContainer.style.display = 'none';
      if (pollsContainer) pollsContainer.style.display = 'none';
      if (title) title.innerText = 'Chat de Cameră';
      this.renderChatMessages();
      const chatInput = document.getElementById('chat-text-input') as HTMLInputElement | null;
      if (chatInput) {
        setTimeout(() => chatInput.focus(), 80);
      }
    } else if (this.isParticipantsOpen) {
      panel.style.display = 'flex';
      if (chatContainer) chatContainer.style.display = 'none';
      if (partContainer) partContainer.style.display = 'flex';
      if (pollsContainer) pollsContainer.style.display = 'none';
      if (title) title.innerText = `Participanți Conectați (${this.participants.length})`;
      const inviteUrlInput = document.getElementById('input-invite-room-url') as HTMLInputElement | null;
      if (inviteUrlInput) inviteUrlInput.value = this.getRoomInviteUrl();
      this.renderParticipantsList();
    } else if (this.isPollsOpen) {
      panel.style.display = 'flex';
      if (chatContainer) chatContainer.style.display = 'none';
      if (partContainer) partContainer.style.display = 'none';
      if (pollsContainer) pollsContainer.style.display = 'flex';
      if (title) title.innerText = 'Sondaje Live în Sală';
      const floatPopup = document.getElementById('live-poll-popup');
      if (floatPopup) floatPopup.style.display = 'none';
      this.renderPollsView();
    } else {
      panel.style.display = 'none';
      if (pollsContainer) pollsContainer.style.display = 'none';
    }
  }

  private attachDeviceSettingsListeners() {
    const modal = document.getElementById('device-settings-modal');
    const openBtn = document.getElementById('btn-open-devices');
    const closeBtn = document.getElementById('btn-close-device-settings');
    const saveBtn = document.getElementById('btn-save-device-settings');
    const testSpeakerBtn = document.getElementById('btn-test-speakers');
    const micSelect = document.getElementById('conf-select-mic') as HTMLSelectElement | null;
    const camSelect = document.getElementById('conf-select-cam') as HTMLSelectElement | null;
    const speakerSelect = document.getElementById('conf-select-speaker') as HTMLSelectElement | null;

    const quickAudio = document.getElementById('quick-audio-menu');
    const quickCamera = document.getElementById('quick-camera-menu');
    const closeQuick = () => {
      [quickAudio, quickCamera].forEach(menu => { if (menu) menu.style.display = 'none'; });
      ['btn-quick-audio', 'btn-quick-camera'].forEach(id => document.getElementById(id)?.setAttribute('aria-expanded', 'false'));
    };
    const openQuick = async (kind: 'audio' | 'camera') => {
      const menu = kind === 'audio' ? quickAudio : quickCamera;
      const trigger = document.getElementById(kind === 'audio' ? 'btn-quick-audio' : 'btn-quick-camera');
      if (!menu) return;
      const wasOpen = menu.style.display !== 'none'; closeQuick(); if (wasOpen) return;
      menu.style.display = 'block'; trigger?.setAttribute('aria-expanded', 'true');
      menu.textContent = 'Se caută dispozitive…';
      try {
        const devices = await LiveKitClientManager.getAvailableDevices();
        if (menu.style.display === 'none') return;
        menu.replaceChildren();
        const addGroup = (title: string, values: MediaDeviceInfo[], deviceKind: MediaDeviceKind, key: string) => {
          const heading = document.createElement('strong'); heading.textContent = title; menu.append(heading);
          if (!values.length) { const empty = document.createElement('strong'); empty.textContent = 'Niciun dispozitiv disponibil'; menu.append(empty); }
          values.forEach((device, index) => {
            const button = document.createElement('button'); button.type = 'button';
            button.textContent = `${device.deviceId === localStorage.getItem(key) ? '✓ ' : ''}${device.label || `Dispozitiv ${index + 1}`}`;
            button.addEventListener('click', async () => {
              const enabled = deviceKind === 'audioinput' ? this.manager.isMicEnabled : deviceKind === 'videoinput' ? this.manager.isCameraEnabled : true;
              if (enabled && !await this.manager.switchDevice(deviceKind, device.deviceId)) { this.showFloatingToast('Dispozitivul nu a putut fi schimbat.'); return; }
              localStorage.setItem(key, device.deviceId);
              if (enabled && deviceKind === 'videoinput') this.refreshCameraAfterSwitch();
              closeQuick();
              this.showFloatingToast('Dispozitiv selectat.');
            });
            menu.append(button);
          });
        };
        if (kind === 'audio') { addGroup('Microfon', devices.audioInputs, 'audioinput', 'vorbello_selected_mic'); addGroup('Difuzor', devices.audioOutputs, 'audiooutput', 'vorbello_selected_speaker'); }
        else addGroup('Cameră video', devices.videoInputs, 'videoinput', 'vorbello_selected_cam');
        const settings = document.createElement('button'); settings.type = 'button'; settings.textContent = 'Mai multe setări…';
        settings.addEventListener('click', () => { closeQuick(); document.getElementById('btn-open-devices')?.click(); }); menu.append(settings);
      } catch (_) { menu.textContent = 'Dispozitivele nu sunt disponibile.'; }
    };
    document.getElementById('btn-quick-audio')?.addEventListener('click', e => { e.stopPropagation(); void openQuick('audio'); });
    document.getElementById('btn-quick-camera')?.addEventListener('click', e => { e.stopPropagation(); void openQuick('camera'); });
    document.addEventListener('click', e => { if (!(e.target as Element)?.closest('.quick-device-wrapper')) closeQuick(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeQuick(); });

    const populateDevices = async () => {
      const devices = await LiveKitClientManager.getAvailableDevices();
      let currentMic = localStorage.getItem('vorbello_selected_mic') || '';
      let currentCam = localStorage.getItem('vorbello_selected_cam') || '';
      const currentSpeaker = localStorage.getItem('vorbello_selected_speaker') || '';

      const isBadCam = (d: MediaDeviceInfo | undefined) => {
        if (!d) return true;
        const l = (d.label || '').toLowerCase();
        return l.includes('epson') || l.includes('scanner') || l.includes('wf-') || l.includes('print');
      };
      const usableCams = devices.videoInputs.filter(d => !isBadCam(d));
      const videoList = usableCams.length > 0 ? usableCams : devices.videoInputs;

      const selectedCamObj = devices.videoInputs.find(d => d.deviceId === currentCam);
      if ((!currentCam || isBadCam(selectedCamObj)) && videoList.length > 0) {
        currentCam = videoList[0].deviceId;
        localStorage.setItem('vorbello_selected_cam', currentCam);
      }

      if (!devices.audioInputs.some(d => d.deviceId === currentMic) && devices.audioInputs.length > 0) {
        // Prioritize Yeti Nano
        const bestMic = devices.audioInputs.find(d => {
          const l = (d.label || '').toLowerCase();
          return l.includes('yeti') || l.includes('nano');
        }) || devices.audioInputs[0];
        currentMic = bestMic.deviceId;
        localStorage.setItem('vorbello_selected_mic', currentMic);
      }

      if (micSelect) {
        micSelect.innerHTML = devices.audioInputs.length > 0
          ? devices.audioInputs.map(d => `<option value="${d.deviceId}" ${d.deviceId === currentMic ? 'selected' : ''}>${d.label || 'Microfon'}</option>`).join('')
          : '<option value="">Microfon implicit de sistem</option>';
      }

      if (camSelect) {
        camSelect.innerHTML = videoList.length > 0
          ? videoList.map(d => `<option value="${d.deviceId}" ${d.deviceId === currentCam ? 'selected' : ''}>${d.label || 'Cameră Web'}</option>`).join('')
          : '<option value="">Cameră implicită de sistem</option>';
      }

      if (speakerSelect) {
        speakerSelect.innerHTML = devices.audioOutputs.length > 0
          ? devices.audioOutputs.map((d, i) => `<option value="${d.deviceId}" ${d.deviceId === currentSpeaker || (!currentSpeaker && i === 0) ? 'selected' : ''}>${d.label || `Difuzor / Căști ${i + 1}`}</option>`).join('')
          : '<option value="">Dispozitiv audio implicit de sistem</option>';
      }

      // Highlight selected video effect
      const currentEff = this.selectedVideoEffect;
      modal?.querySelectorAll('.effect-btn').forEach(btn => {
        const eff = btn.getAttribute('data-effect');
        if (eff === currentEff || (currentEff === 'background' && btn.id === 'btn-select-background')) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
      const effectLabel = document.getElementById('label-active-effect');
      if (effectLabel) {
        effectLabel.innerText = currentEff === 'background' ? 'Fundal propriu' : currentEff === 'none' ? 'Normal' : (currentEff === 'blur-soft' ? 'Blur Fin' : (currentEff === 'blur-heavy' ? 'Blur Intens' : (currentEff === 'studio' ? 'Studio local' : 'Ambient local')));
      }

      const exposure = document.getElementById('camera-exposure') as HTMLInputElement | null;
      const exposureStatus = document.getElementById('camera-exposure-status');
      const camTrack = this.manager.getRoom()?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
      const track = camTrack?.getProcessor() ? undefined : camTrack?.mediaStreamTrack;
      const capabilities = track?.getCapabilities?.() as (MediaTrackCapabilities & { exposureCompensation?: { min: number; max: number; step?: number } }) | undefined;
      const range = capabilities?.exposureCompensation;
      if (exposure) {
        exposure.disabled = !range || !track;
        if (range) {
          exposure.min = String(range.min); exposure.max = String(range.max); exposure.step = String(range.step || 0.1);
          exposure.value = String((track?.getSettings() as MediaTrackSettings & { exposureCompensation?: number })?.exposureCompensation ?? 0);
        }
      }
      if (exposureStatus) exposureStatus.textContent = range && track ? 'Expunere reglată de cameră; afectează imaginea transmisă.' : 'Reglarea expunerii nu este disponibilă pentru camera sau efectul actual.';

      const activeMic = micSelect?.value || currentMic;
      this.startVuMeter(activeMic);
    };

    // Effect buttons selection in modal
    modal?.querySelectorAll('.effect-btn:not(#btn-select-background)').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const eff = btn.getAttribute('data-effect') || 'none';
        if (!await this.applyVideoEffect(eff)) return;
        modal.querySelectorAll('.effect-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const effectLabel = document.getElementById('label-active-effect');
        if (effectLabel) {
          effectLabel.innerText = eff === 'none' ? 'Normal' : (eff === 'blur-soft' ? 'Blur Fin' : (eff === 'blur-heavy' ? 'Blur Intens' : (eff === 'studio' ? 'Studio HD' : 'Ambient')));
        }
      });
    });

    const backgroundInput = document.getElementById('background-image-input') as HTMLInputElement | null;
    document.getElementById('btn-select-background')?.addEventListener('click', () => backgroundInput?.click());
    backgroundInput?.addEventListener('change', async () => {
      const file = backgroundInput.files?.[0];
      backgroundInput.value = '';
      if (!file) return;
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 8 * 1024 * 1024) {
        this.showFloatingToast('Alege o imagine PNG, JPEG sau WebP de cel mult 8 MB.'); return;
      }
      const url = URL.createObjectURL(file);
      if (!await this.applyVideoEffect('background', url)) { URL.revokeObjectURL(url); return; }
      if (this.backgroundImageUrl) URL.revokeObjectURL(this.backgroundImageUrl);
      this.backgroundImageUrl = url;
      modal?.querySelectorAll('.effect-btn').forEach(b => b.classList.remove('active'));
      document.getElementById('btn-select-background')?.classList.add('active');
      const label = document.getElementById('label-active-effect'); if (label) label.innerText = 'Fundal propriu';
    });
    const exposure = document.getElementById('camera-exposure') as HTMLInputElement | null;
    exposure?.addEventListener('change', async () => {
      const camera = this.manager.getRoom()?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
      if (!camera || camera.getProcessor()) return;
      try {
        await camera.mediaStreamTrack.applyConstraints({ advanced: [{ exposureCompensation: Number(exposure.value) } as MediaTrackConstraintSet] });
      } catch (_) { this.showFloatingToast('Camera nu a acceptat reglarea expunerii.'); }
    });

    openBtn?.addEventListener('click', async () => {
      if (modal) {
        modal.style.display = 'flex';
        await populateDevices();
      }
    });

    const closeModal = () => {
      if (modal) modal.style.display = 'none';
      this.stopVuMeter();
    };

    closeBtn?.addEventListener('click', closeModal);

    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closeModal();
      }
    });

    micSelect?.addEventListener('change', () => {
      const selectedMic = micSelect.value;
      this.startVuMeter(selectedMic);
    });

    testSpeakerBtn?.addEventListener('click', () => {
      this.playTestSound();
    });

    saveBtn?.addEventListener('click', async () => {
      const micVal = micSelect?.value || '';
      const camVal = camSelect?.value || '';
      const speakerVal = speakerSelect?.value || '';

      (saveBtn as HTMLButtonElement).disabled = true;
      let failed = false;
      try {
        const selections: Array<[MediaDeviceKind, string, string, boolean]> = [
          ['audioinput', micVal, 'vorbello_selected_mic', this.manager.isMicEnabled],
          ['videoinput', camVal, 'vorbello_selected_cam', this.manager.isCameraEnabled],
          ['audiooutput', speakerVal, 'vorbello_selected_speaker', true],
        ];
        for (const [kind, value, key, enabled] of selections) {
          if (!value) continue;
          if (enabled && !await this.manager.switchDevice(kind, value)) { failed = true; continue; }
          localStorage.setItem(key, value);
          if (enabled && kind === 'videoinput') { this.refreshCameraAfterSwitch(); }
        }
        if (failed) this.showFloatingToast('Unele dispozitive nu au putut fi schimbate. Verifica permisiunile si incearca din nou.');
        else { closeModal(); this.showFloatingToast('Setarile dispozitivelor au fost salvate.'); }
      } finally { (saveBtn as HTMLButtonElement).disabled = false; }
    });

    const camQHd = document.getElementById('btn-cam-quality-hd');
    const camQFhd = document.getElementById('btn-cam-quality-fullhd');
    const camQLabel = document.getElementById('label-camera-quality');

    const updateCamQualityUi = () => {
      const isFhd = this.selectedCameraQuality === 'fullhd';
      if (camQLabel) camQLabel.innerText = isFhd ? 'Full HD 1080p' : 'HD 720p';
      if (isFhd) {
        camQFhd?.style.setProperty('background', '#3b82f6');
        camQFhd?.style.setProperty('color', '#fff');
        camQHd?.style.setProperty('background', 'rgba(15,23,42,0.7)');
        camQHd?.style.setProperty('color', '#94a3b8');
      } else {
        camQHd?.style.setProperty('background', '#3b82f6');
        camQHd?.style.setProperty('color', '#fff');
        camQFhd?.style.setProperty('background', 'rgba(15,23,42,0.7)');
        camQFhd?.style.setProperty('color', '#94a3b8');
      }
    };

    camQHd?.addEventListener('click', async () => {
      this.selectedCameraQuality = 'hd';
      if (typeof localStorage !== 'undefined') localStorage.setItem('vorbello_camera_quality', 'hd');
      updateCamQualityUi();
      if (this.manager.isCameraEnabled) {
        await this.manager.enableCamera(false);
        await new Promise(r => setTimeout(r, 60));
        await this.manager.enableCamera(true, 'hd');
        this.updateSelfTile();
        this.updateStageLayout();
      }
    });

    camQFhd?.addEventListener('click', async () => {
      this.selectedCameraQuality = 'fullhd';
      if (typeof localStorage !== 'undefined') localStorage.setItem('vorbello_camera_quality', 'fullhd');
      updateCamQualityUi();
      if (this.manager.isCameraEnabled) {
        await this.manager.enableCamera(false);
        await new Promise(r => setTimeout(r, 60));
        await this.manager.enableCamera(true, 'fullhd');
        this.updateSelfTile();
        this.updateStageLayout();
      }
    });
    updateCamQualityUi();

    const screenQHd = document.getElementById('btn-modal-share-quality-hd');
    const screenQFhd = document.getElementById('btn-modal-share-quality-fullhd');
    const screenQLabel = document.getElementById('label-screen-quality');

    const updateModalScreenQualityUi = () => {
      const isFhd = this.selectedScreenQuality === 'fullhd';
      if (screenQLabel) screenQLabel.innerText = isFhd ? 'Full HD 1080p' : 'HD 720p';
      if (isFhd) {
        screenQFhd?.style.setProperty('background', '#3b82f6');
        screenQFhd?.style.setProperty('color', '#fff');
        screenQHd?.style.setProperty('background', 'rgba(15,23,42,0.7)');
        screenQHd?.style.setProperty('color', '#94a3b8');
      } else {
        screenQHd?.style.setProperty('background', '#3b82f6');
        screenQHd?.style.setProperty('color', '#fff');
        screenQFhd?.style.setProperty('background', 'rgba(15,23,42,0.7)');
        screenQFhd?.style.setProperty('color', '#94a3b8');
      }
    };

    screenQHd?.addEventListener('click', () => {
      this.selectedScreenQuality = 'hd';
      if (typeof localStorage !== 'undefined') localStorage.setItem('vorbello_screen_quality', 'hd');
      updateModalScreenQualityUi();
    });

    screenQFhd?.addEventListener('click', () => {
      this.selectedScreenQuality = 'fullhd';
      if (typeof localStorage !== 'undefined') localStorage.setItem('vorbello_screen_quality', 'fullhd');
      updateModalScreenQualityUi();
    });
    updateModalScreenQualityUi();
  }

  private attachVersionModalListeners() {
    const modal = document.getElementById('version-info-modal');
    const openBadge = document.getElementById('btn-conf-version-panel');
    const openHeaderPill = document.getElementById('btn-header-version');
    const closeBtn = document.getElementById('btn-close-version-info');
    const okBtn = document.getElementById('btn-version-ok');
    const openDevicesFromVersion = document.getElementById('btn-version-open-devices');
    const camNameEl = document.getElementById('version-modal-cam-name');
    const micNameEl = document.getElementById('version-modal-mic-name');

    const updateDeviceNames = async () => {
      try {
        const devices = await LiveKitClientManager.getAvailableDevices();
        const currentCamId = localStorage.getItem('vorbello_selected_cam');
        const currentMicId = localStorage.getItem('vorbello_selected_mic');

        const isBadCam = (d: MediaDeviceInfo | undefined) => {
          if (!d) return true;
          const l = (d.label || '').toLowerCase();
          return l.includes('epson') || l.includes('scanner') || l.includes('wf-') || l.includes('print');
        };

        const activeCam = devices.videoInputs.find(d => d.deviceId === currentCamId) || devices.videoInputs.find(d => !isBadCam(d)) || devices.videoInputs[0];
        const activeMic = devices.audioInputs.find(d => d.deviceId === currentMicId) || devices.audioInputs.find(d => (d.label || '').toLowerCase().includes('yeti')) || devices.audioInputs[0];

        if (camNameEl) {
          camNameEl.textContent = activeCam?.label || 'Angetube Live Camera (HD)';
        }
        if (micNameEl) {
          micNameEl.textContent = activeMic?.label || 'Microfono (Yeti Nano)';
        }
      } catch (e) {
        if (camNameEl) camNameEl.textContent = 'Hardware webcam detectat';
        if (micNameEl) micNameEl.textContent = 'Hardware microfon detectat';
      }
    };

    const openModal = async () => {
      if (modal) {
        modal.style.display = 'flex';
        await updateDeviceNames();
      }
    };

    const closeModal = () => {
      if (modal) modal.style.display = 'none';
    };

    openBadge?.addEventListener('click', openModal);
    openHeaderPill?.addEventListener('click', openModal);
    closeBtn?.addEventListener('click', closeModal);
    okBtn?.addEventListener('click', closeModal);

    openDevicesFromVersion?.addEventListener('click', () => {
      closeModal();
      const openDevBtn = document.getElementById('btn-open-devices');
      openDevBtn?.click();
    });

    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closeModal();
      }
    });
  }

  private async startVuMeter(deviceId?: string) {
    this.stopVuMeter();
    try {
      const constraints: MediaStreamConstraints = {
        audio: (deviceId && deviceId.trim()) ? { deviceId: { exact: deviceId } } : true,
        video: false,
      };
      this.vuMediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.vuAudioContext = new AudioCtx();
      const source = this.vuAudioContext.createMediaStreamSource(this.vuMediaStream);
      const analyser = this.vuAudioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const bar = document.getElementById('device-vu-meter-bar');

      const updateMeter = () => {
        if (!this.vuAudioContext) return;
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const avg = sum / bufferLength;
        const percent = Math.min(100, Math.round((avg / 128) * 100 * 1.5));
        if (bar) {
          bar.style.width = `${percent}%`;
        }
        this.vuAnimationId = requestAnimationFrame(updateMeter);
      };

      updateMeter();
    } catch (e) {
      console.warn('[ConferenceUI] startVuMeter error:', e);
    }
  }

  private stopVuMeter() {
    if (this.vuAnimationId !== null) {
      cancelAnimationFrame(this.vuAnimationId);
      this.vuAnimationId = null;
    }
    if (this.vuMediaStream) {
      this.vuMediaStream.getTracks().forEach(t => t.stop());
      this.vuMediaStream = null;
    }
    if (this.vuAudioContext) {
      this.vuAudioContext.close().catch(() => {});
      this.vuAudioContext = null;
    }
    const bar = document.getElementById('device-vu-meter-bar');
    if (bar) bar.style.width = '0%';
  }

  private playTestSound() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      // Two-tone pleasant test chime: 523.25Hz (C5) and 659.25Hz (E5)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523.25, now);
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.5);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(659.25, now + 0.15);
      gain2.gain.setValueAtTime(0.35, now + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.15);
      osc2.stop(now + 0.7);
    } catch (e) {
      console.warn('[ConferenceUI] playTestSound error:', e);
    }
  }

  private playChatChime() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const now = ctx.currentTime;
      // High-pitched soft Zoom-style pop/chime: 880Hz (A5) then 1174.66Hz (D6)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now);
      gain1.gain.setValueAtTime(0.18, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.12);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1174.66, now + 0.08);
      gain2.gain.setValueAtTime(0.22, now + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.08);
      osc2.stop(now + 0.35);

      setTimeout(() => {
        try { ctx.close(); } catch (_) {}
      }, 500);
    } catch (e) {
      console.warn('[ConferenceUI] playChatChime error:', e);
    }
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
  private escapeAttribute(str: string): string {
    return this.escapeHtml(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  private showZoomChatNotification(msg: ChatMessage) {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    // The local recording is composed from video tracks, not from this on-screen popup.
    if (isMobile) {
      return;
    }

    let popup = document.getElementById('zoom-chat-preview');
    if (!popup) {
      popup = document.createElement('div');
      popup.id = 'zoom-chat-preview';
      popup.className = 'zoom-chat-preview-popup';
      document.body.appendChild(popup);
    }

    const initial = (msg.senderName || 'P').trim().charAt(0).toUpperCase();
    const cleanText = msg.text.length > 80 ? msg.text.slice(0, 80) + '...' : msg.text;

    popup.innerHTML = `
      <div class="zoom-preview-avatar">${this.escapeHtml(initial)}</div>
      <div class="zoom-preview-body">
        <div class="zoom-preview-header">
          <span class="zoom-preview-sender">${this.escapeHtml(msg.senderName)}</span>
          <span class="zoom-preview-time">${this.escapeHtml(formatChatTime(msg.timestamp))}</span>
        </div>
        <div class="zoom-preview-text">${this.escapeHtml(cleanText)}</div>
        <div class="zoom-preview-hint">💬 Clic pentru a deschide chatul</div>
      </div>
      <button class="zoom-preview-close" title="Închide">&times;</button>
    `;

    popup.style.display = 'flex';
    popup.style.opacity = '1';
    popup.style.transform = '';

    // Click handler to open chat
    popup.onclick = (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('zoom-preview-close')) {
        e.stopPropagation();
        popup?.remove();
        return;
      }
      this.isChatOpen = true;
      this.isParticipantsOpen = false;
      this.toggleDrawer();
      popup?.remove();
    };

    // Auto-remove after 6 seconds
    const existingTimeout = (popup as any)._timeout;
    if (existingTimeout) clearTimeout(existingTimeout);
    (popup as any)._timeout = setTimeout(() => {
      if (popup && popup.parentNode) {
        popup.style.opacity = '0';
        popup.style.transform = 'translateX(-50%) translateY(10px)';
        setTimeout(() => popup?.remove(), 250);
      }
    }, 6000);
  }

  private async kickParticipant(identity: string): Promise<void> {
    if (!identity || identity === 'self') return;
    const targetPart = this.participants.find(p => p.identity === identity);
    if (targetPart?.role === 'admin') { this.showFloatingToast('Administratorul nu poate fi eliminat din acest meniu.'); return; }
    const targetName = targetPart?.name || '';

    console.info('[LiveKit UI] Moderation kick initiated for:', identity, targetName);

    try {
      await this.manager.sendDataMessage({
        type: 'moderation',
        action: 'kick',
        targetIdentity: identity,
        targetName: targetName,
      });
      setTimeout(() => {
        this.manager.sendDataMessage({
          type: 'moderation',
          action: 'kick',
          targetIdentity: identity,
          targetName: targetName,
        });
      }, 100);
    } catch (e) {
      console.warn('[LiveKit UI] sendDataMessage kick error:', e);
    }

    this.participants = this.participants.filter(p => p.identity !== identity);
    const audioEl = document.getElementById(`audio-remote-${identity}`);
    if (audioEl) audioEl.remove();
    const tile = document.getElementById(`tile-${identity}`);
    if (tile) tile.remove();
    if (this.pinnedIdentity === identity) this.pinnedIdentity = null;

    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();

    this.showFloatingToast(`⛔ ${targetName || 'Participantul'} a fost eliminat din sală.`);
  }

  private showFloatingToast(text: string, durationMs: number = 3500) {
    let toast = document.getElementById('conf-floating-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'conf-floating-toast';
      toast.style.cssText = 'position:fixed; top:2rem; left:50%; transform:translateX(-50%); background:rgba(15,23,42,0.92); border:1px solid rgba(255,255,255,0.2); backdrop-filter:blur(16px); color:#fff; padding:0.6rem 1.4rem; border-radius:12px; font-size:0.85rem; font-weight:600; z-index:99999; box-shadow:0 10px 30px rgba(0,0,0,0.6); transition:all 0.3s ease; pointer-events:none;';
      document.body.appendChild(toast);
    }
    toast.innerText = text;
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
    setTimeout(() => {
      if (toast) {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(-10px)';
      }
    }, durationMs);
  }

  public triggerFloatingReaction(emoji: string, senderIdentity: string, senderName: string) {
    // 1. Zoom/Teams style floating reaction stream on bottom-right
    const stream = document.getElementById('floating-reactions-stream');
    if (stream) {
      const bubble = document.createElement('div');
      bubble.className = 'reaction-bubble-float';
      bubble.innerHTML = `
        <span class="reaction-bubble-emoji">${emoji}</span>
        <span class="reaction-bubble-name">${senderName}</span>
      `;
      stream.appendChild(bubble);
      setTimeout(() => {
        bubble.remove();
      }, 3300);
    }

    // 2. Sender's tile reaction badge popup
    const tile = document.getElementById(`tile-${senderIdentity}`);
    if (tile) {
      const tileBadge = document.createElement('div');
      tileBadge.className = 'tile-reaction-badge';
      tileBadge.textContent = emoji;
      tile.appendChild(tileBadge);
      setTimeout(() => {
        tileBadge.remove();
      }, 2500);
    }
  }

  // Local Meeting Recording Methods (Zoom Style - Host Only)
  private openRecordingOptionsModal() {
    if (!this.isHost && !this.config.canRecord) {
      this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a gestiona înregistrarea.');
      return;
    }
    if (this.isRecording || this.roomRecordingActive) {
      this.stopRecording();
      return;
    }
    const modal = document.getElementById('recording-options-modal');
    if (modal) {
      this.selectedRecordingLayout = 'composite';
      const layoutCompositeBtn = document.getElementById('rec-layout-composite');
      const layoutScreenBtn = document.getElementById('rec-layout-screen');
      const layoutCamerasBtn = document.getElementById('rec-layout-cameras');
      layoutCompositeBtn?.classList.add('selected');
      layoutScreenBtn?.classList.remove('selected');
      layoutCamerasBtn?.classList.remove('selected');
      modal.style.display = 'flex';
    }
  }

  private closeRecordingOptionsModal() {
    const modal = document.getElementById('recording-options-modal');
    if (modal) modal.style.display = 'none';
  }

  private syncRecordingAudioInputs() {
    if (!this.recordingAudioCtx || !this.recordingAudioDest || this.recordingAudioCtx.state === 'closed') return;

    // 1. Sync local microphone track (LiveKit publication track or local mic stream track)
    try {
      const room = this.manager.getRoom();
      const localMicPub = room?.localParticipant?.getTrackPublication(Track.Source.Microphone);
      const mst = (localMicPub?.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
      const liveMicTrack = (mst && mst.readyState === 'live')
        ? mst
        : this.manager.getLocalMicStream()?.getAudioTracks().find(t => t.readyState === 'live');

      if (liveMicTrack && liveMicTrack !== this.recordingLocalMicTrack) {
        if (this.recordingLocalMicSource) {
          try { this.recordingLocalMicSource.disconnect(); } catch (_) {}
        }
        const micStream = new MediaStream([liveMicTrack]);
        const micSource = this.recordingAudioCtx.createMediaStreamSource(micStream);
        const micGain = this.recordingAudioCtx.createGain();
        micGain.gain.value = 1.0;
        micSource.connect(micGain);
        micGain.connect(this.recordingAudioDest);

        this.recordingLocalMicSource = micSource;
        this.recordingLocalMicTrack = liveMicTrack;
        this.recordingAudioNodes.push(micSource, micGain);
        console.info('[Recording] Connected active local microphone track:', liveMicTrack.label || liveMicTrack.id);
      }
    } catch (e) {
      console.warn('[Recording] Local mic sync warning:', e);
    }

    // 2. Sync local screen share audio
    try {
      const localScreenStream = this.manager.getLocalScreenStream();
      const screenAudioTrack = localScreenStream?.getAudioTracks().find(t => t.readyState === 'live');
      if (screenAudioTrack && screenAudioTrack !== this.recordingScreenAudioTrack) {
        if (this.recordingScreenAudioSource) {
          try { this.recordingScreenAudioSource.disconnect(); } catch (_) {}
        }
        const screenStream = new MediaStream([screenAudioTrack]);
        const screenSource = this.recordingAudioCtx.createMediaStreamSource(screenStream);
        const screenGain = this.recordingAudioCtx.createGain();
        screenGain.gain.value = 1.0;
        screenSource.connect(screenGain);
        screenGain.connect(this.recordingAudioDest);

        this.recordingScreenAudioSource = screenSource;
        this.recordingScreenAudioTrack = screenAudioTrack;
        this.recordingAudioNodes.push(screenSource, screenGain);
        console.info('[Recording] Connected local screen share audio');
      }
    } catch (e) {
      console.warn('[Recording] Local screen audio sync warning:', e);
    }

    // 3. Sync all remote participants' audio elements
    try {
      document.querySelectorAll('audio[id^="audio-remote-"]').forEach(el => {
        const audioEl = el as HTMLAudioElement;
        if (audioEl.srcObject && (audioEl.srcObject as MediaStream).getAudioTracks().length > 0) {
          const remoteStream = audioEl.srcObject as MediaStream;
          const track = remoteStream.getAudioTracks().find(t => t.readyState === 'live');
          if (track && !(audioEl as any)._vorbelloRecAttached) {
            try {
              const remoteSource = this.recordingAudioCtx!.createMediaStreamSource(remoteStream);
              const remoteGain = this.recordingAudioCtx!.createGain();
              remoteGain.gain.value = 1.0;
              remoteSource.connect(remoteGain);
              remoteGain.connect(this.recordingAudioDest!);
              (audioEl as any)._vorbelloRecAttached = true;
              this.recordingAudioNodes.push(remoteSource, remoteGain);
              console.info('[Recording] Connected remote participant audio element:', audioEl.id);
            } catch (err) {
              console.warn('[Recording] Remote participant audio attach warning:', err);
            }
          }
        }
      });
    } catch (e) {
      console.warn('[Recording] Remote audio elements sync warning:', e);
    }
  }

  private async startRecording(type: 'video-hd' | 'video-fhd' | 'audio', container: 'webm' | 'mp4' = 'webm') {
    if (!this.isHost && !this.config.canRecord) {
      this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a înregistra.');
      return;
    }
    if (this.isRecording) return;
    if (this.roomRecordingActive && !this.isRecording) {
      this.showFloatingToast(`ℹ️ Sala este deja înregistrată pe celălalt dispozitiv (${this.roomRecorderName || 'Admin'}).`);
      return;
    }

    try {
      // 1. Build comprehensive audio mix of all conference participants (local + remote)
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const recAudioCtx = new AudioCtx({ sampleRate: 48000 });
      this.recordingAudioCtx = recAudioCtx;
      if (recAudioCtx.state === 'suspended') {
        await recAudioCtx.resume().catch(() => {});
      }
      const audioDest = recAudioCtx.createMediaStreamDestination();
      this.recordingAudioDest = audioDest;
      this.recordingAudioNodes = [];

      // Keep Web Audio clock running continuously with a persistent carrier oscillator
      try {
        const silentOsc = recAudioCtx.createOscillator();
        const silentGain = recAudioCtx.createGain();
        silentGain.gain.value = 0.00001;
        silentOsc.connect(silentGain);
        silentGain.connect(audioDest);
        silentOsc.start();
        this.recordingAudioNodes.push(silentOsc, silentGain);
      } catch (oscErr) {
        console.warn('[Recording] Silent oscillator setup error:', oscErr);
      }

      // Initial synchronization of all active audio inputs
      this.syncRecordingAudioInputs();

      // Continuous audio synchronization interval (every 1.5s) to capture unmutes and new participants
      if (this.recordingAudioSyncInterval) clearInterval(this.recordingAudioSyncInterval);
      this.recordingAudioSyncInterval = setInterval(() => {
        if (this.isRecording) {
          this.syncRecordingAudioInputs();
        }
      }, 1500);

      if (type === 'video-hd' || type === 'video-fhd') {
        const quality: 'hd' | 'fhd' = type === 'video-fhd' ? 'fhd' : 'hd';
        this.recordingQuality = quality;
        let videoTrack: MediaStreamTrack | null = null;

        // 1. Direct hardware camera track for cameras_only courses layout (isolated clone)
        if (this.selectedRecordingLayout === 'cameras_only') {
          const localCamStream = this.manager.getLocalCameraStream();
          if (localCamStream && localCamStream.getVideoTracks().length > 0 && localCamStream.getVideoTracks()[0].readyState === 'live') {
            videoTrack = localCamStream.getVideoTracks()[0].clone();
            console.info('[Recording] Using isolated cloned camera track for pristine courses recording (0 canvas latency)');
          }
        }

        // 2. Direct Conference Composite Video Track at requested resolution & layout @ 30 fps
        if (!videoTrack) {
          videoTrack = this.createConferenceCompositeTrack(quality, this.selectedRecordingLayout);
        }

        // 3. Fallback to local screen share if active
        if (!videoTrack && this.manager.isScreenShareEnabled && this.manager.getLocalScreenStream()) {
          videoTrack = this.manager.getLocalScreenStream()!.getVideoTracks()[0] || null;
        }

        // Fallback to local or remote camera track directly if canvas couldn't start
        if (!videoTrack) {
          const localCamStream = this.manager.getLocalCameraStream();
          if (localCamStream && localCamStream.getVideoTracks().length > 0) {
            videoTrack = localCamStream.getVideoTracks()[0];
          }
        }
        if (!videoTrack) {
          const room = this.manager.getRoom();
          if (room && room.remoteParticipants) {
            for (const rp of room.remoteParticipants.values()) {
              const camPub = rp.getTrackPublication(Track.Source.Camera);
              if (camPub && camPub.track && camPub.track.mediaStreamTrack && camPub.track.mediaStreamTrack.readyState === 'live') {
                videoTrack = camPub.track.mediaStreamTrack;
                break;
              }
            }
          }
        }

        if (videoTrack) {
          videoTrack.onended = () => {
            if (this.isRecording) {
              console.info('[Recording] Captured video track ended, stopping recording...');
              this.stopRecording();
            }
          };

          const combinedStream = new MediaStream([videoTrack, ...audioDest.stream.getAudioTracks()]);
          this.startRecordingStream(combinedStream, 'video', quality, container);
        } else {
          // Fallback to audio if absolutely no video track could be acquired
          this.showFloatingToast('ℹ️ Nicio cameră activă detectată. Se înregistrează în format Audio HD.');
          this.startRecordingStream(new MediaStream(audioDest.stream.getAudioTracks()), 'audio', 'hd', container);
        }
      } else {
        // Audio HD Only
        this.recordingQuality = 'hd';
        this.startRecordingStream(new MediaStream(audioDest.stream.getAudioTracks()), 'audio', 'hd', container);
      }
    } catch (err: any) {
      console.error('[Recording] startRecording error:', err);
      this.showFloatingToast(`❌ Eroare la pornirea înregistrării: ${err?.message || err}`);
    }
  }

  private createConferenceCompositeTrack(
    quality: 'hd' | 'fhd' = 'hd',
    layout: 'composite' | 'screen_only' | 'cameras_only' = 'composite'
  ): MediaStreamTrack | null {
    try {
      const isFhd = quality === 'fhd';
      document.getElementById('vorbello-recording-canvas')?.remove();

      const canvas = document.createElement('canvas');
      canvas.id = 'vorbello-recording-canvas';
      canvas.width = isFhd ? 1920 : 1280;
      canvas.height = isFhd ? 1080 : 720;
      canvas.style.cssText = 'position:fixed; top:0; left:0; width:1px; height:1px; opacity:0.99; pointer-events:none; z-index:-9999; transform:scale(0.01); clip-path:inset(100%);';
      document.body.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;


      // Ensure dedicated hidden admin video element is ready for raw camera capture
      let recAdminVideo = document.getElementById('vorbello-rec-admin-video') as HTMLVideoElement | null;
      if (!recAdminVideo) {
        recAdminVideo = document.createElement('video');
        recAdminVideo.id = 'vorbello-rec-admin-video';
        recAdminVideo.autoplay = true;
        recAdminVideo.muted = true;
        recAdminVideo.playsInline = true;
        recAdminVideo.style.cssText = 'position:fixed; top:0; left:0; width:160px; height:90px; opacity:0.99; pointer-events:none; z-index:-9999; transform:scale(0.01); clip-path:inset(100%);';
        document.body.appendChild(recAdminVideo);
      }
      const rawCamStream = this.manager.getLocalCameraStream();
      if (rawCamStream && recAdminVideo.srcObject !== rawCamStream) {
        recAdminVideo.srcObject = rawCamStream;
        recAdminVideo.play().catch(() => {});
      }

      const drawFrame = () => {
        // Background
        ctx.fillStyle = '#0a0f1d';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Find active video elements
        // 1. Remote videos
        const remoteVideos = Array.from(document.querySelectorAll('video.remote-cam-video')) as HTMLVideoElement[];
        const activeRemoteVideos = remoteVideos.filter(v =>
          v.srcObject &&
          v.readyState >= 2 &&
          v.videoWidth > 0 &&
          v.style.display !== 'none'
        );

        // 2. Local self video
        const selfVideo = (document.getElementById('tile-self')?.querySelector('video') as HTMLVideoElement | null) ||
          (document.querySelector('video.self-cam-video') as HTMLVideoElement | null);
        const isSelfActive = !!(selfVideo &&
          selfVideo.srcObject &&
          selfVideo.readyState >= 2 &&
          selfVideo.videoWidth > 0 &&
          selfVideo.style.display !== 'none');

        const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
        const spotlightContainer = document.getElementById('spotlight-container');
        const isSpotlightActive = !!(spotlightVideo &&
          spotlightVideo.srcObject &&
          spotlightVideo.readyState >= 2 &&
          spotlightVideo.videoWidth > 0 &&
          spotlightContainer &&
          spotlightContainer.style.display !== 'none');

        const galleryVideos = [...(isSelfActive && selfVideo ? [selfVideo] : []), ...activeRemoteVideos];
        const board = this.isWhiteboardOpen ? document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null : null;
        const sharing = Boolean(spotlightContainer?.classList.contains('is-screen-share'));
        const scene = recordingScene(Boolean(board && board.width > 0 && board.height > 0), sharing, Boolean(this.pinnedIdentity), this.currentViewMode);
        if (layout === 'composite' && scene === 'whiteboard' && board) {
          const sideWidth = galleryVideos.length ? Math.round(canvas.width * 0.24) : 0;
          const contentWidth = canvas.width - sideWidth;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, contentWidth, canvas.height);
          ctx.drawImage(board, 0, 0, contentWidth, canvas.height);
          if (sideWidth) {
            const visible = galleryVideos.slice(0, 8);
            const h = Math.min(canvas.height / visible.length, sideWidth * 9 / 16);
            visible.forEach((video, i) => this.drawVideoCentered(ctx, video, contentWidth, i * h, sideWidth, h, 'contain'));
          }
          return;
        }

        // 1. "Cameră Curată": EXCLUSIVELY record Admin/Instructor camera full-screen for training courses
        // Zero remote students, zero splits, zero thumbnail overlays.
        if (layout === 'cameras_only') {
          let instructorCam: HTMLVideoElement | null = null;

          // Priority 1: Raw local camera stream in dedicated recording video
          if (recAdminVideo) {
            if (!recAdminVideo.srcObject || recAdminVideo.readyState < 2) {
              const currentRaw = this.manager.getLocalCameraStream();
              if (currentRaw && recAdminVideo.srcObject !== currentRaw) {
                recAdminVideo.srcObject = currentRaw;
                recAdminVideo.play().catch(() => {});
              }
            }
            if (recAdminVideo.readyState >= 2 && recAdminVideo.videoWidth > 0) {
              instructorCam = recAdminVideo;
            }
          }
          if (!instructorCam && isSelfActive && selfVideo) {
            instructorCam = selfVideo;
          } else if (!instructorCam && isSpotlightActive && spotlightVideo && !spotlightContainer?.classList.contains('is-screen-share')) {
            // Stage camera spotlight (Admin or Instructor pinned on stage)
            instructorCam = spotlightVideo;
          } else if (!instructorCam) {
            // Priority 2: Remote Admin participant tile
            const adminP = this.participants.find(p => (p.role === 'admin' || p.name.toLowerCase().includes('admin')) && p.identity !== 'self');
            if (adminP) {
              const adminTile = document.getElementById(`tile-${adminP.identity}`);
              const adminVid = adminTile?.querySelector('video') as HTMLVideoElement | null;
              if (adminVid && adminVid.readyState >= 2 && adminVid.videoWidth > 0) {
                instructorCam = adminVid;
              }
            }
          }

          if (!instructorCam && activeRemoteVideos.length > 0) {
            instructorCam = activeRemoteVideos[0];
          }

          if (instructorCam) {
            // Draw instructor camera across 100% of the canvas in 16:9 full-screen cover mode
            this.drawVideoCentered(ctx, instructorCam, 0, 0, canvas.width, canvas.height, 'contain');
          } else {
            ctx.fillStyle = '#0a0f1d';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#6366f1';
            ctx.font = `bold ${isFhd ? '48px' : '36px'} Inter, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('Vorbello • Cameră Admin (Cursuri)', canvas.width / 2, canvas.height / 2 - 20);
            ctx.fillStyle = '#94a3b8';
            ctx.font = `${isFhd ? '24px' : '18px'} Inter, system-ui, sans-serif`;
            ctx.fillText('Camera este oprită', canvas.width / 2, canvas.height / 2 + 25);
          }
          return;
        }

        // 2. "Doar Ecran": EXCLUSIVELY record shared screen full-screen
        if (layout === 'screen_only') {
          if (sharing && isSpotlightActive && spotlightVideo) {
            this.drawVideoCentered(ctx, spotlightVideo, 0, 0, canvas.width, canvas.height, 'contain');
          } else {
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#6366f1';
            ctx.font = `bold ${isFhd ? '48px' : '36px'} Inter, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('Partajare Ecran Oprită', canvas.width / 2, canvas.height / 2);
          }
          return;
        }

        // 3. "Mixt (Composite)": Screen share full-screen with Admin camera PiP in bottom-right
        if (scene === 'screen' && isSpotlightActive && spotlightVideo) {
          const visible = galleryVideos.slice(0, 8);
          const sideWidth = visible.length ? Math.round(canvas.width * 0.24) : 0;
          const contentWidth = canvas.width - sideWidth;
          this.drawVideoCentered(ctx, spotlightVideo, 0, 0, contentWidth, canvas.height, 'contain');
          if (sideWidth) {
            const h = Math.min(canvas.height / visible.length, sideWidth * 9 / 16);
            visible.forEach((video, i) => this.drawVideoCentered(ctx, video, contentWidth, i * h, sideWidth, h, 'contain'));
          }
        } else if (scene === 'speaker' && isSpotlightActive && spotlightVideo) {
          this.drawVideoCentered(ctx, spotlightVideo, 0, 0, canvas.width, canvas.height, 'contain');
        } else if (scene === 'speaker' && this.participants.length > 1) {
          ctx.fillStyle = '#94a3b8';
          ctx.textAlign = 'center';
          ctx.font = `bold ${isFhd ? '40px' : '28px'} Inter, system-ui, sans-serif`;
          ctx.fillText(document.getElementById('spotlight-pinned-name')?.textContent || 'Camera participantului este oprită', canvas.width / 2, canvas.height / 2);
        } else if (galleryVideos.length === 1) {
          this.drawVideoCentered(ctx, galleryVideos[0], 0, 0, canvas.width, canvas.height, 'contain');
        } else if (galleryVideos.length > 1) {
          const allVideos = galleryVideos;
          const count = allVideos.length;
          const cols = count <= 2 ? 2 : (count <= 4 ? 2 : 3);
          const rows = Math.ceil(count / cols);
          const cellW = canvas.width / cols;
          const cellH = canvas.height / rows;
          allVideos.forEach((v, idx) => {
            const col = idx % cols;
            const row = Math.floor(idx / cols);
            this.drawVideoCentered(ctx, v, col * cellW, row * cellH, cellW, cellH, 'contain');
          });
        } else {
          // No video cameras active: Draw branded conference card with active pulsating frame
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Pulsing REC badge to guarantee Chromium canvas capture stream stays active
          const pulse = Math.sin(Date.now() / 250) * 0.5 + 0.5;
          ctx.fillStyle = `rgba(239, 68, 68, ${0.4 + 0.6 * pulse})`;
          ctx.beginPath();
          ctx.arc(canvas.width / 2 - (isFhd ? 260 : 190), canvas.height / 2 - (isFhd ? 35 : 23), isFhd ? 12 : 8, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#6366f1';
          ctx.font = `bold ${isFhd ? '54px' : '40px'} Inter, system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText('Vorbello Conference', canvas.width / 2, canvas.height / 2 - (isFhd ? 30 : 20));

          ctx.fillStyle = '#94a3b8';
          ctx.font = `${isFhd ? '28px' : '20px'} Inter, system-ui, sans-serif`;
          ctx.fillText(`Înregistrare în curs • Calitate ${isFhd ? 'Full HD 1080p' : 'HD 720p'} (30 FPS)`, canvas.width / 2, canvas.height / 2 + (isFhd ? 35 : 25));
        }

        // Microscopic 2x2 dynamic frame heart-beat in corner (alternating #0a0f1d / #0a0f1e)
        // Guarantees Chromium CanvasCaptureMediaStreamTrack encoder NEVER stalls or emits 0 kbps!
        const heartBeat = (Date.now() % 2 === 0) ? '#0a0f1d' : '#0a0f1e';
        ctx.fillStyle = heartBeat;
        ctx.fillRect(0, 0, 2, 2);
      };

      // Draw initial frame immediately
      ctx.fillStyle = '#0a0f1d';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawFrame();

      // Capture stream from canvas at solid 30 fps
      const canvasStream = (canvas as any).captureStream ? (canvas as any).captureStream(30) : null;
      const tr = canvasStream?.getVideoTracks()[0];
      if (tr && (tr as any).requestFrame) {
        try { (tr as any).requestFrame(); } catch (_) {}
      }

      // Run render loop at solid 30 fps
      if (this.recordingCanvasInterval) clearInterval(this.recordingCanvasInterval);
      this.recordingCanvasInterval = setInterval(() => {
        drawFrame();
        if (tr && (tr as any).requestFrame) {
          try { (tr as any).requestFrame(); } catch (_) {}
        }
      }, 1000 / 30);

      if (canvasStream && canvasStream.getVideoTracks().length > 0) {
        return canvasStream.getVideoTracks()[0];
      }
    } catch (e) {
      console.warn('[Recording] Composite canvas failed:', e);
    }
    return null;
  }

  private drawVideoCentered(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    x: number,
    y: number,
    w: number,
    h: number,
    fitMode: 'cover' | 'contain' = 'cover'
  ) {
    try {
      const vw = video.videoWidth || 1280;
      const vh = video.videoHeight || 720;
      const fit = recordingFit(vw, vh, x, y, w, h, fitMode);

      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.drawImage(video, 0, 0, vw, vh, fit.x, fit.y, fit.width, fit.height);
      ctx.restore();
    } catch (_) {}
  }

  private startRecordingStream(stream: MediaStream, mode: 'video' | 'audio', quality: 'hd' | 'fhd' = 'hd', container: 'webm' | 'mp4' = 'webm') {
    this.recordingStream = stream;
    this.recordedChunks = [];
    this.isRecording = true;
    this.recordingMode = mode;
    this.recordingQuality = quality;
    this.recordingContainer = container;

    // Local recording quality: 3 Mbps for HD and 6 Mbps for Full HD.
    // This affects the saved file, not network bandwidth.
    const isFhd = quality === 'fhd';
    const videoBits = mode === 'video' ? (isFhd ? 6000000 : 3000000) : undefined;
    const audioBits = 128000;
    const totalBits = videoBits ? (videoBits + audioBits) : audioBits;

    this.recordedMimeType = this.getSupportedMimeType(mode === 'video', container);
    if (this.recordedMimeType.includes('mp4')) {
      this.recordingContainer = 'mp4';
    } else {
      this.recordingContainer = 'webm';
    }
    const recOptions: MediaRecorderOptions = {
      mimeType: this.recordedMimeType,
      videoBitsPerSecond: videoBits,
      audioBitsPerSecond: audioBits,
      bitsPerSecond: totalBits,
    };

    try {
      this.mediaRecorder = new MediaRecorder(stream, recOptions);
    } catch (e) {
      console.warn('[Recording] MediaRecorder with mimeType failed, falling back to default:', e);
      try {
        this.mediaRecorder = new MediaRecorder(stream, {
          videoBitsPerSecond: videoBits,
          audioBitsPerSecond: audioBits,
          bitsPerSecond: totalBits,
        });
      } catch (e2) {
        this.mediaRecorder = new MediaRecorder(stream);
      }
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
        console.debug(`[Recording] Chunk received: ${event.data.size} bytes (total chunks: ${this.recordedChunks.length})`);
      }
    };

    this.mediaRecorder.onerror = (event: any) => {
      console.error('[Recording] MediaRecorder runtime error:', event?.error || event);
      this.showFloatingToast(`❌ Eroare codec MediaRecorder: ${event?.error?.message || 'Eroare flux'}`);
    };

    this.mediaRecorder.onstop = async () => {
      // Allow browser async threads to finish pushing final dataavailable events
      await new Promise(r => setTimeout(r, 300));
      await this.handleRecordingFinished();
    };

    // Request data every 500ms for stable streaming chunks and rapid availability
    this.mediaRecorder.start(500);

    // Update UI
    this.recordingStartTime = Date.now();
    this.recordingSeconds = 0;
    this.updateRecordingUI(true);

    if (this.recordingInterval) clearInterval(this.recordingInterval);
    this.recordingInterval = setInterval(() => {
      this.recordingSeconds = Math.max(0, Math.floor((Date.now() - this.recordingStartTime) / 1000));
      this.updateRecordingTimerDisplay();
    }, 500);

    // Broadcast to entire room via LiveKit Data Channel if participant notification is enabled
    const shouldNotify = this.config.roomSettings?.autoRecordNotify !== false;
    if (shouldNotify) {
      this.manager.sendDataMessage({
        type: 'recording_status',
        isRecording: true,
        recorderName: this.config.displayName || 'Gazdă',
        recordingSeconds: this.recordingSeconds,
      });
    }

    const qualityLabel = mode === 'video' ? (isFhd ? 'Video Full HD 1080p (30 FPS)' : 'Video HD 720p (30 FPS)') : 'Audio HD';
    this.showFloatingToast(`🔴 Înregistrarea a început (${qualityLabel}).`);
  }

  private stopRecording() {
    this.closeRecordingOptionsModal();

    if (!this.isRecording) return;

    this.isRecording = false;
    this.recordingStopTime = Date.now();

    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
    if (this.recordingAudioSyncInterval) {
      clearInterval(this.recordingAudioSyncInterval);
      this.recordingAudioSyncInterval = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        if (this.mediaRecorder.state === 'recording') {
          // Flush pending data before final stop
          try { this.mediaRecorder.requestData(); } catch (_) {}
        }
        this.mediaRecorder.stop();
      } catch (e) {
        console.warn('[Recording] MediaRecorder stop error:', e);
      }
    }

    // AudioContext is closed in handleRecordingFinished() so audio tracks stay alive during final flush

    // Isolate current streams and intervals so that any subsequent recording is not affected
    const streamToStop = this.recordingStream;
    const canvasIntervalToClear = this.recordingCanvasInterval;
    this.recordingStream = null;
    this.recordingCanvasInterval = null;

    setTimeout(() => {
      if (canvasIntervalToClear) {
        clearInterval(canvasIntervalToClear);
      }
      if (streamToStop) {
        const liveLocalCam = this.manager.getLocalCameraStream()?.getVideoTracks()[0];
        const liveLocalScreen = this.manager.getLocalScreenStream()?.getVideoTracks()[0];
        streamToStop.getTracks().forEach(t => {
          // Never stop the user's ongoing camera or screen share hardware tracks!
          if (t !== liveLocalCam && t !== liveLocalScreen) {
            try { t.stop(); } catch (_) {}
          }
        });
      }
      const recAdminVid = document.getElementById('vorbello-rec-admin-video') as HTMLVideoElement | null;
      if (recAdminVid) {
        recAdminVid.srcObject = null;
        recAdminVid.remove();
      }
      const recCanvas = document.getElementById('vorbello-recording-canvas');
      if (recCanvas) {
        recCanvas.remove();
      }
    }, 600);

    this.updateRecordingUI(false);

    // Broadcast to entire room
    this.manager.sendDataMessage({
      type: 'recording_status',
      isRecording: false,
      recorderName: this.config.displayName,
    });
  }

  private updateRecordingUI(active: boolean) {
    const btn = document.getElementById('btn-toggle-record');
    const label = document.getElementById('label-record');
    const badge = document.getElementById('conf-rec-badge');
    const timer = document.getElementById('rec-timer');
    const sheetBtn = document.getElementById('sheet-btn-record');

    if (btn && label) {
      if (active) {
        btn.classList.add('active-record');
        label.textContent = 'Oprește Înreg.';
        const icon = btn.querySelector('.btn-icon');
        if (icon) icon.textContent = '⏹️';
      } else {
        btn.classList.remove('active-record');
        label.textContent = 'Înregistrează';
        const icon = btn.querySelector('.btn-icon');
        if (icon) icon.textContent = '⏺️';
      }
    }

    if (sheetBtn) {
      const iconCircle = sheetBtn.querySelector('.sheet-icon-circle');
      const itemLabel = sheetBtn.querySelector('.sheet-item-label');
      if (active) {
        sheetBtn.classList.add('active-record');
        if (iconCircle) iconCircle.textContent = '⏹️';
        if (itemLabel) itemLabel.textContent = 'Oprește Înreg.';
      } else {
        sheetBtn.classList.remove('active-record');
        if (iconCircle) iconCircle.textContent = '⏺️';
        if (itemLabel) itemLabel.textContent = 'Înregistrare';
      }
    }

    if (badge) {
      badge.style.display = active ? 'inline-flex' : 'none';
      const stopHint = document.getElementById('rec-stop-badge-hint');
      const canStop = this.isRecording || (active && (this.isHost || this.isAdmin));
      if (canStop) {
        badge.title = 'Înregistrare activă. Click pentru a opri înregistrarea.';
        badge.style.cursor = 'pointer';
        if (stopHint) stopHint.style.display = 'inline-flex';
      } else if (active) {
        badge.title = `Conferință înregistrată de ${this.roomRecorderName || 'Gazdă'}. Gestionată de organizator.`;
        badge.style.cursor = 'default';
        if (stopHint) stopHint.style.display = 'none';
      }
      if (!active && timer) timer.textContent = '00:00';
    }
  }

  private updateRecordingTimerDisplay() {
    const timer = document.getElementById('rec-timer');
    const label = document.getElementById('label-record');
    const sheetBtn = document.getElementById('sheet-btn-record');
    const mins = Math.floor(this.recordingSeconds / 60);
    const secs = this.recordingSeconds % 60;
    const timeStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    if (timer) timer.textContent = timeStr;
    if (label && this.isRecording) label.textContent = `Oprește (${timeStr})`;
    if (sheetBtn && this.isRecording) {
      const itemLabel = sheetBtn.querySelector('.sheet-item-label');
      if (itemLabel) itemLabel.textContent = `Oprește (${timeStr})`;
    }
  }

  private async handleRecordingFinished() {
    if (this.recordingAudioSyncInterval) {
      clearInterval(this.recordingAudioSyncInterval);
      this.recordingAudioSyncInterval = null;
    }
    document.querySelectorAll('audio[id^="audio-remote-"]').forEach(el => {
      delete (el as any)._vorbelloRecAttached;
    });
    if (this.recordingAudioNodes && this.recordingAudioNodes.length > 0) {
      for (const node of this.recordingAudioNodes) {
        try { node.disconnect(); } catch (_) {}
      }
      this.recordingAudioNodes = [];
    }
    this.recordingLocalMicSource = null;
    this.recordingLocalMicTrack = null;
    this.recordingScreenAudioSource = null;
    this.recordingScreenAudioTrack = null;
    this.recordingAudioDest = null;

    // Safely close the recording AudioContext now that the recorder has finished flushing
    if (this.recordingAudioCtx) {
      try {
        this.recordingAudioCtx.close();
      } catch (_) {}
      this.recordingAudioCtx = null;
    }

    if (this.recordedChunks.length === 0) {
      // Extra 250ms guard for slow browser background thread chunk delivery
      await new Promise(r => setTimeout(r, 250));
    }

    if (this.recordedChunks.length === 0) {
      console.warn('[Recording] No chunks received. MediaRecorder state:', this.mediaRecorder?.state, 'mimeType:', this.mediaRecorder?.mimeType);
      this.showFloatingToast('⚠️ Înregistrarea nu conține date.');
      return;
    }

    const isVideo = this.recordingMode === 'video';
    const actualMime = (this.mediaRecorder && this.mediaRecorder.mimeType) || this.recordedMimeType || (isVideo ? 'video/webm' : 'audio/webm');
    const isMp4 = actualMime.includes('mp4') || this.recordingContainer === 'mp4';
    const isOgg = actualMime.includes('ogg');
    const ext = isMp4 ? 'mp4' : (isOgg ? 'ogg' : 'webm');
    let blob = new Blob(this.recordedChunks, { type: actualMime });
    this.recordedChunks = [];

    const stopTime = this.recordingStopTime > 0 ? this.recordingStopTime : Date.now();
    const elapsedMs = this.recordingStartTime > 0 ? (stopTime - this.recordingStartTime) : (this.recordingSeconds * 1000);
    this.recordingSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const durationMs = Math.max(1000, this.recordingSeconds * 1000);

    // Fix WebM Duration so video players can seek/scrub through the file!
    if (ext === 'webm' && durationMs > 0) {
      try {
        blob = await fixWebmDuration(blob, durationMs);
        console.info(`[Recording] Successfully injected WebM duration header: ${durationMs}ms`);
      } catch (e) {
        console.warn('[Recording] Could not inject WebM duration:', e);
      }
    }

    const sizeMB = (blob.size / (1024 * 1024)).toFixed(1) + ' MB';
    const cleanRoom = (this.config.roomName || 'Conferinta').replace(/[^a-zA-Z0-9_-]/g, '_');
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    const resTag = isVideo ? (this.recordingQuality === 'fhd' ? '_1080p' : '_720p') : '_Audio';
    const fileName = `Vorbello_${cleanRoom}${resTag}_${dateStr}_${timeStr}.${ext}`;

    console.info(`[Recording] Finalized file: ${fileName} (${sizeMB}) format: ${ext}`);

    let savedPath: string | null = null;

    // 1. In Desktop Electron app: Save automatically to Videos/Vorbello Recordings using direct Uint8Array
    if ((window as any).vorbelloDesktop?.saveRecordingFile) {
      try {
        const arrayBuf = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuf);
        const res = await (window as any).vorbelloDesktop.saveRecordingFile({
          fileName,
          uint8Array,
        });
        if (res && res.success) {
          savedPath = res.path;
          console.info(`[Recording] Native desktop save successful: ${savedPath} (${res.size} bytes)`);
        } else {
          console.warn('[Recording] Desktop save returned error:', res?.error);
        }
      } catch (err) {
        console.warn('[Recording] Desktop direct buffer save error:', err);
      }
    }

    // 2. Browser direct file download fallback ONLY when not running in Desktop or if native save failed
    let browserDownloadUrl: string | undefined;
    if (!savedPath) {
      try {
        browserDownloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = browserDownloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          if (browserDownloadUrl) URL.revokeObjectURL(browserDownloadUrl);
          a.remove();
        }, 10000);
      } catch (e) {
        console.warn('[Recording] Download anchor click warning:', e);
      }
    }

    // Persist recording metadata to localStorage so it appears in the Admin Portal "Înregistrări" tab
    try {
      const existing = JSON.parse(localStorage.getItem('vorbello_recordings') || '[]');
      const mins = Math.floor(this.recordingSeconds / 60);
      const secs = this.recordingSeconds % 60;
      const durationFormatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      existing.unshift({
        id: `rec-${Date.now()}`,
        roomName: this.config.roomName,
        roomSlug: this.config.roomSlug,
        meetingTitle: `Înregistrare ${this.config.roomName}`,
        createdAt: `${dateStr} ${timeStr.replace(/-/g, ':')}`,
        duration: durationFormatted,
        durationSeconds: this.recordingSeconds,
        fileSize: sizeMB,
        format: this.selectedRecordingType,
        recordedBy: this.config.displayName || 'Administrator',
        fileName: fileName,
        filePath: savedPath || undefined,
        downloadUrl: browserDownloadUrl || undefined,
        storageType: savedPath ? 'disk' : 'download',
      });
      localStorage.setItem('vorbello_recordings', JSON.stringify(existing));
    } catch (e) {
      console.warn('[Recording] Failed to persist recording metadata:', e);
    }

    // 3. Show celebratory success modal
    this.showRecordingSuccessModal(fileName, sizeMB, savedPath);
  }

  private showRecordingSuccessModal(fileName: string, sizeMB: string, savedPath: string | null) {
    const modal = document.getElementById('recording-success-modal');
    const filenameEl = document.getElementById('rec-success-filename');
    const detailsEl = document.getElementById('rec-success-details');
    const openFolderBtn = document.getElementById('btn-open-rec-folder') as HTMLElement | null;

    if (filenameEl) filenameEl.textContent = fileName;
    if (detailsEl) {
      const mins = Math.floor(this.recordingSeconds / 60);
      const secs = this.recordingSeconds % 60;
      const durStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      detailsEl.innerHTML = `
        <div>Dimensiune: <strong>${sizeMB}</strong> &bull; Durată: <strong>${durStr}</strong></div>
        ${savedPath ? `<div style="margin-top: 6px; font-size: 0.72rem; color: #64748b; word-break: break-all;">Salvat în: ${savedPath}</div>` : ''}
      `;
    }

    if (openFolderBtn) {
      if (savedPath && (window as any).vorbelloDesktop?.showInFolder) {
        openFolderBtn.style.display = 'inline-flex';
        openFolderBtn.onclick = () => {
          (window as any).vorbelloDesktop.showInFolder(savedPath);
        };
      } else {
        openFolderBtn.style.display = 'none';
      }
    }

    if (modal) modal.style.display = 'flex';
  }

  private getSupportedMimeType(isVideo: boolean, container: 'webm' | 'mp4' = 'webm'): string {
    if (isVideo) {
      if (container === 'mp4') {
        // ONLY allow MP4 if dual audio/video codecs are truly supported by browser MediaRecorder!
        // Never allow video-only types like 'video/mp4;codecs=avc1' or 'video/mp4' which discard the audio track (0 kbps audio)!
        const mp4Types = [
          'video/mp4;codecs=avc1,mp4a.40.2',
          'video/mp4;codecs=avc1,opus',
        ];
        for (const t of mp4Types) {
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
            return t;
          }
        }
        console.warn('[Recording] Chromium does not support combined AVC1+AAC/Opus MP4 encoder, safely falling back to standard WebM VP8+Opus');
      }
      const types = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm',
      ];
      for (const t of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
          return t;
        }
      }
      return 'video/webm;codecs=vp8,opus';
    } else {
      if (container === 'mp4') {
        const mp4Types = [
          'audio/mp4;codecs=mp4a.40.2',
          'audio/mp4',
        ];
        for (const t of mp4Types) {
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
            return t;
          }
        }
        console.warn('[Recording] MP4 audio format not supported, falling back to WebM audio');
      }
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
      ];
      for (const t of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
          return t;
        }
      }
      return 'audio/webm;codecs=opus';
    }
  }

  // ==========================================
  // TABLĂ INTERACTIVĂ COLABORATIVĂ (WHITEBOARD)
  // ==========================================

  public toggleWhiteboard() {
    if (this.isWhiteboardOpen) {
      this.closeWhiteboard(true);
    } else {
      this.openWhiteboard(true);
    }
  }

  public openWhiteboard(broadcast: boolean = true) {
    this.isWhiteboardOpen = true;
    const container = document.getElementById('whiteboard-container');
    const btn = document.getElementById('btn-toggle-whiteboard');
    const label = document.getElementById('label-whiteboard');

    if (container) container.style.display = 'flex';
    if (btn) btn.classList.add('active-screen');
    if (label) label.textContent = 'Închide Tablă';

    setTimeout(() => {
      this.initWhiteboardCanvas();
    }, 50);
    setTimeout(() => {
      this.initWhiteboardCanvas();
    }, 250);

    if (broadcast) {
      this.manager.sendWhiteboardToggle(true);
    }
  }

  public closeWhiteboard(broadcast: boolean = true) {
    this.isWhiteboardOpen = false;
    const container = document.getElementById('whiteboard-container');
    const btn = document.getElementById('btn-toggle-whiteboard');
    const label = document.getElementById('label-whiteboard');

    if (container) container.style.display = 'none';
    if (btn) btn.classList.remove('active-screen');
    if (label) label.textContent = 'Tablă Albă';

    if (broadcast) {
      this.manager.sendWhiteboardToggle(false);
    }
  }

  private initWhiteboardCanvas() {
    const canvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    const wrapper = document.getElementById('wb-canvas-wrapper');
    if (!canvas || !wrapper) return;

    this.wbCanvas = canvas;
    const dpr = window.devicePixelRatio || 1;
    const rect = wrapper.getBoundingClientRect();
    const w = Math.floor(rect.width || 800);
    const h = Math.floor(rect.height || 600);

    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx && canvas.width > 0 && canvas.height > 0) {
        tempCtx.drawImage(canvas, 0, 0);
      }

      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      this.wbCtx = canvas.getContext('2d');
      if (this.wbCtx) {
        this.wbCtx.scale(dpr, dpr);
        if (tempCanvas.width > 0) {
          this.wbCtx.drawImage(tempCanvas, 0, 0, w, h);
        }
      }
    } else if (!this.wbCtx) {
      this.wbCtx = canvas.getContext('2d');
      if (this.wbCtx) {
        this.wbCtx.scale(dpr, dpr);
      }
    }

    if (typeof ResizeObserver !== 'undefined' && !(wrapper as any)._wbObserverAttached) {
      (wrapper as any)._wbObserverAttached = true;
      const ro = new ResizeObserver(() => {
        if (this.isWhiteboardOpen) {
          this.initWhiteboardCanvas();
        }
      });
      ro.observe(wrapper);
    }

    if ((canvas as any)._wbListenersAttached) return;
    (canvas as any)._wbListenersAttached = true;

    const getPos = (e: PointerEvent) => {
      const cRect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - cRect.left,
        y: e.clientY - cRect.top,
        w: cRect.width,
        h: cRect.height,
      };
    };

    canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      e.preventDefault();
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
      this.isDrawing = true;
      const pos = getPos(e);
      this.lastX = pos.x;
      this.lastY = pos.y;
    });

    canvas.addEventListener('pointermove', (e: PointerEvent) => {
      if (!this.isDrawing) return;
      e.preventDefault();
      const pos = getPos(e);
      const x0 = this.lastX;
      const y0 = this.lastY;
      const x1 = pos.x;
      const y1 = pos.y;

      this.drawWhiteboardSegment(x0, y0, x1, y1, this.wbColor, this.wbSize, this.wbTool);

      if (pos.w > 0 && pos.h > 0) {
        this.manager.sendWhiteboardDraw({
          x0: x0 / pos.w,
          y0: y0 / pos.h,
          x1: x1 / pos.w,
          y1: y1 / pos.h,
          color: this.wbColor,
          size: this.wbSize,
          tool: this.wbTool,
        });
      }

      this.lastX = x1;
      this.lastY = y1;
    });

    const stopDrawing = (e: PointerEvent) => {
      if (this.isDrawing) {
        try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
      }
      this.isDrawing = false;
    };
    canvas.addEventListener('pointerup', stopDrawing);
    canvas.addEventListener('pointerleave', stopDrawing);
    canvas.addEventListener('pointercancel', stopDrawing);
  }

  private drawWhiteboardSegment(x0: number, y0: number, x1: number, y1: number, color: string, size: number, tool: string) {
    if (!this.wbCtx) {
      if (this.wbCanvas) this.wbCtx = this.wbCanvas.getContext('2d');
    }
    if (!this.wbCtx) return;

    this.wbCtx.save();
    if (tool === 'eraser') {
      this.wbCtx.globalCompositeOperation = 'destination-out';
      this.wbCtx.lineWidth = size * 6;
    } else {
      this.wbCtx.globalCompositeOperation = 'source-over';
      this.wbCtx.strokeStyle = color;
      this.wbCtx.lineWidth = size;
    }
    this.wbCtx.lineCap = 'round';
    this.wbCtx.lineJoin = 'round';
    this.wbCtx.beginPath();
    this.wbCtx.moveTo(x0, y0);
    this.wbCtx.lineTo(x1, y1);
    this.wbCtx.stroke();
    this.wbCtx.restore();
  }

  public handleRemoteWhiteboardDraw(data: { x0: number; y0: number; x1: number; y1: number; color: string; size: number; tool: string }) {
    if (!this.isWhiteboardOpen) {
      this.openWhiteboard(false);
    }
    const canvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (w <= 0 || h <= 0) return;

    this.drawWhiteboardSegment(
      data.x0 * w,
      data.y0 * h,
      data.x1 * w,
      data.y1 * h,
      data.color,
      data.size,
      data.tool
    );
  }

  public clearWhiteboard(broadcast: boolean = true) {
    if (!this.wbCanvas) {
      this.wbCanvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    }
    if (this.wbCanvas) {
      const ctx = this.wbCanvas.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.wbCanvas.width, this.wbCanvas.height);
        ctx.restore();
      }
    }
    if (broadcast) {
      this.manager.sendWhiteboardClear();
      this.showFloatingToast('🗑️ Ați curățat tabla albă.');
    } else {
      this.showFloatingToast('🗑️ Tabla albă a fost curățată de un participant.');
    }
  }

  public downloadWhiteboardPNG() {
    if (!this.wbCanvas) {
      this.wbCanvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    }
    if (!this.wbCanvas) return;

    try {
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = this.wbCanvas.width;
      exportCanvas.height = this.wbCanvas.height;
      const expCtx = exportCanvas.getContext('2d');
      if (expCtx) {
        expCtx.fillStyle = '#0b0f19';
        expCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        expCtx.drawImage(this.wbCanvas, 0, 0);
      }
      const dataUrl = exportCanvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      const dateStr = new Date().toISOString().slice(0, 10);
      a.download = `Vorbello-Tabla-${dateStr}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      this.showFloatingToast('💾 Imaginea tablei a fost salvată ca PNG!');
    } catch (e) {
      console.warn('[Whiteboard] Download error:', e);
    }
  }

  // ==========================================
  // SONDAJE LIVE ÎN SALĂ (LIVE POLLS)
  // ==========================================

  public openCreatePollModal() {
    if (!this.isHost) { this.showFloatingToast('Doar gazda poate crea sondaje.'); return; }
    const modal = document.getElementById('create-poll-modal');
    const questionInput = document.getElementById('input-poll-question') as HTMLInputElement | null;
    const inputsList = document.getElementById('poll-options-inputs-list');

    if (questionInput) questionInput.value = '';
    if (inputsList) {
      inputsList.innerHTML = `
        <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 1 (ex: Da, sunt de acord)" />
        <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 2 (ex: Nu, am obiecții)" />
      `;
    }
    if (modal) modal.style.display = 'flex';
  }

  public closeCreatePollModal() {
    const modal = document.getElementById('create-poll-modal');
    if (modal) modal.style.display = 'none';
  }

  public submitNewPoll() {
    if (!this.isHost) { this.showFloatingToast('Doar gazda poate crea sondaje.'); return; }
    if (this.activePoll && !this.activePoll.isClosed) { this.showFloatingToast('Incheie sondajul activ inainte de a crea altul.'); return; }
    const questionInput = document.getElementById('input-poll-question') as HTMLInputElement | null;
    const question = (questionInput?.value || '').trim();
    if (!question) {
      alert('Vă rugăm să introduceți întrebarea sondajului.');
      return;
    }

    const optInputs = document.querySelectorAll('.poll-opt-input') as NodeListOf<HTMLInputElement>;
    const options: string[] = [];
    optInputs.forEach(inp => {
      const val = inp.value.trim();
      if (val) options.push(val);
    });

    if (options.length < 2) {
      alert('Vă rugăm să introduceți cel puțin 2 opțiuni de răspuns.');
      return;
    }

    const pollId = `poll-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    const votes: { [key: number]: number } = {};
    options.forEach((_, idx) => {
      votes[idx] = 0;
    });

    this.pollVoters.clear();
    this.activePoll = {
      id: pollId,
      question,
      options,
      votes,
      totalVotes: 0,
      myVoteIndex: null,
      creatorName: this.config.displayName || 'Gazda',
      creatorIdentity: this.manager.getRoom()?.localParticipant.identity || '',
      isClosed: false,
    };

    const headerPoll = document.getElementById('header-poll-count'); if (headerPoll) headerPoll.textContent = '1';

    this.manager.sendPollLaunch(pollId, question, options);
    this.closeCreatePollModal();

    this.isPollsOpen = true;
    this.isChatOpen = false;
    this.isParticipantsOpen = false;
    this.toggleDrawer();

    this.showFloatingToast('🚀 Sondajul a fost lansat cu succes tuturor participanților!');
  }

  public handleRemotePollLaunch(data: { pollId: string; question: string; options: string[]; creatorName: string; creatorIdentity: string }) {
    const votes: { [key: number]: number } = {};
    data.options.forEach((_, idx) => {
      votes[idx] = 0;
    });

    this.pollVoters.clear();
    this.activePoll = {
      id: data.pollId,
      question: data.question,
      options: data.options,
      votes,
      totalVotes: 0,
      myVoteIndex: null,
      creatorName: data.creatorName,
      creatorIdentity: data.creatorIdentity,
      isClosed: false,
    };
    const headerPoll = document.getElementById('header-poll-count'); if (headerPoll) headerPoll.textContent = '1';

    if (this.isPollsOpen) {
      this.renderPollsView();
    } else {
      this.showLivePollPopup();
    }

    this.playChatChime();
    this.showFloatingToast(`📊 Sondaj nou de la ${data.creatorName}: ${data.question}`);
  }

  public voteInPoll(pollId: string, optionIndex: number) {
    if (!this.activePoll || this.activePoll.id !== pollId || this.activePoll.isClosed) return;
    if (this.activePoll.myVoteIndex !== null) return;

    const myIdentity = this.manager.getRoom()?.localParticipant.identity || '';
    if (!validPollVote(optionIndex, this.activePoll.options.length, myIdentity, this.pollVoters)) return;
    this.pollVoters.add(myIdentity);
    this.activePoll.myVoteIndex = optionIndex;
    this.activePoll.votes[optionIndex] = (this.activePoll.votes[optionIndex] || 0) + 1;
    this.activePoll.totalVotes++;

    this.manager.sendPollVote(pollId, optionIndex);
    this.renderPollsView();

    const popup = document.getElementById('live-poll-popup');
    if (popup && popup.style.display !== 'none') {
      this.showLivePollPopup();
    }

    this.showFloatingToast('✅ Votul dumneavoastră a fost înregistrat!');
  }

  public handleRemotePollVote(data: { pollId: string; optionIndex: number; voterName: string; voterIdentity: string }) {
    if (!this.activePoll || this.activePoll.id !== data.pollId || this.activePoll.isClosed || !validPollVote(data.optionIndex, this.activePoll.options.length, data.voterIdentity, this.pollVoters)) return;
    this.pollVoters.add(data.voterIdentity);
    const optIdx = data.optionIndex;
    this.activePoll.votes[optIdx] = (this.activePoll.votes[optIdx] || 0) + 1;
    this.activePoll.totalVotes++;

    if (this.isPollsOpen) {
      this.renderPollsView();
    }
    const popup = document.getElementById('live-poll-popup');
    if (popup && popup.style.display !== 'none') {
      this.showLivePollPopup();
    }
  }

  public closeActivePoll(broadcast: boolean = true) {
    if (!this.isHost) { this.showFloatingToast('Doar gazda poate incheia sondajul.'); return; }
    if (!this.activePoll || this.activePoll.creatorIdentity !== this.manager.getRoom()?.localParticipant.identity) return;
    this.activePoll.isClosed = true;
    const headerPoll = document.getElementById('header-poll-count'); if (headerPoll) headerPoll.textContent = '0';
    if (this.config.pollArchiveOrganization) {
      try { savePollArchive(localStorage, this.config.pollArchiveOrganization, {id:this.activePoll.id, question:this.activePoll.question, options:[...this.activePoll.options], votes:this.activePoll.options.map((_,i)=>this.activePoll!.votes[i] || 0), closedAt:new Date().toISOString()}); }
      catch (_) { this.showFloatingToast('Rezultatul nu a putut fi salvat local. Descarca fisierul CSV.'); }
    }

    if (broadcast) {
      this.manager.sendPollClose(this.activePoll.id);
    }

    this.renderPollsView();
    const popup = document.getElementById('live-poll-popup');
    if (popup) popup.style.display = 'none';

    this.showFloatingToast('⏹️ Sondajul a fost încheiat.');
  }

  public handleRemotePollClose(data: { pollId: string; senderIdentity: string }) {
    if (this.activePoll && this.activePoll.id === data.pollId && this.activePoll.creatorIdentity === data.senderIdentity) {
      this.activePoll.isClosed = true;
      const headerPoll = document.getElementById('header-poll-count'); if (headerPoll) headerPoll.textContent = '0';
      if (this.isPollsOpen) {
        this.renderPollsView();
      }
      const popup = document.getElementById('live-poll-popup');
      if (popup) popup.style.display = 'none';
      this.showFloatingToast('⏹️ Sondajul a fost încheiat de organizator.');
    }
  }

  public renderPollsView() {
    const container = document.getElementById('polls-list-content');
    if (!container) return;

    if (!this.activePoll) {
      container.innerHTML = `
        <div style="text-align: center; padding: 2.5rem 1rem; color: #94a3b8; display: flex; flex-direction: column; align-items: center; gap: 0.75rem;">
          <div style="font-size: 2.2rem;">📊</div>
          <div style="font-weight: 600; font-size: 0.95rem; color: #e2e8f0;">Nu există sondaje active</div>
          <div style="font-size: 0.75rem; max-width: 240px; line-height: 1.4;">Apasă butonul de mai sus „➕ Creează Sondaj” pentru a adresa o întrebare tuturor participanților.</div>
        </div>
      `;
      return;
    }

    const poll = this.activePoll;
    const hasVoted = poll.myVoteIndex !== null;
    const total = poll.totalVotes;

    const optionsHtml = poll.options.map((optText, idx) => {
      const voteCount = poll.votes[idx] || 0;
      const pct = total > 0 ? Math.round((voteCount / total) * 100) : 0;
      const isMyChoice = poll.myVoteIndex === idx;

      return `
        <div class="poll-option-row ${isMyChoice ? 'voted' : ''}" data-poll-id="${poll.id}" data-opt-idx="${idx}" style="${!hasVoted && !poll.isClosed ? 'cursor: pointer;' : 'cursor: default;'}">
          <div class="poll-option-progress-bar" style="width: ${pct}%;"></div>
          <div class="poll-option-content">
            <span>${isMyChoice ? '✅ ' : ''}${optText}</span>
          </div>
          <div class="poll-option-stats">
            <span>${pct}%</span>
            <span style="font-size: 0.7rem; color: #64748b; margin-left: 4px;">(${voteCount})</span>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="poll-card">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem;">
          <div>
            <div class="poll-question-title">${poll.question}</div>
            <div class="poll-creator-sub">Lansat de ${poll.creatorName}</div>
            <div style="margin-top:8px"><span style="display:inline-flex;align-items:center;padding:4px 10px;border:1px solid #5571aa;border-radius:999px;background:#253658;color:#dceaff;font-size:.75rem;font-weight:700">Voturi exprimate: ${total}</span></div>
          </div>
          <span class="poll-active-badge" style="${poll.isClosed ? 'background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.35); color: #f87171;' : ''}">
            ${poll.isClosed ? '⏹️ Încheiat' : '🟢 Activ'}
          </span>
        </div>

        <div class="poll-options-list">
          ${optionsHtml}
        </div>

        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 4px; font-size: 0.72rem; color: #94a3b8;">
          <span>${hasVoted ? 'Ai votat deja' : (poll.isClosed ? 'Votarea este încheiată' : 'Apasă pe o opțiune pentru a vota')}</span>
          ${!poll.isClosed && this.isHost ? `
            <button class="btn btn-secondary btn-sm" id="btn-close-this-poll" style="font-size: 0.7rem; padding: 2px 8px; color: #f87171;">
              ⏹️ Încheie Sondaj
            </button>
          ` : ''}
        </div>
      </div>
    `;

    if (!hasVoted && !poll.isClosed) {
      container.querySelectorAll('.poll-option-row').forEach(row => {
        row.addEventListener('click', () => {
          const pId = row.getAttribute('data-poll-id');
          const optIdx = Number(row.getAttribute('data-opt-idx'));
          if (pId && !isNaN(optIdx)) {
            this.voteInPoll(pId, optIdx);
          }
        });
      });
    }

    if (poll.isClosed && this.isHost && poll.creatorIdentity === this.manager.getRoom()?.localParticipant.identity) {
      const exportButton = document.createElement('button');
      exportButton.type = 'button'; exportButton.className = 'btn btn-secondary btn-sm';
      exportButton.textContent = 'Descarca rezultate CSV';
      exportButton.addEventListener('click', () => {
        const data = pollResultCsv({id:poll.id, question:poll.question, options:poll.options, votes:poll.options.map((_,i)=>poll.votes[i] || 0), closedAt:new Date().toISOString()});
        const url = URL.createObjectURL(new Blob([data], {type:'text/csv;charset=utf-8'}));
        const link = document.createElement('a'); link.href=url; link.download=`sondaj-${poll.id}.csv`; document.body.append(link); link.click(); link.remove(); setTimeout(()=>URL.revokeObjectURL(url),1000);
      });
      container.append(exportButton);
    }

    document.getElementById('btn-close-this-poll')?.addEventListener('click', () => {
      this.closeActivePoll(true);
    });
  }

  public showLivePollPopup() {
    const popup = document.getElementById('live-poll-popup');
    if (!popup || !this.activePoll || this.activePoll.isClosed) {
      if (popup) popup.style.display = 'none';
      return;
    }

    const poll = this.activePoll;
    const hasVoted = poll.myVoteIndex !== null;
    const total = poll.totalVotes;

    const optionsHtml = poll.options.map((optText, idx) => {
      const voteCount = poll.votes[idx] || 0;
      const pct = total > 0 ? Math.round((voteCount / total) * 100) : 0;
      const isMyChoice = poll.myVoteIndex === idx;

      return `
        <div class="poll-option-row ${isMyChoice ? 'voted' : ''}" data-pop-poll-id="${poll.id}" data-pop-opt-idx="${idx}" style="${!hasVoted ? 'cursor: pointer;' : 'cursor: default;'} margin-bottom: 6px;">
          <div class="poll-option-progress-bar" style="width: ${pct}%;"></div>
          <div class="poll-option-content" style="font-size: 0.8rem;">
            <span>${isMyChoice ? '✅ ' : ''}${optText}</span>
          </div>
          <div class="poll-option-stats" style="font-size: 0.75rem;">
            <span>${pct}%</span>
          </div>
        </div>
      `;
    }).join('');

    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
        <div style="font-weight: 700; font-size: 0.85rem; color: #38bdf8; display: flex; align-items: center; gap: 5px;">
          <span>📊</span> Sondaj Live
        </div>
        <button id="btn-close-poll-pop" style="background: none; border: none; color: #94a3b8; font-size: 1.1rem; cursor: pointer;">&times;</button>
      </div>
      <div style="font-weight: 700; font-size: 0.9rem; color: #fff; margin-bottom: 8px;">${poll.question}</div>
      <div style="display: flex; flex-direction: column;">
        ${optionsHtml}
      </div>
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px; font-size: 0.72rem;">
        <span style="color: #94a3b8;">${hasVoted ? 'Vot salvat' : 'Alege răspunsul'}</span>
        <button id="btn-pop-open-drawer" class="btn btn-primary btn-sm" style="font-size: 0.7rem; padding: 2px 8px;">
          Deschide Panou &rarr;
        </button>
      </div>
    `;

    popup.style.display = 'block';

    if (!hasVoted) {
      popup.querySelectorAll('[data-pop-poll-id]').forEach(row => {
        row.addEventListener('click', () => {
          const pId = row.getAttribute('data-pop-poll-id');
          const optIdx = Number(row.getAttribute('data-pop-opt-idx'));
          if (pId && !isNaN(optIdx)) {
            this.voteInPoll(pId, optIdx);
          }
        });
      });
    }

    document.getElementById('btn-close-poll-pop')?.addEventListener('click', () => {
      popup.style.display = 'none';
    });

    document.getElementById('btn-pop-open-drawer')?.addEventListener('click', () => {
      popup.style.display = 'none';
      this.isPollsOpen = true;
      this.isChatOpen = false;
      this.isParticipantsOpen = false;
      this.toggleDrawer();
    });
  }

  private dataUrlToBlob(dataUrl: string, fallbackMime = 'application/octet-stream'): Blob {
    try {
      const commaIdx = dataUrl.indexOf(',');
      const header = commaIdx !== -1 ? dataUrl.substring(0, commaIdx) : '';
      const b64Data = commaIdx !== -1 ? dataUrl.substring(commaIdx + 1) : dataUrl;
      const mimeMatch = header.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : fallbackMime;
      const byteChars = atob(b64Data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mime });
    } catch (e) {
      console.warn('[ConferenceUI] dataUrlToBlob fallback:', e);
      return new Blob([dataUrl], { type: fallbackMime });
    }
  }

  private playKnockChime() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523.25, now);
      osc.frequency.setValueAtTime(659.25, now + 0.15);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.45);
    } catch (_) {}
  }

  private enterWaitingRoomLobby(isMoveBack: boolean = false) {
    this.isWaitingInLobby = true;
    try {
      if (this.manager.isMicEnabled) {
        this.manager.enableMicrophone(false).catch(() => {});
      }
      if (this.manager.isCameraEnabled) {
        this.manager.enableCamera(false).catch(() => {});
      }
    } catch (_) {}

    let overlay = document.getElementById('waiting-room-lobby-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'waiting-room-lobby-overlay';
      overlay.style.cssText = `
        position: fixed; inset: 0; z-index: 99999;
        background: radial-gradient(circle at 50% 30%, #1e293b 0%, #090d16 100%);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        padding: 24px; color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        text-align: center; backdrop-filter: blur(20px);
      `;
      document.body.appendChild(overlay);
    }

    const isWaitForHost = !this.config.roomSettings?.waitingRoomEnabled;
    const hostTitle = isWaitForHost ? 'În așteptarea gazdei...' : 'Sala de Așteptare este Activă';
    const hostDesc = isMoveBack
      ? 'Gazda v-a redirecționat temporar în sala de așteptare.'
      : (isWaitForHost
          ? `Conferința „${this.config.roomName || this.config.roomSlug}” va începe automat imediat ce gazda (${this.config.roomSettings?.creatorName || 'organizatorul'}) se conectează.`
          : 'Vă rugăm să așteptați. Gazda ședinței vă va permite accesul în conferință în scurt timp.');
    const statusText = isWaitForHost ? '● În așteptarea conectării gazdei' : '● Cerere transmisă gazdei';

    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div style="max-width: 480px; width: 100%; background: rgba(30, 41, 59, 0.7); border: 1px solid rgba(245, 158, 11, 0.35); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7); border-radius: 20px; padding: 2.2rem; display: flex; flex-direction: column; align-items: center; gap: 1.25rem;">
        <div style="position: relative; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center;">
          <div style="position: absolute; inset: 0; border: 3px solid rgba(245, 158, 11, 0.2); border-top-color: #f59e0b; border-radius: 50%; animation: spin 1.2s linear infinite;"></div>
          <span style="font-size: 2.2rem;">⏳</span>
        </div>
        
        <div>
          <h2 style="font-size: 1.4rem; font-weight: 800; color: #fff; margin-bottom: 0.35rem;">${hostTitle}</h2>
          <p style="font-size: 0.875rem; color: #94a3b8; line-height: 1.5;">
            ${hostDesc}
          </p>
        </div>

        <div style="width: 100%; background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1rem; text-align: left; font-size: 0.8rem;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: var(--text-muted);">Sală:</span>
            <strong style="color: #38bdf8;">${this.config.roomName || this.config.roomSlug}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: var(--text-muted);">Participant:</span>
            <strong style="color: #f8fafc;">${this.config.displayName}</strong>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span style="color: var(--text-muted);">Statut:</span>
            <span style="color: #fbbf24; font-weight: 700;">${statusText}</span>
          </div>
        </div>

        <div style="display: flex; gap: 0.75rem; width: 100%;">
          <button id="btn-leave-waiting-room" class="btn btn-secondary btn-sm" style="flex: 1; padding: 0.65rem; border-radius: 10px; font-weight: 600; display:flex; align-items:center; justify-content:center; gap:8px;">
            ${ICONS.leave} <span>Părăsește Ședința</span>
          </button>
        </div>
      </div>
    `;

    document.getElementById('btn-leave-waiting-room')?.addEventListener('click', () => {
      this.cleanup();
      this.config.onLeave('Ați părăsit sala de așteptare.');
    });

    if (isMoveBack) {
      this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
    }
  }

  private async handleWaitingRoomDecision(decision: 'admitted' | 'rejected') {
    if (decision === 'admitted') {
      this.isWaitingInLobby = false;
      if (this.waitingRoomKnockInterval) {
        clearInterval(this.waitingRoomKnockInterval);
        this.waitingRoomKnockInterval = null;
      }
      const overlay = document.getElementById('waiting-room-lobby-overlay');
      if (overlay) {
        overlay.style.transition = 'opacity 0.4s ease-out';
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 400);
      }
      this.showFloatingToast('✅ Gazda v-a permis accesul în conferință!');

      try {
        if (this.manager.isCameraEnabled) {
          await this.manager.enableCamera(true);
        }
        if (!this.userExplicitlyMuted && this.manager.isMicEnabled) {
          await this.manager.enableMicrophone(true);
        }
      } catch (e) {
        console.warn('[ConferenceUI] Post-admission track publish error:', e);
      }
      this.updateControlsState();
      this.updateSelfTile();
      this.renderVideoGrid();
    } else if (decision === 'rejected') {
      this.isWaitingInLobby = false;
      if (this.waitingRoomKnockInterval) {
        clearInterval(this.waitingRoomKnockInterval);
        this.waitingRoomKnockInterval = null;
      }
      const overlay = document.getElementById('waiting-room-lobby-overlay');
      if (overlay) {
        overlay.innerHTML = `
          <div style="max-width: 440px; width: 100%; background: rgba(30, 41, 59, 0.9); border: 1px solid rgba(239, 68, 68, 0.4); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7); border-radius: 20px; padding: 2.2rem; text-align: center;">
            <div style="font-size: 2.8rem; margin-bottom: 0.75rem;">⛔</div>
            <h2 style="font-size: 1.3rem; font-weight: 800; color: #f87171; margin-bottom: 0.5rem;">Acces Neautorizat</h2>
            <p style="font-size: 0.875rem; color: #cbd5e1; margin-bottom: 1.5rem; line-height: 1.5;">Gazda a respins cererea de acces la această conferință.</p>
            <button id="btn-rejected-leave" class="btn btn-primary" style="width: 100%; padding: 0.65rem; border-radius: 10px;">Înapoi la Pagina Principală</button>
          </div>
        `;
        document.getElementById('btn-rejected-leave')?.addEventListener('click', () => {
          this.cleanup();
          this.config.onLeave('Acces respins de gazdă.');
        });
      }
    }
  }

  private handleWaitingRoomKnock(data: { identity: string; name: string; timestamp: number }) {
    // Both admins and regular participants must know that this identity is waiting in lobby,
    // so that neither admin nor regular attendee shows their tile on stage before admission!
    const exists = this.waitingParticipants.find(w => w.identity === data.identity);
    if (!exists) {
      this.waitingParticipants.push({
        identity: data.identity,
        name: data.name,
        timestamp: data.timestamp || Date.now(),
      });
      // Only host/admins hear chime and see admission banner
      if (this.isAdmin || this.isModerator) {
        this.playKnockChime();
        this.showWaitingRoomBanner(data.name, data.identity);
      }
      this.renderVideoGrid();
      this.renderParticipantsList();
      this.updateControlsState();
      this.updateStageLayout();
    }
  }

  private showWaitingRoomBanner(name: string, identity: string) {
    let banner = document.getElementById('waiting-room-host-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'waiting-room-host-banner';
      banner.style.cssText = `
        position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
        z-index: 1000; background: rgba(15, 23, 42, 0.95);
        border: 1px solid rgba(245, 158, 11, 0.5); box-shadow: 0 10px 25px -5px rgba(0,0,0,0.6);
        border-radius: 12px; padding: 10px 18px; display: flex; align-items: center; gap: 14px;
        backdrop-filter: blur(12px); color: #fff;
      `;
      const stage = document.getElementById('conf-container') || document.body;
      stage.appendChild(banner);
    }

    banner.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 1.25rem;">⏳</span>
        <div>
          <div style="font-weight: 700; font-size: 0.85rem; color: #fbbf24;">${this.escapeHtml(name)} este în sala de așteptare</div>
          <div style="font-size: 0.72rem; color: #94a3b8;">${this.waitingParticipants.length} participant(ți) în așteptare</div>
        </div>
      </div>
      <div style="display: flex; align-items: center; gap: 6px;">
        <button class="btn btn-primary btn-sm btn-admit-banner" data-id="${this.escapeAttribute(identity)}" style="padding: 4px 10px; font-size: 0.75rem; border-radius: 6px;">✅ Admite</button>
        <button class="btn btn-secondary btn-sm btn-reject-banner" data-id="${this.escapeAttribute(identity)}" style="padding: 4px 10px; font-size: 0.75rem; border-radius: 6px; color: #f87171;">❌ Respinge</button>
      </div>
    `;

    banner.querySelector('.btn-admit-banner')?.addEventListener('click', () => {
      this.admitWaitingParticipant(identity);
    });
    banner.querySelector('.btn-reject-banner')?.addEventListener('click', () => {
      this.rejectWaitingParticipant(identity);
    });
  }

  private startAdmissionPolling(): void {
    if (!this.config.admission || this.admissionPollTimer) return;
    this.admissionStopped=false;
    const poll=async () => {
      if (this.admissionStopped || this.admissionPolling || !this.config.admission) return;
      this.admissionPolling=true;
      try {
        const requests=await this.config.admission.list();
        if (this.admissionStopped) return;
        const prior=this.waitingParticipants;
        const nonServer=prior.filter(item=>!item.identity.startsWith('request-'));
        const server=requests.map(item=>({identity:'request-'+item.id,name:item.name,timestamp:Date.now()}));
        const changed=JSON.stringify(prior.map(item=>[item.identity,item.name]))!==JSON.stringify([...nonServer,...server].map(item=>[item.identity,item.name]));
        this.waitingParticipants=[...nonServer,...server];
        if (changed) {
          const firstNew=server.find(item=>!prior.some(previous=>previous.identity===item.identity));
          if (firstNew) { this.playKnockChime(); this.showWaitingRoomBanner(firstNew.name,firstNew.identity); }
          else if (!this.waitingParticipants.length) document.getElementById('waiting-room-host-banner')?.remove();
          else if (document.getElementById('waiting-room-host-banner')) {
            const first=this.waitingParticipants[0]; this.showWaitingRoomBanner(first.name,first.identity);
          }
          this.renderParticipantsList(); this.updateControlsState();
        }
      } catch (_) { /* Keep the last known state; decisions are always checked by the server. */ }
      finally { this.admissionPolling=false; }
    };
    void poll();
    this.admissionPollTimer=setInterval(()=>void poll(),3000);
  }

  private async decideWaitingParticipant(identity:string, decision:'admitted'|'rejected'):Promise<boolean> {
    if (identity.startsWith('request-')) {
      if (!this.config.admission || !/^request-[a-f0-9]{32}$/.test(identity)) return false;
      try { await this.config.admission.decide(identity.slice(8),decision==='admitted'?'approved':'rejected'); return true; }
      catch (_) { return false; }
    }
    return this.manager.sendWaitingRoomDecision(identity,decision);
  }

  public async admitWaitingParticipant(identity: string) {
    if (!await this.decideWaitingParticipant(identity, 'admitted')) {
      this.showFloatingToast('Admiterea nu a fost trimisă. Verifică conexiunea și încearcă din nou.');
      return;
    }
    this.waitingParticipants = this.waitingParticipants.filter(w => w.identity !== identity);
    const banner = document.getElementById('waiting-room-host-banner');
    if (banner) {
      if (this.waitingParticipants.length === 0) {
        banner.remove();
      } else {
        const next = this.waitingParticipants[0];
        this.showWaitingRoomBanner(next.name, next.identity);
      }
    }
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();
    this.showFloatingToast('Participant admis în conferință.');
  }

  public async admitAllWaitingParticipants() {
    for (const wp of [...this.waitingParticipants]) {
      if (!await this.decideWaitingParticipant(wp.identity, 'admitted')) {
        this.showFloatingToast('Unele admiteri nu au fost trimise. Verifică conexiunea.');
        return;
      }
    }
    this.waitingParticipants = [];
    document.getElementById('waiting-room-host-banner')?.remove();
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();
    this.showFloatingToast('Toți participanții din sala de așteptare au fost admiși.');
  }

  public async rejectWaitingParticipant(identity: string) {
    if (!await this.decideWaitingParticipant(identity, 'rejected')) {
      this.showFloatingToast('Respingerea nu a fost trimisă. Verifică conexiunea.');
      return;
    }
    this.waitingParticipants = this.waitingParticipants.filter(w => w.identity !== identity);
    const banner = document.getElementById('waiting-room-host-banner');
    if (banner) {
      if (this.waitingParticipants.length === 0) {
        banner.remove();
      } else {
        const next = this.waitingParticipants[0];
        this.showWaitingRoomBanner(next.name, next.identity);
      }
    }
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();
    this.showFloatingToast('Acces respins.');
  }

  public async moveParticipantToWaitingRoom(identity: string) {
    if (this.participants.find(p => p.identity === identity)?.role === 'admin') { this.showFloatingToast('Administratorul nu poate fi trimis în sala de așteptare din acest meniu.'); return; }
    await this.manager.sendWaitingRoomMoveBack(identity);
    const p = this.participants.find(part => part.identity === identity);
    if (p) {
      this.waitingParticipants.push({
        identity: p.identity,
        name: p.name,
        timestamp: Date.now(),
      });
      this.participants = this.participants.filter(part => part.identity !== identity);
      this.renderVideoGrid();
      this.renderParticipantsList();
      this.showFloatingToast(`${p.name} a fost trimis în sala de așteptare.`);
    }
  }

  public async cleanup(): Promise<void> {
    document.getElementById('pre-event-share-dialog')?.remove();
    this.preEventShare?.dispose(); this.preEventShare = null;
    this.webinarPermissionDialog?.remove();
    this.webinarPermissionDialog = null;
    this.webinarPermissionQueue = [];
    (window as any).VorbelloNativeBridge?.setMeetingActive?.(false);
    (window as any).vorbelloSetNativePip?.(false);
    delete (window as any).vorbelloSetNativePip;
    if (this.pipRefreshTimer) clearInterval(this.pipRefreshTimer);
    this.pipRefreshTimer = undefined;
    if (this.pipVideo) { if (document.pictureInPictureElement === this.pipVideo) void document.exitPictureInPicture().catch(() => {}); this.pipVideo.srcObject = null; this.pipVideo.remove(); this.pipVideo = undefined; }
    this.disposeWebMeetingBack?.();
    this.disposeWebMeetingBack = undefined;
    if ((window as any).vorbelloHandleMeetingBack === handleMeetingBack) delete (window as any).vorbelloHandleMeetingBack;
    if (this.backgroundImageUrl) { URL.revokeObjectURL(this.backgroundImageUrl); this.backgroundImageUrl = undefined; }
    this.disposeControlsAutoHide?.();
    this.disposeControlsAutoHide = undefined;
    this.admissionStopped=true;
    if (this.admissionPollTimer) { clearInterval(this.admissionPollTimer); this.admissionPollTimer=null; }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    if (this.isRecording) {
      this.stopRecording();
    }
    this.stopVuMeter();
    this.handleAudioPlaybackBanner(false);
    if (this.waitingRoomKnockInterval) {
      clearInterval(this.waitingRoomKnockInterval);
      this.waitingRoomKnockInterval = null;
    }
    document.getElementById('waiting-room-lobby-overlay')?.remove();
    document.getElementById('waiting-room-host-banner')?.remove();
    if (this.localAudioMonitorInterval) {
      clearInterval(this.localAudioMonitorInterval);
      this.localAudioMonitorInterval = null;
    }
    if (this.localAudioMonitorCtx) {
      this.localAudioMonitorCtx.close().catch(() => {});
      this.localAudioMonitorCtx = null;
    }
    if (this.trackHealthInterval) {
      clearInterval(this.trackHealthInterval);
      this.trackHealthInterval = null;
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.p2pMesh) {
      try {
        this.p2pMesh.leave();
      } catch (_) {}
      this.p2pMesh = null;
    }
    try {
      await this.manager.disconnect();
    } catch (e) {
      console.warn('[ConferenceUI] Disconnect error during cleanup:', e);
    }
    // Clean up any remaining remote audio elements and global tile action dropdown
    document.querySelectorAll('audio[id^="audio-remote-"]').forEach(el => el.remove());
    if ((this as any)._globalTileClickListener) {
      document.removeEventListener('click', (this as any)._globalTileClickListener);
      (this as any)._globalTileClickListener = null;
    }
    document.getElementById('global-tile-dropdown')?.remove();
    document.getElementById('vorbello-recording-canvas')?.remove();
    document.getElementById('vorbello-rec-admin-video')?.remove();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('in-conference');
    }
  }
}
